import { sql } from "drizzle-orm";
import { pgTable, pgEnum, text, varchar, timestamp, integer, bigint, boolean, real, numeric, jsonb, primaryKey, uniqueIndex, index, check, date, customType, type AnyPgColumn } from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";
import { isValidPhoneNumber } from "libphonenumber-js";
import { paymentTermsSchema as basePaymentTermsSchema } from "./payment-terms";
import { securitiesEngagementSchema, type SecuritiesEngagement } from "./securities-engagement";
import { passwordPolicySchema } from "./password-policy";
import type { PartnerPricingQuote, ProjectPricingMenuInput } from "./partner-pricing";
import type { PricingProposalSnapshot } from "./partner-pricing-term-sheet";
import type { PitchCloseProposalSnapshot } from "./pitch-close-invite";
import type { FeeDisclosureItem } from "./partner-rebate";
import type {
  PitchAttendee,
  PitchConversion,
  PitchEngagementMirror,
  PitchInvite,
  PitchPaidLead,
  PitchPartnerSnapshot,
  PitchProgram,
} from "./pitch-session";
import type { ContactOutreachSummary } from "./contact-outreach";
import type {
  DueDiligenceSectionDef,
  DueDiligenceQuestionDef,
  DueDiligenceItemStatus,
  DueDiligencePriority,
  DueDiligenceQuestionnaireStatus,
  DueDiligenceEventKind,
} from "./due-diligence";

export type PartnerPricingEngagementSnapshot = {
  version: number;
  quote: Omit<PartnerPricingQuote, "verasCapitalAllocationCents" | "elCaminoAllocationCents">;
  menu?: ProjectPricingMenuInput;
  projectName?: string;
};

// Re-validation: empty array (use role default) or installments summing to ~100.
export const vendorPaymentTermsSchema = basePaymentTermsSchema.refine(
  (terms) => {
    if (terms.length === 0) return true;
    const total = terms.reduce((s, i) => s + (i.portionPct || 0), 0);
    return Math.abs(total - 100) < 0.01;
  },
  { message: "Payment installments must sum to 100%" },
);

export const users = pgTable("users", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  email: text("email").notNull().unique(),
  // Task #1263 — `password` column dropped. Supabase Auth is the sole
  // credential store for every portal (investor + admin + agent +
  // vendor + sponsor). The local users row links to Supabase via
  // `auth_principal_id`.
  name: text("name").notNull(),
  firstName: text("first_name"),
  lastName: text("last_name"),
  country: text("country").notNull(),
  // Task #1714 — richer EB-5 lead geography captured by brokers when
  // they self-serve a single lead in the partner portal. All optional.
  // `countryOfBirth` / `countryOfResidence` hold country codes from the
  // shared LEAD_COUNTRIES picker; `stateOfResidence` holds a US state
  // code when residence country is US, otherwise free-text region.
  countryOfBirth: text("country_of_birth"),
  countryOfResidence: text("country_of_residence"),
  cityOfResidence: text("city_of_residence"),
  stateOfResidence: text("state_of_residence"),
  role: text("role").default("user").notNull(),
  // Self-identified signup branch from the /register type picker.
  // One of: 'investor' | 'attorney' | 'broker_dealer' | 'foreign_agent'.
  // Nullable for pre-existing rows that signed up before the picker
  // existed; the matching CHECK constraint lives in migration 0145.
  userType: text("user_type"),
  accreditationStatus: text("accreditation_status").default("none").notNull(),
  // Task #1791 — lightweight one-click "I am an accredited investor"
  // acknowledgment gate in front of project detail pages. Distinct from
  // the SEC Rule 501 self-certification flow that drives
  // `accreditationStatus`: this only records that the user clicked the
  // single-checkbox acknowledgment. Source of truth for whether a
  // logged-in user has acknowledged. Nullable for pre-existing rows.
  accreditationAcknowledgedAt: timestamp("accreditation_acknowledged_at"),
  phone: text("phone"),
  pipelineStage: text("pipeline_stage").default("signed_up").notNull(),
  // Set whenever `pipelineStage` is changed (Task #708). Lets the agent
  // timeline compute "days in stage" from a persisted source of truth
  // instead of walking timeline events. Backfilled by migration
  // 0093b_stage_history.sql; new rows default to NOW() at creation
  // time so a brand-new investor in `signed_up` already has a value.
  pipelineStageEnteredAt: timestamp("pipeline_stage_entered_at").defaultNow(),
  pipelineNotes: text("pipeline_notes"),
  pipelineStageOverride: boolean("pipeline_stage_override").default(false).notNull(),
  attorneyId: varchar("attorney_id"),
  referralAgentId: varchar("referral_agent_id"),
  sourceOfFundsStatus: text("source_of_funds_status").default("not_started").notNull(),
  sourceOfFundsApprovedAt: timestamp("source_of_funds_approved_at"),
  communicationPreferences: jsonb("communication_preferences"),
  mustChangePassword: boolean("must_change_password").default(false).notNull(),
  headshot: text("headshot"),
  bio: text("bio"),
  // Task #1328 — profile job title + admin approval workflow.
  // `headshot` and `bio` already exist above; this block adds the
  // status machinery that is shared with referral_agents / attorneys
  // / project_contacts. Status: 'draft' | 'pending' | 'approved' |
  // 'rejected'. The application layer flips back to 'pending' on any
  // change to the approvable fields (headshot / job title / bio).
  jobTitle: text("job_title"),
  profileApprovalStatus: text("profile_approval_status").default("draft").notNull(),
  profileApprovedAt: timestamp("profile_approved_at"),
  profileApprovedById: varchar("profile_approved_by_id"),
  profileRejectionNote: text("profile_rejection_note"),
  profileUpdatedAt: timestamp("profile_updated_at"),
  // Task #1336 — primary-admin-controlled opt-in toggle for the public
  // /about team grid. Layered on top of the #1328 approval workflow:
  // even if a profile is admin-approved, the primary admin still has
  // to flip this on for the row to appear on the public team page.
  // Defaults to false; flipping requires the primary admin AND a
  // complete profile (jobTitle + bio + headshot).
  publicTeamApproved: boolean("public_team_approved").default(false).notNull(),
  // Task #1875 — optional LinkedIn profile URL shown on the public
  // About Us page (card/modal) for approved team members. Low-risk
  // metadata: editing it does NOT bounce the profile back to 'pending'
  // the way the approvable fields (headshot / job title / bio) do.
  linkedinUrl: text("linkedin_url"),
  // Admin personal public-link page (ImmILink-style vanity URL /:slug).
  // Claimable slug is unique when set; page is live only when
  // publicLinkEnabled is true AND the row is an undeleted admin with
  // a headshot. Social URLs below are low-risk metadata (no approval
  // bounce). publicWhatsappNumber is digits-only E.164 without '+'.
  publicSlug: text("public_slug"),
  publicLinkEnabled: boolean("public_link_enabled").default(false).notNull(),
  instagramUrl: text("instagram_url"),
  facebookUrl: text("facebook_url"),
  xUrl: text("x_url"),
  youtubeUrl: text("youtube_url"),
  tiktokUrl: text("tiktok_url"),
  publicWhatsappNumber: text("public_whatsapp_number"),
  adminDivisions: text("admin_divisions").array(),
  // Task #1156 (review round 2) — first-class division references.
  // Sits alongside the legacy text array `adminDivisions` so scope
  // helpers can prefer IDs going forward and gracefully fall back to
  // slugs for any pre-#1156 row that hasn't been backfilled.
  adminDivisionIds: varchar("admin_division_ids").array(),
  // Task #2740 — per-member configurable left-menu permissions. For a
  // division-scoped admin this holds the EXTRA sidebar menu hrefs the
  // owner has granted them beyond the default scoped set (see
  // DEFAULT_SCOPED_HREFS / GRANTABLE_MENU_ITEMS). NULL or empty array =
  // fall back to today's default scoped menu. Ignored for core admins
  // (they always see everything). For machine accounts this is the
  // exclusive menu (no default union).
  menuPermissions: text("menu_permissions").array(),
  // human (default) | machine. Machine seats are named purpose bots
  // (Grokbot QA/brand/product): exclusive menu, read-only, excluded
  // from digests and the public team page. Never a core admin.
  accountKind: text("account_kind").default("human").notNull(),
  utmSource: text("utm_source"),
  utmMedium: text("utm_medium"),
  utmCampaign: text("utm_campaign"),
  utmTerm: text("utm_term"),
  utmContent: text("utm_content"),
  referrer: text("referrer"),
  landingPage: text("landing_page"),
  leadScore: integer("lead_score"),
  leadTags: jsonb("lead_tags").$type<string[]>(),
  // Task #1004 — Lead attribution capture and confirmation workflow.
  // `attributedSource` is one of: 'veras_digital' | 'veras_inperson' |
  // 'referral_agent' | 'unknown'. `attributionStatus` lifecycle:
  // 'pending_review' → 'confirmed_agent' | 'confirmed_house' |
  // 'disputed'. `referralAgentId` is the denormalized *agent* owner
  // (My Investors / RLS); intro claims leave it null.
  // `claimedReferrerType` + `claimedReferrerId` are the single owner.
  // `payProduct` is bounty XOR commission (`none` until elected).
  attributedSource: text("attributed_source").default("unknown").notNull(),
  provisionalReferralAgentId: varchar("provisional_referral_agent_id"),
  attributionConfidence: integer("attribution_confidence").default(0).notNull(),
  attributionStatus: text("attribution_status").default("pending_review").notNull(),
  attributionCapturedAt: timestamp("attribution_captured_at"),
  attributionSlaDueAt: timestamp("attribution_sla_due_at"),
  attributionFirstTouch: jsonb("attribution_first_touch"),
  attributionLastTouch: jsonb("attribution_last_touch"),
  // Server-minted first-party visitor id (veras_vid). Not an IP address.
  visitorId: text("visitor_id"),
  newLeadScore: integer("new_lead_score"),
  claimedReferrerType: text("claimed_referrer_type"),
  claimedReferrerId: varchar("claimed_referrer_id"),
  payProduct: text("pay_product").notNull().default("none"),
  // Task #1094 — per-agent lead ownership window. `lastActivityAt` is
  // bumped by `recordLeadActivity()` on any fresh investor touchpoint
  // (login, message, booking, admin note). `leadOwnershipExpiresAt`
  // is the derived deadline (lastActivity + agent.window OR house
  // default); the sweep worker flips `confirmed_agent` rows whose
  // expiry has elapsed back to `confirmed_house` and clears the
  // referralAgentId so the lead returns to the house pool.
  lastActivityAt: timestamp("last_activity_at"),
  leadOwnershipExpiresAt: timestamp("lead_ownership_expires_at"),
  i526eReceiptNumber: text("i526e_receipt_number"),
  i526eFiledAt: timestamp("i526e_filed_at"),
  i526eStatus: text("i526e_status").default("not_filed"),
  i526eApprovedAt: timestamp("i526e_approved_at"),
  i526eNotes: text("i526e_notes"),
  conditionalResidencyAt: timestamp("conditional_residency_at"),
  i829FiledAt: timestamp("i829_filed_at"),
  permanentResidencyAt: timestamp("permanent_residency_at"),
  // Task #1200 — Supabase auth prep. Nullable stub that will hold the
  // matching `auth.users.id` after the cutover; until then RLS
  // policies that key off this column simply deny access. A partial
  // unique index (migration 0133) enforces uniqueness when set.
  authPrincipalId: varchar("auth_principal_id"),
  // Task #1230 — Investment project the user was attributed to at
  // signup, derived from the QR/short-link click that dropped the
  // attribution cookie. Optional: organic signups stay null.
  attributedProjectId: varchar("attributed_project_id"),
  // Set by the agent portal when an agent submits a deal-room access
  // request on behalf of this investor. Cleared automatically when an
  // admin grants access. A non-NULL value surfaces a "Pending" state on
  // the agent side so they cannot send duplicate requests.
  dealRoomAccessRequestedAt: timestamp("deal_room_access_requested_at"),
  // Task #2499 — I-956H "Bona Fides of Persons Involved with a Regional
  // Center Program" filing stored per admin user. Purely additive: null
  // on every existing row; no gate or requirement downstream.
  i956hFilePath: text("i956h_file_path"),
  i956hFileName: text("i956h_file_name"),
  i956hUploadedAt: timestamp("i956h_uploaded_at"),
  // Task #2852 — optional WeChat ID captured for investors/leads who
  // prefer WeChat over email. A normalized (trim + case-insensitive)
  // match is treated as a hard duplicate across contacts/leads/users.
  wechatId: text("wechat_id"),
  // CIO GAP stamps — durable product truth for Customer.io segments.
  // portalInviteSentAt is set when an invite email is actually sent
  // (not when a password_reset_tokens row is minted alone).
  portalInviteSentAt: timestamp("portal_invite_sent_at"),
  lastLoginAt: timestamp("last_login_at"),
  emailChangedAt: timestamp("email_changed_at"),
  previousEmail: text("previous_email"),
  personId: varchar("person_id"),
  createdAt: timestamp("created_at").defaultNow(),
  deletedAt: timestamp("deleted_at"),
}, (table) => ({
  // Backstop: WeChat ID is unique when present (app layer does the
  // normalized cross-table check; this guards exact same-table dupes).
  wechatIdUniqueIdx: uniqueIndex("users_wechat_id_unique_idx")
    .on(table.wechatId)
    .where(sql`wechat_id IS NOT NULL`),
}));

export const investmentProjects = pgTable("investment_projects", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  title: text("title").notNull(),
  slug: text("slug").notNull().unique(),
  partnerStandingApprovalReference: text("partner_standing_approval_reference"),
  subtitle: text("subtitle"),
  description: text("description").notNull(),
  city: text("city").notNull().default(""),
  state: text("state").notNull().default(""),
  // Captured at intake from the Google Places result's
  // `administrative_area_level_2` (with a manual fallback when the API
  // doesn't return one). Persisted on the project so the underwriting
  // workspace can pre-fill its address-related Phase 1 / type-specific
  // fields by canonical key without the underwriter re-entering it.
  county: text("county"),
  zip: text("zip"),
  // Task #1156 round 2 — FK alongside the legacy free-text `division`
  // slug. New code should write divisionId; reads still hydrate slug
  // for compat.
  divisionId: varchar("division_id"),
  location: text("location").notNull(),
  latitude: text("latitude"),
  longitude: text("longitude"),
  investmentMinimum: integer("investment_minimum").notNull(),
  investmentType: text("investment_type").notNull(),
  investmentStructure: text("investment_structure").default("debt"),
  positionType: text("position_type").default("senior"),
  category: text("category").notNull(),
  currentRaise: integer("current_raise").default(0),
  jobCreationEstimate: integer("job_creation_estimate"),
  jobCreationEstimateNotes: text("job_creation_estimate_notes"),
  jobCreationEstimateAt: timestamp("job_creation_estimate_at"),
  // Finalized job-creation figure printed in the economist's final report.
  // Once set, the early estimate is locked from further edits.
  finalReportJobs: integer("final_report_jobs"),
  finalReportFinalizedAt: timestamp("final_report_finalized_at"),
  finalReportFinalizedById: varchar("final_report_finalized_by_id"),
  // Actual jobs created at the end of the project.
  actualJobsCreated: integer("actual_jobs_created"),
  actualJobsRecordedAt: timestamp("actual_jobs_recorded_at"),
  economistId: varchar("economist_id"),
  businessPlanWriterId: varchar("business_plan_writer_id"),
  businessPlanWriterPrice: integer("business_plan_writer_price"),
  businessPlanCompletedAt: timestamp("business_plan_completed_at"),
  fundAdministratorId: varchar("fund_administrator_id"),
  fundAdministratorFee: integer("fund_administrator_fee"),
  economistFee: integer("economist_fee"),
  securitiesAttorneyId: varchar("securities_attorney_id"),
  securitiesAttorneyFee: integer("securities_attorney_fee"),
  immigrationAttorneyId: varchar("immigration_attorney_id"),
  immigrationAttorneyFee: integer("immigration_attorney_fee"),
  securitiesCompletedAt: timestamp("securities_completed_at"),
  immigrationCompletedAt: timestamp("immigration_completed_at"),
  // Per-phase engagement timestamps ("started_at"). NULL = not yet
  // engaged (term sheet not yet executed for the phase). Set on the
  // POST .../engage routes; cleared when the phase is reset.
  economistStartedAt: timestamp("economist_started_at"),
  businessPlanStartedAt: timestamp("business_plan_started_at"),
  securitiesStartedAt: timestamp("securities_started_at"),
  immigrationStartedAt: timestamp("immigration_started_at"),
  fundAdministratorStartedAt: timestamp("fund_administrator_started_at"),
  workingRegionalCenterStartedAt: timestamp("working_regional_center_started_at"),
  // Per-project turnaround overrides (in days). When non-null these
  // supersede the vendor profile turnaround in the I-956F forecast.
  economistTurnaroundDaysOverride: integer("economist_turnaround_days_override"),
  businessPlanTurnaroundDaysOverride: integer("business_plan_turnaround_days_override"),
  securitiesTurnaroundDaysOverride: integer("securities_turnaround_days_override"),
  immigrationTurnaroundDaysOverride: integer("immigration_turnaround_days_override"),
  fundAdministratorTurnaroundDaysOverride: integer("fund_administrator_turnaround_days_override"),
  workingRegionalCenterTurnaroundDaysOverride: integer("working_regional_center_turnaround_days_override"),
  // Per-project, per-vendor-role "economics not applicable" flags (Task
  // #1845). When true, the platform stops requiring a fee for that vendor,
  // excludes it from the project P&L / cost forecast, and suppresses
  // fee-drift / stale-vs-vendor warnings (replaced by an "Economics N/A"
  // indicator). The optional reason captures the audit trail. Use case: a
  // co-GP deal where vendor fees were paid before Veras came on board.
  economistEconomicsNa: boolean("economist_economics_na").notNull().default(false),
  economistEconomicsNaReason: text("economist_economics_na_reason"),
  businessPlanEconomicsNa: boolean("business_plan_economics_na").notNull().default(false),
  businessPlanEconomicsNaReason: text("business_plan_economics_na_reason"),
  securitiesEconomicsNa: boolean("securities_economics_na").notNull().default(false),
  securitiesEconomicsNaReason: text("securities_economics_na_reason"),
  immigrationEconomicsNa: boolean("immigration_economics_na").notNull().default(false),
  immigrationEconomicsNaReason: text("immigration_economics_na_reason"),
  fundAdministratorEconomicsNa: boolean("fund_administrator_economics_na").notNull().default(false),
  fundAdministratorEconomicsNaReason: text("fund_administrator_economics_na_reason"),
  workingRegionalCenterEconomicsNa: boolean("working_regional_center_economics_na").notNull().default(false),
  workingRegionalCenterEconomicsNaReason: text("working_regional_center_economics_na_reason"),
  highlights: jsonb("highlights"),
  teaserContent: text("teaser_content"),
  status: text("status").default("draft").notNull(),
  // Brief 1 — Silver and Gold partners see a first-look project while it is
  // still coming soon; Registered partners only after general release.
  firstLook: boolean("first_look").notNull().default(false),
  coverImageUrl: text("cover_image_url"),
  galleryImages: jsonb("gallery_images"),
  // ── Marketing Master Image (Task #917) ─────────────────────────────
  // Single canonical hero per project. Admin uploads a 3000×2000+ image
  // (3:2 ±2%) and the server derives every downstream marketing
  // surface (cover thumb, page header, OG cards, flyer/one-pager hero)
  // with a consistent branded treatment. Promotion-to-marketing and
  // every agent / public visibility surface are gated on
  // `marketingImageStatus === 'compliant'`. Replacing the master
  // bumps the checksum (and therefore every derivative URL), which
  // cache-busts cobranded social/QR outputs implicitly.
  marketingMasterImageUrl: text("marketing_master_image_url"),
  marketingMasterChecksum: text("marketing_master_checksum"),
  marketingMasterWidth: integer("marketing_master_width"),
  marketingMasterHeight: integer("marketing_master_height"),
  marketingMasterBytes: integer("marketing_master_bytes"),
  marketingMasterContentType: text("marketing_master_content_type"),
  // 'compliant' | 'non_compliant' | 'missing'. Hard gate for promotion
  // and for agent/public visibility.
  marketingImageStatus: text("marketing_image_status").default("missing").notNull(),
  marketingImageDerivatives: jsonb("marketing_image_derivatives"),
  marketingImageGeneratedAt: timestamp("marketing_image_generated_at"),
  // ── Hero video (optional) ───────────────────────────────────────────
  // Admin-uploaded MP4/WebM/MOV that replaces the static hero on the
  // public project landing page when present. The poster is always the
  // marketing-master `cover_header` derivative (falling back to
  // `coverImageUrl`), so the image hero stays the fallback everywhere.
  heroVideoUrl: text("hero_video_url"),
  heroVideoContentType: text("hero_video_content_type"),
  heroVideoBytes: integer("hero_video_bytes"),
  heroVideoDurationSeconds: integer("hero_video_duration_seconds"),
  heroVideoUploadedAt: timestamp("hero_video_uploaded_at"),
  // Admin publish toggle. FALSE keeps the file but hides it from every
  // public / investor / agent project payload; the image hero takes over.
  heroVideoPublished: boolean("hero_video_published").notNull().default(true),
  // ── Feature film (optional) ─────────────────────────────────────────
  // Process / story MP4 with captions or motion graphics. Renders in a
  // sticky stage below the key-facts strip — never as the landing hero
  // background (that clash is why hero_video_* stays a separate role).
  // Poster is the same cover_header / coverImageUrl fallback as hero.
  featureVideoUrl: text("feature_video_url"),
  featureVideoContentType: text("feature_video_content_type"),
  featureVideoBytes: integer("feature_video_bytes"),
  featureVideoDurationSeconds: integer("feature_video_duration_seconds"),
  featureVideoUploadedAt: timestamp("feature_video_uploaded_at"),
  // Admin publish toggle — same contract as heroVideoPublished.
  featureVideoPublished: boolean("feature_video_published").notNull().default(true),
  regionalCenterId: text("regional_center_id"),
  regionalCenterName: text("regional_center_name"),
  regionalCenterLogoUrl: text("regional_center_logo_url"),
  workingRegionalCenterId: varchar("working_regional_center_id"),
  // Per-project snapshot of the assigned RC's pricing, copied from the
  // vendor when the RC is assigned. Admins can override these values
  // per-deal without mutating the vendor's defaults. NULL = use the
  // assigned vendor's current default for that field.
  wrcInitialAffiliationFee: integer("wrc_initial_affiliation_fee"),
  wrcPerInvestorAdminFee: integer("wrc_per_investor_admin_fee"),
  wrcAnnualPerInvestorFeeBps: integer("wrc_annual_per_investor_fee_bps"),
  wrcLegalReviewReimbursement: integer("wrc_legal_review_reimbursement"),
  wrcIntegrityFundContribution: integer("wrc_integrity_fund_contribution"),
  wrcAnnualLegalFeeCap: integer("wrc_annual_legal_fee_cap"),
  // Vendor's pricing values at the moment of the last snapshot
  // (assignment or explicit refresh). Used to distinguish a stale
  // snapshot — `wrc*` matches the baseline but the vendor has since
  // moved — from a deliberate per-deal override (`wrc*` was edited
  // away from the baseline). Shape: `{ wrcInitialAffiliationFee:
  // number|null, ... }`.
  wrcPricingBaseline: jsonb("wrc_pricing_baseline"),
  // Per-project EB-5 minimum sustainment period (months). Drives the
  // SFH "Construction period meets EB-5 minimum sustainment period"
  // benchmark (#598). Default 24mo matches USCIS guidance under RIA;
  // admins can override per-deal when a project memo requires longer.
  eb5MinSustainmentMonths: integer("eb5_min_sustainment_months").notNull().default(24),
  principals: jsonb("principals"),
  division: text("division"),
  pipelineStage: text("pipeline_stage").default("qualification").notNull(),
  pipelineOwnerId: varchar("pipeline_owner_id"),
  stageEnteredAt: timestamp("stage_entered_at").defaultNow(),
  developerOrg: text("developer_org"),
  expectedTeaPathway: text("expected_tea_pathway"),
  teaQualificationStatus: text("tea_qualification_status").default("pending").notNull(),
  projectType: text("project_type"),
  totalProjectCost: integer("total_project_cost"),
  eb5RaiseTarget: integer("eb5_raise_target"),
  minInvestmentPerInvestor: integer("min_investment_per_investor"),
  dealQualifiedAt: timestamp("deal_qualified_at"),
  dealQualifiedById: varchar("deal_qualified_by_id"),
  materialConcerns: jsonb("material_concerns").$type<Array<{
    id: string;
    summary: string;
    severity: "low" | "medium" | "high";
    status: "open" | "resolved" | "dismissed";
    owner?: string | null;
    disposition?: string | null;
  }>>().default(sql`'[]'::jsonb`).notNull(),
  // Recorded actuals for the I-956F (exemplar) filing lifecycle. When set,
  // these replace the corresponding leg of the marketing-ready forecast.
  i956fFiledAt: timestamp("i956f_filed_at"),
  i956fApprovedAt: timestamp("i956f_approved_at"),
  // Securities engagement-letter state (per Torres-style engagements).
  // `securitiesOfferingCommencedAt` flips on when the offering officially
  // commences (PPM finalized & marketing live). After this point, any
  // securities-attorney work is treated as additional/hourly per the
  // engagement letter, and the SEC Form D fee installment becomes due.
  securitiesOfferingCommencedAt: timestamp("securities_offering_commenced_at"),
  // Per-project drop-dead date for the second Offering Documents installment
  // (the "earlier of working draft or this date" trigger).
  securitiesOfferingDropDeadDate: timestamp("securities_offering_drop_dead_date"),
  // Conditional engagement-letter items toggled by admins.
  securitiesFormDApplicable: boolean("securities_form_d_applicable").default(false).notNull(),
  securitiesIntercreditorApplicable: boolean("securities_intercreditor_applicable").default(false).notNull(),
  // Loan documents bundle: which collateral variant applies to this deal.
  // `pledge_security` (NCE-pledged equity), `mortgage` (real-estate),
  // or null = neither marked applicable yet.
  loanCollateralChoice: text("loan_collateral_choice"),
  // Per-lane engagement + pricing-confirm state for the two-lane Legal UI
  // (#366). Engagement letter is recorded per lane (date + uploaded PDF
  // reference into projectInternalDocuments). Pricing confirmation locks
  // the project's estimated legal fee for that lane; the confirmed amount
  // also mirrors into securities/immigration AttorneyFee so the existing
  // P&L rollup picks it up unchanged.
  securitiesEngagementDate: timestamp("securities_engagement_date"),
  securitiesEngagementLetterDocId: varchar("securities_engagement_letter_doc_id"),
  securitiesPricingConfirmedAt: timestamp("securities_pricing_confirmed_at"),
  securitiesPricingConfirmedById: varchar("securities_pricing_confirmed_by_id"),
  securitiesPricingConfirmedAmount: integer("securities_pricing_confirmed_amount"),
  immigrationEngagementDate: timestamp("immigration_engagement_date"),
  immigrationEngagementLetterDocId: varchar("immigration_engagement_letter_doc_id"),
  immigrationPricingConfirmedAt: timestamp("immigration_pricing_confirmed_at"),
  immigrationPricingConfirmedById: varchar("immigration_pricing_confirmed_by_id"),
  immigrationPricingConfirmedAmount: integer("immigration_pricing_confirmed_amount"),
  // Lost / archived off-ramp. When `lostAt` is set the project is dead and
  // is hidden from the active pipeline board, table, and stage counts. The
  // reason / notes / actor capture *why* and *who* for the archived view.
  lostAt: timestamp("lost_at"),
  lostReason: text("lost_reason"),
  lostNotes: text("lost_notes"),
  lostByUserId: varchar("lost_by_user_id"),
  // Archive off-ramp (parallel to Lost). Hides the project from the
  // active board / table / stage counts without requiring a reason.
  // Cleared when the project is restored from archive.
  archivedAt: timestamp("archived_at"),
  archivedByUserId: varchar("archived_by_user_id"),
  // Task #2371 — Room-level deal-room publish flag.
  // NULL = room not published (investors see "being prepared" placeholder).
  // Non-NULL = room published; every latest deal-room document is visible
  // to investors who pass the existing access gates (accreditation, access
  // grant, NDA). Per-document isPublic is no longer the visibility gate.
  dealRoomPublishedAt: timestamp("deal_room_published_at"),
  // Co-GP late-stage track (Task #535). When `isCoGp` is true the
  // project skips Underwriting/Term Sheet/Pre-Legal/Legal (those
  // phases are sponsor-owned) and goes straight to I-956F Filed; the
  // I-956F checklist is re-framed as sponsor-provided (per-item
  // Verified toggle without requiring a file upload); the standard
  // timeline P&L is replaced with the `coGpEconomics` blob; and
  // publishing requires an explicit Approve-for-Marketing sign-off
  // gated by the filed receipt + economics + diligence notes below.
  isCoGp: boolean("is_co_gp").default(false).notNull(),
  coGpMarketingApprovalStatus: text("co_gp_marketing_approval_status").default("pending").notNull(),
  coGpMarketingApprovedAt: timestamp("co_gp_marketing_approved_at"),
  coGpMarketingApprovedById: varchar("co_gp_marketing_approved_by_id"),
  // Canonical shape: { interestRateSpreadPct, gpFeeSharePct, notes }.
  // Legacy snapshots may still carry `irSpreadPoints` (same units, points).
  coGpEconomics: jsonb("co_gp_economics"),
  // Task #623 — "How we get paid" on the deal. Single fee-model picker
  // with conditional sub-fields stored in `dealEconomics`. Values:
  // `upfront_fee` | `ir_spread` | `co_gp_equity` | `combination`.
  feeModel: text("fee_model"),
  // Superset blob covering both Co-GP and standard deals. Canonical
  // shape: { feeModel?, upfrontFeePct?, upfrontFeeAmount?,
  //          developerInterestRatePct?, investorInterestRatePct?,
  //          interestRateSpreadPct?, gpFeeSharePct?, notes? }.
  // Legacy keys (`rateToJcePct`, `rateToInvestorsPct`, `irSpreadPoints`,
  // `spreadBps`) are still tolerated by the read shim in
  // `shared/deal-economics-rates.ts` for older snapshots, but the API
  // sanitizers only emit the canonical keys (Task #668).
  // Co-GP deals continue to mirror interestRateSpreadPct / gpFeeSharePct /
  // notes onto the legacy `coGpEconomics` column so existing Co-GP P&L and
  // diligence surfaces keep working unchanged.
  dealEconomics: jsonb("deal_economics"),
  // Task #995 — per-project marketing-mix assumptions. Drives the
  // forecast sourcing-cost rollup on the project P&L (and the
  // master P&L). Canonical shape lives in
  // shared/marketing-assumptions.ts (`MarketingAssumptions`):
  // `{ channels: [{ channel, pct, costPerInvestor }] }` covering
  // Brokers, Attorneys, Referral Agents, and Direct. NULL =
  // fall back to the 100%-Direct, blank-cost default.
  marketingAssumptions: jsonb("marketing_assumptions"),
  // Task #1078 — per-project investor flow forecast overrides (channel
  // run rates, ramp shape, subscription→funding lag). Null/missing
  // keys fall back to `marketing_house_defaults`.
  investorFlowOverrides: jsonb("investor_flow_overrides"),
  // Task #1103 — Engagement override audit. When an admin engages the
  // economist or business plan writer despite the upfront fee gate not
  // being satisfied, the override timestamp + actor + reason are stamped
  // here AND captured in pipeline activity. NULL = no override (engagement
  // proceeded normally with the fee received).
  economistEngageOverrideAt: timestamp("economist_engage_override_at"),
  economistEngageOverrideById: varchar("economist_engage_override_by_id"),
  economistEngageOverrideReason: text("economist_engage_override_reason"),
  businessPlanEngageOverrideAt: timestamp("business_plan_engage_override_at"),
  businessPlanEngageOverrideById: varchar("business_plan_engage_override_by_id"),
  businessPlanEngageOverrideReason: text("business_plan_engage_override_reason"),
  // Task #838 / #844 — the pipeline "Target marketing launch" date is now
  // auto-calculated from the I-956F filing date (filed date + 7 days)
  // by buildTargetMarketingLaunch in
  // server/routes/pipeline/timeline-forecast.ts and stamped onto API
  // responses by server/routes/pipeline/core.ts. There is no longer a
  // stored column for it; manual writes are rejected at the API.
  // Per-project notification silencer (Task #1649). When true, all
  // outbound emails to sponsor team contacts and assigned vendors are
  // suppressed. Investor-facing and admin internal emails are unaffected.
  // Default true so projects are silenced during backfill; flip to false
  // per project as each one goes live with real contacts.
  notificationsSilenced: boolean("notifications_silenced").default(true).notNull(),
  // Per-project deal-room document AI assistant. When false, accredited
  // investors cannot see or use the chatbot; admin preview still works and
  // surfaces that the bot is off. Default true so published rooms keep the
  // previously-live assistant without surprising existing users.
  dealRoomChatEnabled: boolean("deal_room_chat_enabled").default(true).notNull(),
  // Per-project deal-room longform video (20–30 min pitch). One video +
  // optional poster per project. Bytes live under deal-room/{project_id}/
  // in the private bucket; these columns are metadata only. Served only
  // through gated deal-room routes (accreditation + grant + NDA + published).
  // Investor visibility is OFF by default so uploads can sit on the admin
  // slot without accredited investors seeing them until an admin flips it on.
  dealRoomVideoFileUrl: text("deal_room_video_file_url"),
  dealRoomVideoFileName: text("deal_room_video_file_name"),
  dealRoomVideoFileSize: bigint("deal_room_video_file_size", { mode: "number" }),
  dealRoomVideoContentType: text("deal_room_video_content_type"),
  dealRoomVideoDurationSec: integer("deal_room_video_duration_sec"),
  dealRoomVideoUploadedAt: timestamp("deal_room_video_uploaded_at"),
  dealRoomVideoPosterFileUrl: text("deal_room_video_poster_file_url"),
  dealRoomVideoPosterFileName: text("deal_room_video_poster_file_name"),
  dealRoomVideoPosterContentType: text("deal_room_video_poster_content_type"),
  dealRoomVideoInvestorVisible: boolean("deal_room_video_investor_visible")
    .default(false)
    .notNull(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  deletedAt: timestamp("deleted_at"),
});

// Task #623 — "How we get paid" picker on the project create wizard.
// `combination` allows admins to record more than one stream on the same
// deal (e.g. an upfront placement fee plus an IR spread); the per-option
// sub-fields and the free-text `notes` capture the specifics.
export const FEE_MODELS = [
  "upfront_fee",
  "ir_spread",
  "co_gp_equity",
  "combination",
] as const;
export type FeeModel = (typeof FEE_MODELS)[number];

export const PIPELINE_STAGES = [
  "qualification",
  "terms",
  "package_build",
  "filing_approval",
  "approved_raise",
] as const;
export type PipelineStage = (typeof PIPELINE_STAGES)[number];

const LEGACY_PIPELINE_STAGE_MAP: Record<string, PipelineStage> = {
  intake: "qualification",
  underwriting: "qualification",
  term_sheet: "terms",
  pre_legal: "package_build",
  legal: "filing_approval",
  i956f_filed: "filing_approval",
};

export function normalizePipelineStage(stage: string | null | undefined): PipelineStage {
  if (stage && (PIPELINE_STAGES as readonly string[]).includes(stage)) {
    return stage as PipelineStage;
  }
  return (stage && LEGACY_PIPELINE_STAGE_MAP[stage]) || "qualification";
}

// ---------------------------------------------------------------------------
// Project types (admin-managed taxonomy)
// ---------------------------------------------------------------------------
export const PROJECT_TYPES = [
  { value: "real_estate_sfh_development", label: "Real Estate \u2014 SFH Development", division: "real_estate" },
  { value: "energy_natural_gas", label: "Energy \u2014 Natural Gas", division: "energy" },
] as const;
export type ProjectType = (typeof PROJECT_TYPES)[number]["value"];
export const PROJECT_TYPE_VALUES: readonly ProjectType[] =
  PROJECT_TYPES.map(p => p.value) as readonly ProjectType[];

export type StageAdvanceCheck = {
  jobCreationEstimate?: number | null;
  economistId?: string | null;
  pipelineStage?: string | null;
  businessPlanWriterId?: string | null;
  businessPlanWriterPrice?: number | null;
  // When true the business plan writer's economics are "not applicable" for
  // this deal (Task #1845), so the price gate below is waived.
  businessPlanEconomicsNa?: boolean | null;
  businessPlanCompletedAt?: Date | string | null;
  businessPlanFinalDraftUploaded?: boolean;
  securitiesAttorneyId?: string | null;
  securitiesCompletedAt?: Date | string | null;
  securitiesLoanAgreementUploaded?: boolean;
  immigrationAttorneyId?: string | null;
  immigrationCompletedAt?: Date | string | null;
  immigrationChecklistComplete?: boolean;
  termSheetSignedAt?: Date | string | null;
  termSheetExecutedDocumentId?: string | null;
  // Intake gate fields
  developerOrg?: string | null;
  location?: string | null;
  division?: string | null;
  expectedTeaPathway?: string | null;
  contactCount?: number;
  pipelineOwnerId?: string | null;
  title?: string | null;
  totalProjectCost?: number | null;
  eb5RaiseTarget?: number | null;
  investmentMinimum?: number | null;
  investmentStructure?: string | null;
  positionType?: string | null;
  feeModel?: string | null;
  dealEconomics?: unknown;
  materialConcerns?: Array<{ severity?: string; status?: string }> | null;
  regionalCenterName?: string | null;
  hasNce?: boolean;
  hasJce?: boolean;
  economicReportUploaded?: boolean;
  i956fApprovedAt?: Date | string | null;
  dealQualifiedAt?: Date | string | null;
  projectType?: string | null;
  // Co-GP late-stage projects (Task #535) skip Underwriting / Term Sheet /
  // Pre-Legal / Legal — those phases are sponsor-owned. The only forward
  // gate left is the I-956F readiness checklist applied route-side.
  isCoGp?: boolean | null;
};

export const TERM_SHEET_EXECUTED_DOC_CATEGORY = "term_sheet_executed";

export const TERM_SHEET_STRUCTURES = ["upfront_fees", "rate_spread", "hybrid"] as const;
export type TermSheetStructure = (typeof TERM_SHEET_STRUCTURES)[number];

export const TERM_SHEET_STATUSES = [
  "not_started",
  "drafting",
  "sent",
  "negotiating",
  "signed",
  "declined",
] as const;
export type TermSheetStatus = (typeof TERM_SHEET_STATUSES)[number];

export const TERM_SHEET_PAYMENT_TRIGGERS = [
  "execution_of_term_sheet",
  "i956f_filed",
  "i956f_approved",
  "first_close",
  "custom",
] as const;
export type TermSheetPaymentTrigger = (typeof TERM_SHEET_PAYMENT_TRIGGERS)[number];

// Securities checklist groups define how items render in the legal panel
// and which fee bundle on the attorney's engagement letter satisfies them.
//   - core      : project-formation prerequisites (no engagement-letter fee).
//   - offering  : offering documents bundle (Offering Documents Fee).
//   - loan      : loan documents bundle (Loan Documents Fee). Items keyed
//                 `loan_pledge_security`, `loan_mortgage`, `loan_ucc1` are
//                 collateral-variant rows: their applicability depends on
//                 `loanCollateralChoice` on the project.
//   - conditional: only required when an admin marks the engagement-level
//                 condition applicable (Form D, Intercreditor).
export type SecuritiesChecklistGroup = "core" | "offering" | "loan" | "conditional";

export const SECURITIES_CHECKLIST_ITEMS: ReadonlyArray<{
  key: string;
  label: string;
  docCategory: string | null;
  group: SecuritiesChecklistGroup;
  // Project-state key that gates applicability for conditional / variant items.
  applicabilityKey?: "form_d" | "intercreditor" | "loan_pledge" | "loan_mortgage";
}> = [
  { key: "entity_formed", label: "Entity formed", docCategory: null, group: "core" },
  { key: "ein_obtained", label: "EIN obtained", docCategory: null, group: "core" },
  // Offering Documents bundle.
  { key: "operating_agreement", label: "Operating agreement", docCategory: "operating_agreement", group: "offering" },
  { key: "subscription_agreement", label: "Subscription agreement", docCategory: "subscription_agreement", group: "offering" },
  { key: "ppm", label: "PPM / offering documents", docCategory: "ppm_offering", group: "offering" },
  { key: "escrow_agreement", label: "Escrow agreement", docCategory: "escrow_agreement", group: "offering" },
  // Loan Documents bundle (NCE \u2194 JCE).
  { key: "loan_loan_agreement", label: "Loan agreement (NCE \u2194 JCE)", docCategory: "loan_agreement", group: "loan" },
  { key: "loan_promissory_note", label: "Promissory note", docCategory: "promissory_note", group: "loan" },
  { key: "loan_pledge_security", label: "Pledge & security agreement", docCategory: "pledge_security_agreement", group: "loan", applicabilityKey: "loan_pledge" },
  { key: "loan_mortgage", label: "Mortgage / deed of trust", docCategory: "mortgage_deed_of_trust", group: "loan", applicabilityKey: "loan_mortgage" },
  { key: "loan_ucc1", label: "UCC-1 financing statements", docCategory: "ucc1_filings", group: "loan" },
  // Conditional fee items.
  { key: "sec_form_d", label: "SEC Form D filing", docCategory: "sec_form_d", group: "conditional", applicabilityKey: "form_d" },
  { key: "intercreditor_agreement", label: "Intercreditor agreement", docCategory: "intercreditor_agreement", group: "conditional", applicabilityKey: "intercreditor" },
] as const;

export const SECURITIES_CHECKLIST_GROUP_LABEL: Record<SecuritiesChecklistGroup, string> = {
  core: "Entity / formation",
  offering: "Offering Documents",
  loan: "Loan Documents",
  conditional: "Conditional",
};

export const IMMIGRATION_CHECKLIST_ITEMS = [
  { key: "matter_of_ho_business_plan", label: "Matter of Ho compliant business plan received", docCategory: "business_plan_doc" },
  { key: "economic_report_received", label: "Economic report received", docCategory: "econ_impact_report" },
  { key: "tea_evidence", label: "TEA evidence assembled", docCategory: "tea_evidence" },
  { key: "source_of_funds_template", label: "Source-of-funds template prepared", docCategory: "source_of_funds_template" },
  { key: "nce_jce_org_docs", label: "NCE / JCE organizational documents", docCategory: "nce_jce_org_docs" },
  { key: "i956f_exhibits", label: "I-956F exhibits assembled", docCategory: "i956f_exhibits" },
] as const;

// ---------------------------------------------------------------------------
// Legal document exchange (Step 4 of the two-lane Legal UI, #366)
// ---------------------------------------------------------------------------
// Each lane (securities, immigration) has a predefined exchange checklist of
// documents we send and they send. Admins can add freeform "Other documents"
// rows on top. Document exchange is tracked-only — the actual files still
// move over email.
export const LEGAL_DOC_EXCHANGE_LANES = ["securities", "immigration"] as const;
export type LegalDocExchangeLane = (typeof LEGAL_DOC_EXCHANGE_LANES)[number];

export const LEGAL_DOC_EXCHANGE_DIRECTIONS = ["we_send", "they_send"] as const;
export type LegalDocExchangeDirection = (typeof LEGAL_DOC_EXCHANGE_DIRECTIONS)[number];

export const LEGAL_DOC_EXCHANGE_STATUSES = ["not_sent", "sent", "received"] as const;
export type LegalDocExchangeStatus = (typeof LEGAL_DOC_EXCHANGE_STATUSES)[number];

export type LegalDocExchangeDef = {
  key: string;
  label: string;
  direction: LegalDocExchangeDirection;
  docCategory?: string | null;
};

export const SECURITIES_DOC_EXCHANGE_ITEMS: ReadonlyArray<LegalDocExchangeDef> = [
  // We send to securities counsel
  { key: "sec_we_term_sheet", label: "Term sheet", direction: "we_send", docCategory: "term_sheet_executed" },
  { key: "sec_we_business_plan", label: "Business plan", direction: "we_send", docCategory: "business_plan_doc" },
  { key: "sec_we_economist_report", label: "Economist report", direction: "we_send", docCategory: "econ_impact_report" },
  { key: "sec_we_loan_term_sheet", label: "Loan term sheet (NCE \u2194 JCE)", direction: "we_send", docCategory: "loan_agreement" },
  // They send back
  { key: "sec_they_ppm", label: "PPM", direction: "they_send", docCategory: "ppm_offering" },
  { key: "sec_they_subscription_agreement", label: "Subscription agreement", direction: "they_send", docCategory: "subscription_agreement" },
  { key: "sec_they_operating_agreement", label: "Operating agreement", direction: "they_send", docCategory: "operating_agreement" },
  { key: "sec_they_escrow_agreement", label: "Escrow agreement", direction: "they_send", docCategory: "escrow_agreement" },
  { key: "sec_they_loan_docs", label: "Loan documents (loan agreement, note, security)", direction: "they_send", docCategory: "loan_agreement" },
] as const;

export const IMMIGRATION_DOC_EXCHANGE_ITEMS: ReadonlyArray<LegalDocExchangeDef> = [
  // We send to immigration counsel
  { key: "imm_we_business_plan", label: "Business plan", direction: "we_send", docCategory: "business_plan_doc" },
  { key: "imm_we_economist_report", label: "Economist report", direction: "we_send", docCategory: "econ_impact_report" },
  { key: "imm_we_tea_evidence", label: "TEA evidence", direction: "we_send", docCategory: "tea_evidence" },
  { key: "imm_we_org_docs", label: "Organizational docs (NCE / JCE)", direction: "we_send", docCategory: "nce_jce_org_docs" },
  // They send back
  { key: "imm_they_i956f_exhibits", label: "I-956F exhibits", direction: "they_send", docCategory: "i956f_exhibits" },
  { key: "imm_they_sof_template", label: "Source-of-funds template", direction: "they_send", docCategory: "source_of_funds_template" },
  { key: "imm_they_filing_package", label: "Filing-ready package", direction: "they_send", docCategory: "i956f_exhibits" },
] as const;

export function getLegalDocExchangeDefs(lane: LegalDocExchangeLane): ReadonlyArray<LegalDocExchangeDef> {
  return lane === "securities" ? SECURITIES_DOC_EXCHANGE_ITEMS : IMMIGRATION_DOC_EXCHANGE_ITEMS;
}

// ============================================================================
// I-956F Filing Readiness catalog
// ============================================================================
// Mirrors the attorney-supplied checklist (see
// attached_assets/Documentation_Requirements_for_Form_I-956F_V3_*.pdf).
// Each entry is a stable row in the filing-readiness checklist UI. Items
// flagged `repeatsPerEntity` render once per filing entity (e.g. each JCE
// in the chain). Items flagged `allowsMultipleFiles` accept more than one
// uploaded document under the same row.
export const I956F_SECTIONS = [
  { key: "regional_center", label: "Regional Center Documents" },
  { key: "nce", label: "NCE Documents" },
  { key: "nce_manager", label: "NCE Manager Documents", repeatsPerEntity: true, entityKind: "nce_manager" as const, requiresAtLeastOne: false },
  { key: "jce", label: "JCE Documents", repeatsPerEntity: true, entityKind: "jce" as const, requiresAtLeastOne: true },
  { key: "investment_offering", label: "Investment & Offering Documents" },
  { key: "business_plan_econ", label: "Business Plan & Economic Reports" },
  { key: "supporting", label: "Supporting Documents" },
  { key: "fund_admin", label: "Fund Administration" },
  { key: "marketing", label: "Marketing Materials" },
  { key: "promoters", label: "Promoter Information" },
  { key: "sec_litigation", label: "SEC, Litigation, and Bankruptcy" },
] as const;
export type I956FSectionKey = (typeof I956F_SECTIONS)[number]["key"];

export const FILING_ENTITY_KINDS = ["jce", "nce_manager"] as const;
export type FilingEntityKind = (typeof FILING_ENTITY_KINDS)[number];

export interface I956FRequirement {
  key: string;
  section: I956FSectionKey;
  label: string;
  applicability: "required" | "conditional";
  condition?: string;
  allowsMultipleFiles?: boolean;
  repeatsPerEntity?: boolean;
  entityKind?: FilingEntityKind;
  docCategory?: string;
}

export const I956F_REQUIREMENTS: readonly I956FRequirement[] = [
  // Regional Center
  // The actual I-956F submission package (the large PDF bundle) sent to USCIS.
  // Distinct category from i956f_exhibits (immigration-exchange) and i956f_receipt
  // so it does not collide with either. Placed first so it appears above the
  // receipt notice in the Filing summary card (Filed → Approved order).
  { key: "rc_i956f_filing_package", section: "regional_center", label: "I-956F filing package (complete submission PDF sent to USCIS)", applicability: "required", docCategory: "i956f_filing_package" },
  // Form I-797 receipt notice USCIS issues after the I-956F is filed.
  // This is the actual *proof* the filing happened — the date column on
  // the project (i956fFiledAt) is just an anchor and can be set without
  // having seen anything. The Co-GP marketing-approval gate (Task #535)
  // requires this row to be verified (Co-GP semantics) or have a file
  // attached so admins can't approve a project sight-unseen.
  { key: "rc_i956f_receipt", section: "regional_center", label: "I-956F receipt notice (Form I-797 from USCIS confirming the filing)", applicability: "required", docCategory: "i956f_receipt" },
  // Form I-797 approval notice USCIS issues when the I-956F is approved.
  // This is the proof the project is approved — it anchors marketing windows,
  // term-sheet timing, and fee spreads. The approval date lives alongside this
  // row on the I-956F tab so date and proof stay co-located for admins.
  { key: "rc_i956f_approval_notice", section: "regional_center", label: "I-956F approval notice (Form I-797 from USCIS confirming approval)", applicability: "required", docCategory: "i956f_approval_notice" },
  { key: "rc_designation_letter", section: "regional_center", label: "Regional Center designation letter (and any amendments)", applicability: "required", docCategory: "rc_designation" },
  { key: "rc_formation_docs", section: "regional_center", label: "Regional Center formation docs (incl. name change & trade name certs)", applicability: "required", docCategory: "rc_formation" },
  { key: "rc_fein", section: "regional_center", label: "Regional Center FEIN documentation (e.g. Form SS-4)", applicability: "required", docCategory: "rc_fein" },
  { key: "rc_nce_mou", section: "regional_center", label: "MOU / Oversight Agreement between Regional Center and NCE", applicability: "required", docCategory: "rc_nce_mou" },
  { key: "rc_policies_manual", section: "regional_center", label: "Regional Center policies & procedures manual", applicability: "required", docCategory: "rc_policies" },

  // NCE
  { key: "nce_formation_docs", section: "nce", label: "NCE formation documents (incl. name change & trade name certs)", applicability: "required", docCategory: "nce_jce_org_docs" },
  { key: "nce_fein", section: "nce", label: "NCE FEIN documentation (e.g. Form SS-4)", applicability: "required", docCategory: "nce_fein" },
  { key: "nce_bank_account", section: "nce", label: "NCE bank account info (segregated investor account: bank, account #, routing #)", applicability: "required", docCategory: "nce_bank" },
  { key: "nce_jce_contracts", section: "nce", label: "Contracts between NCE and JCE (loan agreements, etc.)", applicability: "required", allowsMultipleFiles: true, docCategory: "loan_agreement" },
  { key: "nce_job_allocation", section: "nce", label: "Job Allocation Agreement", applicability: "conditional", condition: "If applicable", docCategory: "job_allocation" },

  // NCE Manager (repeats per entity — only enforced if a manager entity is added)
  { key: "nce_mgr_formation", section: "nce_manager", label: "NCE Manager formation documents", applicability: "required", repeatsPerEntity: true, entityKind: "nce_manager", docCategory: "nce_jce_org_docs" },
  { key: "nce_mgr_fein", section: "nce_manager", label: "NCE Manager FEIN documentation", applicability: "required", repeatsPerEntity: true, entityKind: "nce_manager", docCategory: "nce_fein" },
  { key: "nce_mgr_ownership", section: "nce_manager", label: "NCE Manager ownership info (up to natural persons)", applicability: "required", repeatsPerEntity: true, entityKind: "nce_manager", docCategory: "nce_jce_org_docs" },

  // JCE (repeats per entity)
  { key: "jce_formation_docs", section: "jce", label: "JCE formation documents (incl. name change & trade name certs)", applicability: "required", repeatsPerEntity: true, docCategory: "nce_jce_org_docs" },
  { key: "jce_fein", section: "jce", label: "JCE FEIN documentation (e.g. Form SS-4)", applicability: "required", repeatsPerEntity: true, docCategory: "nce_fein" },
  { key: "jce_operating_agreement", section: "jce", label: "JCE operating agreement / LP agreement (incl. amendments & supplements)", applicability: "required", repeatsPerEntity: true, docCategory: "operating_agreement" },
  { key: "jce_mgr_formation", section: "jce", label: "JCE Manager formation documents", applicability: "required", repeatsPerEntity: true, docCategory: "nce_jce_org_docs" },
  { key: "jce_mgr_fein", section: "jce", label: "JCE Manager FEIN documentation", applicability: "required", repeatsPerEntity: true, docCategory: "nce_fein" },
  { key: "jce_org_chart", section: "jce", label: "JCE & JCE Manager org chart (ownership to natural persons)", applicability: "required", repeatsPerEntity: true, docCategory: "org_chart" },

  // Investment & Offering
  { key: "io_ppm", section: "investment_offering", label: "Private Placement Memorandum (PPM)", applicability: "required", docCategory: "ppm_offering" },
  { key: "io_operating_agreement", section: "investment_offering", label: "Investment / Partnership / Operating Agreement", applicability: "required", docCategory: "operating_agreement" },
  { key: "io_subscription", section: "investment_offering", label: "Subscription Agreement", applicability: "required", docCategory: "subscription_agreement" },
  { key: "io_term_sheets", section: "investment_offering", label: "Term Sheets", applicability: "required", allowsMultipleFiles: true, docCategory: "term_sheet_executed" },
  { key: "io_escrow_agreement", section: "investment_offering", label: "Escrow Agreement", applicability: "conditional", condition: "If applicable", docCategory: "escrow_agreement" },

  // Business Plan & Economic Reports
  { key: "bp_business_plan", section: "business_plan_econ", label: "Comprehensive Business Plan (budget, timeline, org chart, capital stack, competitor analysis)", applicability: "required", docCategory: "business_plan_doc" },
  { key: "bp_economic_report", section: "business_plan_econ", label: "Economic Impact Report (job creation analysis)", applicability: "required", docCategory: "econ_impact_report" },
  { key: "bp_tea_analysis", section: "business_plan_econ", label: "TEA analysis (separate or part of econ report)", applicability: "required", docCategory: "tea_evidence" },
  { key: "bp_support_letters", section: "business_plan_econ", label: "Support letters (community, political, economic development, industry)", applicability: "conditional", condition: "If applicable", allowsMultipleFiles: true, docCategory: "support_letters" },
  { key: "bp_management_bios", section: "business_plan_econ", label: "Biographies of management, officers, directors", applicability: "required", allowsMultipleFiles: true, docCategory: "bios" },

  // Supporting
  { key: "sup_loan_agreement", section: "supporting", label: "EB-5 Loan Agreement", applicability: "required", docCategory: "loan_agreement" },
  { key: "sup_land_evidence", section: "supporting", label: "Evidence of land ownership or control", applicability: "required", docCategory: "land_evidence" },
  { key: "sup_capital_stack", section: "supporting", label: "Evidence of capital stack availability", applicability: "required", docCategory: "capital_stack" },
  { key: "sup_bank_financing", section: "supporting", label: "Evidence for all bank financing sources and developer equity", applicability: "required", allowsMultipleFiles: true, docCategory: "bank_financing" },
  { key: "sup_budget_schedule", section: "supporting", label: "Evidence to support budget and schedule", applicability: "required", docCategory: "budget_evidence" },
  { key: "sup_feasibility_study", section: "supporting", label: "Feasibility study, appraisal, market study, or similar", applicability: "required", docCategory: "feasibility" },
  { key: "sup_construction_docs", section: "supporting", label: "Construction / design documents (approvals, permits, zoning, contracts)", applicability: "required", allowsMultipleFiles: true, docCategory: "construction_docs" },
  { key: "sup_construction_budget", section: "supporting", label: "Detailed construction budget and timeline", applicability: "required", docCategory: "construction_budget" },
  { key: "sup_third_party_agreements", section: "supporting", label: "Third party agreements, including LOIs", applicability: "conditional", condition: "If any", allowsMultipleFiles: true, docCategory: "third_party_agreements" },
  { key: "sup_bridge_financing", section: "supporting", label: "Bridge financing documents", applicability: "conditional", condition: "If any", allowsMultipleFiles: true, docCategory: "bridge_financing" },
  { key: "sup_site_photos", section: "supporting", label: "Photos of the site", applicability: "required", allowsMultipleFiles: true, docCategory: "site_photos" },

  // Fund Administration
  { key: "fa_segregated_accounts", section: "fund_admin", label: "Per-investor segregated accounts (incl. escrow) at NCE", applicability: "conditional", condition: "If applicable", docCategory: "fund_admin" },
  { key: "fa_transfer_controls", section: "fund_admin", label: "Transfer controls (only to JCE separate account / project / investor)", applicability: "conditional", condition: "If applicable", docCategory: "fund_admin" },
  { key: "fa_jce_separate_account", section: "fund_admin", label: "JCE separate account until project deployment", applicability: "conditional", condition: "If applicable", docCategory: "fund_admin" },
  { key: "fa_deployment_notice", section: "fund_admin", label: "30-day deployment notice & verification process", applicability: "conditional", condition: "If applicable", docCategory: "fund_admin" },
  { key: "fa_administrator_engagement", section: "fund_admin", label: "Fund administrator engagement (third-party CPA / atty / BD / IA)", applicability: "conditional", condition: "If applicable, unless waiver", docCategory: "fund_admin" },
  { key: "fa_waiver", section: "fund_admin", label: "Waiver of administrator (broker-dealer or annual audit)", applicability: "conditional", condition: "If using waiver", docCategory: "fund_admin" },

  // Marketing
  { key: "mk_marketing_materials", section: "marketing", label: "Copies of marketing materials (or drafts)", applicability: "conditional", condition: "If available", allowsMultipleFiles: true, docCategory: "marketing_materials" },
  { key: "mk_promotional_website", section: "marketing", label: "Promotional materials / website", applicability: "conditional", condition: "If available", allowsMultipleFiles: true, docCategory: "marketing_materials" },

  // Promoters
  { key: "pr_fee_documentation", section: "promoters", label: "Documentation of fees paid/payable to agents, finders, broker-dealers", applicability: "conditional", condition: "If available at filing", docCategory: "promoter_fees" },
  { key: "pr_sample_contracts", section: "promoters", label: "Sample agent contracts (USCIS-registered, securities-compliant)", applicability: "conditional", condition: "If available at filing", allowsMultipleFiles: true, docCategory: "agent_contracts" },
  { key: "pr_disclosure_form", section: "promoters", label: "Agent / broker disclosure form", applicability: "conditional", condition: "If available at filing", docCategory: "agent_disclosure" },
  { key: "pr_contact_info", section: "promoters", label: "Name & contact info of any such person", applicability: "conditional", condition: "If known at filing", docCategory: "promoter_contact" },

  // SEC, Litigation, Bankruptcy
  { key: "sl_sec_filings", section: "sec_litigation", label: "Securities Act / state securities filings", applicability: "conditional", condition: "If applicable", allowsMultipleFiles: true, docCategory: "sec_filings" },
  { key: "sl_litigation_bankruptcy", section: "sec_litigation", label: "Pending material litigation or bankruptcy info (10-year lookback)", applicability: "conditional", condition: "If applicable", docCategory: "litigation" },
  { key: "sl_investment_risks", section: "sec_litigation", label: "Material investment risks for NCE and JCE", applicability: "conditional", condition: "If applicable", docCategory: "investment_risks" },
  { key: "sl_conflicts_of_interest", section: "sec_litigation", label: "Conflicts of interest among RC, NCE, JCE, principals, attorneys, promoters", applicability: "conditional", condition: "If applicable", docCategory: "conflicts" },
];

export const I956F_DOC_CATEGORIES = Array.from(
  new Set(I956F_REQUIREMENTS.map(r => r.docCategory).filter((c): c is string => !!c)),
);
export const I956F_ROLE = "i956f" as const;

export const LEGAL_ROLES = ["securities", "immigration"] as const;
export type LegalRole = (typeof LEGAL_ROLES)[number];

export function getStageAdvanceUnmet(
  project: StageAdvanceCheck,
  toStage: string,
): string[] {
  const unmet: string[] = [];
  const fromStage = normalizePipelineStage(project.pipelineStage);
  const targetStage = normalizePipelineStage(toStage);
  const fromIdx = PIPELINE_STAGES.indexOf(fromStage);
  const toIdx = PIPELINE_STAGES.indexOf(targetStage);
  const isForward = toIdx > fromIdx;
  if (!isForward) return unmet;

  if (project.isCoGp && targetStage !== "approved_raise") return unmet;

  const qualificationIdx = PIPELINE_STAGES.indexOf("qualification");
  const termsIdx = PIPELINE_STAGES.indexOf("terms");
  const packageIdx = PIPELINE_STAGES.indexOf("package_build");
  const filingIdx = PIPELINE_STAGES.indexOf("filing_approval");

  if (!project.isCoGp && !project.dealQualifiedAt) unmet.push("Mark the deal qualified");

  if (fromIdx <= qualificationIdx && toIdx > qualificationIdx) {
    if (!project.pipelineOwnerId) unmet.push("Assign the Veras deal owner");
    if (!project.developerOrg || !project.developerOrg.trim()) {
      unmet.push("Set the developer entity");
    }
    if (!project.title || !project.title.trim()) unmet.push("Set the project name");
    if (!project.location || !project.location.trim()) {
      unmet.push("Set the project location");
    }
    if (!project.division || !project.division.trim()) {
      unmet.push("Pick the deal type");
    }
    if (!project.expectedTeaPathway || !project.expectedTeaPathway.trim()) {
      unmet.push("Pick the expected TEA pathway");
    }
    if (!project.contactCount || project.contactCount < 1) {
      unmet.push("Add a primary sponsor contact");
    }
    if (!project.totalProjectCost || project.totalProjectCost <= 0) unmet.push("Set total project cost");
    if (!project.eb5RaiseTarget || project.eb5RaiseTarget <= 0) unmet.push("Set the EB-5 raise");
    if (!project.investmentMinimum || project.investmentMinimum <= 0) unmet.push("Set the minimum investment");
    if (!project.investmentStructure) unmet.push("Set the investment structure");
    if (!project.positionType) unmet.push("Set the investment position");
    if (!project.jobCreationEstimate && !project.economistId) unmet.push("Add a job estimate or engage an economist");
    if (!project.regionalCenterName?.trim()) unmet.push("Assign the Regional Center");
    if (!project.hasNce) unmet.push("Assign the NCE");
    if (!project.hasJce) unmet.push("Assign at least one JCE");
    if (!project.feeModel || !project.dealEconomics) unmet.push("Record how Veras gets paid");
    const openHighConcern = (project.materialConcerns || []).some(
      (concern) => concern?.severity === "high" && concern?.status === "open",
    );
    if (openHighConcern) unmet.push("Resolve open high-severity material concerns");
  }

  if (fromIdx <= termsIdx && toIdx > termsIdx) {
    if (!project.termSheetSignedAt) {
      unmet.push("Record the executed term sheet date");
    }
    if (!project.termSheetExecutedDocumentId) {
      unmet.push("Upload the final executed term sheet document");
    }
  }

  if (fromIdx <= packageIdx && toIdx > packageIdx) {
    if (!project.businessPlanFinalDraftUploaded) {
      unmet.push("Upload the final business plan");
    }
    if (!project.economicReportUploaded) unmet.push("Upload the final economic study");
    if (!project.hasNce) unmet.push("Assign the NCE");
    if (!project.hasJce) unmet.push("Assign at least one JCE");
  }

  if (fromIdx <= filingIdx && toIdx > filingIdx && !project.i956fApprovedAt) {
    unmet.push("Record I-956F approval");
  }
  return unmet;
}

export const projectContacts = pgTable("project_contacts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  projectId: varchar("project_id").notNull().references(() => investmentProjects.id, { onDelete: "cascade" }),
  name: text("name").notNull(),
  firstName: text("first_name"),
  lastName: text("last_name"),
  role: text("role"),
  organization: text("organization"),
  email: text("email"),
  phone: text("phone"),
  // Section A — optional link to a project legal party (NCE / Manager / JCE / Holding).
  entityId: varchar("entity_id"),
  entityRole: text("entity_role"),
  isPrimary: boolean("is_primary").default(false).notNull(),
  notes: text("notes"),
  // Task #1328 — public-facing profile fields + admin approval flow.
  headshotUrl: text("headshot_url"),
  jobTitle: text("job_title"),
  bio: text("bio"),
  // Task #1884 — optional public LinkedIn profile URL, surfaced on the
  // (future) public project-contact card alongside the headshot / bio.
  linkedinUrl: text("linkedin_url"),
  profileApprovalStatus: text("profile_approval_status").default("draft").notNull(),
  profileApprovedAt: timestamp("profile_approved_at"),
  profileApprovedById: varchar("profile_approved_by_id"),
  profileRejectionNote: text("profile_rejection_note"),
  profileUpdatedAt: timestamp("profile_updated_at"),
  // Task #2473 — Principal portal access columns.
  authPrincipalId: varchar("auth_principal_id"),
  portalAccessStatus: text("portal_access_status").default("not_granted").notNull(),
  portalInvitedAt: timestamp("portal_invited_at"),
  portalSetupToken: varchar("portal_setup_token"),
  portalSetupTokenExpiresAt: timestamp("portal_setup_token_expires_at"),
  // Task #2487 — stamped on every successful principal portal login so admins
  // can see who's actually engaged vs. ignoring the invite.
  portalLastSeenAt: timestamp("portal_last_seen_at"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const projectInternalDocuments = pgTable("project_internal_documents", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  projectId: varchar("project_id").notNull().references(() => investmentProjects.id, { onDelete: "cascade" }),
  title: text("title").notNull(),
  category: text("category").notNull(),
  fileName: text("file_name").notNull(),
  fileUrl: text("file_url").notNull(),
  fileSize: integer("file_size"),
  notes: text("notes"),
  uploadedBy: varchar("uploaded_by"),
  uploadedAt: timestamp("uploaded_at").defaultNow(),
  // Optional link to an I-956F filing-readiness checklist row.
  // When set, this doc is treated as evidence for that requirement.
  linkedChecklistItemId: varchar("linked_checklist_item_id"),
  // Optional workflow phase tag (e.g. "business_plan"). Used to disambiguate
  // documents that share a category across phases — most notably the
  // "engagement_letter" category, which is reused by securities, immigration,
  // and business-plan engagements on the same project (#425).
  relatedPhase: text("related_phase"),
  /** Brief 1 B7 — clears one American Energy SOURCE_GAPS row when attached. */
  clearedFinancialSourceGap: text("cleared_financial_source_gap"),
});

// Many-to-many: a single document can satisfy multiple checklist rows
// (e.g. an I-956F requirement and the matching securities checklist
// item). `linkedChecklistItemId` above remains the "primary" link for
// backwards compatibility; reads consult this join table for the full
// set of associations.
export const projectChecklistDocumentLinks = pgTable("project_checklist_document_links", {
  documentId: varchar("document_id").notNull().references(() => projectInternalDocuments.id, { onDelete: "cascade" }),
  checklistItemId: varchar("checklist_item_id").notNull().references(() => projectLegalChecklistItems.id, { onDelete: "cascade" }),
  createdAt: timestamp("created_at").defaultNow(),
}, (t) => ({
  pk: primaryKey({ columns: [t.documentId, t.checklistItemId] }),
}));

export const projectFilingEntities = pgTable("project_filing_entities", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  projectId: varchar("project_id").notNull().references(() => investmentProjects.id, { onDelete: "cascade" }),
  kind: text("kind").notNull(),
  label: text("label").notNull(),
  sortOrder: integer("sort_order").default(0).notNull(),
  notes: text("notes"),
  createdAt: timestamp("created_at").defaultNow(),
});
export type ProjectFilingEntity = typeof projectFilingEntities.$inferSelect;
export const insertProjectFilingEntitySchema = createInsertSchema(projectFilingEntities).omit({ id: true, createdAt: true });
export type InsertProjectFilingEntity = z.infer<typeof insertProjectFilingEntitySchema>;

export const projectWorkstreams = pgTable("project_workstreams", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  projectId: varchar("project_id").notNull().references(() => investmentProjects.id, { onDelete: "cascade" }),
  kind: text("kind").notNull(),
  title: text("title").notNull(),
  vendor: text("vendor"),
  status: text("status").default("not_started").notNull(),
  dueAt: timestamp("due_at"),
  completedAt: timestamp("completed_at"),
  notes: text("notes"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const projectPipelineNotes = pgTable("project_pipeline_notes", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  projectId: varchar("project_id").notNull().references(() => investmentProjects.id, { onDelete: "cascade" }),
  authorId: varchar("author_id"),
  authorName: text("author_name"),
  body: text("body").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
});

// Task #1134 — Thin record. The structured term-sheet editor / Word
// generator / drift snapshot / underwriting mirror were all removed; the
// only thing this row tracks now is the executed document, when it was
// signed/locked, and a freeform signer note. Upfront-fee A/R lifecycle
// (offering payments + receipts) still hangs off this row's id.
export const projectTermSheet = pgTable("project_term_sheet", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  projectId: varchar("project_id").notNull().references(() => investmentProjects.id, { onDelete: "cascade" }).unique(),
  status: text("status").default("not_started").notNull(),
  signedAt: timestamp("signed_at"),
  lockedAt: timestamp("locked_at"),
  executedDocumentId: varchar("executed_document_id"),
  signerNote: text("signer_note"),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const projectTermSheetOfferingPayments = pgTable("project_term_sheet_offering_payments", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  termSheetId: varchar("term_sheet_id").notNull().references(() => projectTermSheet.id, { onDelete: "cascade" }),
  projectId: varchar("project_id").notNull().references(() => investmentProjects.id, { onDelete: "cascade" }),
  label: text("label").notNull(),
  amount: integer("amount").notNull(),
  triggerType: text("trigger_type").default("custom").notNull(),
  triggerDescription: text("trigger_description"),
  daysAfterTrigger: integer("days_after_trigger").default(0),
  position: integer("position").default(0).notNull(),
  paidAt: timestamp("paid_at"),
  // Task #1103 — Upfront fee A/R lifecycle. `paymentStatus` is the
  // operator-set top-line status; receipts (below) are the source of
  // truth for actually-received dollars.
  // Allowed: 'not_due' | 'due' | 'invoiced' | 'received' | 'past_due' | 'waived'.
  paymentStatus: text("payment_status").notNull().default("not_due"),
  // 'economist' | 'business_plan' | NULL — links this fee row to the
  // corresponding vendor engagement gate. NULL = no gate.
  gatingRole: text("gating_role"),
  dueAt: timestamp("due_at"),
  waivedAt: timestamp("waived_at"),
  waivedById: varchar("waived_by_id"),
  waivedReason: text("waived_reason"),
  createdAt: timestamp("created_at").defaultNow(),
});

// Task #1103 — partial / multi-receipt history for an upfront fee row.
// One fee row can have many receipts (wire today, second wire next week).
// Sum of non-voided receipt amounts is the row's "received to date".
export const projectTermSheetOfferingPaymentReceipts = pgTable(
  "project_term_sheet_offering_payment_receipts",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    paymentId: varchar("payment_id").notNull()
      .references(() => projectTermSheetOfferingPayments.id, { onDelete: "cascade" }),
    projectId: varchar("project_id").notNull()
      .references(() => investmentProjects.id, { onDelete: "cascade" }),
    amount: integer("amount").notNull(),
    receivedAt: timestamp("received_at").notNull(),
    method: text("method"),
    reference: text("reference"),
    note: text("note"),
    receiptDocId: varchar("receipt_doc_id"),
    recordedById: varchar("recorded_by_id"),
    recordedByName: text("recorded_by_name"),
    recordedAt: timestamp("recorded_at").defaultNow(),
    voidedAt: timestamp("voided_at"),
    voidedById: varchar("voided_by_id"),
    voidedReason: text("voided_reason"),
  },
);
export const PAYMENT_STATUSES = ["not_due", "due", "invoiced", "received", "past_due", "waived"] as const;
export type PaymentStatus = typeof PAYMENT_STATUSES[number];
export const PAYMENT_GATING_ROLES = ["economist", "business_plan"] as const;
export type PaymentGatingRole = typeof PAYMENT_GATING_ROLES[number];

export const projectJobCreationEstimates = pgTable("project_job_creation_estimates", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  projectId: varchar("project_id").notNull().references(() => investmentProjects.id, { onDelete: "cascade" }),
  jobs: integer("jobs").notNull(),
  economistId: varchar("economist_id"),
  notes: text("notes"),
  recordedAt: timestamp("recorded_at").defaultNow().notNull(),
  recordedBy: varchar("recorded_by"),
});

export const projectPipelineActivity = pgTable("project_pipeline_activity", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  projectId: varchar("project_id").notNull().references(() => investmentProjects.id, { onDelete: "cascade" }),
  actorId: varchar("actor_id"),
  actorName: text("actor_name"),
  action: text("action").notNull(),
  fromStage: text("from_stage"),
  toStage: text("to_stage"),
  metadata: jsonb("metadata"),
  createdAt: timestamp("created_at").defaultNow(),
});

// Top-level audit row for hard-deleted pipeline projects. Survives the
// project's deletion (no FK on project_id) so we keep a permanent record
// of who deleted what and when even after every related row is gone.
export const pipelineProjectDeletions = pgTable("pipeline_project_deletions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  projectId: varchar("project_id").notNull(),
  projectTitle: text("project_title").notNull(),
  projectSlug: text("project_slug"),
  division: text("division"),
  pipelineStage: text("pipeline_stage"),
  actorId: varchar("actor_id"),
  actorName: text("actor_name"),
  metadata: jsonb("metadata"),
  deletedAt: timestamp("deleted_at").defaultNow(),
});
export type PipelineProjectDeletion = typeof pipelineProjectDeletions.$inferSelect;

export const projectStoryChapters = pgTable("project_story_chapters", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  projectId: varchar("project_id").notNull().references(() => investmentProjects.id, { onDelete: "cascade" }),
  slug: text("slug").notNull(),
  label: text("label").notNull(),
  sortOrder: integer("sort_order").notNull().default(0),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => ({
  projectSlugUnique: uniqueIndex("project_story_chapters_project_slug_uniq")
    .on(table.projectId, table.slug),
  projectOrderIdx: index("project_story_chapters_project_order_idx")
    .on(table.projectId, table.sortOrder, table.createdAt),
}));

export const projectPhotos = pgTable("project_photos", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  projectId: varchar("project_id").notNull().references(() => investmentProjects.id, { onDelete: "cascade" }),
  uploadedBy: varchar("uploaded_by"),
  originalFileUrl: text("original_file_url").notNull(),
  originalFileName: text("original_file_name").notNull(),
  originalContentType: text("original_content_type").notNull(),
  originalFileSize: integer("original_file_size"),
  viewableFileUrl: text("viewable_file_url").notNull(),
  viewableContentType: text("viewable_content_type").notNull().default("image/jpeg"),
  viewableFileSize: integer("viewable_file_size"),
  thumbnailFileUrl: text("thumbnail_file_url"),
  thumbnailContentType: text("thumbnail_content_type"),
  thumbnailFileSize: integer("thumbnail_file_size"),
  width: integer("width"),
  height: integer("height"),
  caption: text("caption"),
  intendedUseTags: text("intended_use_tags").array().notNull().default(sql`ARRAY[]::text[]`),
  // Task #1378 — per-photo "show on website" flag and ordering. When
  // true, the photo appears in the public project detail gallery
  // (behind login). `displayOrder` controls the sequence (lower first;
  // ties broken by createdAt).
  visibleOnWebsite: boolean("visible_on_website").notNull().default(false),
  displayOrder: integer("display_order").notNull().default(0),
  // Editorial metadata for the canonical, cross-audience project story.
  // Focal points are percentages used with CSS object-position, preserving
  // the original and derivatives while supporting multiple responsive crops.
  storyChapter: text("story_chapter").notNull().default("why"),
  storyOrder: integer("story_order").notNull().default(0),
  altText: text("alt_text"),
  focalPointX: integer("focal_point_x").notNull().default(50),
  focalPointY: integer("focal_point_y").notNull().default(50),
  isStoryCover: boolean("is_story_cover").notNull().default(false),
  createdAt: timestamp("created_at").defaultNow(),
});

export type ProjectContact = typeof projectContacts.$inferSelect;
export type ProjectInternalDocument = typeof projectInternalDocuments.$inferSelect;
export type ProjectStoryChapterRecord = typeof projectStoryChapters.$inferSelect;
export type ProjectPhoto = typeof projectPhotos.$inferSelect;
export const insertProjectPhotoSchema = createInsertSchema(projectPhotos).omit({ id: true, createdAt: true });
export type InsertProjectPhoto = z.infer<typeof insertProjectPhotoSchema>;
export type ProjectWorkstream = typeof projectWorkstreams.$inferSelect;
export type ProjectPipelineNote = typeof projectPipelineNotes.$inferSelect;
export type ProjectTermSheet = typeof projectTermSheet.$inferSelect;
export type ProjectTermSheetOfferingPayment = typeof projectTermSheetOfferingPayments.$inferSelect;
export type ProjectPipelineActivity = typeof projectPipelineActivity.$inferSelect;
export type ProjectJobCreationEstimate = typeof projectJobCreationEstimates.$inferSelect;
export const insertProjectJobCreationEstimateSchema = createInsertSchema(projectJobCreationEstimates).omit({ id: true, recordedAt: true });
export type InsertProjectJobCreationEstimate = z.infer<typeof insertProjectJobCreationEstimateSchema>;

export const insertProjectContactSchema = createInsertSchema(projectContacts).omit({ id: true, createdAt: true });
export type InsertProjectContact = z.infer<typeof insertProjectContactSchema>;
export const insertProjectInternalDocumentSchema = createInsertSchema(projectInternalDocuments).omit({ id: true, uploadedAt: true });
export type InsertProjectInternalDocument = z.infer<typeof insertProjectInternalDocumentSchema>;
export const insertProjectWorkstreamSchema = createInsertSchema(projectWorkstreams).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertProjectWorkstream = z.infer<typeof insertProjectWorkstreamSchema>;
export const insertProjectPipelineNoteSchema = createInsertSchema(projectPipelineNotes).omit({ id: true, createdAt: true });
export type InsertProjectPipelineNote = z.infer<typeof insertProjectPipelineNoteSchema>;
export const insertProjectTermSheetSchema = createInsertSchema(projectTermSheet).omit({ id: true, updatedAt: true });
export type InsertProjectTermSheet = z.infer<typeof insertProjectTermSheetSchema>;
export const insertProjectTermSheetOfferingPaymentSchema = createInsertSchema(projectTermSheetOfferingPayments).omit({ id: true, createdAt: true });
export type InsertProjectTermSheetOfferingPayment = z.infer<typeof insertProjectTermSheetOfferingPaymentSchema>;
// Task #1103
export type ProjectTermSheetOfferingPaymentReceipt = typeof projectTermSheetOfferingPaymentReceipts.$inferSelect;
export const insertProjectTermSheetOfferingPaymentReceiptSchema = createInsertSchema(projectTermSheetOfferingPaymentReceipts).omit({ id: true, recordedAt: true });
export type InsertProjectTermSheetOfferingPaymentReceipt = z.infer<typeof insertProjectTermSheetOfferingPaymentReceiptSchema>;
export const insertProjectPipelineActivitySchema = createInsertSchema(projectPipelineActivity).omit({ id: true, createdAt: true });
export type InsertProjectPipelineActivity = z.infer<typeof insertProjectPipelineActivitySchema>;

export const accreditationDocuments = pgTable("accreditation_documents", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  documentType: text("document_type").notNull(),
  fileName: text("file_name").notNull(),
  fileUrl: text("file_url").notNull(),
  contentType: text("content_type"),
  uploadedAt: timestamp("uploaded_at").defaultNow(),
  reviewedAt: timestamp("reviewed_at"),
  reviewedBy: varchar("reviewed_by"),
  status: text("status").default("pending").notNull(),
  notes: text("notes"),
});

export const MEETING_TYPES = [
  "pitch_call",
  "consult",
  "sponsor_checkin",
  "internal_review",
  "other",
] as const;
export type MeetingType = (typeof MEETING_TYPES)[number];

export const MEETING_MODES = ["video", "in_person"] as const;
export type MeetingMode = (typeof MEETING_MODES)[number];

export const callBookings = pgTable("call_bookings", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  investorId: varchar("investor_id").references(() => users.id, { onDelete: "cascade" }),
  // Partner-firm subject for admin-scheduled partner calls. Exactly one of
  // investorId / referralAgentId / contactId is set (enforced in migration 0321).
  referralAgentId: varchar("referral_agent_id").references(() => referralAgents.id, { onDelete: "set null" }),
  // CRM contact subject when the person is not yet an investor or partner seat.
  contactId: varchar("contact_id").references((): AnyPgColumn => contacts.id, { onDelete: "set null" }),
  projectId: varchar("project_id").references(() => investmentProjects.id, { onDelete: "set null" }),
  meetingType: text("meeting_type").default("pitch_call").notNull(),
  hostUserIds: jsonb("host_user_ids").$type<string[]>().default([]).notNull(),
  participantUserIds: jsonb("participant_user_ids").$type<string[]>().default([]).notNull(),
  title: text("title"),
  meetingNotes: text("meeting_notes"),
  status: text("status").default("scheduled").notNull(),
  investorNotes: text("investor_notes"),
  adminNotes: text("admin_notes"),
  scheduledAt: timestamp("scheduled_at"),
  googleCalendarEventId: text("google_calendar_event_id"),
  investorEmail: text("investor_email"),
  investorName: text("investor_name"),
  investorTimezone: text("investor_timezone"),
  meetingDuration: integer("meeting_duration").default(30),
  cancelledAt: timestamp("cancelled_at"),
  cancellationReason: text("cancellation_reason"),
  // Task #888 — populated by the reschedule route so the timeline
  // drawer can emit a distinct call_rescheduled event (in addition
  // to the activity-feed row written by agent_investor_activity).
  rescheduledAt: timestamp("rescheduled_at"),
  // Task #1402 — self-serve booking fields.
  discussionNote: text("discussion_note"),
  // Task #1628 — investor qualifiers captured by the public booking
  // flow, stored as the raw enum value (e.g. "0_6_months",
  // "have_docs") so downstream consumers can filter/report on them
  // without string-parsing the discussion note.
  investorTimeline: text("investor_timeline"),
  sourceOfFundsReadiness: text("source_of_funds_readiness"),
  guestEmails: text("guest_emails").array(),
  outcome: text("outcome"),
  followUpRequired: boolean("follow_up_required").default(false),
  followUpNotes: text("follow_up_notes"),
  followUpDueDate: timestamp("follow_up_due_date"),
  followUpCompletedAt: timestamp("follow_up_completed_at"),
  completedAt: timestamp("completed_at"),
  division: text("division"),
  // In-person soft-hold: status='held' until accreditation converts the
  // row into a real Google Calendar booking (or the TTL expires).
  holdExpiresAt: timestamp("hold_expires_at"),
  meetingMode: text("meeting_mode").default("video").notNull(),
  locationCity: text("location_city"),
  locationCountry: text("location_country"),
  inPersonWindowId: varchar("in_person_window_id"),
  createdAt: timestamp("created_at").defaultNow(),
});

/** Admin-configured travel windows offered as in-person consultation slots. */
export const inPersonMeetingWindows = pgTable("in_person_meeting_windows", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  hostAdminId: varchar("host_admin_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  city: text("city").notNull(),
  country: text("country").notNull(),
  startsAt: timestamp("starts_at").notNull(),
  endsAt: timestamp("ends_at").notNull(),
  timezone: text("timezone").default("America/New_York").notNull(),
  isActive: boolean("is_active").default(true).notNull(),
  createdByAdminId: varchar("created_by_admin_id").references(() => users.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const adminCalendarSettings = pgTable("admin_calendar_settings", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  adminId: varchar("admin_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  calendarEmail: text("calendar_email").notNull(),
  timezone: text("timezone").default("America/New_York").notNull(),
  workingHoursStart: text("working_hours_start").default("09:00").notNull(),
  workingHoursEnd: text("working_hours_end").default("17:00").notNull(),
  workingDays: jsonb("working_days").default([1, 2, 3, 4, 5]).notNull(),
  meetingDuration: integer("meeting_duration").default(30).notNull(),
  bufferMinutes: integer("buffer_minutes").default(15).notNull(),
  isRequired: boolean("is_required").default(false).notNull(),
  isActive: boolean("is_active").default(true).notNull(),
  // Default optional guests on every pitch_call (investor consult) invite —
  // e.g. Craig — without requiring a per-booking hand add. Unioned across
  // active rows at book/reschedule time.
  defaultGuestEmails: text("default_guest_emails").array().default([]).notNull(),
  googleAccessToken: text("google_access_token"),
  googleRefreshToken: text("google_refresh_token"),
  googleTokenExpiry: timestamp("google_token_expiry"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const meetingReminders = pgTable("meeting_reminders", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  bookingId: varchar("booking_id").notNull().references(() => callBookings.id, { onDelete: "cascade" }),
  reminderType: text("reminder_type").notNull(),
  scheduledFor: timestamp("scheduled_for").notNull(),
  sentAt: timestamp("sent_at"),
  status: text("status").default("pending").notNull(),
});

export const dealRoomAccess = pgTable("deal_room_access", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  projectId: varchar("project_id").notNull().references(() => investmentProjects.id, { onDelete: "cascade" }),
  grantedBy: varchar("granted_by").references(() => users.id, { onDelete: "set null" }),
  // Task #2027 — when a referral-agent seat grants access (rather than an
  // admin), the granting principal is an `agent_users` row, not a `users`
  // row, so `grantedBy` can't hold it. This nullable column records which
  // agent seat granted the access for audit. Exactly one of `grantedBy` /
  // `grantedByAgentUserId` is set for a grant (both null = legacy rows).
  grantedByAgentUserId: varchar("granted_by_agent_user_id").references(() => agentUsers.id, { onDelete: "set null" }),
  grantedAt: timestamp("granted_at").defaultNow(),
  // First time the granted investor actually loaded the deal room
  // (GET /api/deal-room/:id/access). Not stamped from chat or document opens.
  viewedAt: timestamp("viewed_at"),
  revokedAt: timestamp("revoked_at"),
  status: text("status").default("active").notNull(),
});

export const dealRoomDocuments = pgTable("deal_room_documents", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  projectId: varchar("project_id").notNull().references(() => investmentProjects.id, { onDelete: "cascade" }),
  title: text("title").notNull(),
  description: text("description"),
  documentType: text("document_type").notNull(),
  fileName: text("file_name").notNull(),
  fileUrl: text("file_url").notNull(),
  // Publication/visibility rows point back to the canonical internal
  // artifact instead of requiring an independently uploaded copy.
  canonicalInternalDocumentId: varchar("canonical_internal_document_id")
    .references(() => projectInternalDocuments.id, { onDelete: "set null" }),
  fileSize: integer("file_size"),
  sortOrder: integer("sort_order").default(0),
  isPublic: boolean("is_public").default(true),
  version: integer("version").default(1).notNull(),
  previousVersionId: varchar("previous_version_id"),
  effectiveDate: timestamp("effective_date"),
  expiresAt: timestamp("expires_at"),
  isLatest: boolean("is_latest").default(true).notNull(),
  uploadedAt: timestamp("uploaded_at").defaultNow(),
  // Task #1557 — Bare Minimum Investor Pack. When set, this document
  // fills one of the 9 fixed pack slots (PPM, Subscription, LPA,
  // I-956F Approval, Escrow, Economic Report, Business Plan,
  // One-Pager, Pitch Deck). NULL means the document is a non-pack
  // upload that lands in the investor "Other" bucket.
  slotKey: text("slot_key"),
  // Task #1557 — Admin who uploaded this version. Surfaced in the
  // investor-pack panel so reviewers can see who attached each slot
  // without cross-referencing the activity log.
  uploadedById: varchar("uploaded_by_id").references(() => users.id, { onDelete: "set null" }),
});

// Task #1557 — Per-project "mark slot N/A with reason" record. All 9 pack
// slots are required for promotion; "required" is satisfied either by
// uploading the document OR by marking the slot N/A here with a reason
// (the escape hatch for offerings that legitimately don't carry one).
export const projectInvestorPackNa = pgTable("project_investor_pack_na", {
  projectId: varchar("project_id").notNull().references(() => investmentProjects.id, { onDelete: "cascade" }),
  slotKey: text("slot_key").notNull(),
  reason: text("reason").notNull(),
  markedAt: timestamp("marked_at").defaultNow().notNull(),
  markedBy: varchar("marked_by").references(() => users.id, { onDelete: "set null" }),
});

// Single source of truth for the 9-slot pack. Slot order drives both
// the admin panel and the investor-facing deal-room ordering.
//
// Every slot is REQUIRED — the Bare Minimum Investor Pack is the
// closing kit an EB-5 investor needs to evaluate and subscribe, and
// promoting a project to marketing demands all nine accounted for.
// "Accounted for" means uploaded OR explicitly marked N/A with a
// reason (e.g. no escrow because funds release on subscription). The
// N/A path is the operator's escape hatch for offerings that legitimately
// don't carry one of these documents; the reason is logged in project
// activity. I-956F Approval has `suppressExpiry` because the I-797
// approval notice does not expire and exposing an expiry date would
// mislead the investor surface.
export const INVESTOR_PACK_SLOTS = [
  { key: "ppm", label: "PPM (Private Placement Memorandum)", required: true, suppressExpiry: false },
  { key: "subscription", label: "Subscription Agreement & Booklet", required: true, suppressExpiry: false },
  { key: "lpa", label: "Limited Partnership Agreement (LPA)", required: true, suppressExpiry: false },
  { key: "i956f_approval", label: "I-956F Approval Notice", required: true, suppressExpiry: true },
  { key: "escrow", label: "Executed Escrow Agreement", required: true, suppressExpiry: false },
  { key: "economic_report", label: "Economic Impact Report", required: true, suppressExpiry: false },
  { key: "business_plan", label: "Business Plan", required: true, suppressExpiry: false },
  { key: "one_pager", label: "One-Pager", required: true, suppressExpiry: false },
  { key: "pitch_deck", label: "Pitch Deck / Investor Presentation", required: true, suppressExpiry: false },
] as const;
export type InvestorPackSlotKey = typeof INVESTOR_PACK_SLOTS[number]["key"];
export const INVESTOR_PACK_SLOT_KEYS: ReadonlyArray<InvestorPackSlotKey> =
  INVESTOR_PACK_SLOTS.map(s => s.key);
export const INVESTOR_PACK_REQUIRED_KEYS: ReadonlyArray<InvestorPackSlotKey> =
  INVESTOR_PACK_SLOTS.filter(s => s.required).map(s => s.key);
export const INVESTOR_PACK_LABELS: Record<string, string> = Object.fromEntries(
  INVESTOR_PACK_SLOTS.map(s => [s.key, s.label]),
);

/** pgvector(1536) for deal-room RAG embeddings (OpenAI text-embedding-3-small). */
const embeddingVector1536 = customType<{ data: number[]; driverData: string }>({
  dataType() {
    return "vector(1536)";
  },
  toDriver(value: number[]): string {
    return `[${value.join(",")}]`;
  },
  fromDriver(value: unknown): number[] {
    if (Array.isArray(value)) return value.map(Number);
    if (typeof value === "string") {
      const trimmed = value.replace(/^\[/, "").replace(/\]$/, "").trim();
      if (!trimmed) return [];
      return trimmed.split(",").map((v) => Number(v.trim()));
    }
    return [];
  },
});

export const dealRoomDocChunks = pgTable("deal_room_doc_chunks", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  projectId: varchar("project_id").notNull().references(() => investmentProjects.id, { onDelete: "cascade" }),
  documentId: varchar("document_id").notNull().references(() => dealRoomDocuments.id, { onDelete: "cascade" }),
  documentVersion: integer("document_version").default(1).notNull(),
  slotKey: text("slot_key"),
  documentTitle: text("document_title").notNull(),
  page: integer("page"),
  section: text("section"),
  chunkIndex: integer("chunk_index").default(0).notNull(),
  content: text("content").notNull(),
  contentHash: text("content_hash").notNull(),
  embedding: embeddingVector1536("embedding"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const dealRoomChatThreads = pgTable("deal_room_chat_threads", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  projectId: varchar("project_id").notNull().references(() => investmentProjects.id, { onDelete: "cascade" }),
  userId: varchar("user_id").references(() => users.id, { onDelete: "cascade" }),
  agentUserId: varchar("agent_user_id").references(() => agentUsers.id, { onDelete: "cascade" }),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  userProjectUniq: uniqueIndex("deal_room_chat_threads_user_project_uniq").on(table.userId, table.projectId),
  agentProjectUniq: uniqueIndex("deal_room_chat_threads_agent_project_uniq").on(table.agentUserId, table.projectId),
}));

export const dealRoomChatMessages = pgTable("deal_room_chat_messages", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  threadId: varchar("thread_id").notNull().references(() => dealRoomChatThreads.id, { onDelete: "cascade" }),
  projectId: varchar("project_id").notNull().references(() => investmentProjects.id, { onDelete: "cascade" }),
  userId: varchar("user_id").references(() => users.id, { onDelete: "cascade" }),
  agentUserId: varchar("agent_user_id").references(() => agentUsers.id, { onDelete: "cascade" }),
  role: text("role").notNull(),
  content: text("content").notNull(),
  citations: jsonb("citations"),
  retrievedChunkIds: jsonb("retrieved_chunk_ids"),
  refusalReason: text("refusal_reason"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const dealRoomChatMessageFeedback = pgTable("deal_room_chat_message_feedback", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  messageId: varchar("message_id").notNull().references(() => dealRoomChatMessages.id, { onDelete: "cascade" }),
  projectId: varchar("project_id").notNull().references(() => investmentProjects.id, { onDelete: "cascade" }),
  rating: text("rating"),
  feedbackNote: text("feedback_note"),
  reviewedByAdminId: varchar("reviewed_by_admin_id").notNull().references(() => users.id, { onDelete: "restrict" }),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  messageUniq: uniqueIndex("deal_room_chat_message_feedback_message_id_key").on(table.messageId),
  projectUpdatedIdx: index("deal_room_chat_message_feedback_project_idx").on(table.projectId, table.updatedAt.desc()),
}));

export const marketingPages = pgTable("marketing_pages", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  language: text("language").notNull(),
  languageCode: text("language_code").notNull(),
  slug: text("slug").notNull().unique(),
  heroTitle: text("hero_title").notNull(),
  heroSubtitle: text("hero_subtitle"),
  bodyContent: jsonb("body_content"),
  ctaText: text("cta_text"),
  ctaUrl: text("cta_url"),
  strategy: text("strategy"),
  isActive: boolean("is_active").default(true),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

/**
 * Daily first-party public page views. Path only — no PII.
 * Incremented by POST /api/page-views; read by Admin → Pages.
 */
export const publicPageViewDaily = pgTable(
  "public_page_view_daily",
  {
    path: text("path").notNull(),
    day: date("day").notNull(),
    views: integer("views").notNull().default(0),
  },
  (t) => ({
    pk: primaryKey({ columns: [t.path, t.day] }),
  }),
);

export type PublicPageViewDaily = typeof publicPageViewDaily.$inferSelect;

/** Visitor-level page event. Written only after an analytics consent grant. */
export const pageEvents = pgTable("page_events", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  occurredAt: timestamp("occurred_at", { withTimezone: true }).notNull().defaultNow(),
  visitorUuid: varchar("visitor_uuid").notNull(),
  sessionId: varchar("session_id"),
  landingPath: text("landing_path").notNull(),
  referrer: text("referrer"),
  utmSource: text("utm_source"),
  utmMedium: text("utm_medium"),
  utmCampaign: text("utm_campaign"),
  utmTerm: text("utm_term"),
  utmContent: text("utm_content"),
  sourceClass: text("source_class").notNull(),
  ruleVersion: text("rule_version").notNull(),
});

export type PageEvent = typeof pageEvents.$inferSelect;

/**
 * Daily Search Console export. Aggregate query and page rows only.
 * Written later by the search-export loader; migration 0437 inserts nothing.
 */
export const gscSearchDaily = pgTable(
  "gsc_search_daily",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    day: date("day").notNull(),
    property: text("property").notNull(),
    searchType: text("search_type").notNull(),
    query: text("query").notNull().default(""),
    page: text("page").notNull().default(""),
    country: text("country").notNull().default(""),
    device: text("device").notNull().default(""),
    searchAppearance: text("search_appearance").notNull().default(""),
    clicks: integer("clicks").notNull(),
    impressions: integer("impressions").notNull(),
    ctr: real("ctr").notNull(),
    position: real("position").notNull(),
    dataState: text("data_state"),
    isIncomplete: boolean("is_incomplete").notNull().default(false),
    sourceRowHash: text("source_row_hash").notNull(),
    loadedAt: timestamp("loaded_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => ({
    hashUniq: uniqueIndex("gsc_search_daily_hash_uniq").on(t.sourceRowHash),
    dayPropertyIdx: index("gsc_search_daily_day_property_idx").on(t.day, t.property),
    metricsChk: check(
      "gsc_search_daily_metrics_chk",
      sql`clicks >= 0 AND impressions >= 0 AND ctr >= 0 AND position >= 0`,
    ),
  }),
);

export type GscSearchDaily = typeof gscSearchDaily.$inferSelect;

/** Immutable record of a public URL changed by a deploy or content publish. */
export const siteChangeLog = pgTable(
  "site_change_log",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    pageUrl: text("page_url").notNull(),
    changedAt: timestamp("changed_at", { withTimezone: true }).notNull(),
    sourceKind: text("source_kind").notNull(),
    sourceRef: text("source_ref").notNull(),
    summary: text("summary"),
    recordedAt: timestamp("recorded_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => ({
    pageSourceUniq: uniqueIndex("site_change_log_page_source_uniq").on(t.pageUrl, t.sourceKind, t.sourceRef),
    pageChangedIdx: index("site_change_log_page_changed_idx").on(t.pageUrl, t.changedAt),
    pageUrlChk: check("site_change_log_page_url_chk", sql`char_length(btrim(${t.pageUrl})) > 0`),
    sourceKindChk: check("site_change_log_source_kind_chk", sql`${t.sourceKind} IN ('deploy', 'content_publish')`),
    sourceRefChk: check("site_change_log_source_ref_chk", sql`char_length(btrim(${t.sourceRef})) > 0`),
  }),
);

export type SiteChangeLog = typeof siteChangeLog.$inferSelect;

/** Daily GA4 traffic report. Aggregate sessions only, not raw events. */
export const ga4TrafficDaily = pgTable(
  "ga4_traffic_daily",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    day: date("day").notNull(),
    propertyId: text("property_id").notNull(),
    source: text("source").notNull().default(""),
    medium: text("medium").notNull().default(""),
    campaign: text("campaign").notNull().default(""),
    defaultChannelGroup: text("default_channel_group").notNull().default(""),
    landingPage: text("landing_page").notNull().default(""),
    device: text("device").notNull().default(""),
    country: text("country").notNull().default(""),
    sessions: integer("sessions").notNull(),
    engagedSessions: integer("engaged_sessions").notNull(),
    users: integer("users").notNull(),
    newUsers: integer("new_users").notNull(),
    engagementTimeMsec: bigint("engagement_time_msec", { mode: "number" }).notNull(),
    keyEvents: integer("key_events").notNull(),
    sourceRowHash: text("source_row_hash").notNull(),
    loadedAt: timestamp("loaded_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => ({
    hashUniq: uniqueIndex("ga4_traffic_daily_hash_uniq").on(t.sourceRowHash),
    dayPropertyIdx: index("ga4_traffic_daily_day_property_idx").on(t.day, t.propertyId),
  }),
);

export type Ga4TrafficDaily = typeof ga4TrafficDaily.$inferSelect;

/** Daily Bing Webmaster export. Aggregate query and page rows only. */
export const bingSearchDaily = pgTable(
  "bing_search_daily",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    day: date("day").notNull(),
    siteUrl: text("site_url").notNull(),
    query: text("query").notNull().default(""),
    page: text("page").notNull().default(""),
    country: text("country").notNull().default(""),
    device: text("device").notNull().default(""),
    clicks: integer("clicks").notNull(),
    impressions: integer("impressions").notNull(),
    avgClickPosition: real("avg_click_position"),
    avgImpressionPosition: real("avg_impression_position"),
    sourceRowHash: text("source_row_hash").notNull(),
    loadedAt: timestamp("loaded_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => ({
    hashUniq: uniqueIndex("bing_search_daily_hash_uniq").on(t.sourceRowHash),
    daySiteIdx: index("bing_search_daily_day_site_idx").on(t.day, t.siteUrl),
  }),
);

export type BingSearchDaily = typeof bingSearchDaily.$inferSelect;

/** IndexNow submit log. Dry runs insert a row and do not call the network. */
export const indexnowSubmissions = pgTable(
  "indexnow_submissions",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    url: text("url").notNull(),
    submittedAt: timestamp("submitted_at", { withTimezone: true }).notNull().defaultNow(),
    trigger: text("trigger").notNull(),
    mode: text("mode").notNull(),
    httpStatus: integer("http_status"),
    runId: varchar("run_id").notNull(),
    outcome: text("outcome").notNull(),
    detail: text("detail"),
    contentDate: date("content_date"),
  },
  (t) => ({
    urlSubmittedIdx: index("indexnow_submissions_url_submitted_idx").on(t.url, t.submittedAt),
    urlChk: check("indexnow_submissions_url_chk", sql`char_length(btrim(url)) > 0`),
    triggerChk: check("indexnow_submissions_trigger_chk", sql`trigger IN ('publish', 'update', 'manual')`),
    modeChk: check("indexnow_submissions_mode_chk", sql`mode IN ('dry_run', 'live')`),
    outcomeChk: check("indexnow_submissions_outcome_chk", sql`outcome IN ('submitted', 'skipped', 'refused')`),
    statusChk: check("indexnow_submissions_status_chk", sql`http_status IS NULL OR http_status BETWEEN 100 AND 599`),
    detailChk: check("indexnow_submissions_detail_chk", sql`detail IS NULL OR char_length(detail) <= 500`),
  }),
);

export type IndexnowSubmission = typeof indexnowSubmissions.$inferSelect;

/** One coverage cell per URL, index, and ISO week. */
export const indexCoverageObservations = pgTable(
  "index_coverage_observations",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    url: text("url").notNull(),
    indexName: text("index_name").notNull(),
    weekStart: date("week_start").notNull(),
    observedAt: timestamp("observed_at", { withTimezone: true }).notNull().defaultNow(),
    result: text("result").notNull(),
    sourceLabel: text("source_label").notNull(),
    sourceDetail: text("source_detail").notNull(),
    runId: varchar("run_id").notNull(),
  },
  (t) => ({
    urlIndexWeekUniq: uniqueIndex("index_coverage_observations_url_index_week_uniq").on(
      t.url,
      t.indexName,
      t.weekStart,
    ),
    urlChk: check("index_coverage_observations_url_chk", sql`char_length(btrim(url)) > 0`),
    indexChk: check(
      "index_coverage_observations_index_chk",
      sql`index_name IN ('google', 'bing', 'brave')`,
    ),
    resultChk: check(
      "index_coverage_observations_result_chk",
      sql`result IN ('present', 'absent', 'unknown')`,
    ),
    labelChk: check(
      "index_coverage_observations_label_chk",
      sql`source_label IN ('measured', 'flag_off', 'no_source')`,
    ),
    detailChk: check(
      "index_coverage_observations_detail_chk",
      sql`char_length(btrim(source_detail)) > 0`,
    ),
  }),
);

export type IndexCoverageObservation = typeof indexCoverageObservations.$inferSelect;

/** Advisory answer-first finding. Review severity only. */
export const answerFirstFindings = pgTable(
  "answer_first_findings",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    url: text("url").notNull(),
    weekStart: date("week_start").notNull(),
    sectionHeading: text("section_heading").notNull().default(""),
    rule: text("rule").notNull(),
    excerpt: text("excerpt").notNull().default(""),
    severity: text("severity").notNull(),
    runId: varchar("run_id").notNull(),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => ({
    urlWeekIdx: index("answer_first_findings_url_week_idx").on(t.url, t.weekStart),
    urlChk: check("answer_first_findings_url_chk", sql`char_length(btrim(url)) > 0`),
    ruleChk: check(
      "answer_first_findings_rule_chk",
      sql`rule IN ('s1.h1-stands-alone', 's1.h2-answer-first')`,
    ),
    severityChk: check("answer_first_findings_severity_chk", sql`severity IN ('review')`),
    excerptChk: check("answer_first_findings_excerpt_chk", sql`char_length(excerpt) <= 180`),
  }),
);

export type AnswerFirstFindingRow = typeof answerFirstFindings.$inferSelect;

// Leads are pre-registration prospects (Task #573). They sit at the front
// of the Investors pipeline as the new "Lead" stage and are auto-promoted
// onto a `users` row at registration via email match. Email is stored
// lowercased and is unique. `userId` is set when the lead becomes a user.
// `importBatchId` groups all rows from the same CSV upload so admins can
// audit/undo a batch later.
export const leads = pgTable("leads", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  email: text("email").notNull().unique(),
  firstName: text("first_name"),
  lastName: text("last_name"),
  phone: text("phone"),
  source: text("source"),
  tags: text("tags").array(),
  notes: text("notes"),
  utmSource: text("utm_source"),
  utmMedium: text("utm_medium"),
  utmCampaign: text("utm_campaign"),
  utmTerm: text("utm_term"),
  utmContent: text("utm_content"),
  unsubscribed: boolean("unsubscribed").default(false).notNull(),
  userId: varchar("user_id").references(() => users.id, { onDelete: "set null" }),
  importBatchId: varchar("import_batch_id"),
  // Task #1004 — same attribution shape as `users`. Bulk imports flag
  // `pending_review` for any row that scored a probable duplicate, so
  // the admin queue can show the partner what got matched and why.
  attributedSource: text("attributed_source").default("unknown").notNull(),
  provisionalReferralAgentId: varchar("provisional_referral_agent_id"),
  attributionConfidence: integer("attribution_confidence").default(0).notNull(),
  attributionStatus: text("attribution_status").default("pending_review").notNull(),
  attributionCapturedAt: timestamp("attribution_captured_at"),
  attributionFirstTouch: jsonb("attribution_first_touch"),
  attributionLastTouch: jsonb("attribution_last_touch"),
  newLeadScore: integer("new_lead_score"),
  claimedReferrerType: text("claimed_referrer_type"),
  claimedReferrerId: varchar("claimed_referrer_id"),
  payProduct: text("pay_product").notNull().default("none"),
  // Country of citizenship from the public /refer brief. Nullable for
  // every lead that predates that form.
  country: text("country"),
  contactConsentAt: timestamp("contact_consent_at", { withTimezone: true }),
  // Task #2852 — optional WeChat ID carried from a contact on promotion
  // or captured at lead intake; unique-when-present backstop below.
  wechatId: text("wechat_id"),
  personId: varchar("person_id"),
  // Earliest linked page event. Not the form utm_* columns and not
  // attribution_first_touch. Written only when FIRST_TOUCH_GO is set.
  firstTouchUtmSource: text("first_touch_utm_source"),
  firstTouchUtmMedium: text("first_touch_utm_medium"),
  firstTouchUtmCampaign: text("first_touch_utm_campaign"),
  firstTouchUtmTerm: text("first_touch_utm_term"),
  firstTouchUtmContent: text("first_touch_utm_content"),
  firstTouchSourceClass: text("first_touch_source_class"),
  firstTouchLandingPath: text("first_touch_landing_path"),
  firstTouchAt: timestamp("first_touch_at", { withTimezone: true }),
  firstTouchPageEventId: varchar("first_touch_page_event_id"),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => ({
  wechatIdUniqueIdx: uniqueIndex("leads_wechat_id_unique_idx")
    .on(table.wechatId)
    .where(sql`wechat_id IS NOT NULL`),
}));

export const insertLeadSchema = createInsertSchema(leads)
  .omit({
    id: true,
    createdAt: true,
    userId: true,
    firstTouchUtmSource: true,
    firstTouchUtmMedium: true,
    firstTouchUtmCampaign: true,
    firstTouchUtmTerm: true,
    firstTouchUtmContent: true,
    firstTouchSourceClass: true,
    firstTouchLandingPath: true,
    firstTouchAt: true,
    firstTouchPageEventId: true,
  })
  .extend({
    email: z
      .string()
      .trim()
      .toLowerCase()
      .min(1, "Email is required")
      .email("Please enter a valid email address"),
    firstName: z.string().trim().nullable().optional(),
    lastName: z.string().trim().nullable().optional(),
    phone: z
      .string()
      .nullable()
      .optional()
      .refine(
        (v) => v == null || v === "" || (v.startsWith("+") && isValidPhoneNumber(v)),
        { message: "Phone number must be a valid international number (E.164 format)" },
      ),
    source: z.string().trim().max(120).nullable().optional(),
    tags: z.array(z.string().trim().min(1)).optional(),
    notes: z.string().nullable().optional(),
  });

export type InsertLead = z.infer<typeof insertLeadSchema>;
export type Lead = typeof leads.$inferSelect;

// Used by the bulk-import endpoint. Caller pre-parses the CSV client-side
// then submits an array of rows; the server dedups against existing leads
// and existing users by email and reports per-row results back.
export const bulkLeadImportSchema = z.object({
  source: z.string().trim().min(1, "Source label is required").max(120),
  consent: z.literal(true, {
    error: "Consent attestation is required",
  }),
  tags: z.array(z.string().trim().min(1)).optional(),
  rows: z
    .array(
      z.object({
        email: z.string().trim().toLowerCase(),
        firstName: z.string().trim().optional().nullable(),
        lastName: z.string().trim().optional().nullable(),
        phone: z.string().trim().optional().nullable(),
        notes: z.string().optional().nullable(),
        tags: z.array(z.string().trim().min(1)).optional(),
        utmSource: z.string().trim().optional().nullable(),
        utmMedium: z.string().trim().optional().nullable(),
        utmCampaign: z.string().trim().optional().nullable(),
        utmTerm: z.string().trim().optional().nullable(),
        utmContent: z.string().trim().optional().nullable(),
      }),
    )
    .min(1, "At least one row is required")
    .max(10000, "Import is limited to 10,000 rows per batch"),
});

export type BulkLeadImportInput = z.infer<typeof bulkLeadImportSchema>;

export type BulkLeadImportRowResult = {
  email: string;
  status: "created" | "skipped_duplicate_lead" | "skipped_existing_user" | "invalid_email";
  leadId?: string;
  // Task #1010 — when the dedupe scorer flagged the imported row as a
  // probable duplicate (newLeadScore < 80) it lands in
  // attribution_status = pending_review and shows up in the admin
  // /admin/leads/attribution queue. The flag is surfaced per row so
  // the import-result UI can highlight which rows the operator should
  // review.
  flaggedDuplicate?: boolean;
  newLeadScore?: number;
};

export type BulkLeadImportResult = {
  batchId: string;
  total: number;
  created: number;
  skippedDuplicateLeads: number;
  skippedExistingUsers: number;
  invalidEmails: number;
  // Task #1010 — count of rows that were CREATED but flagged as
  // probable duplicates by the dedupe scorer. These land in
  // pending_review and need admin triage in /admin/leads/attribution.
  flaggedDuplicates: number;
  rows: BulkLeadImportRowResult[];
};

// ===========================================================================
// Contacts hub (Task #1853) — a neutral, admin-only address book for
// marketing contacts (conference attendees, partner lists) whose role is not
// yet known: they could become an investor, agent, vendor, or sponsor.
//
// Deliberately SEPARATE from `leads` (which is investor-attribution shaped)
// and from `users` so these contacts never enter investor metrics, the
// investor pipeline, or the leads/marketing analytics. Importing a contact
// NEVER creates a login. Only an explicit "promote" action converts a
// contact into a typed record once a real relationship forms.
// ===========================================================================

// Allowed classification types + lifecycle statuses, shared between the
// Drizzle insert schemas and the import/classify validators below so the
// server and client agree on the vocabulary.
export const CONTACT_TYPES = ["investor", "agent", "vendor", "sponsor", "attorney", "unknown"] as const;
export type ContactType = (typeof CONTACT_TYPES)[number];
export const CONTACT_STATUSES = [
  "new",
  "reviewing",
  "qualified",
  "promoted",
  "unsubscribed",
  "bounced",
  "invalid",
  "archived",
] as const;
export type ContactStatus = (typeof CONTACT_STATUSES)[number];

export const contactImportBatches = pgTable("contact_import_batches", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  // Human-readable batch label, e.g. "HCMC Conference — 2026-06-05".
  label: text("label").notNull(),
  // Source event metadata stamped on every contact in the batch.
  sourceEvent: text("source_event"),
  sourceCity: text("source_city"),
  sourceDate: text("source_date"),
  fileName: text("file_name"),
  // The sheet/list names imported in this batch (e.g. ATTENDEE, SPONSORS).
  sheetNames: text("sheet_names").array(),
  totalRows: integer("total_rows").default(0).notNull(),
  createdCount: integer("created_count").default(0).notNull(),
  skippedCount: integer("skipped_count").default(0).notNull(),
  invalidCount: integer("invalid_count").default(0).notNull(),
  missingEmailCount: integer("missing_email_count").default(0).notNull(),
  importedBy: varchar("imported_by").references(() => users.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").defaultNow(),
});

// Employer / firm entity for Contacts Hub. People who work together share one
// row so the admin UI can show coworker relationships. Distinct from
// referral_agents.company_name and promoter organization forms.
export const contactOrganizations = pgTable("contact_organizations", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull(),
  nameKey: text("name_key").notNull(),
  website: text("website"),
  domain: text("domain"),
  linkedinUrl: text("linkedin_url"),
  organizationType: text("organization_type"),
  country: text("country"),
  notes: text("notes"),
  hqCity: text("hq_city"),
  /** US: 2-letter code; non-US: null or free-text region. */
  hqState: text("hq_state"),
  marketsServed: text("markets_served").array(),
  /** Firm WeChat account (not a personal contact wechat_id). */
  wechatId: text("wechat_id"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
  /** Registrable domain. One firm per value. */
  rootDomain: text("root_domain"),
  lastSignalAt: timestamp("last_signal_at"),
  signalCount: integer("signal_count").default(0).notNull(),
  /** Rules score 1–10. Fallback for firms that are not in TAM. Null until a FIRM_FIT_GO write. */
  firmFitScore: integer("firm_fit_score"),
  firmFitReason: text("firm_fit_reason"),
  /** Signal score captured with the firm fit write. Null until then. */
  signalScore: integer("signal_score"),
  /** Rollup of firm fit (weight 2) and signal score (weight 1). */
  combinedScore: integer("combined_score"),
  firmFitRubricVersion: text("firm_fit_rubric_version"),
  firmFitScoredAt: timestamp("firm_fit_scored_at"),
}, (table) => ({
  nameKeyIdx: index("contact_organizations_name_key_idx").on(table.nameKey),
  rootDomainUniqueIdx: uniqueIndex("contact_organizations_root_domain_unique_idx")
    .on(table.rootDomain)
    .where(sql`root_domain IS NOT NULL`),
  linkedinUrlUniqueIdx: uniqueIndex("contact_organizations_linkedin_url_unique_idx")
    .on(table.linkedinUrl)
    .where(sql`linkedin_url IS NOT NULL`),
  domainUniqueIdx: uniqueIndex("contact_organizations_domain_unique_idx")
    .on(table.domain)
    .where(sql`domain IS NOT NULL`),
}));

export type ContactOrganization = typeof contactOrganizations.$inferSelect;
export type ContactOrganizationSummary = {
  id: string;
  name: string;
  linkedinUrl: string | null;
  website: string | null;
  domain: string | null;
  organizationType: string | null;
  country: string | null;
  hqCity: string | null;
  hqState: string | null;
  marketsServed: string[] | null;
  wechatId: string | null;
  memberCount: number;
  createdAt: Date;
};

export const persons = pgTable("persons", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  displayName: text("display_name"),
  primaryEmail: text("primary_email"),
  primaryPhone: text("primary_phone"),
  wechatId: text("wechat_id"),
  knownToVerasAt: timestamp("known_to_veras_at").notNull().defaultNow(),
  deadAt: timestamp("dead_at"),
  deadBy: varchar("dead_by"),
  deadReasonId: varchar("dead_reason_id"),
  deadNote: text("dead_note"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
}, (table) => ({
  primaryEmailUniq: uniqueIndex("persons_primary_email_uniq")
    .on(sql`lower(btrim(${table.primaryEmail}))`)
    .where(sql`${table.primaryEmail} IS NOT NULL`),
  wechatIdUniq: uniqueIndex("persons_wechat_id_uniq")
    .on(sql`lower(btrim(${table.wechatId}))`)
    .where(sql`${table.wechatId} IS NOT NULL`),
}));

export type Person = typeof persons.$inferSelect;

export const investorDeadReasons = pgTable("investor_dead_reasons", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  key: text("key").notNull().unique(),
  label: text("label").notNull(),
  noteRequired: boolean("note_required").notNull().default(false),
  sortOrder: integer("sort_order").notNull().default(0),
  retiredAt: timestamp("retired_at"),
  createdBy: varchar("created_by"),
  updatedBy: varchar("updated_by"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export type InvestorDeadReason = typeof investorDeadReasons.$inferSelect;

export const investorLifecycleEvents = pgTable("investor_lifecycle_events", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  personId: varchar("person_id").notNull(),
  action: text("action").notNull().$type<"marked_dead" | "revived">(),
  reasonId: varchar("reason_id"),
  reasonKey: text("reason_key"),
  reasonLabel: text("reason_label"),
  note: text("note"),
  actorAdminId: varchar("actor_admin_id"),
  sourceSnapshot: text("source_snapshot").notNull().default("unknown"),
  countrySnapshot: text("country_snapshot").notNull().default("unknown"),
  recordKind: text("record_kind").notNull().$type<"user" | "lead" | "inquiry" | "deal_room_grant">(),
  recordId: varchar("record_id").notNull(),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export type InvestorLifecycleEvent = typeof investorLifecycleEvents.$inferSelect;

export const personIdentityKeys = pgTable("person_identity_keys", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  personId: varchar("person_id").references(() => persons.id, { onDelete: "cascade" }),
  organizationId: varchar("organization_id").references(() => contactOrganizations.id, { onDelete: "cascade" }),
  keyKind: text("key_kind").notNull().$type<"email" | "name_org" | "linkedin" | "phone" | "domain">(),
  keyValue: text("key_value").notNull(),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (table) => ({
  valueUniq: uniqueIndex("person_identity_keys_value_uniq").on(table.keyKind, table.keyValue),
  personIdx: index("person_identity_keys_person_idx").on(table.personId),
  organizationIdx: index("person_identity_keys_organization_idx").on(table.organizationId),
}));

export type PersonIdentityKey = typeof personIdentityKeys.$inferSelect;

// Original country notes and organization_type labels rewritten by
// 0397_identity_dedupe.sql. Service-role read. No persona policies.
export const identityLabelBackups = pgTable("identity_label_backups", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  sourceTable: text("source_table").notNull(),
  rowId: varchar("row_id").notNull(),
  columnName: text("column_name").notNull(),
  oldValue: text("old_value").notNull(),
  backedUpAt: timestamp("backed_up_at").defaultNow().notNull(),
});

// Field-level provenance for Hub enrichment. Email verification columns
// (verifier, result class, verified at) apply only when field_name = 'email'.
export const fieldProvenance = pgTable("field_provenance", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  entityType: text("entity_type").notNull(),
  entityId: varchar("entity_id").notNull(),
  fieldName: text("field_name").notNull(),
  source: text("source").notNull(),
  sourceUrl: text("source_url"),
  confidence: integer("confidence"),
  collectedAt: timestamp("collected_at").defaultNow().notNull(),
  collectedBy: text("collected_by").notNull(),
  verifier: text("verifier"),
  resultClass: text("result_class"),
  verifiedAt: timestamp("verified_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  runId: varchar("run_id"),
  collectedAtKind: text("collected_at_kind").notNull().default("observed"),
  /** Value collected by this row. Null on rows written before the waterfall. */
  fieldValue: text("field_value"),
}, (table) => ({
  entityIdx: index("field_provenance_entity_idx").on(
    table.entityType,
    table.entityId,
    table.fieldName,
    table.collectedAt,
  ),
  collectedByIdx: index("field_provenance_collected_by_idx").on(table.collectedBy),
}));

export type FieldProvenance = typeof fieldProvenance.$inferSelect;

/** One enrichment-waterfall apply. The migration does not fill contacts. */
export const enrichmentWaterfallRuns = pgTable("enrichment_waterfall_runs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  projectRef: text("project_ref").notNull(),
  operatorName: text("operator_name").notNull(),
  goReference: text("go_reference").notNull(),
  flagState: text("flag_state").notNull(),
  status: text("status").notNull(),
  startedAt: timestamp("started_at").defaultNow().notNull(),
  finishedAt: timestamp("finished_at"),
  creditCapRun: integer("credit_cap_run").notNull(),
  creditCapDay: integer("credit_cap_day").notNull(),
  creditsUsed: integer("credits_used").notNull().default(0),
  contactsExamined: integer("contacts_examined").notNull().default(0),
  contactsTouched: integer("contacts_touched").notNull().default(0),
  summary: jsonb("summary").notNull().default({}),
});

export const enrichmentSourceOrders = pgTable("enrichment_source_orders", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  fieldName: text("field_name").notNull(),
  sourceKey: text("source_key").notNull(),
  sortOrder: integer("sort_order").notNull(),
  minConfidence: integer("min_confidence").notNull(),
  version: integer("version").notNull(),
  active: boolean("active").notNull().default(true),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const enrichmentConflicts = pgTable("enrichment_conflicts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  runId: varchar("run_id").notNull(),
  contactId: varchar("contact_id").notNull(),
  fieldName: text("field_name").notNull(),
  existingValue: text("existing_value"),
  existingSource: text("existing_source"),
  existingConfidence: integer("existing_confidence"),
  proposedValue: text("proposed_value").notNull(),
  proposedSource: text("proposed_source").notNull(),
  proposedConfidence: integer("proposed_confidence").notNull(),
  kept: text("kept").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const enrichmentWaterfallWrites = pgTable("enrichment_waterfall_writes", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  runId: varchar("run_id").notNull(),
  contactId: varchar("contact_id").notNull(),
  fieldName: text("field_name").notNull(),
  previousValue: text("previous_value"),
  newValue: text("new_value").notNull(),
  sourceKey: text("source_key").notNull(),
  confidence: integer("confidence").notNull(),
  sourceUrl: text("source_url"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

// July 2026 Digital Leads stamp run log. Service-role only. The migration
// that creates these tables does not update contacts.
export const julyStampRuns = pgTable("july_stamp_runs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  batchId: varchar("batch_id").notNull(),
  projectRef: text("project_ref").notNull(),
  operatorName: text("operator_name").notNull(),
  goReference: text("go_reference").notNull(),
  status: text("status").notNull(),
  startedAt: timestamp("started_at").defaultNow().notNull(),
  endedAt: timestamp("ended_at"),
  startedAtEt: text("started_at_et").notNull(),
  endedAtEt: text("ended_at_et"),
  targetCount: integer("target_count").notNull(),
  contactsUpdated: integer("contacts_updated").notNull().default(0),
  entriesWritten: jsonb("entries_written").notNull().default({}),
  skips: jsonb("skips").notNull().default({}),
});

export const julyStampContactBackups = pgTable("july_stamp_contact_backups", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  runId: varchar("run_id").notNull(),
  contactId: varchar("contact_id").notNull(),
  previousSourceEvent: text("previous_source_event"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const linkedinKeyBackfillRuns = pgTable("linkedin_key_backfill_runs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  projectRef: text("project_ref").notNull(),
  operatorName: text("operator_name").notNull(),
  goReference: text("go_reference").notNull(),
  status: text("status").notNull(),
  startedAt: timestamp("started_at").defaultNow().notNull(),
  endedAt: timestamp("ended_at"),
  keysCreated: integer("keys_created").notNull().default(0),
  personsCreated: integer("persons_created").notNull().default(0),
  exceptions: jsonb("exceptions").notNull().default([]),
});

export const GROWTH_SIGNAL_TYPES = [
  "competitor_engagement",
  "comment",
  "reaction",
  "owned_post_engagement",
  "page_visit",
  "sequence_click",
  "hot_board_touch",
  "deal_room_view",
  "reply",
  "meeting",
] as const;
export type GrowthSignalType = (typeof GROWTH_SIGNAL_TYPES)[number];

export const growthSignals = pgTable("growth_signals", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  signalType: text("signal_type").notNull().$type<GrowthSignalType>(),
  idempotencyKey: text("idempotency_key").notNull(),
  sourceUrl: text("source_url"),
  occurredAt: timestamp("occurred_at").notNull(),
  contactId: varchar("contact_id"),
  organizationId: varchar("organization_id"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  runId: varchar("run_id"),
  occurredAtKind: text("occurred_at_kind").notNull().default("observed"),
  sourceChannel: text("source_channel"),
  externalThreadId: text("external_thread_id"),
  metadataSnippet: text("metadata_snippet"),
  outcomeType: text("outcome_type"),
}, (table) => ({
  idempotencyUniq: uniqueIndex("growth_signals_idempotency_key_uniq").on(table.idempotencyKey),
  contactOccurredIdx: index("growth_signals_contact_occurred_idx").on(table.contactId, table.occurredAt),
}));

export type GrowthSignal = typeof growthSignals.$inferSelect;

export const growthJobRuns = pgTable("growth_job_runs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  job: text("job").notNull(),
  slotKey: text("slot_key").notNull(),
  attempt: integer("attempt").notNull(),
  startedAt: timestamp("started_at", { withTimezone: true }).notNull(),
  finishedAt: timestamp("finished_at", { withTimezone: true }).notNull(),
  status: text("status").notNull(),
  rowsRead: integer("rows_read").notNull(),
  rowsWritten: integer("rows_written").notNull(),
  configVersion: text("config_version"),
  flagState: text("flag_state").notNull(),
  errorText: text("error_text"),
  note: text("note"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
}, (table) => ({
  attemptUidx: uniqueIndex("growth_job_runs_attempt_uniq").on(table.job, table.slotKey, table.attempt),
  slotIdx: index("growth_job_runs_slot_idx").on(table.job, table.slotKey),
}));

export const growthCountChecks = pgTable("growth_count_checks", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  checkedOn: date("checked_on").notNull(),
  tableName: text("table_name").notNull(),
  rowCount: integer("row_count").notNull(),
  priorCount: integer("prior_count"),
  withinThreshold: boolean("within_threshold").notNull(),
  thresholdNote: text("threshold_note").notNull(),
  jobRunId: varchar("job_run_id"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
}, (table) => ({
  tableIdx: index("growth_count_checks_table_idx").on(table.tableName, table.checkedOn),
}));

export const canonicalMeetings = pgTable("canonical_meetings", {
  id: text("id").primaryKey(),
  contactId: varchar("contact_id").notNull(),
  occurredAt: timestamp("occurred_at", { withTimezone: true }).notNull(),
  status: text("status").notNull(),
  matchMethod: text("match_method").notNull(),
  anchorSignalId: varchar("anchor_signal_id").notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
}, (table) => ({
  contactOccurredIdx: index("canonical_meetings_contact_occurred_idx").on(table.contactId, table.occurredAt),
}));

export const canonicalMeetingSources = pgTable("canonical_meeting_sources", {
  canonicalMeetingId: text("canonical_meeting_id").notNull().references(() => canonicalMeetings.id, { onDelete: "cascade" }),
  signalId: varchar("signal_id").notNull().references(() => growthSignals.id, { onDelete: "cascade" }),
  signalType: text("signal_type").notNull(),
  sourceChannel: text("source_channel"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
}, (table) => ({
  pk: primaryKey({ columns: [table.canonicalMeetingId, table.signalId] }),
  signalUidx: uniqueIndex("canonical_meeting_sources_signal_uniq").on(table.signalId),
  meetingIdx: index("canonical_meeting_sources_meeting_idx").on(table.canonicalMeetingId),
}));

export const unlinkedGrowthSignalReviews = pgTable("unlinked_growth_signal_reviews", {
  signalId: varchar("signal_id").primaryKey().references(() => growthSignals.id, { onDelete: "cascade" }),
  signalType: text("signal_type").notNull(),
  occurredAt: timestamp("occurred_at", { withTimezone: true }),
  reason: text("reason").notNull().default("missing_contact"),
  parkedAt: timestamp("parked_at", { withTimezone: true }).defaultNow().notNull(),
});

export const engagerSources = pgTable("engager_sources", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  kind: text("kind").notNull().$type<"page" | "post">(),
  sourceUrl: text("source_url").notNull(),
  tier: text("tier").notNull().$type<"tier_1" | "tier_2" | "owned">(),
  watched: boolean("watched").notNull().default(true),
  label: text("label"),
  lastHarvestedAt: timestamp("last_harvested_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  urlUniq: uniqueIndex("engager_sources_url_uniq").on(table.sourceUrl),
}));

export type EngagerSource = typeof engagerSources.$inferSelect;

export const harvestStagingRows = pgTable("harvest_staging_rows", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  harvestId: text("harvest_id").notNull(),
  sourceId: varchar("source_id"),
  idempotencyKey: text("idempotency_key").notNull(),
  postUrl: text("post_url"),
  engagementType: text("engagement_type"),
  personUrl: text("person_url"),
  bodyText: text("body_text"),
  occurredAt: timestamp("occurred_at"),
  outcome: text("outcome").notNull().$type<"create" | "skip">(),
  skipReason: text("skip_reason"),
  runId: varchar("run_id"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  harvestKeyUniq: uniqueIndex("harvest_staging_harvest_key_uniq").on(table.harvestId, table.idempotencyKey),
  harvestIdx: index("harvest_staging_harvest_id_idx").on(table.harvestId),
}));

export type HarvestStagingRow = typeof harvestStagingRows.$inferSelect;

// Manual outreach list (PRD T4). The list itself is computed from contacts.
// These two tables hold the configurable practitioner definition and the
// CSV download log. They do not enroll or message anyone.
export const outreachPractitionerRules = pgTable("outreach_practitioner_rules", {
  id: text("id").primaryKey(),
  contactTypes: text("contact_types").array().notNull(),
  organizationTypes: text("organization_types").array().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export type OutreachPractitionerRule = typeof outreachPractitionerRules.$inferSelect;

export const outreachListExports = pgTable("outreach_list_exports", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  exportedBy: varchar("exported_by"),
  exportedAt: timestamp("exported_at").defaultNow().notNull(),
  rowCount: integer("row_count").notNull(),
});

export type OutreachListExport = typeof outreachListExports.$inferSelect;

export const growthStageDefinitions = pgTable("growth_stage_definitions", {
  stageKey: text("stage_key").primaryKey(),
  funnel: text("funnel").$type<"practitioner" | "investor">().notNull(),
  stageOrder: integer("stage_order").notNull(),
  label: text("label").notNull(),
  definition: text("definition").notNull(),
  status: text("status").$type<"active" | "no_source" | "retired">().notNull(),
  sourceTable: text("source_table"),
  countingRule: text("counting_rule"),
  timestampField: text("timestamp_field"),
  definitionVersion: integer("definition_version").notNull().default(1),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export type GrowthStageDefinition = typeof growthStageDefinitions.$inferSelect;

// Contact scoring (PRD P2). Writes happen only when CONTACT_SCORING_GO is a
// named go, on the dev project or the production project. History is
// insert-only; a version is removed as a set.
export const contactScoringConfigs = pgTable("contact_scoring_configs", {
  version: text("version").primaryKey(),
  weights: jsonb("weights").notNull(),
  decayMethod: text("decay_method").notNull(),
  halfLifeDays: numeric("half_life_days").notNull(),
  unverifiedConfidence: integer("unverified_confidence").notNull(),
  roundDecimals: integer("round_decimals").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  createdBy: text("created_by").notNull(),
});

export type ContactScoringConfig = typeof contactScoringConfigs.$inferSelect;

export const contactScores = pgTable("contact_scores", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  contactId: varchar("contact_id").notNull(),
  personId: varchar("person_id"),
  fitType: text("fit_type").notNull(),
  score: numeric("score", { precision: 12, scale: 2 }),
  reasons: jsonb("reasons").notNull(),
  configVersion: text("config_version").notNull(),
  computedAt: timestamp("computed_at").notNull(),
  excluded: boolean("excluded").notNull(),
  exclusionCause: text("exclusion_cause"),
  inputFingerprint: text("input_fingerprint").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  currentIdx: index("contact_scores_current_idx").on(
    table.contactId,
    table.fitType,
    table.configVersion,
    table.computedAt,
  ),
}));

export type ContactScore = typeof contactScores.$inferSelect;

export const investorMilestones = pgTable("investor_milestones", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  personId: varchar("person_id").notNull().references(() => persons.id, { onDelete: "cascade" }),
  projectId: varchar("project_id").notNull().references(() => investmentProjects.id, { onDelete: "cascade" }),
  registeredAt: date("registered_at"),
  accreditationReviewedAt: date("accreditation_reviewed_at"),
  subscribedAt: date("subscribed_at"),
  preReceiptRefundAt: date("pre_receipt_refund_at"),
  preReceiptRefundProportionBps: integer("pre_receipt_refund_proportion_bps"),
  i526eReceiptAt: date("i526e_receipt_at"),
  adminFeeReceivedAt: date("admin_fee_received_at"),
  capitalDeployedAt: date("capital_deployed_at"),
  interestYear1At: date("interest_year_1_at"),
  interestYear2At: date("interest_year_2_at"),
  interestYear3At: date("interest_year_3_at"),
  deniedAt: date("denied_at"),
  withdrawnAt: date("withdrawn_at"),
  repaidAt: date("repaid_at"),
  ledgerSyncPending: jsonb("ledger_sync_pending").$type<Array<{
    kind: string;
    sourceField: string;
    reason: string;
  }> | null>(),
  enteredByUserId: varchar("entered_by_user_id").references((): AnyPgColumn => users.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
}, (table) => ({
  personProjectUniq: uniqueIndex("investor_milestones_person_project_uniq")
    .on(table.personId, table.projectId),
  projectIdx: index("investor_milestones_project_idx").on(table.projectId),
}));

export type InvestorMilestone = typeof investorMilestones.$inferSelect;

export const contacts = pgTable("contacts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  // Normalized (lowercased/trimmed) email. Unique-when-present via a
  // partial unique index (see table config below) so the same person
  // isn't imported twice, while WeChat-only contacts with NO email are
  // allowed (multiple NULL emails coexist).
  email: text("email"),
  firstName: text("first_name"),
  lastName: text("last_name"),
  // Denormalized display/search cache of the employer name. Prefer
  // organizationId for relationships; keep this in sync when linking.
  company: text("company"),
  // FK to contact_organizations — null until normalized / resolved.
  organizationId: varchar("organization_id").references(() => contactOrganizations.id, {
    onDelete: "set null",
  }),
  phone: text("phone"),
  country: text("country"),
  /** US: 2-letter code; non-US: null or free-text region. Not notes. */
  state: text("state"),
  // Source provenance (denormalized from the batch for fast filtering).
  sourceEvent: text("source_event"),
  sourceCity: text("source_city"),
  sourceDate: text("source_date"),
  // Which sheet/list inside the file this row came from (ATTENDEE/SPONSORS).
  sourceList: text("source_list"),
  importBatchId: varchar("import_batch_id").references(() => contactImportBatches.id, {
    onDelete: "set null",
  }),
  // Classification: the admin-confirmed type.
  contactType: text("contact_type").default("unknown").notNull(),
  status: text("status").default("new").notNull(),
  emailValid: boolean("email_valid").default(false).notNull(),
  tags: text("tags").array(),
  notes: text("notes"),
  // Promotion: when a real relationship forms, record the intended type and
  // (where the intake path makes it straightforward) the downstream record id.
  promotedToType: text("promoted_to_type"),
  promotedRecordId: varchar("promoted_record_id"),
  promotedAt: timestamp("promoted_at"),
  promotedBy: varchar("promoted_by").references(() => users.id, { onDelete: "set null" }),
  // Task #2630 — marketing consent & unsubscribe.
  // State values: 'not_opted_in' | 'subscribed' | 'unsubscribed'.
  marketingSubscriptionState: text("marketing_subscription_state").default("not_opted_in").notNull(),
  // Provenance of the most recent consent or opt-out action.
  marketingConsentSource: text("marketing_consent_source"),
  // When the state last changed (consent granted, or unsubscribed).
  marketingConsentAt: timestamp("marketing_consent_at"),
  // Task #3003 — consent provenance. `marketingConsentBasis` classifies *why*
  // we may market to this contact (one of MARKETING_CONSENT_BASES); kept as
  // plain text so a new basis value needs no enum migration.
  // `marketingConsentNote` is an optional free-text reference to where the
  // proof lives (e.g. "booth sign-up sheet, HCMC 2026-06-05").
  marketingConsentBasis: text("marketing_consent_basis"),
  marketingConsentNote: text("marketing_consent_note"),
  // Unguessable token embedded in one-click unsubscribe links.
  // The DB column has a DEFAULT (set in migration) that auto-generates a token
  // on every INSERT; app-layer inserts also set this explicitly via
  // generateUnsubscribeToken() in server/storage/contacts.ts.
  unsubscribeToken: text("unsubscribe_token").unique(),
  // Task #2631 — marketing funnel stage & project interest for segmentation.
  // funnelStage: 'cold' | 'engaged' | 'accredited' | 'committed' — admin-set.
  // projectInterest: array of project names/ids the contact signalled interest in.
  marketingFunnelStage: text("marketing_funnel_stage"),
  projectInterest: text("project_interest").array(),
  // Task #2656 — richer profile fields.
  salutation: text("salutation"),
  jobTitle: text("job_title"),
  website: text("website"),
  organizationType: text("organization_type"),
  // Task #2852 — optional WeChat ID so admins can create WeChat-only
  // contacts (no email). Normalized cross-table duplicate check lives in
  // the app layer; the partial unique index below is a same-table backstop.
  wechatId: text("wechat_id"),
  // Research ingest (Grok /api/v1/contacts) — LinkedIn profile URL is a
  // first-class identity key alongside email / WeChat. Normalized by
  // server/services/research-contact-ingest.ts before insert.
  linkedinUrl: text("linkedin_url"),
  // Source-agnostic research channel (linkedin is one value, not the only).
  researchChannel: text("research_channel"),
  // Bot-reported confidence 0–100; nullable when the caller omits it.
  researchConfidence: integer("research_confidence"),
  // Task #2999 — recipient timezone (IANA name, e.g. "America/New_York") used
  // to hold sequence sends to the contact's local send window (quiet-hours
  // gating). Nullable: when null the engine infers a zone from `country`, and
  // failing that uses the admin-configured global default. An admin may set
  // this explicitly to override the inference.
  timezone: text("timezone"),
  // Task #3000 — email hygiene "sunset" marker. Non-null = the hygiene sweep
  // auto-sunset this contact (received several sequence emails over a window
  // with no opens/clicks). Sunsetted contacts are excluded from future bulk
  // sequence enrollment to protect sending reputation. Cleared automatically
  // when the contact genuinely re-engages (a tracked click).
  marketingSunsettedAt: timestamp("marketing_sunsetted_at"),
  // Person-safety clustering: when true, normalized name + organization may
  // link sibling contact rows (different emails) for opt-out / one-per-day.
  // Never driven by Contacts audience tags.
  personIdentityReviewed: boolean("person_identity_reviewed").default(false).notNull(),
  personId: varchar("person_id"),
  // Earliest page event already linked to this contact. Nullable until
  // FIRST_TOUCH_GO copies it. Intake cannot set these fields.
  firstTouchUtmSource: text("first_touch_utm_source"),
  firstTouchUtmMedium: text("first_touch_utm_medium"),
  firstTouchUtmCampaign: text("first_touch_utm_campaign"),
  firstTouchUtmTerm: text("first_touch_utm_term"),
  firstTouchUtmContent: text("first_touch_utm_content"),
  firstTouchSourceClass: text("first_touch_source_class"),
  firstTouchLandingPath: text("first_touch_landing_path"),
  firstTouchAt: timestamp("first_touch_at", { withTimezone: true }),
  firstTouchPageEventId: varchar("first_touch_page_event_id"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  lastSignalAt: timestamp("last_signal_at"),
  signalCount: integer("signal_count").default(0).notNull(),
}, (table) => ({
  // Email is unique only when present, so e-mail-less (WeChat-only)
  // contacts are not blocked by the uniqueness rule.
  emailUniqueIdx: uniqueIndex("contacts_email_unique_idx")
    .on(table.email)
    .where(sql`email IS NOT NULL`),
  wechatIdUniqueIdx: uniqueIndex("contacts_wechat_id_unique_idx")
    .on(table.wechatId)
    .where(sql`wechat_id IS NOT NULL`),
  linkedinUrlUniqueIdx: uniqueIndex("contacts_linkedin_url_unique_idx")
    .on(table.linkedinUrl)
    .where(sql`linkedin_url IS NOT NULL`),
  organizationIdIdx: index("contacts_organization_id_idx")
    .on(table.organizationId)
    .where(sql`organization_id IS NOT NULL`),
}));

// TAM drill-down. Rows load through the import path, never this schema.
// Admin-only under app_auth.is_admin(); see migrations/0434_tam_accounts.sql.
export const tamCities = pgTable("tam_cities", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  region: text("region").notNull(),
  country: text("country").notNull(),
  city: text("city").notNull(),
  includes: text("includes").array().notNull().default(sql`'{}'::text[]`),
  lat: real("lat"),
  lng: real("lng"),
  geonamesId: integer("geonames_id"),
  tripScore: real("trip_score"),
  tripRank: integer("trip_rank"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  countryCityUidx: uniqueIndex("tam_cities_country_city_uidx").on(table.country, table.city),
}));

export type TamCity = typeof tamCities.$inferSelect;

export const tamAccounts = pgTable("tam_accounts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  region: text("region").notNull(),
  segment: text("segment").notNull(),
  country: text("country").notNull(),
  city: text("city"),
  citySource: text("city_source"),
  firm: text("firm").notNull(),
  size: text("size"),
  language: text("language"),
  website: text("website"),
  linkedin: text("linkedin"),
  decisionMaker: text("decision_maker"),
  decisionMakerSource: text("decision_maker_source"),
  warmCold: text("warm_cold").notNull(),
  fitScore: integer("fit_score").notNull(),
  tier: text("tier").notNull(),
  fitReason: text("fit_reason"),
  sourceUrl: text("source_url"),
  sourceType: text("source_type"),
  rootDomain: text("root_domain").notNull(),
  siteCheck: text("site_check"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  rootDomainUidx: uniqueIndex("tam_accounts_root_domain_uidx").on(table.rootDomain),
  placeIdx: index("tam_accounts_place_idx").on(table.region, table.country, table.city),
}));

export type TamAccount = typeof tamAccounts.$inferSelect;

/** One link pass. Ships empty. This migration does not insert a run. */
export const tamLinkRuns = pgTable("tam_link_runs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  startedAt: timestamp("started_at").defaultNow().notNull(),
  finishedAt: timestamp("finished_at"),
  actorLabel: text("actor_label"),
  dryRun: boolean("dry_run").notNull().default(true),
  insertedLinks: integer("inserted_links").notNull().default(0),
  conflictCount: integer("conflict_count").notNull().default(0),
}, (table) => ({
  countsChk: check(
    "tam_link_runs_counts_chk",
    sql`${table.insertedLinks} >= 0 AND ${table.conflictCount} >= 0`,
  ),
}));

export type TamLinkRun = typeof tamLinkRuns.$inferSelect;

/**
 * One TAM account, one Hub organization, matched on root domain.
 * The link does not rename or merge the organization.
 */
export const tamFirmLinks = pgTable("tam_firm_links", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tamAccountId: varchar("tam_account_id").notNull().references(() => tamAccounts.id, { onDelete: "cascade" }),
  organizationId: varchar("organization_id").notNull().references(() => contactOrganizations.id, { onDelete: "cascade" }),
  method: text("method").notNull(),
  linkedAt: timestamp("linked_at").defaultNow().notNull(),
  runId: varchar("run_id").notNull().references(() => tamLinkRuns.id),
  rootDomain: text("root_domain").notNull(),
}, (table) => ({
  tamUidx: uniqueIndex("tam_firm_links_tam_uidx").on(table.tamAccountId),
  orgUidx: uniqueIndex("tam_firm_links_org_uidx").on(table.organizationId),
  methodChk: check("tam_firm_links_method_chk", sql`${table.method} = 'root_domain_match'`),
}));

export type TamFirmLink = typeof tamFirmLinks.$inferSelect;

/** A domain that was not linked, with the cause left unresolved. */
export const tamLinkConflicts = pgTable("tam_link_conflicts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  runId: varchar("run_id").notNull().references(() => tamLinkRuns.id, { onDelete: "cascade" }),
  cause: text("cause").notNull(),
  rootDomain: text("root_domain"),
  tamAccountId: varchar("tam_account_id"),
  organizationId: varchar("organization_id"),
  detail: text("detail"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  runIdx: index("tam_link_conflicts_run_idx").on(table.runId),
  causeChk: check(
    "tam_link_conflicts_cause_chk",
    sql`${table.cause} IN ('duplicate_hub_domain', 'duplicate_tam_domain', 'no_domain', 'locked_contact')`,
  ),
}));

export type TamLinkConflict = typeof tamLinkConflicts.$inferSelect;

/** One TAM write or dry run. Ships empty. Counts are the run log. */
export const tamWriteRuns = pgTable("tam_write_runs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  dryRun: boolean("dry_run").notNull(),
  undoneAt: timestamp("undone_at"),
  actorLabel: text("actor_label"),
  idempotencyKey: text("idempotency_key"),
  source: text("source").notNull().default("api"),
  newCount: integer("new_count").notNull().default(0),
  updatedCount: integer("updated_count").notNull().default(0),
  unchangedCount: integer("unchanged_count").notNull().default(0),
  rejectedCount: integer("rejected_count").notNull().default(0),
  cityNewCount: integer("city_new_count").notNull().default(0),
  cityUpdatedCount: integer("city_updated_count").notNull().default(0),
  cityUnchangedCount: integer("city_unchanged_count").notNull().default(0),
  cityRejectedCount: integer("city_rejected_count").notNull().default(0),
  response: jsonb("response"),
});

export type TamWriteRunRow = typeof tamWriteRuns.$inferSelect;

/** Current source, collected_at, and confidence for one TAM field. */
export const tamFieldProvenance = pgTable("tam_field_provenance", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  entityKind: text("entity_kind").notNull(),
  entityKey: text("entity_key").notNull(),
  fieldName: text("field_name").notNull(),
  source: text("source").notNull(),
  collectedAt: timestamp("collected_at").notNull(),
  confidence: integer("confidence").notNull(),
  runId: varchar("run_id").references(() => tamWriteRuns.id, { onDelete: "cascade" }),
}, (table) => ({
  fieldUidx: uniqueIndex("tam_field_provenance_field_uidx").on(table.entityKind, table.entityKey, table.fieldName),
}));

export type TamFieldProvenance = typeof tamFieldProvenance.$inferSelect;

/** Before/after snapshot used to undo one write run. */
export const tamWriteChanges = pgTable("tam_write_changes", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  runId: varchar("run_id").notNull().references(() => tamWriteRuns.id, { onDelete: "cascade" }),
  entityKind: text("entity_kind").notNull(),
  entityKey: text("entity_key").notNull(),
  action: text("action").notNull(),
  beforeRow: jsonb("before_row"),
  afterRow: jsonb("after_row").notNull(),
}, (table) => ({
  runIdx: index("tam_write_changes_run_idx").on(table.runId),
}));

export type TamWriteChangeRow = typeof tamWriteChanges.$inferSelect;

export const tamWriteRejections = pgTable("tam_write_rejections", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  runId: varchar("run_id").notNull().references(() => tamWriteRuns.id, { onDelete: "cascade" }),
  entityKind: text("entity_kind").notNull(),
  entityKey: text("entity_key"),
  reason: text("reason").notNull(),
  detail: text("detail"),
}, (table) => ({
  runIdx: index("tam_write_rejections_run_idx").on(table.runId),
}));

export type TamWriteRejectionRow = typeof tamWriteRejections.$inferSelect;

/** Apollo organization id for one TAM root domain. Ships empty. */
export const tamApolloIdentities = pgTable("tam_apollo_identities", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  apolloOrgId: text("apollo_org_id").notNull(),
  rootDomain: text("root_domain").notNull(),
  tamAccountId: varchar("tam_account_id").references(() => tamAccounts.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  orgUidx: uniqueIndex("tam_apollo_identities_org_uidx").on(table.apolloOrgId),
  domainUidx: uniqueIndex("tam_apollo_identities_domain_uidx").on(table.rootDomain),
}));

export type TamApolloIdentity = typeof tamApolloIdentities.$inferSelect;

export const tamApolloSyncRuns = pgTable("tam_apollo_sync_runs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  inputKind: text("input_kind").notNull(),
  inputName: text("input_name").notNull(),
  dryRun: boolean("dry_run").notNull().default(false),
  refused: boolean("refused").notNull().default(false),
  refuseReason: text("refuse_reason"),
  creditsPlanned: integer("credits_planned").notNull().default(0),
  creditsUsed: integer("credits_used").notNull().default(0),
  runCreditCap: integer("run_credit_cap").notNull(),
  dailyCreditCap: integer("daily_credit_cap").notNull(),
  planCreditsRemaining: integer("plan_credits_remaining"),
  enrichMissing: boolean("enrich_missing").notNull().default(false),
  pushbackRequested: boolean("pushback_requested").notNull().default(false),
  pushbackApplied: boolean("pushback_applied").notNull().default(false),
  writeRunId: varchar("write_run_id").references(() => tamWriteRuns.id, { onDelete: "set null" }),
});

export type TamApolloSyncRun = typeof tamApolloSyncRuns.$inferSelect;

export const tamApolloCreditDays = pgTable("tam_apollo_credit_days", {
  day: date("day").primaryKey(),
  creditsUsed: integer("credits_used").notNull().default(0),
});

export type TamApolloCreditDay = typeof tamApolloCreditDays.$inferSelect;


// Frozen weekly outreach slate. Membership is outreach_list_for_admin.
// Outcome rows do not change rank. See migrations/0445_weekly_pull_list.sql.
export const weeklyPullLists = pgTable("weekly_pull_lists", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  isoYear: integer("iso_year").notNull(),
  isoWeek: integer("iso_week").notNull(),
  ruleVersion: text("rule_version").notNull(),
  rankingMode: text("ranking_mode").notNull(),
  generatedAt: timestamp("generated_at").defaultNow().notNull(),
  frozenAt: timestamp("frozen_at").defaultNow().notNull(),
  slateSize: integer("slate_size").notNull(),
  generatedBy: text("generated_by"),
  sourceFunction: text("source_function").notNull().default("outreach_list_for_admin"),
}, (table) => ({
  weekUidx: uniqueIndex("weekly_pull_lists_week_uidx").on(table.isoYear, table.isoWeek, table.ruleVersion),
}));

export const weeklyPullListRows = pgTable("weekly_pull_list_rows", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  listId: varchar("list_id").notNull().references(() => weeklyPullLists.id, { onDelete: "restrict" }),
  rank: integer("rank").notNull(),
  contactId: varchar("contact_id").notNull(),
  name: text("name").notNull(),
  firm: text("firm"),
  country: text("country"),
  reasons: jsonb("reasons").notNull(),
  rankingInputs: jsonb("ranking_inputs").notNull(),
  carriedWeeks: integer("carried_weeks").notNull().default(0),
  hubPath: text("hub_path"),
}, (table) => ({
  rankUidx: uniqueIndex("weekly_pull_list_rows_rank_uidx").on(table.listId, table.rank),
  contactUidx: uniqueIndex("weekly_pull_list_rows_contact_uidx").on(table.listId, table.contactId),
}));

export const weeklyPullOutcomes = pgTable("weekly_pull_outcomes", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  rowId: varchar("row_id").notNull().references(() => weeklyPullListRows.id, { onDelete: "restrict" }),
  outcomeType: text("outcome_type").notNull(),
  actor: text("actor").notNull(),
  occurredAt: timestamp("occurred_at").defaultNow().notNull(),
  channel: text("channel"),
  note: text("note"),
}, (table) => ({
  rowIdx: index("weekly_pull_outcomes_row_idx").on(table.rowId, table.occurredAt),
}));

// Next 10 to call (PRD L1). One live list per business day; a forced
// regeneration supersedes the old list. See migrations/0466_next_ten_to_call.sql.
export const dailyCallLists = pgTable("daily_call_lists", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  listDate: date("list_date").notNull(),
  ruleVersion: text("rule_version").notNull(),
  rankingMode: text("ranking_mode").notNull(),
  runLabel: text("run_label").notNull(),
  generatedAt: timestamp("generated_at", { withTimezone: true }).defaultNow().notNull(),
  generatedBy: text("generated_by").notNull(),
  rowCount: integer("row_count").notNull(),
  qualifiedCount: integer("qualified_count").notNull(),
  shortReason: text("short_reason"),
  replacesListId: varchar("replaces_list_id"),
  forcedReason: text("forced_reason"),
  supersededAt: timestamp("superseded_at", { withTimezone: true }),
  supersededBy: text("superseded_by"),
}, (table) => ({
  dayUidx: uniqueIndex("daily_call_lists_day_uidx")
    .on(table.listDate, table.ruleVersion)
    .where(sql`superseded_at IS NULL`),
  runLabelIdx: index("daily_call_lists_run_label_idx").on(table.runLabel),
}));

export const dailyCallListRows = pgTable("daily_call_list_rows", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  listId: varchar("list_id").notNull().references(() => dailyCallLists.id, { onDelete: "restrict" }),
  rank: integer("rank").notNull(),
  contactId: varchar("contact_id").notNull(),
  organizationId: varchar("organization_id"),
  name: text("name").notNull(),
  firm: text("firm"),
  reasons: jsonb("reasons").notNull(),
  rankingInputs: jsonb("ranking_inputs").notNull(),
  rankingBasis: text("ranking_basis").notNull(),
  ownerUserId: varchar("owner_user_id").notNull(),
  taskType: text("task_type").notNull(),
  dueDate: date("due_date").notNull(),
  hotBoardItemId: varchar("hot_board_item_id"),
  hubPath: text("hub_path"),
}, (table) => ({
  rankUidx: uniqueIndex("daily_call_list_rows_rank_uidx").on(table.listId, table.rank),
  contactUidx: uniqueIndex("daily_call_list_rows_contact_uidx").on(table.listId, table.contactId),
  contactIdx: index("daily_call_list_rows_contact_idx").on(table.contactId),
}));

export const dailyCallOutcomes = pgTable("daily_call_outcomes", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  rowId: varchar("row_id").notNull().references(() => dailyCallListRows.id, { onDelete: "restrict" }),
  outcomeType: text("outcome_type").notNull(),
  actor: text("actor").notNull(),
  occurredAt: timestamp("occurred_at", { withTimezone: true }).defaultNow().notNull(),
  note: text("note"),
  reassignedTo: varchar("reassigned_to"),
}, (table) => ({
  rowIdx: index("daily_call_outcomes_row_idx").on(table.rowId, table.occurredAt),
}));

export const dailyCallSettings = pgTable("daily_call_settings", {
  id: text("id").primaryKey(),
  defaultOwnerUserId: varchar("default_owner_user_id"),
  dueOffsetBusinessDays: integer("due_offset_business_days").notNull().default(0),
  cooldownDays: integer("cooldown_days").notNull().default(14),
  updatedBy: text("updated_by").notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

/**
 * Consented visitor ↔ contact. Many visitors per contact. The unique pair
 * allows one visitor to link to several contacts without merging them.
 * Provenance (source event, method, linked_at) is written once.
 */
export const visitorContactLinks = pgTable("visitor_contact_links", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  visitorUuid: varchar("visitor_uuid").notNull(),
  contactId: varchar("contact_id").notNull().references(() => contacts.id, { onDelete: "cascade" }),
  sourceEvent: text("source_event").notNull(),
  method: text("method").notNull(),
  linkedAt: timestamp("linked_at", { withTimezone: true }).notNull().defaultNow(),
  marketingBlocked: boolean("marketing_blocked").notNull().default(false),
  blockReason: text("block_reason"),
}, (table) => ({
  pairUniq: uniqueIndex("visitor_contact_links_pair_uniq").on(table.visitorUuid, table.contactId),
  contactIdx: index("visitor_contact_links_contact_idx").on(table.contactId),
  visitorIdx: index("visitor_contact_links_visitor_idx").on(table.visitorUuid),
}));

/** Earlier page_events for a visitor, attached per contact. Not a contact merge. */
export const pageEventContactLinks = pgTable("page_event_contact_links", {
  pageEventId: varchar("page_event_id").notNull().references(() => pageEvents.id, { onDelete: "cascade" }),
  contactId: varchar("contact_id").notNull().references(() => contacts.id, { onDelete: "cascade" }),
  visitorUuid: varchar("visitor_uuid").notNull(),
  visitorContactLinkId: varchar("visitor_contact_link_id").notNull().references(() => visitorContactLinks.id, { onDelete: "cascade" }),
  linkedAt: timestamp("linked_at", { withTimezone: true }).notNull().defaultNow(),
}, (table) => ({
  pk: primaryKey({ columns: [table.pageEventId, table.contactId] }),
  contactIdx: index("page_event_contact_links_contact_idx").on(table.contactId),
  visitorIdx: index("page_event_contact_links_visitor_idx").on(table.visitorUuid),
}));

// Admin CRM Hot Board — weekly mid-tier referral working list (manual add +
// status mark-down). RLS default-deny; service-role via Contacts storage.
// Status / projectFit / channel enums live in shared/hot-board.ts.
export const hotBoardItems = pgTable("hot_board_items", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  organizationId: varchar("organization_id").references(() => contactOrganizations.id, {
    onDelete: "cascade",
  }),
  contactId: varchar("contact_id").references(() => contacts.id, {
    onDelete: "set null",
  }),
  status: text("status").notNull().default("Hot"),
  notes: text("notes"),
  source: text("source"),
  warmth: text("warmth"),
  projectFit: text("project_fit"),
  score: integer("score"),
  w9Able: boolean("w9_able"),
  outcomeNote: text("outcome_note"),
  nextTouchAt: timestamp("next_touch_at"),
  nextChannel: text("next_channel"),
  lastTouchAt: timestamp("last_touch_at"),
  repliedAt: timestamp("replied_at"),
  /** First operator Send — stable cadence anchor (FU#1 at +4d, FU#2 at +9d). */
  initialOutreachAt: timestamp("initial_outreach_at"),
  /** 0 = initial only, 1 = follow-up #1 sent, 2 = follow-up #2 sent. */
  followUpNudgeCount: integer("follow_up_nudge_count").notNull().default(0),
  /** Last time the due-nudge job surfaced this card for Leo/ops. */
  lastNudgeAt: timestamp("last_nudge_at"),
  pipelineStage: text("pipeline_stage").notNull().default("to_outreach"),
  pipelineStageEnteredAt: timestamp("pipeline_stage_entered_at").defaultNow().notNull(),
  /** Calendar day the operator recorded when the card entered Meeting completed. */
  meetingCompletedAt: timestamp("meeting_completed_at"),
  /** Set when a partner invite is sent. The card leaves the active roster. */
  invitedAt: timestamp("invited_at"),
  /** Bearer archive. NULL means the card is on the board. */
  archivedAt: timestamp("archived_at"),
  createdBy: varchar("created_by").references(() => users.id, { onDelete: "set null" }),
  updatedBy: varchar("updated_by").references(() => users.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
  /** Manual TAM Pick up. Null on cards that were not picked up from TAM. */
  tamAccountId: varchar("tam_account_id").references(() => tamAccounts.id, { onDelete: "set null" }),
}, (table) => ({
  statusUpdatedIdx: index("hot_board_items_status_updated_idx").on(table.status, table.updatedAt),
  tamAccountIdUidx: uniqueIndex("hot_board_items_tam_account_id_uidx")
    .on(table.tamAccountId)
    .where(sql`tam_account_id IS NOT NULL`),
  organizationIdIdx: index("hot_board_items_organization_id_idx")
    .on(table.organizationId)
    .where(sql`organization_id IS NOT NULL`),
  contactIdIdx: index("hot_board_items_contact_id_idx")
    .on(table.contactId)
    .where(sql`contact_id IS NOT NULL`),
  nextTouchAtIdx: index("hot_board_items_next_touch_at_idx")
    .on(table.nextTouchAt)
    .where(
      sql`next_touch_at IS NOT NULL AND replied_at IS NULL AND status NOT IN ('Park', 'Out', 'Done')`,
    ),
}));

export type HotBoardItem = typeof hotBoardItems.$inferSelect;
export type InsertHotBoardItem = typeof hotBoardItems.$inferInsert;

export const hotBoardTouches = pgTable("hot_board_touches", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  itemId: varchar("item_id")
    .notNull()
    .references(() => hotBoardItems.id, { onDelete: "cascade" }),
  kind: text("kind").notNull(),
  note: text("note"),
  createdBy: varchar("created_by").references(() => users.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  /** Set when reply capture logs the touch. Null on operator-typed touches. */
  captureKey: text("capture_key"),
}, (table) => ({
  itemCreatedIdx: index("hot_board_touches_item_created_idx").on(table.itemId, table.createdAt),
  captureKeyUidx: uniqueIndex("hot_board_touches_capture_key_uniq").on(table.captureKey),
}));

export type HotBoardTouch = typeof hotBoardTouches.$inferSelect;
export type InsertHotBoardTouch = typeof hotBoardTouches.$inferInsert;

export const hotBoardBlocks = pgTable("hot_board_blocks", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  organizationId: varchar("organization_id")
    .notNull()
    .references(() => contactOrganizations.id, { onDelete: "cascade" }),
  contactId: varchar("contact_id").references(() => contacts.id, { onDelete: "cascade" }),
  reason: text("reason").notNull(),
  sourceStatus: text("source_status").notNull(),
  createdBy: varchar("created_by").references(() => users.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  organizationIdIdx: index("hot_board_blocks_organization_id_idx").on(table.organizationId),
}));

export type HotBoardBlock = typeof hotBoardBlocks.$inferSelect;
export type InsertHotBoardBlock = typeof hotBoardBlocks.$inferInsert;

/** Hot Board → partner document share ledger (signup-via-access funnel). */
export const partnerDocumentShares = pgTable("partner_document_shares", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tokenHash: text("token_hash").notNull().unique(),
  hotBoardItemId: varchar("hot_board_item_id")
    .notNull()
    .references(() => hotBoardItems.id, { onDelete: "cascade" }),
  contactId: varchar("contact_id")
    .notNull()
    .references(() => contacts.id, { onDelete: "cascade" }),
  agentId: varchar("agent_id")
    .notNull()
    .references(() => referralAgents.id, { onDelete: "cascade" }),
  projectId: varchar("project_id")
    .notNull()
    .references(() => investmentProjects.id, { onDelete: "cascade" }),
  documentIds: jsonb("document_ids")
    .notNull()
    .default(sql`'[]'::jsonb`)
    .$type<string[]>(),
  message: text("message"),
  expiresAt: timestamp("expires_at").notNull(),
  emailSentAt: timestamp("email_sent_at"),
  redeemedAt: timestamp("redeemed_at"),
  createdById: varchar("created_by_id").references(() => users.id, { onDelete: "set null" }),
  revokedAt: timestamp("revoked_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  hotBoardItemIdx: index("partner_document_shares_hot_board_item_id_idx").on(table.hotBoardItemId),
  agentIdx: index("partner_document_shares_agent_id_idx").on(table.agentId),
  contactIdx: index("partner_document_shares_contact_id_idx").on(table.contactId),
}));

export type PartnerDocumentShare = typeof partnerDocumentShares.$inferSelect;
export type InsertPartnerDocumentShare = typeof partnerDocumentShares.$inferInsert;

export type HotBoardTouchSummary = {
  id: string;
  kind: string;
  note: string | null;
  createdAt: Date;
};

export type HotBoardItemCard = HotBoardItem & {
  organizationName: string | null;
  /** CRM only, invited, or active partner. Absent on older payloads. */
  companyStage?: "crm_only" | "invited" | "active_partner";
  /** True when the linked firm is flagged `is_testing`. Absent on older payloads. */
  firmIsTesting?: boolean;
  organizationType: string | null;
  organizationCountry: string | null;
  organizationHqCity: string | null;
  organizationHqState: string | null;
  /** Countries the firm works (markets served), distinct from HQ country. */
  organizationMarketsServed: string[] | null;
  organizationNotes: string | null;
  organizationWechatId: string | null;
  contactFirstName: string | null;
  contactLastName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  contactWechatId: string | null;
  contactJobTitle: string | null;
  isEmailSuppressed: boolean;
  isUnsubscribed: boolean;
  outreachBlocked: boolean;
  touches: HotBoardTouchSummary[];
  accountPresence: {
    hasAccount: boolean;
    accountType: "investor" | "sponsor" | "agent" | "vendor" | null;
    lastSignInAt: string | null;
  };
  upcomingBooking: {
    id: string;
    scheduledAt: Date;
    title: string | null;
  } | null;
  pastBooking: {
    id: string;
    scheduledAt: Date;
    title: string | null;
    status: string | null;
  } | null;
  /** Latest off-platform meeting datetime parsed from meeting_off_platform touches. */
  offPlatformMeetingAt: string | null;
  scheduleSubject: {
    subjectType: "investor" | "partner" | "contact";
    subjectId: string;
  } | null;
  /** Email-matched portal users.id; set even when scheduleSubject is a partner. */
  matchingInvestorId: string | null;
  /** Partner firm the card resolves to (org link, promoted contact, or owner email). */
  linkedAgentId?: string | null;
  /**
   * Stage derived from the Deal terms checklist facts
   * (`@shared/partner-commercial-stage`). Wins over `pipelineStage` when
   * further along. Null when no firm is linked. Absent on older payloads.
   */
  commercialStage?: "pre_commercial" | "send_proposal" | "awaiting_signed_contract" | "won" | null;
  /** When the facts put the firm in `commercialStage`. */
  commercialStageAt?: string | null;
  /** The facts behind `commercialStage`, oldest first, as display text. */
  commercialStageEvidence?: string | null;
};

export const RESEARCH_CHANNELS = [
  "linkedin",
  "website",
  "directory",
  "conference",
  "referral",
  "other",
] as const;
export type ResearchChannel = (typeof RESEARCH_CHANNELS)[number];

export const MARKETING_SUBSCRIPTION_STATES = ["not_opted_in", "subscribed", "unsubscribed"] as const;
export type MarketingSubscriptionState = (typeof MARKETING_SUBSCRIPTION_STATES)[number];

export const MARKETING_SUPPRESSION_REASONS = ["hard_bounce", "spam_complaint", "manual_do_not_contact"] as const;
export type MarketingSuppressionReason = (typeof MARKETING_SUPPRESSION_REASONS)[number];

// Task #3003 — classified consent basis (how/why we may market to a contact).
// Stored as plain text in `contacts.marketing_consent_basis`; validated here so
// adding a new basis value never requires a Postgres enum migration.
export const MARKETING_CONSENT_BASES = [
  "event_signup",
  "web_form",
  "imported_list",
  "referral",
  "partner_application",
  "manual",
  "other",
] as const;
export type MarketingConsentBasis = (typeof MARKETING_CONSENT_BASES)[number];

export const insertContactSchema = createInsertSchema(contacts)
  .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
    promotedToType: true,
    promotedRecordId: true,
    promotedAt: true,
    promotedBy: true,
    marketingSubscriptionState: true,
    marketingConsentSource: true,
    marketingConsentAt: true,
    marketingConsentBasis: true,
    marketingConsentNote: true,
    unsubscribeToken: true,
    firstTouchUtmSource: true,
    firstTouchUtmMedium: true,
    firstTouchUtmCampaign: true,
    firstTouchUtmTerm: true,
    firstTouchUtmContent: true,
    firstTouchSourceClass: true,
    firstTouchLandingPath: true,
    firstTouchAt: true,
    firstTouchPageEventId: true,
  })
  .extend({
    email: z.string().trim().toLowerCase().email().nullable().optional(),
    firstName: z.string().trim().nullable().optional(),
    lastName: z.string().trim().nullable().optional(),
    company: z.string().trim().nullable().optional(),
    phone: z.string().trim().nullable().optional(),
    tags: z.array(z.string().trim().min(1)).optional(),
    notes: z.string().nullable().optional(),
  });

export type InsertContact = z.infer<typeof insertContactSchema>;
export type Contact = typeof contacts.$inferSelect;
// Returned by listContacts — includes a computed suppression-list flag so the
// derived status badge in the table can be fully accurate without a second query,
// plus account/sign-in presence for non-admin portal users mirrored into Contacts.
export type ContactAccountPresence = {
  hasAccount: boolean;
  accountType: "investor" | "sponsor" | "agent" | "vendor" | null;
  lastSignInAt: string | null;
  /** True only for an investor principal that exists and has never signed in. */
  portalActivationAvailable?: boolean;
};
// Active email-sequence membership returned with listContacts so the Contacts
// Hub can show "on sequence" without a second round-trip. Dates are ISO
// strings (JSON-safe); currentStepIndex is 0-based (= next step to send).
// sequenceStatus is the parent track's status — when "paused", nextSendAt is
// informational only (the engine will not send until the sequence is resumed).
export type ContactActiveSequence = {
  enrollmentId: string;
  sequenceId: string;
  sequenceName: string;
  sequenceKey: string;
  sequenceStatus: "active" | "paused" | string;
  currentStepIndex: number;
  nextSendAt: string | null;
  enrolledAt: string;
};
// Any-status enrollment for a single filtered sequence (Contacts Hub sequence
// membership filter). Includes completed/stopped so operators can see remainder
// vs already-touched contacts for that track.
export type ContactFilteredSequenceEnrollment = {
  enrollmentId: string;
  sequenceId: string;
  sequenceName: string;
  sequenceKey: string;
  enrollmentStatus: "active" | "completed" | "stopped" | string;
  sequenceStatus: "active" | "paused" | string;
  currentStepIndex: number;
  nextSendAt: string | null;
  enrolledAt: string;
};
export type ContactWithFlags = Contact & {
  isEmailSuppressed: boolean;
  // True when suppression reason is hard_bounce or spam_complaint — drives the
  // Bounced badge even if contacts.status has not been stamped yet.
  isEmailBounceSuppressed: boolean;
  accountPresence: ContactAccountPresence;
  activeSequences: ContactActiveSequence[];
  filteredSequenceEnrollment?: ContactFilteredSequenceEnrollment | null;
  // Populated when organizationId is set (list/detail). Null when unlinked.
  organization?: ContactOrganizationSummary | null;
  /** Same-email investor row, when present. Detail GET only. */
  matchedInvestor?: {
    id: string;
    name: string;
    pipelineStage: string | null;
  } | null;
  /** Latest Hot Board, Contacts, or contact-initiated outreach. List and detail. */
  latestOutreach?: ContactOutreachSummary | null;
};
export type ContactImportBatch = typeof contactImportBatches.$inferSelect;

// Personalized outreach draft queue (Contacts Hub → AI draft → human review →
// send as Alex). Admin/service-role only; never auto-sends.
export const CONTACT_OUTREACH_DRAFT_STATUSES = [
  "draft",
  "sent",
  "discarded",
  "failed",
] as const;
export type ContactOutreachDraftStatus = (typeof CONTACT_OUTREACH_DRAFT_STATUSES)[number];

export const contactOutreachDrafts = pgTable("contact_outreach_drafts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  contactId: varchar("contact_id")
    .notNull()
    .references(() => contacts.id, { onDelete: "cascade" }),
  createdBy: varchar("created_by").references(() => users.id, { onDelete: "set null" }),
  status: text("status").default("draft").notNull(),
  batchNotes: text("batch_notes"),
  contactNotesSnapshot: text("contact_notes_snapshot"),
  templateKey: text("template_key"),
  contactTypeSnapshot: text("contact_type_snapshot"),
  subject: text("subject").default("").notNull(),
  htmlBody: text("html_body").default("").notNull(),
  model: text("model"),
  generationError: text("generation_error"),
  sentAt: timestamp("sent_at"),
  resendMessageId: text("resend_message_id"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  statusCreatedIdx: index("contact_outreach_drafts_status_idx").on(table.status, table.createdAt),
  contactIdIdx: index("contact_outreach_drafts_contact_id_idx").on(table.contactId),
}));

export type ContactOutreachDraft = typeof contactOutreachDrafts.$inferSelect;

// Saved 1:1 compose templates (Contacts Hub → Send email). Firm-wide;
// admin/service-role only. Hard-deleted; send rows keep template_id nullable.
export const contactEmailTemplates = pgTable("contact_email_templates", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull(),
  subject: text("subject").default("").notNull(),
  body: text("body").default("").notNull(),
  createdBy: varchar("created_by").references(() => users.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  updatedAtIdx: index("contact_email_templates_updated_at_idx").on(table.updatedAt),
}));

export type ContactEmailTemplate = typeof contactEmailTemplates.$inferSelect;

// Per-send log for Contacts Hub 1:1 compose. resendMessageId correlates
// Resend open/click/delivered webhooks (same pattern as email_sequence_sends).
export const contactEmailSends = pgTable("contact_email_sends", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  contactId: varchar("contact_id")
    .notNull()
    .references(() => contacts.id, { onDelete: "cascade" }),
  toEmail: text("to_email").notNull(),
  subject: text("subject").default("").notNull(),
  templateId: varchar("template_id").references(() => contactEmailTemplates.id, { onDelete: "set null" }),
  sentBy: varchar("sent_by").references(() => users.id, { onDelete: "set null" }),
  resendMessageId: text("resend_message_id"),
  gmailMessageId: text("gmail_message_id"),
  sentAt: timestamp("sent_at").defaultNow().notNull(),
  deliveredAt: timestamp("delivered_at"),
  openedAt: timestamp("opened_at"),
  clickedAt: timestamp("clicked_at"),
}, (table) => ({
  contactSentIdx: index("contact_email_sends_contact_id_idx").on(table.contactId, table.sentAt),
  resendMsgIdx: index("contact_email_sends_resend_msg_idx").on(table.resendMessageId),
}));

export type ContactEmailSend = typeof contactEmailSends.$inferSelect;

// One Google Workspace mailbox per admin, used for Contacts one-off send.
// Tokens are app-encrypted. Default-deny RLS; service-role only.
export const adminGmailConnections = pgTable("admin_gmail_connections", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id")
    .notNull()
    .unique()
    .references(() => users.id, { onDelete: "cascade" }),
  email: text("email").notNull(),
  hostedDomain: text("hosted_domain"),
  accessToken: text("access_token").notNull(),
  refreshToken: text("refresh_token").notNull(),
  tokenExpiry: timestamp("token_expiry"),
  connectedAt: timestamp("connected_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export type AdminGmailConnection = typeof adminGmailConnections.$inferSelect;

// Task #2630 — Suppression list. Keyed by email (unique). Reason values:
// 'hard_bounce' | 'spam_complaint' | 'manual_do_not_contact'.
// Source values: 'resend_webhook' | 'admin'.
// All access is service-role only (default-deny RLS on the table).
export const marketingSuppressionList = pgTable("marketing_suppression_list", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  email: text("email").notNull().unique(),
  reason: text("reason").notNull(),
  source: text("source"),
  metadata: jsonb("metadata"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export type MarketingSuppressionEntry = typeof marketingSuppressionList.$inferSelect;

// Soft-bounce streak ladder (job 3A): after this many softs in the rolling
// window the longest temporary backoff applies (7d by default). Soft bounces
// NEVER escalate to permanent hard_bounce / marketing_suppression_list.
// Backoff hours: EMAIL_SOFT_BOUNCE_BACKOFF_{1,2,3}_HOURS (see soft-bounce-eligibility).
export const SOFT_BOUNCE_THRESHOLD = 3;
export const SOFT_BOUNCE_WINDOW_DAYS = 30;

export const EMAIL_BOUNCE_KINDS = ["soft", "hard", "complaint"] as const;
export type EmailBounceKind = (typeof EMAIL_BOUNCE_KINDS)[number];

// Per-event bounce log. Soft bounces accumulate here for audit + streak;
// hard/complaint are also recorded. Suppression list stays 1-row/email and
// is only written for hard / spam_complaint (never soft).
// Default-deny RLS; service-role only (migration 0282).
// Migration 0292 adds from/subject/tags/payload for shared-Resend attribution.
// Migration 0293 adds to_addresses + ownership stamp (Veras vs Immilink).
export const emailBounceEvents = pgTable(
  "email_bounce_events",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    email: text("email").notNull(),
    bounceKind: text("bounce_kind").notNull(),
    resendEmailId: text("resend_email_id").unique(),
    svixMsgId: text("svix_msg_id"),
    metadata: jsonb("metadata"),
    // Resend webhook data.from / data.subject / data.tags (0292). Nullable on
    // legacy rows ingested before attribution fields existed.
    fromAddress: text("from_address"),
    subject: text("subject"),
    tags: jsonb("tags"),
    // Compact Resend `data` snapshot for later attribution forensics (0292).
    payload: jsonb("payload"),
    // Full webhook data.to array (0293). `email` remains the per-recipient key.
    toAddresses: jsonb("to_addresses"),
    // Veras ownership at ingest (0293): sequence_send | outreach_draft |
    // from_domain | immilink_recipient | unattributed.
    ownership: text("ownership"),
    occurredAt: timestamp("occurred_at").defaultNow().notNull(),
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
  (table) => ({
    emailOccurredIdx: index("email_bounce_events_email_occurred_idx").on(
      table.email,
      table.occurredAt,
    ),
  }),
);

export type EmailBounceEvent = typeof emailBounceEvents.$inferSelect;

// Temporary soft-bounce send hold (migration 0317). Soft ≠ permanent Bounced.
// Successful delivery clears soft_streak / backoff_until; bounce event history
// in email_bounce_events is never deleted by this table.
export const emailSoftBounceState = pgTable(
  "email_soft_bounce_state",
  {
    email: text("email").primaryKey(),
    softStreak: integer("soft_streak").notNull().default(0),
    windowStartedAt: timestamp("window_started_at"),
    backoffUntil: timestamp("backoff_until"),
    lastSoftAt: timestamp("last_soft_at"),
    lastDeliveryAt: timestamp("last_delivery_at"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => ({
    backoffUntilIdx: index("email_soft_bounce_state_backoff_until_idx").on(
      table.backoffUntil,
    ),
  }),
);

export type EmailSoftBounceState = typeof emailSoftBounceState.$inferSelect;

// Task #2631 — Saved marketing segments. Each row names a reusable filter
// definition over the contacts table. Membership is computed dynamically at
// query time (never materialized). filter_definition is a JSON object matching
// ContactSegmentFilter in server/storage/contacts.ts.
// Admin-only: default-deny RLS; all access is via service-role (admin routes).
export const marketingSegments = pgTable("marketing_segments", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull(),
  description: text("description"),
  filterDefinition: jsonb("filter_definition").notNull().default({}),
  createdBy: varchar("created_by").references(() => users.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const MARKETING_FUNNEL_STAGES = ["cold", "engaged", "accredited", "committed"] as const;
export type MarketingFunnelStage = (typeof MARKETING_FUNNEL_STAGES)[number];

export const insertMarketingSegmentSchema = createInsertSchema(marketingSegments).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  createdBy: true,
});
export type InsertMarketingSegment = z.infer<typeof insertMarketingSegmentSchema>;
export type MarketingSegment = typeof marketingSegments.$inferSelect;

// ─── Task #2868 — Email nurture sequences ───────────────────────────────────
// An audience/track-keyed multi-email "warm-up" engine. A sequence belongs to
// an audience (investor / agent / vendor / sponsor) so additional tracks can be
// added later without a rebuild; only the investor track ships now. The step
// *content* is code-defined (server/email.ts); these rows hold the schedule,
// ordering, enrollment state, and per-send log used for stop conditions and
// per-step performance. All access is service-role only (default-deny RLS):
// the background engine job and the admin routes are the only callers.

export const EMAIL_SEQUENCE_AUDIENCES = ["investor", "agent", "vendor", "sponsor"] as const;
export type EmailSequenceAudience = (typeof EMAIL_SEQUENCE_AUDIENCES)[number];

export const EMAIL_SEQUENCE_STATUSES = ["active", "paused"] as const;
export type EmailSequenceStatus = (typeof EMAIL_SEQUENCE_STATUSES)[number];

export const EMAIL_SEQUENCE_ENROLLMENT_STATUSES = ["active", "completed", "stopped"] as const;
export type EmailSequenceEnrollmentStatus = (typeof EMAIL_SEQUENCE_ENROLLMENT_STATUSES)[number];

// Why an enrollment stopped early. null while active or after natural
// completion. "removed" = an admin manually pulled the contact.
// "sequence_deleted" = the whole imported sequence was soft-deleted by an
// admin, which stops every in-flight enrollment (Task #2992).
// "disengaged" = the hygiene sweep auto-sunset a cold contact who received
// several sequence emails over a window with no opens/clicks (Task #3000),
// stopping their active enrollments to protect sending reputation.
export const EMAIL_SEQUENCE_STOP_REASONS = [
  "unsubscribed",
  "suppressed",
  "replied",
  "converted",
  "booked_call",
  "registered",
  "removed",
  "sequence_deleted",
  "disengaged",
] as const;
export type EmailSequenceStopReason = (typeof EMAIL_SEQUENCE_STOP_REASONS)[number];

// The sequence definition. `key` is the stable code identifier the engine and
// content dispatcher use (e.g. "investor_welcome"); `audience` is the track.
export const emailSequences = pgTable("email_sequences", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  key: text("key").notNull().unique(),
  audience: text("audience").notNull(),
  name: text("name").notNull(),
  description: text("description"),
  // Operator-facing 'active' | 'paused' for the claim-aware tip.
  // Mapped to column lifecycle_status so the July 2026 Replit binary (which
  // still SELECTs column "status") never sees active due work. Column "status"
  // is a legacy facade forced to 'paused' by a BEFORE INSERT/UPDATE trigger.
  status: text("lifecycle_status").default("paused").notNull(),
  // Legacy facade column — always 'paused' at rest. Do not use for send gates.
  legacyRunnerStatus: text("status").default("paused").notNull(),
  // 'code' | 'imported'. 'code' = seeded/owned by EMAIL_SEQUENCE_DEFINITIONS;
  // the boot seeder only ever touches these. 'imported' = created by an admin
  // from pasted JSON (Task #2976); fully DB-defined and never seeder-managed.
  origin: text("origin").default("code").notNull(),
  // Optional From / Reply-To overrides for this sequence. When null, sequence
  // sends fall back to Alex (alexandra@verascap.com) — never transactional
  // hello@ / noreply@. Requires verascap.com verified in Resend.
  fromName: text("from_name"),
  fromEmail: text("from_email"),
  replyTo: text("reply_to"),
  // Soft-delete marker (Task #2992). NULL = live. Only imported sequences can
  // be deleted; deleting hides the sequence from the active list and stops every
  // in-flight enrollment. Restore clears this and returns the sequence to paused.
  deletedAt: timestamp("deleted_at"),
  // America/New_York calendar-day send ceiling for THIS sequence. NULL/≤0 is
  // treated as DEFAULT_SEQUENCE_DAILY_CAP (25) at claim time — never unlimited.
  // Enforced by winning email_sequence_daily_cap_claims before Resend.
  dailyCap: integer("daily_cap"),
  // False = retired / legacy alias track. Engine must not send; history kept.
  sendable: boolean("sendable").default(true).notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

// An ordered step within a sequence. `stepIndex` is 0-based; `delayDays` is the
// gap after the previous step (or after enrollment for step 0). `templateKey`
// names the code-defined content function in server/email.ts.
export const emailSequenceSteps = pgTable("email_sequence_steps", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  sequenceId: varchar("sequence_id").notNull().references(() => emailSequences.id, { onDelete: "cascade" }),
  stepIndex: integer("step_index").notNull(),
  delayDays: integer("delay_days").default(0).notNull(),
  subject: text("subject").notNull(),
  // Task #3001 — A/B subject testing. ADDITIONAL subject lines beyond the base
  // `subject` above. The full variant list the engine balances across is
  // [subject, ...subjectVariants]; the A/B test is "on" for a step when this has
  // ≥1 entry. Nullable/empty = no A/B (just the base subject). An admin edit to
  // the subject or variants clears `subjectWinnerIndex` so a stale winner can't
  // pin sends to a since-changed line.
  subjectVariants: jsonb("subject_variants").$type<string[]>(),
  // Task #3001 — promoted winner. When set, the engine stops splitting and sends
  // ONLY this variant (a 0-based index into [subject, ...subjectVariants]) for
  // the step's remaining sends. Null = keep balancing across all variants.
  subjectWinnerIndex: integer("subject_winner_index"),
  // Admin-editable HTML body for this step. Nullable: when null the code-defined
  // builder in server/email.ts supplies the body (seeded on boot, then admins
  // may override per step). The send + preview paths prefer this DB copy.
  body: text("body"),
  // Per-step override for the cold-introduction footer (mailing-address +
  // education disclaimer). Nullable: when null the send/preview paths fall back
  // to the hard-coded COLD_INTRO_TEMPLATE_KEYS check (unchanged behavior for
  // code-owned steps). Imported steps set this explicitly from the sequence's
  // JSON `coldIntro` flag (Task #2976).
  coldIntro: boolean("cold_intro"),
  templateKey: text("template_key").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  stepUniqueIdx: uniqueIndex("email_sequence_steps_seq_index_uniq").on(table.sequenceId, table.stepIndex),
}));

// One row per (sequence, contact). The unique index guarantees no contact is
// ever double-enrolled. `currentStepIndex` is the number of steps already sent
// (= the next step to send). `nextSendAt` is when that next step is due.
export const emailSequenceEnrollments = pgTable("email_sequence_enrollments", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  sequenceId: varchar("sequence_id").notNull().references(() => emailSequences.id, { onDelete: "cascade" }),
  contactId: varchar("contact_id").notNull().references(() => contacts.id, { onDelete: "cascade" }),
  // Snapshot of the contact email at enroll time (normalized lowercase) so the
  // engine's stop-condition lookups are stable even if the contact row changes.
  email: text("email").notNull(),
  // 'active' | 'completed' | 'stopped'.
  status: text("status").default("active").notNull(),
  currentStepIndex: integer("current_step_index").default(0).notNull(),
  nextSendAt: timestamp("next_send_at"),
  stopReason: text("stop_reason"),
  enrolledBy: varchar("enrolled_by").references(() => users.id, { onDelete: "set null" }),
  enrolledAt: timestamp("enrolled_at").defaultNow().notNull(),
  lastSentAt: timestamp("last_sent_at"),
  completedAt: timestamp("completed_at"),
  stoppedAt: timestamp("stopped_at"),
  // Person key + Partner Welcome persona snapshot (job 3B). Mid-series profile
  // edits must not change rendering; person key feeds sibling opt-out + day cap.
  personKey: text("person_key"),
  partnerPersona: text("partner_persona"),
  personaSnapshot: jsonb("persona_snapshot").$type<Record<string, unknown>>(),
  enrollmentSource: text("enrollment_source"),
}, (table) => ({
  enrollmentUniqueIdx: uniqueIndex("email_sequence_enrollments_seq_contact_uniq").on(table.sequenceId, table.contactId),
  dueIdx: index("email_sequence_enrollments_due_idx").on(table.status, table.nextSendAt),
  personKeyIdx: index("email_sequence_enrollments_person_key_idx").on(table.personKey),
}));

// Per-send log. One row per step actually sent to a contact. `resendMessageId`
// correlates Resend open/click webhook events back to this send so per-step
// performance (sent / opens / clicks) is accurate.
export const emailSequenceSends = pgTable("email_sequence_sends", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  enrollmentId: varchar("enrollment_id").notNull().references(() => emailSequenceEnrollments.id, { onDelete: "cascade" }),
  sequenceId: varchar("sequence_id").notNull().references(() => emailSequences.id, { onDelete: "cascade" }),
  contactId: varchar("contact_id").notNull().references(() => contacts.id, { onDelete: "cascade" }),
  stepIndex: integer("step_index").notNull(),
  email: text("email").notNull(),
  resendMessageId: text("resend_message_id"),
  // Task #3001 — A/B subject testing. Which subject variant this send used,
  // as a 0-based index into the step's full variant list ([subject,
  // ...subjectVariants]). NULL for sends of steps with no A/B test (a plain
  // single-subject step); folded into variant 0 for reporting/balancing.
  subjectVariantIndex: integer("subject_variant_index"),
  sentAt: timestamp("sent_at").defaultNow().notNull(),
  // Set when Resend posts email.delivered for this message id. NULL for
  // legacy rows / in-flight; report falls back to sent − hard_bounce.
  deliveredAt: timestamp("delivered_at"),
  openedAt: timestamp("opened_at"),
  clickedAt: timestamp("clicked_at"),
  // First destination URL from Resend email.clicked (`data.click.link`).
  // COALESCE on write — first click wins. Destination only (not recipient).
  firstClickedUrl: text("first_clicked_url"),
  // Which route issued this send (job / canary / admin_test_send / …).
  sendSource: text("send_source"),
  // `pid@hostname` of the process that wrote the row.
  sendProcess: text("send_process"),
  // Deployed git SHA / package version of that process.
  sendAppVersion: text("send_app_version"),
  // Matching daily_cap claim. New inserts require a claim (DB trigger);
  // nullable so pre-gate legacy rows remain readable.
  dailyCapClaimId: varchar("daily_cap_claim_id"),
}, (table) => ({
  sendUniqueIdx: uniqueIndex("email_sequence_sends_enrollment_step_uniq").on(table.enrollmentId, table.stepIndex),
  resendMsgIdx: index("email_sequence_sends_resend_msg_idx").on(table.resendMessageId),
  contactSentIdx: index("email_sequence_sends_contact_sent_idx").on(table.contactId, table.sentAt),
}));

// Per-click log for sequence sends. One row per Resend email.clicked that
// carries a link URL (Resend wraps hrefs; we store the destination). Enables
// marketing queries for which links were clicked and distinguishing scanner
// prefetch (many URLs in <1s) from a single human click.
export const emailSequenceSendClicks = pgTable("email_sequence_send_clicks", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  sendId: varchar("send_id")
    .notNull()
    .references(() => emailSequenceSends.id, { onDelete: "cascade" }),
  url: text("url").notNull(),
  clickedAt: timestamp("clicked_at").defaultNow().notNull(),
  svixMsgId: text("svix_msg_id"),
}, (table) => ({
  sendIdIdx: index("email_sequence_send_clicks_send_id_idx").on(table.sendId),
  urlIdx: index("email_sequence_send_clicks_url_idx").on(table.url),
  svixMsgUniq: uniqueIndex("email_sequence_send_clicks_svix_msg_uniq")
    .on(table.svixMsgId)
    .where(sql`svix_msg_id IS NOT NULL`),
}));

export type EmailSequenceSendClick = typeof emailSequenceSendClicks.$inferSelect;

// daily_cap reservations for the America/New_York operator day. A send MUST
// win a claim before Resend; counted with legacy sends so concurrent workers
// cannot overshoot (Haynesville 50→59 / Mon 2026-08-24 50 claims vs 231 sends).
export const emailSequenceDailyCapClaims = pgTable(
  "email_sequence_daily_cap_claims",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    sequenceId: varchar("sequence_id")
      .notNull()
      .references(() => emailSequences.id, { onDelete: "cascade" }),
    enrollmentId: varchar("enrollment_id")
      .notNull()
      .references(() => emailSequenceEnrollments.id, { onDelete: "cascade" }),
    stepIndex: integer("step_index").notNull(),
    claimedAt: timestamp("claimed_at").defaultNow().notNull(),
    // 'marketing' counts toward sequence + global daily ceilings.
    // 'preview' is the tiny admin test/canary allowance.
    kind: text("kind").default("marketing").notNull(),
  },
  (table) => ({
    enrollmentStepUniq: uniqueIndex("email_sequence_daily_cap_claims_enrollment_step_uniq").on(
      table.enrollmentId,
      table.stepIndex,
    ),
    seqClaimedIdx: index("email_sequence_daily_cap_claims_seq_claimed_idx").on(
      table.sequenceId,
      table.claimedAt,
    ),
  }),
);

/**
 * Max one marketing sequence email per recipient per America/New_York day
 * across ALL sequences. Key kind is `"email"` (normalized address) by default;
 * person-resolution uses `"person"` with a serialized person key so sibling
 * emails share one operator-day budget.
 */
export const emailSequenceRecipientDayClaims = pgTable(
  "email_sequence_recipient_day_claims",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    recipientKeyKind: text("recipient_key_kind").notNull().default("email"),
    recipientKey: text("recipient_key").notNull(),
    dayStart: timestamp("day_start").notNull(),
    enrollmentId: varchar("enrollment_id")
      .notNull()
      .references(() => emailSequenceEnrollments.id, { onDelete: "cascade" }),
    stepIndex: integer("step_index").notNull(),
    claimedAt: timestamp("claimed_at").defaultNow().notNull(),
    kind: text("kind").default("marketing").notNull(),
  },
  (table) => ({
    recipientDayUniq: uniqueIndex("email_sequence_recipient_day_claims_key_day_uniq")
      .on(table.recipientKeyKind, table.recipientKey, table.dayStart)
      .where(sql`kind = 'marketing'`),
    enrollmentStepIdx: index("email_sequence_recipient_day_claims_enrollment_step_idx").on(
      table.enrollmentId,
      table.stepIndex,
    ),
  }),
);

// One send per (person, canonical sequence, step) — blocks resend across legacy
// duplicate partner-welcome rows without rewriting historical sequence_id.
export const emailSequencePersonStepSends = pgTable(
  "email_sequence_person_step_sends",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    personKey: text("person_key").notNull(),
    canonicalSequenceKey: text("canonical_sequence_key").notNull(),
    stepIndex: integer("step_index").notNull(),
    sendId: varchar("send_id").references(() => emailSequenceSends.id, {
      onDelete: "set null",
    }),
    enrollmentId: varchar("enrollment_id").references(() => emailSequenceEnrollments.id, {
      onDelete: "set null",
    }),
    sentAt: timestamp("sent_at").defaultNow().notNull(),
  },
  (table) => ({
    uniq: uniqueIndex("email_sequence_person_step_sends_uniq").on(
      table.personKey,
      table.canonicalSequenceKey,
      table.stepIndex,
    ),
  }),
);

// Legacy sequence key → canonical key for reporting (never for new sends).
export const emailSequenceLegacyAliases = pgTable("email_sequence_legacy_aliases", {
  legacyKey: text("legacy_key").primaryKey(),
  canonicalKey: text("canonical_key").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

// Operational review when auto-enroll cannot resolve partner persona.
export const emailSequenceEnrollmentReviews = pgTable("email_sequence_enrollment_reviews", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  contactId: varchar("contact_id").references(() => contacts.id, { onDelete: "set null" }),
  email: text("email"),
  reason: text("reason").notNull(),
  detail: text("detail"),
  payload: jsonb("payload").$type<Record<string, unknown>>(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  resolvedAt: timestamp("resolved_at"),
});

export const insertEmailSequenceSchema = createInsertSchema(emailSequences).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertEmailSequence = z.infer<typeof insertEmailSequenceSchema>;
export type EmailSequence = typeof emailSequences.$inferSelect;
export type EmailSequenceStep = typeof emailSequenceSteps.$inferSelect;
export type EmailSequenceEnrollment = typeof emailSequenceEnrollments.$inferSelect;
export type EmailSequenceSend = typeof emailSequenceSends.$inferSelect;
export type EmailSequenceDailyCapClaim = typeof emailSequenceDailyCapClaims.$inferSelect;
export type EmailSequenceRecipientDayClaim = typeof emailSequenceRecipientDayClaims.$inferSelect;
export type EmailSequencePersonStepSend = typeof emailSequencePersonStepSends.$inferSelect;
export type EmailSequenceLegacyAlias = typeof emailSequenceLegacyAliases.$inferSelect;
export type EmailSequenceEnrollmentReview = typeof emailSequenceEnrollmentReviews.$inferSelect;

// Task #2998 — sending-reputation guardrails. Single-row global "reputation
// hold" state for the email-sequence engine. When the rolling bounce/complaint
// rate over a recent window crosses the configured threshold, the engine flips
// `paused` to true and stops sending ALL sequence emails until an admin
// resumes. This is a global kill-switch, intentionally separate from
// per-sequence status so resuming the hold does not clobber individually-paused
// sequences. Singleton keyed id='global'.
export const emailSendReputationState = pgTable("email_send_reputation_state", {
  id: text("id").primaryKey().default("global"),
  paused: boolean("paused").default(false).notNull(),
  pausedAt: timestamp("paused_at"),
  pauseReason: text("pause_reason"),
  pauseDetail: jsonb("pause_detail"),
  resumedAt: timestamp("resumed_at"),
  resumedBy: varchar("resumed_by").references(() => users.id, { onDelete: "set null" }),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});
export type EmailSendReputationState = typeof emailSendReputationState.$inferSelect;

// Manual global sequence-engine pause. Distinct from the automatic reputation
// hold: reputation resume must never clear this, and a missing row is treated
// as paused (fail-closed). Operators toggle it from Admin → Sequences.
export const emailSequenceEnginePause = pgTable("email_sequence_engine_pause", {
  id: text("id").primaryKey().default("global"),
  paused: boolean("paused").default(true).notNull(),
  pausedAt: timestamp("paused_at"),
  pausedBy: varchar("paused_by").references(() => users.id, { onDelete: "set null" }),
  pauseReason: text("pause_reason"),
  resumedAt: timestamp("resumed_at"),
  resumedBy: varchar("resumed_by").references(() => users.id, { onDelete: "set null" }),
  resumeReason: text("resume_reason"),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});
export type EmailSequenceEnginePause = typeof emailSequenceEnginePause.$inferSelect;

// Task #2999 — timezone & quiet-hours send window for marketing sequences.
// Single global config row (id='global') the engine reads each tick to decide
// whether a due send falls within the recipient's local send window, and to
// snap the next step's send time into that window. Admin-editable from the
// Sequences page. Separate from per-sequence status and the reputation hold.
//
// sendWindowStartHour / sendWindowEndHour are local hours in [0,24] with start
// < end (no overnight windows); a send is allowed when the recipient's local
// hour h satisfies start <= h < end. quietHoursEnabled is a master switch so
// the gating can be turned off without losing the configured window.
export const emailSequenceSettings = pgTable("email_sequence_settings", {
  id: text("id").primaryKey().default("global"),
  quietHoursEnabled: boolean("quiet_hours_enabled").default(true).notNull(),
  sendWindowStartHour: integer("send_window_start_hour").default(8).notNull(),
  sendWindowEndHour: integer("send_window_end_hour").default(18).notNull(),
  // IANA timezone used when a contact has neither an explicit timezone nor a
  // country the engine can map to a zone.
  defaultTimezone: text("default_timezone").default("America/New_York").notNull(),
  // Operator-settable global marketing ceiling (sends/day across all sequences).
  // NULL = refuse marketing claims (fail-closed). Never seed a production value.
  globalDailyCeiling: integer("global_daily_ceiling"),
  globalDailyCeilingUpdatedAt: timestamp("global_daily_ceiling_updated_at"),
  globalDailyCeilingUpdatedBy: varchar("global_daily_ceiling_updated_by").references(
    () => users.id,
    { onDelete: "set null" },
  ),
  globalDailyCeilingChangeReason: text("global_daily_ceiling_change_reason"),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
  updatedBy: varchar("updated_by").references(() => users.id, { onDelete: "set null" }),
});
export type EmailSequenceSettings = typeof emailSequenceSettings.$inferSelect;

// Aggregated per-step performance returned by the storage layer for the admin
// view (sent / opens / clicks per step, plus optional A/B variant breakdown).
export type EmailSequenceStepStats = {
  stepIndex: number;
  subject: string;
  delayDays: number;
  sent: number;
  opened: number;
  clicked: number;
  // Task #3001 — A/B subject testing. `variants` is present (length ≥ 2) only
  // when the step has at least one extra subject variant configured; each entry
  // carries that variant's current subject line and its own sent/opens/clicks.
  // `subjectWinnerIndex` is the promoted winner (the engine then sends only it),
  // or null while the split is still running. Both optional so non-A/B steps and
  // older cached payloads are unaffected.
  variants?: EmailSequenceVariantStat[];
  subjectWinnerIndex?: number | null;
};

// Task #3001 — per-variant performance for one step's A/B subject test.
// `variantIndex` is a 0-based index into the step's full variant list
// ([subject, ...subjectVariants]); index 0 is the base subject (legacy sends
// with no recorded variant are folded into it).
export type EmailSequenceVariantStat = {
  variantIndex: number;
  subject: string;
  sent: number;
  opened: number;
  clicked: number;
};

// Used by the contacts import endpoint. The client pre-parses the file
// (xlsx via JSZip or CSV) sheet-by-sheet, then submits the parsed rows
// grouped per sheet/list. The server normalizes + validates emails,
// de-dupes within the file and against existing contacts, auto-suggests a
// type, stamps the event metadata, and reports a per-row result back.
export const contactImportRowSchema = z.object({
  firstName: z.string().trim().optional().nullable(),
  lastName: z.string().trim().optional().nullable(),
  company: z.string().trim().optional().nullable(),
  email: z.string().trim().optional().nullable(),
  phone: z.string().trim().optional().nullable(),
  country: z.string().trim().optional().nullable(),
  salutation: z.string().trim().optional().nullable(),
  jobTitle: z.string().trim().optional().nullable(),
  website: z.string().trim().optional().nullable(),
  organizationType: z.string().trim().optional().nullable(),
  linkedinUrl: z.string().trim().optional().nullable(),
  companyLinkedinUrl: z.string().trim().optional().nullable(),
});

export const contactImportSchema = z
  .object({
    label: z.string().trim().min(1, "Batch label is required").max(160),
    sourceEvent: z.string().trim().max(160).optional().nullable(),
    sourceCity: z.string().trim().max(120).optional().nullable(),
    sourceDate: z.string().trim().max(60).optional().nullable(),
    fileName: z.string().trim().max(260).optional().nullable(),
    consent: z.literal(true, {
      error: "Consent attestation is required",
    }),
    // Task #3003 — consent provenance for the whole batch. Stamped on every
    // contact created by this import. `consentBasis` defaults to
    // "imported_list" in the importer when omitted; `consentNote` is an
    // optional free-text reference to where the consent proof lives.
    consentBasis: z.enum(MARKETING_CONSENT_BASES).optional(),
    consentNote: z.string().trim().max(500).optional().nullable(),
    tags: z.array(z.string().trim().min(1)).optional(),
    // Task #2776 — auto-categorization controls. When `autoCategorize` is not
    // false, the importer maps each row's organization-type cell to a canonical
    // tag (+ contactType) via server/lib/contact-category. `categoryOverrides`
    // lets the admin correct unrecognized values before submitting: a map of
    // normalized organization-type value → canonical tag. Neither field changes
    // the DB schema; they only steer the importer.
    autoCategorize: z.boolean().optional(),
    categoryOverrides: z.record(z.string(), z.string()).optional(),
    sheets: z
      .array(
        z.object({
          name: z.string().trim().max(160),
          rows: z.array(contactImportRowSchema),
        }),
      )
      .min(1, "At least one sheet is required")
      .max(50, "A file may contain at most 50 sheets"),
  })
  .refine(
    (v) => v.sheets.reduce((n, s) => n + s.rows.length, 0) <= 20000,
    { message: "Import is limited to 20,000 rows per file" },
  );

export type ContactImportInput = z.infer<typeof contactImportSchema>;

export type ContactImportRowResult = {
  email: string | null;
  status: "created" | "skipped_duplicate" | "invalid_email" | "missing_email" | "invalid_label";
  contactId?: string;
  sheet?: string;
};

export type ContactImportResult = {
  batchId: string;
  total: number;
  created: number;
  skippedDuplicates: number;
  invalidEmails: number;
  missingEmails: number;
  invalidLabels: number;
  rows: ContactImportRowResult[];
};

export const investorInquiries = pgTable("investor_inquiries", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").references(() => users.id, { onDelete: "set null" }),
  projectId: varchar("project_id").references(() => investmentProjects.id, { onDelete: "set null" }),
  name: text("name").notNull(),
  email: text("email").notNull(),
  phone: text("phone"),
  country: text("country").notNull(),
  message: text("message"),
  source: text("source"),
  utmSource: text("utm_source"),
  utmMedium: text("utm_medium"),
  utmCampaign: text("utm_campaign"),
  utmTerm: text("utm_term"),
  utmContent: text("utm_content"),
  referrer: text("referrer"),
  landingPage: text("landing_page"),
  status: text("status").default("new").notNull(),
  // Task #976 — forward link to the inbox_threads row that owns this
  // inquiry's conversation history. Nullable on creation; stamped by
  // the public POST handler immediately after the thread is inserted,
  // and backfilled for legacy rows in migration 0111.
  //
  // The FK constraint (ON DELETE SET NULL) is enforced at the DB
  // level by 0111_inbox_inquiry_link.sql. We intentionally do NOT
  // declare a Drizzle `.references(() => inboxThreads.id, …)` here
  // because inboxThreads.sourceInquiryId already references
  // investorInquiries.id — adding the inverse pointer creates a
  // circular type dependency that breaks Drizzle's type inference
  // for $inferInsert across the codebase. The migration is the
  // source of truth for this constraint.
  threadId: varchar("thread_id"),
  // Task #1004 — attribution mirror.
  attributedSource: text("attributed_source").default("unknown").notNull(),
  provisionalReferralAgentId: varchar("provisional_referral_agent_id"),
  attributionConfidence: integer("attribution_confidence").default(0).notNull(),
  attributionStatus: text("attribution_status").default("pending_review").notNull(),
  attributionCapturedAt: timestamp("attribution_captured_at"),
  attributionFirstTouch: jsonb("attribution_first_touch"),
  attributionLastTouch: jsonb("attribution_last_touch"),
  newLeadScore: integer("new_lead_score"),
  claimedReferrerType: text("claimed_referrer_type"),
  claimedReferrerId: varchar("claimed_referrer_id"),
  payProduct: text("pay_product").notNull().default("none"),
  personId: varchar("person_id"),
  createdAt: timestamp("created_at").defaultNow(),
});

// Task #1004 — Per-decision audit trail. Every confirm / reassign /
// dispute / merge / touch action lands here so support can defend a
// disputed agent commission six months later. `payload` snapshots
// PII-sensitive fields (email/name/phone) — the GDPR redaction tool
// nulls those columns on a forget request without dropping the row.
export const leadAttributionEvents = pgTable("lead_attribution_events", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").references(() => users.id, { onDelete: "cascade" }),
  inquiryId: varchar("inquiry_id"),
  leadId: varchar("lead_id"),
  action: text("action").notNull(),
  fromStatus: text("from_status"),
  toStatus: text("to_status"),
  fromAgentId: varchar("from_agent_id"),
  toAgentId: varchar("to_agent_id"),
  reason: text("reason"),
  actorAdminId: varchar("actor_admin_id").references(() => users.id, { onDelete: "set null" }),
  payload: jsonb("payload"),
  createdAt: timestamp("created_at").defaultNow(),
});

export type LeadAttributionEvent = typeof leadAttributionEvents.$inferSelect;
export type InsertLeadAttributionEvent = typeof leadAttributionEvents.$inferInsert;

// Task #1106 — Explicit "this lead is shared with these partners" rows.
// Created when an admin approves a partner's review-request so the
// second partner can see the lead in their portal as `shared` instead
// of being told it doesn't exist. Per (target row × partner) is unique.
export const leadPartnerShares = pgTable("lead_partner_shares", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").references(() => users.id, { onDelete: "cascade" }),
  inquiryId: varchar("inquiry_id"),
  leadId: varchar("lead_id"),
  partnerId: varchar("partner_id").notNull(),
  shareRole: text("share_role").notNull().default("shared"),
  createdByAdminId: varchar("created_by_admin_id").references(() => users.id, {
    onDelete: "set null",
  }),
  reason: text("reason"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});
export type LeadPartnerShare = typeof leadPartnerShares.$inferSelect;
export type InsertLeadPartnerShare = typeof leadPartnerShares.$inferInsert;

// Task #1106 — Partner-filed "please look at this potential duplicate"
// requests. Created when the partner-portal Add-Investor flow finds a
// duplicate held by someone else; admins resolve via the inbox at
// /admin/leads/review-requests with approve_share / reassign / decline.
export const leadReviewRequests = pgTable("lead_review_requests", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  partnerId: varchar("partner_id").notNull(),
  targetUserId: varchar("target_user_id").references(() => users.id, {
    onDelete: "cascade",
  }),
  targetInquiryId: varchar("target_inquiry_id"),
  targetLeadId: varchar("target_lead_id"),
  submittedEmail: text("submitted_email"),
  submittedName: text("submitted_name"),
  submittedPhone: text("submitted_phone"),
  submittedCountry: text("submitted_country"),
  // Task #1714 — carry the broker-submitted EB-5 geography into the
  // admin review-request payload so an admin can judge a possible
  // duplicate match faster without asking the broker to re-send it.
  submittedCountryOfBirth: text("submitted_country_of_birth"),
  submittedCountryOfResidence: text("submitted_country_of_residence"),
  submittedCityOfResidence: text("submitted_city_of_residence"),
  submittedStateOfResidence: text("submitted_state_of_residence"),
  matchedFields: jsonb("matched_fields"),
  note: text("note"),
  status: text("status").notNull().default("open"),
  resolvedByAdminId: varchar("resolved_by_admin_id").references(() => users.id, {
    onDelete: "set null",
  }),
  resolvedAt: timestamp("resolved_at"),
  resolutionNote: text("resolution_note"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});
export type LeadReviewRequest = typeof leadReviewRequests.$inferSelect;
export type InsertLeadReviewRequest = typeof leadReviewRequests.$inferInsert;

export const forumPosts = pgTable("forum_posts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  title: text("title").notNull(),
  content: text("content").notNull(),
  authorName: text("author_name").notNull(),
  category: text("category").notNull(),
  country: text("country"),
  views: integer("views").default(0),
  likes: integer("likes").default(0),
  isPinned: boolean("is_pinned").default(false),
  createdAt: timestamp("created_at").defaultNow(),
});

export const forumComments = pgTable("forum_comments", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  postId: varchar("post_id").notNull().references(() => forumPosts.id, { onDelete: "cascade" }),
  content: text("content").notNull(),
  authorName: text("author_name").notNull(),
  likes: integer("likes").default(0),
  createdAt: timestamp("created_at").defaultNow(),
});

export const visaBulletinViews = pgTable("visa_bulletin_views", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  country: text("country").notNull(),
  category: text("category").notNull(),
  userId: varchar("user_id").references(() => users.id, { onDelete: "set null" }),
  sessionId: text("session_id"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const fundsAssessments = pgTable("funds_assessments", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  email: text("email").notNull(),
  name: text("name").notNull(),
  country: text("country").notNull(),
  investmentTarget: text("investment_target").notNull(),
  fundSources: jsonb("fund_sources").notNull(),
  overallScore: integer("overall_score").notNull(),
  overallRating: text("overall_rating").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
});

export const rcReviews = pgTable("rc_reviews", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  authorName: text("author_name").notNull(),
  authorCountry: text("author_country").notNull(),
  rcName: text("rc_name").notNull(),
  rcId: text("rc_id").notNull(),
  rating: integer("rating").notNull(),
  projectName: text("project_name"),
  processStartDate: text("process_start_date").notNull(),
  currentPhase: text("current_phase").notNull(),
  investmentRepaid: text("investment_repaid").notNull(),
  title: text("title").notNull(),
  content: text("content").notNull(),
  helpful: integer("helpful").default(0),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertRcReviewSchema = createInsertSchema(rcReviews).omit({
  id: true,
  helpful: true,
  createdAt: true,
});

export type InsertRcReview = z.infer<typeof insertRcReviewSchema>;
export type RcReview = typeof rcReviews.$inferSelect;

export const insertFundsAssessmentSchema = createInsertSchema(fundsAssessments).omit({
  id: true,
  createdAt: true,
});

export type InsertFundsAssessment = z.infer<typeof insertFundsAssessmentSchema>;
export type FundsAssessment = typeof fundsAssessments.$inferSelect;

export interface FundSourceEntry {
  sourceId: string;
  amount: number;
  yearsHeld: string;
  documentationReady: "yes" | "partial" | "no";
  score: number;
  rating: string;
}

export const communicationPreferencesSchema = z.object({
  preferredContact: z.enum(["email", "phone", "whatsapp", "both_email_phone", "all"]).default("email"),
  whatsappNumber: z.string().optional(),
  emailFrequency: z.enum(["realtime", "daily", "weekly"]).default("realtime"),
  bestCallTime: z.enum(["morning", "afternoon", "evening", "anytime"]).default("anytime"),
  timezone: z.string().default("America/New_York"),
  notifications: z.object({
    accountUpdates: z.literal(true).default(true),
    projectUpdates: z.boolean().default(true),
    marketNews: z.boolean().default(false),
    promotional: z.boolean().default(false),
    consultationReminders: z.literal(true).default(true),
  }).prefault({}),
});

export type CommunicationPreferences = z.infer<typeof communicationPreferencesSchema>;

export const insertUserSchema = createInsertSchema(users).omit({
  id: true,
  createdAt: true,
  deletedAt: true,
  role: true,
  accreditationStatus: true,
});

export const loginSchema = z.object({
  email: z.string().email("Invalid email address"),
  password: z.string().min(8, "Password must be at least 8 characters"),
});

export const registerSchema = z.object({
  email: z.string().email("Invalid email address"),
  password: passwordPolicySchema,
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  country: z.string().min(1, "Please select your country"),
  // Optional explicit marketing consent from the register form checkbox.
  // Default/absent = not opted in (never invent consent).
  marketingOptIn: z.boolean().optional(),
  // Optional: the /register picker only ever submits 'investor' here
  // (the other three branches navigate away to /agents/apply or
  // /schedule-call before reaching this endpoint), but we accept
  // any of the four so direct API callers can self-identify.
  userType: z
    .enum(["investor", "attorney", "broker_dealer", "foreign_agent"])
    .optional(),
  utmSource: z.string().optional(),
  utmMedium: z.string().optional(),
  utmCampaign: z.string().optional(),
  utmTerm: z.string().optional(),
  utmContent: z.string().optional(),
  referrer: z.string().optional(),
  landingPage: z.string().optional(),
  // Public /refer/:code signup sends the partner code so credit is not cookie-only.
  // Empty / null must be treated as omitted — Zod `.optional()` rejects null and
  // `.min(1)` rejects "" which would 400 a otherwise-valid signup.
  referralCode: z
    .string()
    .trim()
    .max(64)
    .nullish()
    .transform((v) => (v == null || v === "" ? undefined : v)),
});

export const insertForumPostSchema = createInsertSchema(forumPosts).omit({
  id: true,
  views: true,
  likes: true,
  isPinned: true,
  createdAt: true,
});

export const insertForumCommentSchema = createInsertSchema(forumComments).omit({
  id: true,
  likes: true,
  createdAt: true,
});

export const insertVisaBulletinViewSchema = createInsertSchema(visaBulletinViews).omit({
  id: true,
  createdAt: true,
});

export const insertProjectSchema = createInsertSchema(investmentProjects).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
  currentRaise: true,
});

export const insertAccreditationDocumentSchema = createInsertSchema(accreditationDocuments).omit({
  id: true,
  uploadedAt: true,
  reviewedAt: true,
  reviewedBy: true,
  status: true,
  notes: true,
});

export const insertCallBookingSchema = createInsertSchema(callBookings).omit({
  id: true,
  status: true,
  adminNotes: true,
  createdAt: true,
});

export const insertAdminCalendarSettingsSchema = createInsertSchema(adminCalendarSettings).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertInPersonMeetingWindowSchema = createInsertSchema(inPersonMeetingWindows).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertMeetingReminderSchema = createInsertSchema(meetingReminders).omit({
  id: true,
  sentAt: true,
  status: true,
});

export const insertDealRoomAccessSchema = createInsertSchema(dealRoomAccess).omit({
  id: true,
  grantedAt: true,
  revokedAt: true,
  status: true,
});

export const insertDealRoomDocumentSchema = createInsertSchema(dealRoomDocuments).omit({
  id: true,
  uploadedAt: true,
});

export const insertDealRoomDocChunkSchema = createInsertSchema(dealRoomDocChunks).omit({
  id: true,
  createdAt: true,
});

export const insertDealRoomChatThreadSchema = createInsertSchema(dealRoomChatThreads).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertDealRoomChatMessageSchema = createInsertSchema(dealRoomChatMessages).omit({
  id: true,
  createdAt: true,
});

export const insertDealRoomChatMessageFeedbackSchema = createInsertSchema(dealRoomChatMessageFeedback).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertMarketingPageSchema = createInsertSchema(marketingPages).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertInvestorInquirySchema = createInsertSchema(investorInquiries).omit({
  id: true,
  status: true,
  threadId: true,
  createdAt: true,
});

export type InsertUser = z.infer<typeof insertUserSchema>;
export type User = typeof users.$inferSelect;

// Task #1876 — "One email = one account type" durable registry.
//
// Every account-creation path (investor/admin/sponsor in `users`,
// partner seats in `agent_users`, vendor seats in `vendor_users`)
// writes one row here in the same transaction as the account itself.
// The unique index on lower(email) is the database-level backstop: it
// refuses to let a single email be claimed by a *second* account type,
// even under a race or a future code path that forgets the Express
// pre-check. Multiple seats of the SAME type reuse the existing row
// (no second insert), so one broker-dealer person on two partner firms
// is still allowed — only cross-type collisions are blocked.
//
// `accountType` is one of the three owning families:
//   "investor" — any row in `users` (investor, admin, or sponsor)
//   "partner"  — any seat in `agent_users`
//   "vendor"   — any seat in `vendor_users`
export const accountEmailIdentityTypes = ["investor", "partner", "vendor"] as const;
export type AccountEmailIdentityType = (typeof accountEmailIdentityTypes)[number];

export const accountEmailIdentities = pgTable(
  "account_email_identities",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    email: text("email").notNull(),
    accountType: text("account_type")
      .$type<AccountEmailIdentityType>()
      .notNull(),
    createdAt: timestamp("created_at").notNull().defaultNow(),
  },
  (t) => ({
    // Case-insensitive single owner per email. The real lower(email)
    // expression index is created in the migration (mirrors the
    // project_waitlist / agent_users pattern where the schema names a
    // plain-column index and the migration enforces lower()).
    emailLowerUniq: uniqueIndex("account_email_identities_email_lower_uniq").on(
      t.email,
    ),
  }),
);

export type AccountEmailIdentity = typeof accountEmailIdentities.$inferSelect;
export type InsertAccountEmailIdentity = typeof accountEmailIdentities.$inferInsert;

export type LoginInput = z.infer<typeof loginSchema>;
export type RegisterInput = z.infer<typeof registerSchema>;
// Task #1263 — `users.password` is gone, so `User` no longer carries it
// and the SafeUser alias is structurally identical. Kept as a named
// alias so existing call sites don't churn.
export type SafeUser = User;
export type InsertForumPost = z.infer<typeof insertForumPostSchema>;
export type ForumPost = typeof forumPosts.$inferSelect;
export type InsertForumComment = z.infer<typeof insertForumCommentSchema>;
export type ForumComment = typeof forumComments.$inferSelect;
export type InsertVisaBulletinView = z.infer<typeof insertVisaBulletinViewSchema>;
export type VisaBulletinView = typeof visaBulletinViews.$inferSelect;
export type InsertProject = z.infer<typeof insertProjectSchema>;
export type InvestmentProject = typeof investmentProjects.$inferSelect;
export type InsertAccreditationDocument = z.infer<typeof insertAccreditationDocumentSchema>;
export type AccreditationDocument = typeof accreditationDocuments.$inferSelect;
export type InsertCallBooking = z.infer<typeof insertCallBookingSchema>;
export type CallBooking = typeof callBookings.$inferSelect;
export type InsertInPersonMeetingWindow = z.infer<typeof insertInPersonMeetingWindowSchema>;
export type InPersonMeetingWindow = typeof inPersonMeetingWindows.$inferSelect;
export type InsertAdminCalendarSettings = z.infer<typeof insertAdminCalendarSettingsSchema>;
export type AdminCalendarSettings = typeof adminCalendarSettings.$inferSelect;
export type InsertMeetingReminder = z.infer<typeof insertMeetingReminderSchema>;
export type MeetingReminder = typeof meetingReminders.$inferSelect;
export type InsertDealRoomAccess = z.infer<typeof insertDealRoomAccessSchema>;
export type DealRoomAccess = typeof dealRoomAccess.$inferSelect;
export type InsertDealRoomDocument = z.infer<typeof insertDealRoomDocumentSchema>;
export type DealRoomDocument = typeof dealRoomDocuments.$inferSelect;
export type InsertDealRoomDocChunk = z.infer<typeof insertDealRoomDocChunkSchema>;
export type DealRoomDocChunk = typeof dealRoomDocChunks.$inferSelect;
export type InsertDealRoomChatThread = z.infer<typeof insertDealRoomChatThreadSchema>;
export type DealRoomChatThread = typeof dealRoomChatThreads.$inferSelect;
export type InsertDealRoomChatMessage = z.infer<typeof insertDealRoomChatMessageSchema>;
export type DealRoomChatMessage = typeof dealRoomChatMessages.$inferSelect;
export type InsertDealRoomChatMessageFeedback = z.infer<typeof insertDealRoomChatMessageFeedbackSchema>;
export type DealRoomChatMessageFeedback = typeof dealRoomChatMessageFeedback.$inferSelect;
export type InsertMarketingPage = z.infer<typeof insertMarketingPageSchema>;
export type MarketingPage = typeof marketingPages.$inferSelect;
export type InsertInvestorInquiry = z.infer<typeof insertInvestorInquirySchema>;
export type InvestorInquiry = typeof investorInquiries.$inferSelect;

export const accreditationLogs = pgTable("accreditation_logs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  action: text("action").notNull(),
  criteriaSelected: jsonb("criteria_selected"),
  acknowledgmentText: text("acknowledgment_text"),
  ipAddress: text("ip_address"),
  userAgent: text("user_agent"),
  metadata: jsonb("metadata"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertAccreditationLogSchema = createInsertSchema(accreditationLogs).omit({
  id: true,
  createdAt: true,
});

export type InsertAccreditationLog = z.infer<typeof insertAccreditationLogSchema>;
export type AccreditationLog = typeof accreditationLogs.$inferSelect;

// Task #1967 — Investor Eligibility Questionnaire. A structured, branching
// replacement for the old self-certify step. One *active* record per user
// (kept platform-wide; applies to every deal); prior versions are retained
// for audit by flipping `isActive` to false. Determines whether the investor
// is an accredited investor (Reg D) OR not a U.S. person (Reg S), captures
// the legal representations, and is snapshotted to an immutable PDF on
// submit (`pdfDocumentId` references the accreditation_documents row).
//
// The deal-room gate still reads `users.accreditationStatus`; this table is
// the durable compliance record and admin-review source. RLS (migration
// 0202) scopes reads to the owning investor + admins.
export const eligibilityQuestionnaires = pgTable("eligibility_questionnaires", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  // Bump when the legal wording / structure of the form changes so older
  // records stay interpretable against the template they were answered on.
  questionnaireVersion: text("questionnaire_version").default("v1").notNull(),
  // draft | submitted | reg_s_qualified | accredited_pending_verification
  // | needs_consultation | approved | rejected
  submissionState: text("submission_state").default("draft").notNull(),
  // Branch outcome computed at submit time:
  // reg_s | reg_d_accredited | needs_consultation | none
  computedResult: text("computed_result"),
  // Only the latest record is active; superseded versions keep the history.
  isActive: boolean("is_active").default(true).notNull(),
  // Furthest wizard step reached, for resume-where-left-off (0-indexed).
  currentStep: integer("current_step").default(0).notNull(),

  // --- Step 1: Identity ---
  fullLegalName: text("full_legal_name"),
  nativeAlphabetName: text("native_alphabet_name"),
  dateOfBirth: text("date_of_birth"),
  gender: text("gender"), // male | female | prefer_not_to_say
  placeOfBirthCity: text("place_of_birth_city"),
  placeOfBirthCountry: text("place_of_birth_country"),
  countryOfCitizenship: text("country_of_citizenship"),
  residenceStreet: text("residence_street"),
  residenceCity: text("residence_city"),
  residenceProvince: text("residence_province"),
  residencePostalCode: text("residence_postal_code"),
  residenceCountry: text("residence_country"),
  isLegalResident: boolean("is_legal_resident"),

  // --- Step 2: Contact ---
  mailingAddress: text("mailing_address"),
  homePhone: text("home_phone"),
  mobilePhone: text("mobile_phone"),
  primaryEmail: text("primary_email"),
  secondaryEmail: text("secondary_email"),

  // --- Step 3: U.S.-person status (Reg S branch point) ---
  notUsPerson: boolean("not_us_person"),
  notCitizenOrResident: boolean("not_citizen_or_resident"),
  didNotReceiveOfferInUs: boolean("did_not_receive_offer_in_us"),

  // --- Step 4: Accredited investor (U.S.-person path only) ---
  accreditedNetWorth: boolean("accredited_net_worth"),
  accreditedIncome: boolean("accredited_income"),
  accreditedNoneOfAbove: boolean("accredited_none_of_above"),

  // --- Step 6: Representations (checkbox + typed initials) ---
  repKnowledge: boolean("rep_knowledge"),
  repKnowledgeInitials: text("rep_knowledge_initials"),
  repOwnAccount: boolean("rep_own_account"),
  repOwnAccountInitials: text("rep_own_account_initials"),
  repNoGuarantee: boolean("rep_no_guarantee"),
  repNoGuaranteeInitials: text("rep_no_guarantee_initials"),

  // --- Step 7: Certification / e-signature ---
  signatureName: text("signature_name"),
  signatureNativeName: text("signature_native_name"),
  certificationAccepted: boolean("certification_accepted"),

  // Immutable PDF snapshot reference (accreditation_documents row id) and
  // submit/review audit fields.
  pdfDocumentId: varchar("pdf_document_id"),
  submittedAt: timestamp("submitted_at"),
  reviewedAt: timestamp("reviewed_at"),
  reviewedBy: varchar("reviewed_by"),
  reviewNotes: text("review_notes"),
  // Per-flag review notes recorded by the admin during accreditation review.
  // Maps an advisory flag's `field` identifier (see
  // server/lib/accreditation-flags.ts) to the admin's free-text resolution
  // note, e.g. { "countryOfCitizenship": "Confirmed by phone — typo" }.
  flagReviewNotes: jsonb("flag_review_notes").$type<Record<string, string>>(),

  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertEligibilityQuestionnaireSchema = createInsertSchema(eligibilityQuestionnaires).omit({
  id: true,
  computedResult: true,
  pdfDocumentId: true,
  submittedAt: true,
  reviewedAt: true,
  reviewedBy: true,
  reviewNotes: true,
  flagReviewNotes: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertEligibilityQuestionnaire = z.infer<typeof insertEligibilityQuestionnaireSchema>;
export type EligibilityQuestionnaire = typeof eligibilityQuestionnaires.$inferSelect;

export const documentAccessLogs = pgTable("document_access_logs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  documentId: varchar("document_id").notNull(),
  projectId: varchar("project_id").notNull(),
  action: text("action").notNull(),
  ipAddress: text("ip_address"),
  userAgent: text("user_agent"),
  durationSeconds: integer("duration_seconds"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertDocumentAccessLogSchema = createInsertSchema(documentAccessLogs).omit({
  id: true,
  createdAt: true,
});

export type InsertDocumentAccessLog = z.infer<typeof insertDocumentAccessLogSchema>;
export type DocumentAccessLog = typeof documentAccessLogs.$inferSelect;

export const projectWaitlist = pgTable("project_waitlist", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  email: text("email").notNull(),
  source: text("source").notNull().default("dashboard"),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => ({
  // Task #1552 — Case-insensitive uniqueness on email so the public
  // /api/project-waitlist endpoint can ON CONFLICT upsert instead of
  // duplicating a row every time someone re-submits the Notify Me form.
  emailLowerUniq: uniqueIndex("project_waitlist_email_lower_uniq").on(table.email),
}));

export const insertProjectWaitlistSchema = createInsertSchema(projectWaitlist).omit({
  id: true,
  createdAt: true,
});

export type InsertProjectWaitlist = z.infer<typeof insertProjectWaitlistSchema>;
export type ProjectWaitlist = typeof projectWaitlist.$inferSelect;

export const projectInterests = pgTable("project_interests", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  projectId: varchar("project_id").notNull().references(() => investmentProjects.id, { onDelete: "cascade" }),
  status: text("status").default("interested").notNull(),
  notes: text("notes"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertProjectInterestSchema = createInsertSchema(projectInterests).omit({
  id: true,
  status: true,
  createdAt: true,
});

export type InsertProjectInterest = z.infer<typeof insertProjectInterestSchema>;
export type ProjectInterest = typeof projectInterests.$inferSelect;

export const investmentCommitments = pgTable("investment_commitments", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  projectId: varchar("project_id").notNull().references(() => investmentProjects.id, { onDelete: "cascade" }),
  amount: integer("amount").notNull(),
  status: text("status").default("pending").notNull(),
  timelinePreference: text("timeline_preference"),
  accreditationConfirmed: boolean("accreditation_confirmed").default(false).notNull(),
  notes: text("notes"),
  adminNotes: text("admin_notes"),
  fundingDeadline: timestamp("funding_deadline"),
  fundingReceivedAt: timestamp("funding_received_at"),
  fundingAmount: integer("funding_amount"),
  wireReference: text("wire_reference"),
  wireInstructions: jsonb("wire_instructions"),
  adminChecklist: jsonb("admin_checklist"),
  // Task #692: when set, the commission engine treats the commitment as
  // "investor exited position" and stops accruing further periods.
  commissionPausedAt: timestamp("commission_paused_at"),
  // Snapshot of the investor's referring agent at commitment-create
  // time. Immutable once stamped — the commission engine and funded-
  // attribution reports read this column directly so a mid-stream
  // reassignment of `users.referralAgentId` is purely forward-looking.
  // Null means the investor had no agent when this commitment was
  // created and no commission accrues on it.
  attributedAgentId: varchar("attributed_agent_id").references((): AnyPgColumn => referralAgents.id, { onDelete: "set null" }),
  // Financial model M0 — per-investor Shape A fields (integer cents)
  adminFeePaidCents: bigint("admin_fee_paid_cents", { mode: "number" }),
  // Agent/finder fee on the admin stream — mutually exclusive with agentFinderFeeBps.
  // Precedence (enforced in shared/financial-model/agent-fee.ts):
  //   flat cents when > 0, else bps on admin fee basis, else 0; error if both set.
  agentFinderFeeCents: bigint("agent_finder_fee_cents", { mode: "number" }).notNull().default(0),
  agentFinderFeeBps: integer("agent_finder_fee_bps"),
  /** Bps on gross spread (× term) before external split — independent of admin-side fee fields. */
  agentFinderFeeSpreadBps: integer("agent_finder_fee_spread_bps"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertInvestmentCommitmentSchema = createInsertSchema(investmentCommitments).omit({
  id: true,
  status: true,
  adminNotes: true,
  fundingDeadline: true,
  fundingReceivedAt: true,
  fundingAmount: true,
  wireReference: true,
  wireInstructions: true,
  adminChecklist: true,
  // Stamped by storage.createCommitment from users.referralAgentId — not
  // a caller-supplied field.
  attributedAgentId: true,
  createdAt: true,
  updatedAt: true,
});

// ---------------------------------------------------------------------------
// Task #1000 — per-project escrow ledger
// ---------------------------------------------------------------------------
// Tracks the cash flowing through the project's escrow account. Inflows and
// releases identify the subscriber commitment; refunds may retain that
// reference when they reverse subscriber cash.
// USD only, single escrow per project. Audit fields mirror commission
// entries: `createdByUserId` records who entered the row;
// `voidedAt`/`voidedByUserId`/`voidReason` capture soft-deletes.
export const projectEscrowEntries = pgTable("project_escrow_entries", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  projectId: varchar("project_id").notNull().references(() => investmentProjects.id, { onDelete: "cascade" }),
  // Required by a database constraint for inflow and release rows.
  // Historical unmatched rows may remain because the constraint is NOT VALID.
  commitmentId: varchar("commitment_id").references((): AnyPgColumn => investmentCommitments.id, { onDelete: "set null" }),
  // 'inflow' | 'release' | 'refund'
  kind: text("kind").notNull(),
  // USD whole dollars. Matches investmentCommitments.amount precision.
  amount: integer("amount").notNull(),
  occurredAt: timestamp("occurred_at").notNull(),
  // Optional reference to a capital-stack tranche stored on the project.
  // The stable key/id is retained without a separate FK target.
  trancheRef: text("tranche_ref"),
  notes: text("notes"),
  // 'manual' for admin-entered rows; 'commitment_funding' for the
  // auto-emitted inflow tied to a commitment's funding_received_at.
  source: text("source").notNull().default("manual"),
  createdByUserId: varchar("created_by_user_id"),
  // Task #1200 — direct owner column for sensitive-data RLS prep.
  // Mirrors the underlying commitment's investor whenever `commitmentId` is
  // set, including subscriber-specific inflows, releases, and refunds.
  ownerUserId: varchar("owner_user_id").references((): AnyPgColumn => users.id, { onDelete: "set null" }),
  voidedAt: timestamp("voided_at"),
  voidedByUserId: varchar("voided_by_user_id"),
  voidReason: text("void_reason"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertProjectEscrowEntrySchema = createInsertSchema(projectEscrowEntries).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  voidedAt: true,
  voidedByUserId: true,
  voidReason: true,
  // Task #1200 — derived from `commitmentId` in the storage layer.
  ownerUserId: true,
});
export type InsertProjectEscrowEntry = z.infer<typeof insertProjectEscrowEntrySchema>;
export type ProjectEscrowEntry = typeof projectEscrowEntries.$inferSelect;

export const subscriptionAgreements = pgTable("subscription_agreements", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  projectId: varchar("project_id").notNull().references(() => investmentProjects.id, { onDelete: "cascade" }),
  commitmentId: varchar("commitment_id").notNull().references(() => investmentCommitments.id, { onDelete: "cascade" }),
  status: text("status").notNull().default("pending"),
  signedAt: timestamp("signed_at"),
  signatureData: jsonb("signature_data"),
  signatureType: text("signature_type").default("clickwrap"),
  signatureImage: text("signature_image"),
  documentPath: text("document_path"),
  ipAddress: text("ip_address"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertSubscriptionAgreementSchema = createInsertSchema(subscriptionAgreements).omit({
  id: true,
  createdAt: true,
});

export type InsertSubscriptionAgreement = z.infer<typeof insertSubscriptionAgreementSchema>;
export type SubscriptionAgreement = typeof subscriptionAgreements.$inferSelect;

// Task #1432 — Platform-wide NDA signature. One per investor, covers all deal rooms.
export const ndaSignatures = pgTable("nda_signatures", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  signedAt: timestamp("signed_at").notNull().defaultNow(),
  ipAddress: text("ip_address"),
  signatureType: text("signature_type").notNull(),
  signatureImage: text("signature_image"),
  typedName: text("typed_name"),
  userAgent: text("user_agent"),
  // Task #1453 — E-SIGN / UETA court-readiness audit trail.
  consentAcknowledgedAt: timestamp("consent_acknowledged_at"),
  intentAcknowledgedAt: timestamp("intent_acknowledged_at"),
  documentVersion: text("document_version"),
  contentHash: text("content_hash"),
  signerName: text("signer_name"),
  signerEmail: text("signer_email"),
  sealedPdfPath: text("sealed_pdf_path"),
  sealedPdfHash: text("sealed_pdf_hash"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertNdaSignatureSchema = createInsertSchema(ndaSignatures).omit({
  id: true,
  createdAt: true,
});

export type InsertNdaSignature = z.infer<typeof insertNdaSignatureSchema>;
export type NdaSignature = typeof ndaSignatures.$inferSelect;

// Task #1432 ext — Platform-wide NDA signature for agent portal users.
export const agentNdaSignatures = pgTable("agent_nda_signatures", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  agentUserId: varchar("agent_user_id").notNull().references(() => agentUsers.id, { onDelete: "cascade" }),
  signedAt: timestamp("signed_at").notNull().defaultNow(),
  ipAddress: text("ip_address"),
  signatureType: text("signature_type").notNull(),
  signatureImage: text("signature_image"),
  typedName: text("typed_name"),
  userAgent: text("user_agent"),
  // Task #1453 — E-SIGN / UETA court-readiness audit trail.
  consentAcknowledgedAt: timestamp("consent_acknowledged_at"),
  intentAcknowledgedAt: timestamp("intent_acknowledged_at"),
  documentVersion: text("document_version"),
  contentHash: text("content_hash"),
  signerName: text("signer_name"),
  signerEmail: text("signer_email"),
  sealedPdfPath: text("sealed_pdf_path"),
  sealedPdfHash: text("sealed_pdf_hash"),
  // Partner e-sign: capacity + UETA attribution extras (nullable for
  // historical rows and offline-waiver firms).
  signerTitle: text("signer_title"),
  signerFirmName: text("signer_firm_name"),
  authorityAcknowledgedAt: timestamp("authority_acknowledged_at"),
  documentPresentedAt: timestamp("document_presented_at"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertAgentNdaSignatureSchema = createInsertSchema(agentNdaSignatures).omit({
  id: true,
  createdAt: true,
});

export type InsertAgentNdaSignature = z.infer<typeof insertAgentNdaSignatureSchema>;
export type AgentNdaSignature = typeof agentNdaSignatures.$inferSelect;

// Task #2186 — broker self-attested compliance approval.
// One row per (project, firm); updated in-place on re-attestation.
export const agentComplianceApprovals = pgTable("agent_compliance_approvals", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  projectId: varchar("project_id").notNull().references(() => investmentProjects.id),
  referralAgentId: varchar("referral_agent_id").notNull().references(() => referralAgents.id),
  agentUserId: varchar("agent_user_id").notNull().references(() => agentUsers.id),
  approvedAt: timestamp("approved_at").notNull().defaultNow(),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export type AgentComplianceApproval = typeof agentComplianceApprovals.$inferSelect;

export type InsertInvestmentCommitment = z.infer<typeof insertInvestmentCommitmentSchema>;
export type InvestmentCommitment = typeof investmentCommitments.$inferSelect;

// Task #886 — Structured classification of every referral partner.
// The legacy free-text `licenseType` picker (retired in Task #964)
// was too inconsistent to branch product behavior on (commission
// rules, communications, dashboards). Every agent is now tagged as
// exactly one of these three structured values, captured on the
// public application + admin "Add agent" forms and editable on the
// admin profile.
export const AGENT_TYPES = [
  "agent",
  "immigration_attorney",
  "broker_dealer",
  "foreign_migration_agent",
  // Task #3148 — retainer-paid marketing partners. Full referral
  // toolkit (leads, pipeline, marketing, attribution) but NO
  // commissions: the commission engine + admin rule routes hard-block
  // this type, and compensation is recorded informationally via the
  // retainer* columns on referral_agents.
  "marketing_agent",
] as const;
export type AgentType = (typeof AGENT_TYPES)[number];

export const AGENT_TYPE_LABELS: Record<AgentType, string> = {
  agent: "Agent",
  immigration_attorney: "Immigration Attorney",
  broker_dealer: "Broker-Dealer",
  foreign_migration_agent: "Foreign Migration Agent",
  marketing_agent: "Marketing Agent",
};

// Task #3148 — informational retainer payment frequency for
// marketing_agent partners. Validated app-side (column is text).
export const RETAINER_FREQUENCIES = ["monthly", "quarterly", "one_time", "other"] as const;
export type RetainerFrequency = (typeof RETAINER_FREQUENCIES)[number];
export const RETAINER_FREQUENCY_LABELS: Record<RetainerFrequency, string> = {
  monthly: "Monthly",
  quarterly: "Quarterly",
  one_time: "One-time",
  other: "Other",
};

// USCIS Form I-956K classifies a "promoter" as either an individual
// person or an organization/entity. The two paths require
// different supporting documents (organizations must additionally
// upload formation documents), so the type is captured up front on
// the agent record and drives the registration UI.
export const PROMOTER_TYPES = ["individual", "organization"] as const;
export type PromoterType = (typeof PROMOTER_TYPES)[number];

// Task #1583 — Partner holding-state lifecycle. A self-registered
// partner lands in `pending_approval` and can see only the holding
// portal (profile + project teasers + "book a call" CTA). An admin
// flips them to `approved` once vetted, at which point the full
// partner portal lights up. Distinct from `is_active`, which remains
// the binary on/off switch an admin uses to suspend an already-
// approved partner.
// `rejected` is a terminal state set when an admin rejects a
// self-registered partner: their seats are deactivated and the firm
// leaves the pending-approval queue (and its sidebar badge) for good.
export const PARTNER_PORTAL_STATUSES = ["pending_approval", "approved", "rejected"] as const;
export type PartnerPortalStatus = (typeof PARTNER_PORTAL_STATUSES)[number];

export const PROMOTER_TYPE_LABELS: Record<PromoterType, string> = {
  individual: "Individual promoter",
  organization: "Organization / entity promoter",
};

export const PROMOTER_FORM_TYPES = ["direct", "third_party", "migration_agent"] as const;
export type PromoterFormType = (typeof PROMOTER_FORM_TYPES)[number];
export const PROMOTER_FORM_TYPE_LABELS: Record<PromoterFormType, string> = {
  direct: "Direct promoter",
  third_party: "Third-party promoter",
  migration_agent: "Migration agent / immigration consultant",
};

export const referralAgents = pgTable("referral_agents", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  companyName: text("company_name").notNull(),
  // Task #720 — the legacy single-value `country` / `language_code`
  // columns have been dropped. Coverage now lives exclusively in
  // `agent_countries` / `agent_languages` (see Task #697), with the
  // primary entry serving as the equivalent of the old scalar value.
  contactFirstName: text("contact_first_name").notNull(),
  contactLastName: text("contact_last_name").notNull(),
  contactEmail: text("contact_email").notNull(),
  contactPhone: text("contact_phone"),
  referralCode: text("referral_code").notNull().unique(),
  // Task #1263 — `password_hash` dropped. Supabase Auth owns the
  // credential; this row links to it via the per-seat `agent_users`
  // row (see #698 for the seat model, #1249 for the Supabase cutover).
  // Single-use token used to back the welcome-email password setup link
  // (Task #639). Cleared the first time the agent picks a password.
  passwordSetupToken: text("password_setup_token"),
  passwordSetupTokenExpiresAt: timestamp("password_setup_token_expires_at"),
  website: text("website"),
  // Task #1884 — optional public LinkedIn profile URL, rendered as a
  // link on the agent's /refer/:code landing page when present.
  linkedinUrl: text("linkedin_url"),
  // Public URL of the agent's company logo (lives under PUBLIC object
  // storage; produced by /api/admin/upload/image or
  // /api/agent/upload/logo). NULL = no custom logo, UI falls back to a
  // generic globe + company name. See Task #650.
  logoUrl: text("logo_url"),
  // Task #886 — structured agent classification. One of AGENT_TYPES
  // ("immigration_attorney" / "broker_dealer" / "foreign_migration_agent")
  // or NULL for legacy rows whose type the heuristic backfill could
  // not confidently infer; those rows are flagged via
  // `adminReviewNeeded` so admins can clean them up from the list view.
  agentType: text("agent_type").$type<AgentType>(),
  // Set true when the heuristic backfill (migration 0101) couldn't
  // pick a structured agent_type for this row, OR when an admin
  // explicitly wants the row re-reviewed. Cleared automatically when
  // an admin sets / changes agent_type via the edit form.
  adminReviewNeeded: boolean("admin_review_needed").notNull().default(false),
  // USCIS Form I-956K registration tracking. The promoter type drives
  // both the supporting-document set required on the
  // I-956K registration record (organizations need formation
  // documents in addition to the written agreement) and the copy
  // shown on the registration UI. NULL = not yet classified; the
  // promoter section will fall back to "individual" copy until set.
  promoterType: text("promoter_type").$type<PromoterType>(),
  licenseNumber: text("license_number"),
  // Compliance refresh tracking (Task #699). License expiry is set
  // manually by an admin (no public registry to scrape) and used by the
  // expiry banner / reminder cron. W-9 dates are auto-managed: the
  // upload endpoint stamps `w9LastUploadedAt = now()` and recomputes
  // `w9NextRefreshAt = last + 3 years` per IRS best practice.
  licenseExpiresAt: timestamp("license_expires_at"),
  w9LastUploadedAt: timestamp("w9_last_uploaded_at"),
  w9NextRefreshAt: timestamp("w9_next_refresh_at"),
  commissionStructure: jsonb("commission_structure"),
  notes: text("notes"),
  // Agent-controlled notification preferences (Task #694). Single
  // boolean today (transactional milestone progress emails); jsonb so
  // we can grow it without another migration. Default keeps the agent
  // opted-in. Admin-side edits to the agent record (admin agents
  // route, applications approve flow) deliberately do NOT touch this
  // column so the agent's opt-out survives.
  notificationPreferences: jsonb("notification_preferences").$type<{
    // Task #694 — milestone progress emails (inquiry, KYC, sub, escrow, I-526E).
    progress?: boolean;
    // Task #888 — call-lifecycle notifications (booked / completed /
    // rescheduled / cancelled). Defaults to opted-in like `progress`.
    calls?: boolean;
    // Task #888 — when true, scheduling routes add the agent's
    // contactEmail as an optional attendee on the Google Calendar
    // invite for any call booked / rescheduled for one of their
    // referred investors. Defaults to OFF (opt-in) so we never push
    // an agent into a meeting without an explicit toggle.
    ccCalendarInvites?: boolean;
    // Task #1105 — Per-agent override for the lead-ownership expiry
    // warning schedule (Task #1096). Days BEFORE expiry at which the
    // warnings job (`server/jobs/lead-ownership-warnings.ts`) should
    // notify the agent. Allowed values: 30, 14, 7, 3, 1, 0. When
    // unset/null the job falls back to the house default of
    // [14, 7, 0]. Empty array = "send no warnings" (the agent has
    // explicitly opted out of every milestone but still wants to
    // own the lead).
    ownershipWarningDays?: number[];
    // Task #1971 — when true, every investor this agent registered
    // sees the investor portal with all consultation/scheduling
    // calls-to-action removed (hero buttons, journey card prompt,
    // "My Schedule" + "Consultations" nav) and the in-portal booking
    // page blocked. Defaults to OFF (CTAs shown) so the portal looks
    // exactly as it does today unless the agent opts in.
    hideInvestorConsultationCtas?: boolean;
  }>().default({ progress: true }).notNull(),
  // Task #907 — persona-driven structured profile fields. Each known
  // persona (immigration_attorney / broker_dealer / foreign_migration_agent)
  // has a small set of fields it cares about (bar number, CRD,
  // source-market notes, etc.). Storing them as a jsonb keeps the
  // table narrow and lets the persona module in
  // `shared/agent-persona.ts` evolve the field set without another
  // migration. Validation lives in `validatePersonaProfileExtras`.
  personaProfileExtras: jsonb("persona_profile_extras").$type<Record<string, string>>().default({}).notNull(),
  // Task #1007 — Per-broker-dealer flat compliance-underwriting fee
  // (in cents). Only meaningful when `agentType = "broker_dealer"`;
  // other agent types ignore the column. NULL = "no fee on file
  // yet"; the admin BD-compliance UI nudges admins to set one before
  // granting a project.
  complianceUnderwritingFeeCents: integer("compliance_underwriting_fee_cents"),
  // Task #3148 — informational retainer reference fields for
  // `agentType = "marketing_agent"` partners. Purely admin-facing
  // bookkeeping: no ledger, no accruals, no payout machinery. Other
  // agent types ignore these columns.
  retainerAmountCents: integer("retainer_amount_cents"),
  retainerFrequency: text("retainer_frequency").$type<RetainerFrequency>(),
  retainerNotes: text("retainer_notes"),
  // Task #1094 — per-agent lead ownership window in days. NULL =
  // inherit the house default (`marketing_house_defaults.payload`
  // → `leadOwnershipWindowDays`, default 90). When a confirmed_agent
  // lead has no fresh activity within this window, the sweep worker
  // returns the lead to the house pool.
  leadOwnershipWindowDays: integer("lead_ownership_window_days"),
  // Task #1328 — public-facing profile fields + admin approval flow.
  headshotUrl: text("headshot_url"),
  jobTitle: text("job_title"),
  bio: text("bio"),
  profileApprovalStatus: text("profile_approval_status").default("draft").notNull(),
  profileApprovedAt: timestamp("profile_approved_at"),
  profileApprovedById: varchar("profile_approved_by_id"),
  profileRejectionNote: text("profile_rejection_note"),
  profileUpdatedAt: timestamp("profile_updated_at"),
  isActive: boolean("is_active").default(true).notNull(),
  // Admin-toggled. Testing firms stay on the roster and in view-as /
  // pitch tour, and are left out of partner reporting counts and the
  // invited-by-type chart. The chart reads this flag, not firm names.
  isTesting: boolean("is_testing").notNull().default(false),
  // Task #1583 — Holding-state lifecycle (see PARTNER_PORTAL_STATUSES).
  // Default `approved` preserves today's behavior; flips to
  // `pending_approval` once self-registration ships in a later sprint.
  portalStatus: text("portal_status")
    .$type<PartnerPortalStatus>()
    .notNull()
    .default("approved"),
  // Task #2543 — I-956K registration wizard. The promoter_form_type
  // (Direct / Third-party / Migration agent) is the USCIS form's Part 2
  // promoter classification axis. It is distinct from `promoterType`
  // (Individual / Organization), which is the registrant type. Both are
  // set by admins; the wizard reads them to branch its guided flow.
  // not_a_promoter = true means the admin classified this agent as not
  // needing to file — the module shows a short explanation and marks the
  // checklist item complete / not-applicable.
  promoterFormType: text("promoter_form_type").$type<PromoterFormType>(),
  notAPromoter: boolean("not_a_promoter").notNull().default(false),
  // WeChat handle for the agent's primary contact channel.
  // 6–20 chars; must start with a letter; may contain letters, digits,
  // underscore, and hyphen. Trimmed before storing; NULL = not provided.
  wechatId: text("wechat_id"),
  // Task #3135 — firm-level "NDA already signed offline" admin
  // attestation. When `ndaWaivedAt` is non-null, every seat at this
  // firm skips the in-portal e-signature gate for offering / deal-room
  // documents. This is deliberately NOT a synthetic row in the
  // court-ready `agent_nda_signatures` table — it records only that an
  // admin attested a signature exists offline. `ndaWaivedById` stores
  // the admin user id who set it (for the who/when audit trail).
  // Clearing `ndaWaivedAt` re-gates the firm unless a real signature
  // exists on a seat.
  ndaWaivedAt: timestamp("nda_waived_at"),
  ndaWaivedById: varchar("nda_waived_by_id"),
  // Offline signed-NDA PDF uploaded by an admin (wet ink / off-platform).
  // Cleared together with the waiver attestation. Not a court-ready
  // e-signature — just the executed document on file.
  ndaOfflinePdfPath: text("nda_offline_pdf_path"),
  ndaOfflinePdfFileName: text("nda_offline_pdf_file_name"),
  ndaOfflineUploadedAt: timestamp("nda_offline_uploaded_at"),
  ndaOfflineUploadedById: varchar("nda_offline_uploaded_by_id"),
  /** Brief 1 item 2 — Schedule A vs Schedule B enrollment on the partner record. */
  partnerProgram: text("partner_program").$type<"partner_program" | "qualified_lead">(),
  partnerProgramEffectiveOn: date("partner_program_effective_on"),
  partnerProgramChangedAt: timestamp("partner_program_changed_at"),
  /** Locked partner type for unsigned Partner Program work. Null until an admin saves it. */
  partnerTypeId: varchar("partner_type_id"),
  /** CRM organization for this same company. One firm, one organization. */
  contactOrganizationId: varchar("contact_organization_id").references(() => contactOrganizations.id, {
    onDelete: "set null",
  }),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => ({
  contactOrganizationUniq: uniqueIndex("referral_agents_contact_organization_id_uniq")
    .on(table.contactOrganizationId)
    .where(sql`${table.contactOrganizationId} IS NOT NULL`),
}));

// Per-(agent, investor, milestone) log of transactional progress
// emails actually delivered to the referring agent (Task #694).
// The unique index on (agent_id, investor_id, milestone) backs the
// "at most once" guarantee — a duplicate insert raises a unique
// violation that the notification service treats as "already sent,
// skip" so retries / stage backfills never double-send.
export const agentMilestoneNotifications = pgTable("agent_milestone_notifications", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  agentId: varchar("agent_id").notNull().references(() => referralAgents.id, { onDelete: "cascade" }),
  investorId: varchar("investor_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  milestone: text("milestone").notNull(),
  sentAt: timestamp("sent_at").defaultNow().notNull(),
});

export type AgentMilestoneNotification = typeof agentMilestoneNotifications.$inferSelect;

// Shared WeChat ID format rule. Import this in both client forms for inline
// validation so the rule is never duplicated. The server's insertReferralAgentSchema
// references this same constant so client + server are guaranteed in sync.
// Format: 6–20 chars, must start with a letter, letters/digits/underscore/hyphen only.
export const WECHAT_ID_REGEX = /^[a-zA-Z][a-zA-Z0-9_-]{5,19}$/;

// Reusable WeChat ID field validation. Empty string or omitted → null
// (clears the field). Trims, then enforces WECHAT_ID_REGEX. Shared across
// the referral-agent schema, the admin contact-create route, and the
// lead-create schema so the rule is identical everywhere.
export const wechatIdSchema = z
  .string()
  .trim()
  .transform((v) => (v === "" ? null : v))
  .pipe(
    z
      .string()
      .regex(
        WECHAT_ID_REGEX,
        "WeChat ID must be 6–20 characters, start with a letter, and contain only letters, digits, underscores, or hyphens",
      )
      .nullable(),
  )
  .nullable()
  .optional();

export const insertReferralAgentSchema = createInsertSchema(referralAgents).omit({
  id: true,
  passwordSetupToken: true,
  passwordSetupTokenExpiresAt: true,
  w9LastUploadedAt: true,
  w9NextRefreshAt: true,
  // Task #1583 — `portalStatus` is server-managed (default `approved`
  // today, flips to `pending_approval` once self-registration ships).
  // Callers must not set it via the public insert schema.
  portalStatus: true,
  // Offline NDA attestation + PDF are set only via dedicated admin
  // helpers / upload route — never via the generic agent create/update body.
  ndaWaivedAt: true,
  ndaWaivedById: true,
  ndaOfflinePdfPath: true,
  ndaOfflinePdfFileName: true,
  ndaOfflineUploadedAt: true,
  ndaOfflineUploadedById: true,
  partnerTypeId: true,
  contactOrganizationId: true,
  createdAt: true,
  // Set only by the admin testing toggle / firm save, never by create
  // or partner self-service payloads.
  isTesting: true,
}).extend({
  contactEmail: z
    .string()
    .trim()
    .toLowerCase()
    .min(1, "Email is required")
    .email("Please enter a valid email address"),
  contactPhone: z
    .string()
    .nullable()
    .optional()
    .refine(
      (v) => v == null || v === "" || (v.startsWith("+") && isValidPhoneNumber(v)),
      { message: "Phone number must be a valid international number" },
    ),
  // Task #886 — structured classification. Optional on the wire so
  // partial-update PATCH calls that only touch unrelated fields
  // (e.g. compliance dates) still validate.
  agentType: z.enum(AGENT_TYPES).nullable().optional(),
  adminReviewNeeded: z.boolean().optional(),
  // Promoter classification for the I-956K registration workflow.
  // Optional so existing PATCH callers (license dates,
  // commission edits, etc.) keep validating without sending the
  // field; the dedicated PATCH endpoint enforces presence.
  promoterType: z.enum(PROMOTER_TYPES).nullable().optional(),
  // Task #907 — persona-driven structured fields (bar number, CRD,
  // source market notes, etc.). Validated per-persona at the route
  // layer via `validatePersonaProfileExtras`; this schema only
  // enforces the wire shape so partial updates from the partner
  // portal don't bounce on validation alone.
  personaProfileExtras: z.record(z.string(), z.string()).optional(),
  // Task #1007 — broker-dealer compliance-underwriting fee in cents.
  // Optional so partial PATCH calls that don't touch the fee still
  // validate; coerced from JSON numbers (or omitted entirely).
  complianceUnderwritingFeeCents: z
    .number()
    .int()
    .nonnegative()
    .nullable()
    .optional(),
  // Task #3148 — marketing-agent retainer reference fields. Optional
  // so partial PATCH calls that don't touch them still validate.
  retainerAmountCents: z
    .number()
    .int()
    .nonnegative()
    .nullable()
    .optional(),
  retainerFrequency: z.enum(RETAINER_FREQUENCIES).nullable().optional(),
  retainerNotes: z.string().trim().max(2000).nullable().optional(),
  // WeChat ID: 6–20 chars, starts with a letter, letters/digits/_/- only.
  // Empty string or omitted → null (clears the field). Shared between the
  // admin edit route and the agent self-edit route so the rule is identical.
  wechatId: wechatIdSchema,
});

export type InsertReferralAgent = z.infer<typeof insertReferralAgentSchema>;
export type ReferralAgent = typeof referralAgents.$inferSelect;
// Task #1263 — `passwordHash` is gone from referral_agents.
export type SafeReferralAgent = ReferralAgent;

// Task #697 — Multi-country / multi-language coverage join tables.
//
// An agent (or a public application) can declare every country it
// covers and every language it speaks. Exactly one row per dimension
// is flagged `is_primary` — historically the primary value was also
// mirrored into the legacy `referral_agents.country` / `language_code`
// scalar columns, but those have since been dropped (Task #720) and
// the join tables are now the single source of truth. The "exactly
// one primary" constraint is enforced at the database level by a
// partial unique index on `(agent_id) WHERE is_primary` (see
// migration 0093). The application layer is responsible for ensuring
// at least one primary exists; the storage helpers in
// `server/storage.ts` enforce that invariant on every write.
export const agentCountries = pgTable("agent_countries", {
  agentId: varchar("agent_id").notNull().references(() => referralAgents.id, { onDelete: "cascade" }),
  countryCode: text("country_code").notNull(),
  isPrimary: boolean("is_primary").notNull().default(false),
}, (t) => ({
  pk: primaryKey({ columns: [t.agentId, t.countryCode] }),
}));

export const agentLanguages = pgTable("agent_languages", {
  agentId: varchar("agent_id").notNull().references(() => referralAgents.id, { onDelete: "cascade" }),
  languageCode: text("language_code").notNull(),
  isPrimary: boolean("is_primary").notNull().default(false),
}, (t) => ({
  pk: primaryKey({ columns: [t.agentId, t.languageCode] }),
}));

export type AgentCountry = typeof agentCountries.$inferSelect;
export type AgentLanguage = typeof agentLanguages.$inferSelect;

// Languages a referral agent can declare they speak. We deliberately
// keep this list broad — agents come from every major EB-5 source
// market, and the spoken-languages chips on their public profile are
// purely informational ("this firm can talk to investors in X"). The
// list is *not* tied to which languages the /refer landing page is
// actually translated into — that is `PORTAL_LOCALES` below.
//
// Codes are ISO 639-1 where possible, with `yue` (Cantonese) the only
// 639-3 entry because Cantonese-speaking agents in HK / Guangdong
// typically want to advertise it distinctly from Mandarin.
//
// Native-name suffix in each label helps Chinese / Hindi / Arabic /
// etc. investors recognize their language at a glance even when the
// page chrome around it is in English.
export const SUPPORTED_AGENT_LANGUAGES = [
  { code: "en", label: "English" },
  // East & Southeast Asia
  { code: "zh", label: "Chinese — Mandarin (中文)" },
  { code: "yue", label: "Chinese — Cantonese (廣東話)" },
  { code: "ja", label: "Japanese (日本語)" },
  { code: "ko", label: "Korean (한국어)" },
  { code: "vi", label: "Vietnamese (Tiếng Việt)" },
  { code: "th", label: "Thai (ไทย)" },
  { code: "id", label: "Indonesian (Bahasa Indonesia)" },
  { code: "ms", label: "Malay (Bahasa Melayu)" },
  { code: "tl", label: "Tagalog / Filipino" },
  // South Asia
  { code: "hi", label: "Hindi (हिन्दी)" },
  { code: "bn", label: "Bengali (বাংলা)" },
  { code: "ur", label: "Urdu (اردو)" },
  { code: "pa", label: "Punjabi (ਪੰਜਾਬੀ)" },
  { code: "ta", label: "Tamil (தமிழ்)" },
  { code: "te", label: "Telugu (తెలుగు)" },
  { code: "gu", label: "Gujarati (ગુજરાતી)" },
  { code: "mr", label: "Marathi (मराठी)" },
  // Middle East & Central Asia
  { code: "ar", label: "Arabic (العربية)" },
  { code: "fa", label: "Persian / Farsi (فارسی)" },
  { code: "he", label: "Hebrew (עברית)" },
  { code: "tr", label: "Turkish (Türkçe)" },
  // Europe
  { code: "ru", label: "Russian (Русский)" },
  { code: "uk", label: "Ukrainian (Українська)" },
  { code: "pl", label: "Polish (Polski)" },
  { code: "cs", label: "Czech (Čeština)" },
  { code: "de", label: "German (Deutsch)" },
  { code: "fr", label: "French (Français)" },
  { code: "es", label: "Spanish (Español)" },
  { code: "pt", label: "Portuguese (Português)" },
  { code: "it", label: "Italian (Italiano)" },
  { code: "nl", label: "Dutch (Nederlands)" },
  { code: "ro", label: "Romanian (Română)" },
  { code: "el", label: "Greek (Ελληνικά)" },
  { code: "sv", label: "Swedish (Svenska)" },
  { code: "no", label: "Norwegian (Norsk)" },
  { code: "da", label: "Danish (Dansk)" },
  { code: "fi", label: "Finnish (Suomi)" },
  { code: "hu", label: "Hungarian (Magyar)" },
  // Africa
  { code: "sw", label: "Swahili (Kiswahili)" },
  { code: "am", label: "Amharic (አማርኛ)" },
  { code: "yo", label: "Yoruba (Yorùbá)" },
  { code: "ha", label: "Hausa" },
] as const;

export type SupportedAgentLanguageCode =
  (typeof SUPPORTED_AGENT_LANGUAGES)[number]["code"];

// Subset of `SUPPORTED_AGENT_LANGUAGES` for which the public referral
// landing page (`/refer/:code`) actually has translated copy, including
// Latin American Spanish (`es`). When an
// agent's primary language is outside this set, the landing page falls
// back to English while still displaying the agent's full spoken-
// languages chips. Add a code here only after shipping the matching
// `translations` entries in `client/src/pages/agent-landing.tsx`.
export const PORTAL_LOCALES = ["en", "zh", "vi", "hi", "es"] as const;
export type PortalLocale = (typeof PORTAL_LOCALES)[number];

// Shape used by API endpoints that accept multi-coverage payloads.
// The client sends `{ code, isPrimary }` rows for both countries and
// languages; storage helpers enforce "exactly one primary" before
// committing. Codes are trimmed but otherwise opaque to the storage
// layer (country codes are validated against the static country list
// at the route layer).
export const agentCoverageEntrySchema = z.object({
  code: z.string().trim().min(1).max(8),
  isPrimary: z.boolean().optional(),
});
export type AgentCoverageEntry = z.infer<typeof agentCoverageEntrySchema>;

// At least one entry is required; exactly one must be primary. We
// validate the "one primary" rule via a refinement so the form layer
// can show a single combined error message instead of two separate
// ones.
export const agentCoverageListSchema = z
  .array(agentCoverageEntrySchema)
  .min(1, "Select at least one option")
  .superRefine((items, ctx) => {
    const primaries = items.filter((i) => i.isPrimary).length;
    if (primaries !== 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Mark exactly one entry as primary",
      });
    }
    const seen = new Set<string>();
    for (const i of items) {
      const key = i.code.toLowerCase();
      if (seen.has(key)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Duplicate entry: ${i.code}`,
        });
        return;
      }
      seen.add(key);
    }
  });
// Task #698 — Multi-seat team logins per agent company. Each
// referral_agents row used to have a single password_hash so only one
// person at a partner firm could log in. `agent_users` lets a partner
// firm have multiple seat users, each with their own email + password,
// all scoped to the same parent agent (no per-seat data segregation in
// v1). The original contact is the "owner" seat; additional seats are
// "member". The password_hash on referral_agents is mirrored from the
// owner seat for backwards compatibility until the cutover removes it.
export const AGENT_USER_ROLES = ["owner", "member"] as const;
export type AgentUserRole = (typeof AGENT_USER_ROLES)[number];

export const agentUsers = pgTable(
  "agent_users",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    agentId: varchar("agent_id")
      .notNull()
      .references(() => referralAgents.id, { onDelete: "cascade" }),
    email: text("email").notNull(),
    // Task #1263 — `password_hash` dropped. Supabase Auth is the sole
    // credential store; the seat is linked via `auth_principal_id`.
    role: text("role").$type<AgentUserRole>().notNull().default("member"),
    isActive: boolean("is_active").notNull().default(true),
    // SHA-256 of the single-use invite token. Cleared on redemption.
    inviteToken: text("invite_token"),
    inviteTokenExpiresAt: timestamp("invite_token_expires_at"),
    // Task #1200 — Supabase auth prep. Nullable until the cutover
    // copies the matching `auth.users.id`. Partial-unique-when-set
    // index lives in migration 0133.
    authPrincipalId: varchar("auth_principal_id"),
    // Task #1585 — Light email-verification for self-registered
    // partners. The plaintext token is sent in a transactional email;
    // only its SHA-256 digest is stored (same pattern as the welcome
    // setup token). `emailVerifiedAt` is null until the partner clicks
    // the verify link, and is the latch the admin-approval queue
    // (Task D) reads to decide whether the partner can be approved.
    emailVerifiedAt: timestamp("email_verified_at"),
    emailVerificationToken: text("email_verification_token"),
    emailVerificationTokenExpiresAt: timestamp("email_verification_token_expires_at"),
    // CIO GAP — partner seat login + email-change stamps.
    lastLoginAt: timestamp("last_login_at"),
    emailChangedAt: timestamp("email_changed_at"),
    previousEmail: text("previous_email"),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (t) => ({
    // Same email may exist under different agent companies, but never
    // twice within a single firm. Migration also enforces a case-
    // insensitive unique index so logins don't depend on casing.
    uniqAgentEmail: uniqueIndex("agent_users_agent_email_lower_uniq").on(
      t.agentId,
      sql`lower(${t.email})`,
    ),
  }),
);

export type AgentUser = typeof agentUsers.$inferSelect;
export type SafeAgentUser = Omit<AgentUser, "inviteToken">;

// Task #1200 — Memberships join table for referral-agent firms.
// Backfilled 1:1 from `agent_users` by migration 0134. After the
// Supabase Auth cutover, a single auth principal will be able to
// hold seats in multiple firms by adding more rows here without
// duplicating the agent_users profile.
export const agentFirmMemberships = pgTable(
  "agent_firm_memberships",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    agentUserId: varchar("agent_user_id")
      .notNull()
      .references(() => agentUsers.id, { onDelete: "cascade" }),
    agentFirmId: varchar("agent_firm_id")
      .notNull()
      .references(() => referralAgents.id, { onDelete: "cascade" }),
    // Mirrors agent_users.auth_principal_id so RLS predicates can
    // key off this table directly. Backfilled by the auth cutover.
    authPrincipalId: varchar("auth_principal_id"),
    role: text("role").$type<AgentUserRole>().notNull().default("member"),
    isActive: boolean("is_active").notNull().default(true),
    createdAt: timestamp("created_at").notNull().defaultNow(),
  },
  (t) => ({
    uniqUserFirm: uniqueIndex("idx_agent_firm_memberships_user_firm_uniq").on(
      t.agentUserId,
      t.agentFirmId,
    ),
  }),
);

export type AgentFirmMembership = typeof agentFirmMemberships.$inferSelect;

// Task #1586 — Admin approval queue audit trail. One row per
// admin decision on a self-registered partner sitting in
// `referral_agents.portal_status = 'pending_approval'`. The admin-
// invited path does NOT write here (those partners never enter the
// queue). `actorAdminId` is nullable so a system/scripted action
// (cron sweep, migration backfill) can still leave an audit row.
export const AGENT_APPROVAL_ACTIONS = ["approve", "reject", "reinstate"] as const;
export type AgentApprovalAction = (typeof AGENT_APPROVAL_ACTIONS)[number];

export const agentApprovalEvents = pgTable("agent_approval_events", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  agentId: varchar("agent_id")
    .notNull()
    .references(() => referralAgents.id, { onDelete: "cascade" }),
  action: text("action").$type<AgentApprovalAction>().notNull(),
  actorAdminId: varchar("actor_admin_id"),
  reason: text("reason"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export type AgentApprovalEvent = typeof agentApprovalEvents.$inferSelect;

// Task #1007 — Broker-dealer compliance-underwriting workflow.
//
// `project_bd_compliance_grants` is the join table between an
// investment project and a broker-dealer referral agent that the
// admin has granted compliance-underwriting access to. Status moves
// monotonically forward through the workflow (admin can revoke at
// any point, which is captured by `revokedAt`/`revokedByUserId`
// rather than a status change).
//
// `project_compliance_package_items` is the admin-curated list of
// links the BD downloads as the "compliance underwriting package"
// for a given project. v1 stores plain links — the URL may be a
// `/objects/...` private object path or any external URL. The agent
// portal exposes the URL only after the admin has marked the BD's
// fee as paid for that project.
export const BD_COMPLIANCE_GRANT_STATUSES = [
  "not_started",
  "fee_paid",
  "in_progress",
  "passed",
  "failed",
] as const;
export type BdComplianceGrantStatus =
  (typeof BD_COMPLIANCE_GRANT_STATUSES)[number];

export const BD_COMPLIANCE_DECISIONS = ["passed", "failed"] as const;
export type BdComplianceDecision = (typeof BD_COMPLIANCE_DECISIONS)[number];

// Task #2427 — broker-led compliance redesign.
// The review workflow is now broker-initiated with a self-quoted fee.
export const BD_COMPLIANCE_REVIEW_STATUSES = [
  "not_started",
  "in_progress",
  "completed",
] as const;
export type BdComplianceReviewStatus =
  (typeof BD_COMPLIANCE_REVIEW_STATUSES)[number];

export const BD_COMPLIANCE_OUTCOMES = [
  "passed",
  "passed_with_conditions",
  "did_not_pass",
  "withdrawn",
  // Broker opted out up front: their firm does not require an
  // independent compliance review for this deal.
  "not_required",
] as const;
export type BdComplianceOutcome = (typeof BD_COMPLIANCE_OUTCOMES)[number];

export const projectBdComplianceGrants = pgTable(
  "project_bd_compliance_grants",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    projectId: varchar("project_id")
      .notNull()
      .references(() => investmentProjects.id, { onDelete: "cascade" }),
    referralAgentId: varchar("referral_agent_id")
      .notNull()
      .references(() => referralAgents.id, { onDelete: "cascade" }),
    // Legacy derived status — kept for history/backward compat.
    // New code should use reviewStatus below.
    status: text("status")
      .$type<BdComplianceGrantStatus>()
      .notNull()
      .default("not_started"),
    // Legacy snapshot of BD flat fee at admin-grant time. New code uses
    // brokerProposedFeeCents instead.
    feeCentsAtGrant: integer("fee_cents_at_grant"),
    feePaidAt: timestamp("fee_paid_at"),
    // Task #1170 — all four user FKs use ON DELETE SET NULL so a
    // deleted admin clears the actor pointer instead of blocking
    // the parent delete.
    feePaidByUserId: varchar("fee_paid_by_user_id").references(() => users.id, { onDelete: "set null" }),
    // Legacy admin-decision columns — kept for history.
    decision: text("decision").$type<BdComplianceDecision>(),
    decisionAt: timestamp("decision_at"),
    decisionByUserId: varchar("decision_by_user_id").references(() => users.id, { onDelete: "set null" }),
    bdAcknowledgedAt: timestamp("bd_acknowledged_at"),
    bdAcknowledgedByAgentUserId: varchar("bd_acknowledged_by_agent_user_id")
      .references(() => agentUsers.id),
    grantedAt: timestamp("granted_at").notNull().defaultNow(),
    grantedByUserId: varchar("granted_by_user_id").references(() => users.id, { onDelete: "set null" }),
    revokedAt: timestamp("revoked_at"),
    revokedByUserId: varchar("revoked_by_user_id").references(() => users.id, { onDelete: "set null" }),
    notes: text("notes"),
    // ---- Task #2427 — broker-led engagement columns ------------------
    // Broker self-quotes their due-diligence fee; admin can adjust before
    // marking it paid. NULL until the broker sets it.
    brokerProposedFeeCents: integer("broker_proposed_fee_cents"),
    // Who initiated this engagement (null for admin-created legacy rows).
    initiatedByAgentUserId: varchar("initiated_by_agent_user_id")
      .references(() => agentUsers.id, { onDelete: "set null" }),
    // Broker-controlled review lifecycle.
    reviewStatus: text("review_status")
      .$type<BdComplianceReviewStatus>()
      .notNull()
      .default("not_started"),
    reviewStartedAt: timestamp("review_started_at"),
    reviewStartedByAgentUserId: varchar("review_started_by_agent_user_id")
      .references(() => agentUsers.id, { onDelete: "set null" }),
    reviewCompletedAt: timestamp("review_completed_at"),
    reviewCompletedByAgentUserId: varchar("review_completed_by_agent_user_id")
      .references(() => agentUsers.id, { onDelete: "set null" }),
    reviewOutcome: text("review_outcome").$type<BdComplianceOutcome>(),
    reviewNotes: text("review_notes"),
    createdAt: timestamp("created_at").notNull().defaultNow(),
    updatedAt: timestamp("updated_at").notNull().defaultNow(),
  },
);

export type ProjectBdComplianceGrant =
  typeof projectBdComplianceGrants.$inferSelect;

export const projectCompliancePackageItems = pgTable(
  "project_compliance_package_items",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    projectId: varchar("project_id")
      .notNull()
      .references(() => investmentProjects.id, { onDelete: "cascade" }),
    title: text("title").notNull(),
    description: text("description"),
    url: text("url").notNull(),
    sortOrder: integer("sort_order").notNull().default(0),
    createdAt: timestamp("created_at").notNull().defaultNow(),
  },
);

export const insertProjectCompliancePackageItemSchema = createInsertSchema(
  projectCompliancePackageItems,
)
  .omit({ id: true, createdAt: true })
  .extend({
    title: z.string().trim().min(1, "Title is required").max(200),
    description: z.string().trim().max(2000).nullable().optional(),
    url: z.string().trim().min(1, "URL is required").max(2048),
    sortOrder: z.number().int().nonnegative().optional(),
  });

export type InsertProjectCompliancePackageItem = z.infer<
  typeof insertProjectCompliancePackageItemSchema
>;
export type ProjectCompliancePackageItem =
  typeof projectCompliancePackageItems.$inferSelect;

// Task #2618 — lightweight message thread for an open compliance review.
//
// When a reviewer (broker-dealer or foreign migration agent) asks a
// question / gives feedback during their due-diligence review, the message
// is persisted here so both the reviewer and the Veras admin see a
// timestamped log of the back-and-forth — and the Veras admin can reply
// in-thread. One row per message, tied to the engagement (grant) row.
//
// RLS posture: service-role only (default-deny), mirroring the parent
// `project_bd_compliance_grants` table. Both the agent portal and the admin
// portal run on the app pool (service_role / BYPASSRLS) and enforce scope
// in Express, so there are no per-row policies.
export const BD_COMPLIANCE_MESSAGE_SENDERS = ["reviewer", "admin"] as const;
export type BdComplianceMessageSender =
  (typeof BD_COMPLIANCE_MESSAGE_SENDERS)[number];

export const bdComplianceMessages = pgTable(
  "bd_compliance_messages",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    grantId: varchar("grant_id")
      .notNull()
      .references(() => projectBdComplianceGrants.id, { onDelete: "cascade" }),
    // Who wrote this message: the reviewing partner or the Veras team.
    senderKind: text("sender_kind").$type<BdComplianceMessageSender>().notNull(),
    // Snapshot of the author's display name at send time so the thread reads
    // correctly even if the seat is later renamed or removed.
    authorName: text("author_name").notNull(),
    // Actor pointers (nullable; ON DELETE SET NULL so a removed seat/admin
    // doesn't block deleting the parent or strand the thread).
    authorAgentUserId: varchar("author_agent_user_id").references(
      () => agentUsers.id,
      { onDelete: "set null" },
    ),
    authorUserId: varchar("author_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    body: text("body").notNull(),
    createdAt: timestamp("created_at").notNull().defaultNow(),
  },
);

export type BdComplianceMessage = typeof bdComplianceMessages.$inferSelect;

// Task #695 — first-party click tracking for partner referral links.
// Each row is one resolved hit on `/refer/:code` (via the agent-landing
// API). We dedupe by ipHash + referralCode + 1-hour bucket so basic
// bot/refresh inflation doesn't make the funnel look better than it is.
// We deliberately do NOT store raw IPs or user-agent strings — the only
// PII that should ever land here is what a partner already sees on
// inquiries (country at most). `referralAgentId` may be null when the
// code didn't match any active agent (still useful for surfacing
// broken/typoed links).
export const referralLinkVisits = pgTable("referral_link_visits", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  referralAgentId: varchar("referral_agent_id").references(() => referralAgents.id, { onDelete: "set null" }),
  referralCode: text("referral_code").notNull(),
  utmSource: text("utm_source"),
  utmMedium: text("utm_medium"),
  utmCampaign: text("utm_campaign"),
  utmTerm: text("utm_term"),
  utmContent: text("utm_content"),
  country: text("country"),
  // SHA-256 of the visitor IP, used only for the 1-hour throttle window.
  // Not reversible to a real address; never returned to admins or agents.
  ipHash: text("ip_hash"),
  createdAt: timestamp("created_at").defaultNow(),
});

export type ReferralLinkVisit = typeof referralLinkVisits.$inferSelect;

// ----------------------------------------------------------------------------
// Task #890 — partner marketing toolkit (QR codes, social cards, asset
// tracking, tracked short links).
// ----------------------------------------------------------------------------

// Short, opaque codes that resolve to a longer "destination URL" with
// the agent's referral attribution baked in. Lets a partner share a
// clean `/s/<8-char>` link in places where a long UTM URL would be
// awkward (printed cards, QR codes, video bumpers, SMS) and gives us a
// per-share-link analytics row that sums opens by timestamp / UTM /
// country exactly like `referral_link_visits`.
export const MARKETING_SHORT_LINK_DESTINATION_KINDS = [
  "agent_landing",
  "project",
  "external",
  // Task #1191 — admin-owned destinations: an arbitrary internal page
  // (e.g. /eb5-guide, a marketing landing slug) and a link-hub button
  // that points at any of the above.
  "internal",
  "link_hub_button",
] as const;
export type MarketingShortLinkDestinationKind =
  (typeof MARKETING_SHORT_LINK_DESTINATION_KINDS)[number];

export const marketingShortLinks = pgTable("marketing_short_links", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  // Owning agent. SET NULL on delete so historical rows remain queryable
  // even if the partner row is removed (the visits table behaves the
  // same way).
  referralAgentId: varchar("referral_agent_id").references(
    () => referralAgents.id,
    { onDelete: "set null" },
  ),
  // Optional creator (multi-seat partners). Lets the dashboard show
  // "your" links separately from teammates' links if we ever want to.
  createdByAgentUserId: varchar("created_by_agent_user_id").references(
    () => agentUsers.id,
    { onDelete: "set null" },
  ),
  // Public short slug used in `/s/:code`. Lower-case alphanumeric;
  // we generate 8 chars from a 32-char alphabet → ~10^12 combinations,
  // which is plenty for the unique index to keep collisions theoretical.
  code: text("code").notNull().unique(),
  // Optional human-friendly label so the agent can recognize the link
  // in the analytics table ("Spring webinar QR", "LinkedIn carousel", …).
  label: text("label"),
  destinationKind: text("destination_kind")
    .notNull()
    .$type<MarketingShortLinkDestinationKind>(),
  // Optional project this link is associated with (for `project` kind,
  // and as a soft-tag for the analytics filter).
  projectId: varchar("project_id").references(() => investmentProjects.id, {
    onDelete: "set null",
  }),
  // Fully-resolved destination URL we 302 to. We pre-bake the agent's
  // referral code + UTMs at creation time so the redirect handler is
  // O(1) and the link survives schema changes to UTM parameter names.
  destinationUrl: text("destination_url").notNull(),
  // Convenience denormalized columns for the analytics table — we only
  // surface them in the UI, never use them for resolution. They mirror
  // what was set on the destination URL at creation time.
  utmSource: text("utm_source"),
  utmMedium: text("utm_medium"),
  utmCampaign: text("utm_campaign"),
  utmContent: text("utm_content"),
  // Soft delete — keeps analytics rows pointing at something even after
  // the agent archives the link.
  archivedAt: timestamp("archived_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  // Task #1191 — admin-owned QR codes + link-hub buttons share this
  // table. `ownerKind` discriminates between agent-owned legacy rows,
  // admin campaign QRs, and per-button rows that belong to a hub.
  // `codeNamespace` selects which redirect path resolves the row
  // (`s` = /s/<code> agent links, `q` = /q/<code> admin codes).
  ownerKind: text("owner_kind").$type<
    "agent" | "admin_campaign" | "admin_hub_button"
  >(),
  ownerAdminId: varchar("owner_admin_id"),
  campaignLabel: text("campaign_label"),
  codeNamespace: text("code_namespace").notNull().default("s").$type<"s" | "q">(),
  // When set, this row is a button on the named link hub. `hubPosition`
  // controls the button's order on the hub page.
  linkHubId: varchar("link_hub_id"),
  hubPosition: integer("hub_position"),
  hubButtonDescription: text("hub_button_description"),
  // Task #1332 — Branding choice for downloadable images. "veras" wraps
  // the QR in the framed Veras card (logo + URL caption); "none" emits
  // a bare QR with no frame. Defaults to "veras" so pre-existing rows
  // keep producing the same downloads they did before this column.
  branding: text("branding").notNull().default("veras").$type<"veras" | "none">(),
});

export const insertMarketingShortLinkSchema = createInsertSchema(
  marketingShortLinks,
)
  .omit({ id: true, code: true, createdAt: true, archivedAt: true })
  .extend({
    label: z.string().trim().max(120).nullable().optional(),
    destinationKind: z.enum(MARKETING_SHORT_LINK_DESTINATION_KINDS),
    destinationUrl: z.string().trim().url().max(2048),
    projectId: z.string().uuid().nullable().optional(),
    utmSource: z.string().trim().max(120).nullable().optional(),
    utmMedium: z.string().trim().max(120).nullable().optional(),
    utmCampaign: z.string().trim().max(120).nullable().optional(),
    utmContent: z.string().trim().max(120).nullable().optional(),
    branding: z.enum(["veras", "none"]).optional(),
  });

export type InsertMarketingShortLink = z.infer<
  typeof insertMarketingShortLinkSchema
>;
export type MarketingShortLink = typeof marketingShortLinks.$inferSelect;

// Append-only event log for marketing-asset interactions: when an agent
// downloads a co-branded asset (PDF/hero image), generates a QR code,
// generates a social card, or copies a snippet, we drop one row here so
// the "What's working" widget on the dashboard and the analytics page
// can answer "which assets are partners actually using".
export const MARKETING_ASSET_EVENT_TYPES = [
  // Agent-side actions.
  "asset_download",
  "qr_generated",
  "social_card_generated",
  "snippet_copied",
  "short_link_created",
  // Public-side: the tracked short link was opened.
  "short_link_open",
  // Task #1191 — admin link-hub events.
  "link_hub_view",
  "link_hub_button_click",
  // Admin personal public-link page (vanity /:slug).
  "admin_public_link_view",
] as const;
export type MarketingAssetEventType =
  (typeof MARKETING_ASSET_EVENT_TYPES)[number];

export const MARKETING_ASSET_TARGET_KINDS = [
  "project_marketing_asset",
  "qr_code",
  "social_card",
  "short_link",
  "snippet",
  // Task #1191 — admin link-hub targets.
  "link_hub",
  "link_hub_button",
  "admin_public_link",
] as const;
export type MarketingAssetTargetKind =
  (typeof MARKETING_ASSET_TARGET_KINDS)[number];

export const marketingAssetEvents = pgTable("marketing_asset_events", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  eventType: text("event_type").notNull().$type<MarketingAssetEventType>(),
  // The agent the event is attributed to (e.g. the agent who downloaded
  // the PDF or whose short link was just opened). Nullable so a public
  // open of a stale short link still records the row.
  referralAgentId: varchar("referral_agent_id").references(
    () => referralAgents.id,
    { onDelete: "set null" },
  ),
  // For agent-side events, the seat that actually performed the action.
  agentUserId: varchar("agent_user_id").references(() => agentUsers.id, {
    onDelete: "set null",
  }),
  // Polymorphic target. `targetKind` says what `targetId` points at;
  // `projectId` is denormalized for fast project-scoped analytics.
  targetKind: text("target_kind").notNull().$type<MarketingAssetTargetKind>(),
  targetId: varchar("target_id"),
  projectId: varchar("project_id").references(() => investmentProjects.id, {
    onDelete: "set null",
  }),
  shortLinkId: varchar("short_link_id").references(
    () => marketingShortLinks.id,
    { onDelete: "set null" },
  ),
  // Free-form attribution echoed from the link / request (UTM + country
  // for short link opens, asset kind for downloads, etc).
  utmSource: text("utm_source"),
  utmMedium: text("utm_medium"),
  utmCampaign: text("utm_campaign"),
  utmContent: text("utm_content"),
  country: text("country"),
  // SHA-256 of the visitor IP for short-link opens — used only for the
  // 1-hour throttle and never returned to clients. NULL for agent-side
  // events.
  ipHash: text("ip_hash"),
  // Small free-form bag for per-event-type extras (e.g. asset kind,
  // QR format = png/svg/print). Capped client-side to avoid abuse.
  metadata: jsonb("metadata").$type<Record<string, unknown>>(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  // Task #1191 — link-hub roll-up dims, denormalized so analytics
  // queries can group/filter without a join.
  linkHubId: varchar("link_hub_id"),
  ownerAdminId: varchar("owner_admin_id"),
});

export type MarketingAssetEvent = typeof marketingAssetEvents.$inferSelect;

// ----------------------------------------------------------------------------
// Task #1191 — Admin link hubs (Linktree-style landing pages).
// ----------------------------------------------------------------------------
export const marketingLinkHubs = pgTable("marketing_link_hubs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  // URL slug — public hub address is /h/<slug>.
  slug: text("slug").notNull().unique(),
  title: text("title").notNull(),
  subtitle: text("subtitle"),
  heroImageUrl: text("hero_image_url"),
  ownerAdminId: varchar("owner_admin_id").references(() => users.id, {
    onDelete: "set null",
  }),
  archivedAt: timestamp("archived_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const insertMarketingLinkHubSchema = createInsertSchema(marketingLinkHubs)
  .omit({ id: true, createdAt: true, updatedAt: true, archivedAt: true })
  .extend({
    slug: z
      .string()
      .trim()
      .min(2)
      .max(64)
      .regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/, {
        message:
          "Slug must be lowercase alphanumerics or hyphens (no leading/trailing hyphen)",
      }),
    title: z.string().trim().min(1).max(140),
    subtitle: z.string().trim().max(280).nullable().optional(),
    heroImageUrl: z.string().trim().max(2048).nullable().optional(),
  });

export type InsertMarketingLinkHub = z.infer<typeof insertMarketingLinkHubSchema>;
export type MarketingLinkHub = typeof marketingLinkHubs.$inferSelect;

// ----------------------------------------------------------------------------
// Commissions engine (Task #692). One agent can have many rules; each
// concrete payout/accrual lives in commission_entries.
// ----------------------------------------------------------------------------
export const COMMISSION_RULE_TYPES = [
  "admin_fee_pct",
  "ir_spread_bps",
  "flat_per_investor",
] as const;
export type CommissionRuleType = (typeof COMMISSION_RULE_TYPES)[number];

export const COMMISSION_CADENCES = ["one_time", "monthly", "quarterly"] as const;
export type CommissionCadence = (typeof COMMISSION_CADENCES)[number];

export const COMMISSION_TRIGGER_EVENTS = [
  "subscription_funded",
  "subscription_signed",
  "commitment_confirmed",
  // Task #999 — Partner Deals: brokers and immigration attorneys
  // commonly only earn on issuance of the I-526E receipt notice.
  "i526e_receipt_notice",
] as const;
export type CommissionTriggerEvent = (typeof COMMISSION_TRIGGER_EVENTS)[number];

export const COMMISSION_PROJECT_SCOPES = ["all", "specific"] as const;
export type CommissionProjectScope = (typeof COMMISSION_PROJECT_SCOPES)[number];

export const COMMISSION_ENTRY_STATUSES = ["accrued", "paid", "voided"] as const;
export type CommissionEntryStatus = (typeof COMMISSION_ENTRY_STATUSES)[number];

// Task #999 — Partner Deals: clawback policy is recorded on the rule
// for documentation/operational purposes ONLY. The engine does not
// auto-reverse accruals; admins void/adjust manually if a clawback
// event occurs. `none` is the default.
export const COMMISSION_CLAWBACK_MODES = ["none", "full", "pro_rata", "tiered"] as const;
export type CommissionClawbackMode = (typeof COMMISSION_CLAWBACK_MODES)[number];

export const commissionRules = pgTable("commission_rules", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  agentId: varchar("agent_id").notNull().references(() => referralAgents.id, { onDelete: "cascade" }),
  // All versions of the same logical rule share `ruleGroupId`. Editing a
  // rule writes a new row with the same group id and version+1, while the
  // old row gets `effectiveTo` stamped — already-accrued ledger entries
  // continue to point at the historic row they were generated from.
  ruleGroupId: varchar("rule_group_id").notNull(),
  version: integer("version").notNull().default(1),
  ruleType: text("rule_type").notNull(),
  pctBps: integer("pct_bps"),
  flatAmountCents: integer("flat_amount_cents"),
  cadence: text("cadence").notNull().default("one_time"),
  triggerEvent: text("trigger_event").notNull().default("subscription_funded"),
  projectScope: text("project_scope").notNull().default("all"),
  projectId: varchar("project_id").references(() => investmentProjects.id, { onDelete: "set null" }),
  label: text("label"),
  notes: text("notes"),
  // Task #999 — Partner Deals: clawback policy is record-only. Stored
  // here so the rule fully describes the deal terms, but the engine
  // never auto-reverses accruals based on these fields.
  clawbackMode: text("clawback_mode").$type<CommissionClawbackMode>().notNull().default("none"),
  clawbackWindowDays: integer("clawback_window_days"),
  clawbackNotes: text("clawback_notes"),
  effectiveFrom: timestamp("effective_from").notNull().defaultNow(),
  effectiveTo: timestamp("effective_to"),
  createdByUserId: varchar("created_by_user_id"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const insertCommissionRuleSchema = createInsertSchema(commissionRules)
  .omit({
    id: true,
    // agentId is taken from the URL parameter on the admin route — the
    // client never sends it in the body, so it must be omitted here or
    // every create/revise call would 400.
    agentId: true,
    ruleGroupId: true,
    version: true,
    effectiveFrom: true,
    effectiveTo: true,
    createdByUserId: true,
    createdAt: true,
    updatedAt: true,
  })
  .extend({
    ruleType: z.enum(COMMISSION_RULE_TYPES),
    cadence: z.enum(COMMISSION_CADENCES).default("one_time"),
    triggerEvent: z.enum(COMMISSION_TRIGGER_EVENTS).default("subscription_funded"),
    projectScope: z.enum(COMMISSION_PROJECT_SCOPES).default("all"),
    pctBps: z.number().int().min(0).max(1_000_000).nullable().optional(),
    flatAmountCents: z.number().int().min(0).nullable().optional(),
    projectId: z.string().nullable().optional(),
    label: z.string().max(120).nullable().optional(),
    notes: z.string().max(2000).nullable().optional(),
    // Task #999 — Partner Deals (record-only clawback fields).
    clawbackMode: z.enum(COMMISSION_CLAWBACK_MODES).default("none"),
    clawbackWindowDays: z.number().int().min(0).max(3650).nullable().optional(),
    clawbackNotes: z.string().max(2000).nullable().optional(),
  });

export type InsertCommissionRule = z.infer<typeof insertCommissionRuleSchema>;
export type CommissionRule = typeof commissionRules.$inferSelect;

export const partnerDealTerms = pgTable("partner_deal_terms", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  agentId: varchar("agent_id").notNull().references(() => referralAgents.id, { onDelete: "cascade" }),
  version: integer("version").notNull().default(1),
  programKind: text("program_kind").notNull().default("paid_lead").$type<"paid_lead" | "referral_conversion">(),
  investmentAmountCents: integer("investment_amount_cents").notNull(),
  listAdminFeeCents: integer("list_admin_fee_cents").notNull(),
  retainedAdminFeeCents: integer("retained_admin_fee_cents").notNull(),
  // Legacy compatibility columns. Current deal terms neither accept nor
  // write these controls; retained rows remain readable for audit history.
  minVerasAdminResidualCents: integer("min_veras_admin_residual_cents").notNull().default(1_000_000),
  irSpreadBps: integer("ir_spread_bps").notNull().default(0),
  termYears: integer("term_years").notNull().default(5),
  sourceProjectId: varchar("source_project_id").references(() => investmentProjects.id, { onDelete: "set null" }),
  leadReferralRateCents: integer("lead_referral_rate_cents").notNull(),
  conversionFlatFeeCents: integer("conversion_flat_fee_cents").notNull().default(0),
  conversionRetainedAdminPctBps: integer("conversion_retained_admin_pct_bps").notNull().default(0),
  conversionIrSpreadPctBps: integer("conversion_ir_spread_pct_bps").notNull().default(0),
  // Canonical absolute backend points on investment principal.
  conversionBackendPointsBps: integer("conversion_backend_points_bps").notNull().default(0),
  effectiveFrom: timestamp("effective_from").notNull().defaultNow(),
  effectiveTo: timestamp("effective_to"),
  createdByUserId: varchar("created_by_user_id").references((): AnyPgColumn => users.id, { onDelete: "set null" }),
  lockedAt: timestamp("locked_at"),
  lockedByUserId: varchar("locked_by_user_id").references((): AnyPgColumn => users.id, { onDelete: "set null" }),
  revisionOfId: varchar("revision_of_id"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export type PartnerDealTerms = typeof partnerDealTerms.$inferSelect;

/**
 * Partner types share one agreement. The type only changes plan A/B/C
 * numbers on a project menu. Qualified Lead is not a row here.
 */
export const partnerTypes = pgTable("partner_types", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  code: text("code").notNull(),
  name: text("name").notNull(),
  description: text("description").notNull(),
  active: boolean("active").notNull().default(true),
  isDefault: boolean("is_default").notNull().default(false),
  createdByUserId: varchar("created_by_user_id").references((): AnyPgColumn => users.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
}, (table) => ({
  codeUniq: uniqueIndex("partner_types_code_uniq").on(table.code),
  nameUniq: uniqueIndex("partner_types_name_uniq").on(sql`lower(btrim(${table.name}))`),
  defaultUniq: uniqueIndex("partner_types_default_uniq").on(table.isDefault).where(sql`is_default`),
}));

export type PartnerType = typeof partnerTypes.$inferSelect;

export const projectPricingMenus = pgTable("project_pricing_menus", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  projectId: varchar("project_id").notNull().references(() => investmentProjects.id, { onDelete: "cascade" }),
  partnerTypeId: varchar("partner_type_id").notNull().references(() => partnerTypes.id, { onDelete: "restrict" }),
  version: integer("version").notNull().default(1),
  unitAmountCents: integer("unit_amount_cents").notNull(),
  adminFeeCents: integer("admin_fee_cents").notNull(),
  loanRateBps: integer("loan_rate_bps").notNull(),
  investorPreferredBps: integer("investor_preferred_bps").notNull(),
  verasEnergyInterestPointsBps: integer("veras_energy_interest_points_bps").notNull(),
  interestYears: integer("interest_years").notNull(),
  backendOffered: boolean("backend_offered").notNull(),
  upfrontCapBps: integer("upfront_cap_bps").notNull(),
  silverUpliftBps: integer("silver_uplift_bps").notNull(),
  goldUpliftBps: integer("gold_uplift_bps").notNull(),
  residualFloorCents: integer("residual_floor_cents").notNull(),
  adminFeeClassAShareBps: integer("admin_fee_class_a_share_bps").notNull(),
  partnerUpfrontClassAShareBps: integer("partner_upfront_class_a_share_bps").notNull(),
  backendSharedThroughPointsBps: integer("backend_shared_through_points_bps").notNull(),
  backendSharedClassAShareBps: integer("backend_shared_class_a_share_bps").notNull(),
  internalSplitCapitalBps: integer("internal_split_capital_bps"),
  options: jsonb("options").$type<Array<{
    code: "A" | "B" | "C";
    label: string;
    upfrontCents: number;
    retainedFeePctBps?: number;
    adminPayMode?: "cash" | "percent";
    backendPointsBps: number;
    isDefault: boolean;
  }>>().notNull(),
  publishedAt: timestamp("published_at").notNull().defaultNow(),
  supersededAt: timestamp("superseded_at"),
  createdByUserId: varchar("created_by_user_id").references((): AnyPgColumn => users.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (table) => ({
  activeUniq: uniqueIndex("project_pricing_menus_active_uniq")
    .on(table.projectId, table.partnerTypeId)
    .where(sql`superseded_at IS NULL`),
  versionUniq: uniqueIndex("project_pricing_menus_version_uniq")
    .on(table.projectId, table.partnerTypeId, table.version),
  projectIdx: index("project_pricing_menus_project_idx").on(table.projectId),
  typeIdx: index("project_pricing_menus_type_idx").on(table.partnerTypeId),
}));

export type ProjectPricingMenu = typeof projectPricingMenus.$inferSelect;

export const projectPricingAgreementTemplates = pgTable("project_pricing_agreement_templates", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  projectId: varchar("project_id").notNull().references(() => investmentProjects.id, { onDelete: "cascade" }),
  version: integer("version").notNull(),
  title: text("title").notNull(),
  body: text("body").notNull(),
  publishedAt: timestamp("published_at").notNull().defaultNow(),
  supersededAt: timestamp("superseded_at"),
  createdByUserId: varchar("created_by_user_id").references((): AnyPgColumn => users.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (table) => ({
  currentUniq: uniqueIndex("project_pricing_agreement_templates_current_uniq")
    .on(table.projectId)
    .where(sql`superseded_at IS NULL`),
  versionUniq: uniqueIndex("project_pricing_agreement_templates_version_uniq")
    .on(table.projectId, table.version),
}));

export type ProjectPricingAgreementTemplate = typeof projectPricingAgreementTemplates.$inferSelect;

/**
 * Living Partner Commercial Agreement. One current version for the program.
 * A project keeps its pricing menu. A sent engagement pins this id.
 * Older engagements may still pin project_pricing_agreement_templates.
 */
export const partnerProgramAgreementTemplates = pgTable("partner_program_agreement_templates", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  version: integer("version").notNull(),
  title: text("title").notNull(),
  body: text("body").notNull(),
  publishedAt: timestamp("published_at").notNull().defaultNow(),
  supersededAt: timestamp("superseded_at"),
  createdByUserId: varchar("created_by_user_id").references((): AnyPgColumn => users.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (table) => ({
  currentUniq: uniqueIndex("partner_program_agreement_templates_current_uniq")
    .on(sql`(1)`)
    .where(sql`superseded_at IS NULL`),
  versionUniq: uniqueIndex("partner_program_agreement_templates_version_uniq")
    .on(table.version),
}));

export type PartnerProgramAgreementTemplate = typeof partnerProgramAgreementTemplates.$inferSelect;

export const partnerProjectEngagements = pgTable("partner_project_engagements", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  agentId: varchar("agent_id").notNull().references(() => referralAgents.id, { onDelete: "cascade" }),
  projectId: varchar("project_id").notNull().references(() => investmentProjects.id, { onDelete: "cascade" }),
  partnerTypeId: varchar("partner_type_id").notNull().references(() => partnerTypes.id, { onDelete: "restrict" }),
  menuId: varchar("menu_id").notNull().references(() => projectPricingMenus.id, { onDelete: "restrict" }),
  menuVersion: integer("menu_version").notNull(),
  optionCode: text("option_code").$type<"A" | "B" | "C" | null>(),
  isException: boolean("is_exception").notNull().default(false),
  exceptionReason: text("exception_reason"),
  standardMark: text("standard_mark").notNull().$type<"standard" | "non_standard">(),
  statusAtPricing: text("status_at_pricing").notNull().default("registered").$type<"registered" | "silver" | "gold">(),
  partnerUpfrontCents: integer("partner_upfront_cents").notNull(),
  backendPointsBps: integer("backend_points_bps").notNull(),
  partnerBackendAnnualCents: integer("partner_backend_annual_cents").notNull(),
  partnerBackendLifetimeCents: integer("partner_backend_lifetime_cents").notNull(),
  interestYears: integer("interest_years").notNull(),
  verasEnergyNetCents: integer("veras_energy_net_cents").notNull(),
  directBaselineCents: integer("direct_baseline_cents").notNull(),
  classAPartnerCostCents: integer("class_a_partner_cost_cents").notNull(),
  snapshot: jsonb("snapshot").$type<PartnerPricingEngagementSnapshot>().notNull(),
  scheduleType: text("schedule_type").notNull().default("schedule_a").$type<"schedule_a">(),
  projectApprovalReference: text("project_approval_reference"),
  proposalId: varchar("proposal_id").references((): AnyPgColumn => partnerTermsProposals.id, { onDelete: "set null" }),
  proposalSentAt: timestamp("proposal_sent_at"),
  proposalExpiresAt: timestamp("proposal_expires_at"),
  proposalAcceptedAt: timestamp("proposal_accepted_at"),
  nonStandardApprovalConfirmedAt: timestamp("non_standard_approval_confirmed_at"),
  nonStandardApprovalConfirmedBy: varchar("non_standard_approval_confirmed_by").references((): AnyPgColumn => users.id, { onDelete: "set null" }),
  nonStandardApprovalReference: text("non_standard_approval_reference"),
  agreementTemplateId: varchar("agreement_template_id").references(() => projectPricingAgreementTemplates.id, { onDelete: "restrict" }),
  programAgreementTemplateId: varchar("program_agreement_template_id").references(() => partnerProgramAgreementTemplates.id, { onDelete: "restrict" }),
  agreementTemplateVersion: integer("agreement_template_version"),
  signedPdfPath: text("signed_pdf_path"),
  signedPdfSha256: text("signed_pdf_sha256"),
  signerName: text("signer_name"),
  signerTitle: text("signer_title"),
  signerEmail: text("signer_email"),
  signatureIp: text("signature_ip"),
  signatureUserAgent: text("signature_user_agent"),
  consentAcknowledgedAt: timestamp("consent_acknowledged_at"),
  signedAt: timestamp("signed_at"),
  verasSignerName: text("veras_signer_name"),
  verasSignerTitle: text("veras_signer_title"),
  verasSignerEmail: text("veras_signer_email"),
  verasSignatureIp: text("veras_signature_ip"),
  verasSignatureUserAgent: text("veras_signature_user_agent"),
  verasConsentAcknowledgedAt: timestamp("veras_consent_acknowledged_at"),
  countersignedAt: timestamp("countersigned_at"),
  activeAt: timestamp("active_at"),
  terminatedAt: timestamp("terminated_at"),
  terminationReason: text("termination_reason"),
  signingNotificationClaimedAt: timestamp("signing_notification_claimed_at"),
  signingNotificationSentAt: timestamp("signing_notification_sent_at"),
  signingNotificationMessageId: text("signing_notification_message_id"),
  supersededAt: timestamp("superseded_at"),
  /** Set only when an admin voids a partner-signed, uncountersigned agreement. */
  voidedAt: timestamp("voided_at"),
  voidedReason: text("voided_reason"),
  backendExtensionPayElectedAt: timestamp("backend_extension_pay_elected_at"),
  backendExtensionPayReason: text("backend_extension_pay_reason"),
  backendExtensionYears: integer("backend_extension_years").notNull().default(0),
  createdByUserId: varchar("created_by_user_id").references((): AnyPgColumn => users.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (table) => ({
  currentUniq: uniqueIndex("partner_project_engagements_current_uniq")
    .on(table.agentId, table.projectId)
    .where(sql`superseded_at IS NULL`),
  agentIdx: index("partner_project_engagements_agent_idx").on(table.agentId, table.projectId),
}));

export type PartnerProjectEngagement = typeof partnerProjectEngagements.$inferSelect;

/** Partner-level Schedule B. Not a project engagement. */
export const partnerScheduleBAgreements = pgTable("partner_schedule_b_agreements", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  agentId: varchar("agent_id").notNull().references(() => referralAgents.id, { onDelete: "cascade" }),
  templateKey: text("template_key").notNull(),
  templateBodySha256: text("template_body_sha256").notNull(),
  signerName: text("signer_name"),
  signerTitle: text("signer_title"),
  signerEmail: text("signer_email"),
  signatureIp: text("signature_ip"),
  signatureUserAgent: text("signature_user_agent"),
  consentAcknowledgedAt: timestamp("consent_acknowledged_at"),
  signedAt: timestamp("signed_at"),
  verasSignerName: text("veras_signer_name"),
  verasSignerTitle: text("veras_signer_title"),
  verasSignerEmail: text("veras_signer_email"),
  verasSignatureIp: text("veras_signature_ip"),
  verasSignatureUserAgent: text("veras_signature_user_agent"),
  verasConsentAcknowledgedAt: timestamp("veras_consent_acknowledged_at"),
  countersignedAt: timestamp("countersigned_at"),
  activeAt: timestamp("active_at"),
  supersededAt: timestamp("superseded_at"),
  createdByUserId: varchar("created_by_user_id").references((): AnyPgColumn => users.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (table) => ({
  currentUniq: uniqueIndex("partner_schedule_b_agreements_current_uniq")
    .on(table.agentId)
    .where(sql`superseded_at IS NULL`),
  agentIdx: index("partner_schedule_b_agreements_agent_idx").on(table.agentId, table.createdAt),
}));

export type PartnerScheduleBAgreement = typeof partnerScheduleBAgreements.$inferSelect;

export const partnerProjectIntroductions = pgTable("partner_project_introductions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  agentId: varchar("agent_id").notNull().references(() => referralAgents.id, { onDelete: "cascade" }),
  projectId: varchar("project_id").notNull().references(() => investmentProjects.id, { onDelete: "cascade" }),
  engagementId: varchar("engagement_id").notNull().references(() => partnerProjectEngagements.id, { onDelete: "restrict" }),
  investorEmail: text("investor_email").notNull(),
  investorFirstName: text("investor_first_name").notNull(),
  investorLastName: text("investor_last_name").notNull(),
  leadUserId: varchar("lead_user_id").references((): AnyPgColumn => users.id, { onDelete: "set null" }),
  personId: varchar("person_id").references((): AnyPgColumn => persons.id, { onDelete: "set null" }),
  duplicateWarning: text("duplicate_warning"),
  protectionEndsOn: date("protection_ends_on"),
  duplicateOverrideReason: text("duplicate_override_reason"),
  duplicateOverrideAmountCents: integer("duplicate_override_amount_cents"),
  duplicateOverrideStandardMark: text("duplicate_override_standard_mark"),
  duplicateOverrideAt: timestamp("duplicate_override_at"),
  duplicateOverrideByUserId: varchar("duplicate_override_by_user_id").references((): AnyPgColumn => users.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (table) => ({
  emailUniq: uniqueIndex("partner_project_introductions_email_uniq")
    .on(table.agentId, table.projectId, sql`lower(${table.investorEmail})`),
  agentIdx: index("partner_project_introductions_agent_idx").on(table.agentId, table.projectId, table.createdAt),
}));

export type PartnerProjectIntroduction = typeof partnerProjectIntroductions.$inferSelect;

export const partnerCompensationPositions = pgTable("partner_compensation_positions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  engagementId: varchar("engagement_id").notNull().references(() => partnerProjectEngagements.id, { onDelete: "restrict" }),
  agentId: varchar("agent_id").notNull().references(() => referralAgents.id, { onDelete: "restrict" }),
  projectId: varchar("project_id").notNull().references(() => investmentProjects.id, { onDelete: "restrict" }),
  personId: varchar("person_id").references((): AnyPgColumn => persons.id, { onDelete: "set null" }),
  investorName: text("investor_name").notNull(),
  investorEmail: text("investor_email"),
  voidedAt: timestamp("voided_at"),
  createdByUserId: varchar("created_by_user_id").references((): AnyPgColumn => users.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (table) => ({
  agentIdx: index("partner_compensation_positions_agent_idx").on(table.agentId, table.projectId),
}));

export type PartnerCompensationPosition = typeof partnerCompensationPositions.$inferSelect;

export const partnerCompensationEvents = pgTable("partner_compensation_events", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  kind: text("kind").notNull().$type<
    "nce_release" | "deployment" | "interest_receipt" | "denial" | "withdrawal" | "pre_release_refund" | "pace_bonus" | "investor_rebate" | "misconduct_clawback" | "qualified_lead" | "reversal"
  >(),
  projectId: varchar("project_id").notNull().references(() => investmentProjects.id, { onDelete: "restrict" }),
  agentId: varchar("agent_id").references(() => referralAgents.id, { onDelete: "restrict" }),
  engagementId: varchar("engagement_id").references(() => partnerProjectEngagements.id, { onDelete: "restrict" }),
  positionId: varchar("position_id").references(() => partnerCompensationPositions.id, { onDelete: "restrict" }),
  investorMilestoneId: varchar("investor_milestone_id").references((): AnyPgColumn => investorMilestones.id, { onDelete: "restrict" }),
  sourceMilestoneField: text("source_milestone_field"),
  occurredOn: date("occurred_on").notNull(),
  loanYear: integer("loan_year"),
  amountCents: bigint("amount_cents", { mode: "number" }),
  refundProportionBps: integer("refund_proportion_bps"),
  reversesEventId: varchar("reverses_event_id").references((): AnyPgColumn => partnerCompensationEvents.id, { onDelete: "restrict" }),
  reason: text("reason"),
  voidedAt: timestamp("voided_at"),
  postedByUserId: varchar("posted_by_user_id").references((): AnyPgColumn => users.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (table) => ({
  projectIdx: index("partner_compensation_events_project_idx").on(table.projectId, table.occurredOn),
  agentIdx: index("partner_compensation_events_agent_idx").on(table.agentId, table.occurredOn),
}));

export type PartnerCompensationEvent = typeof partnerCompensationEvents.$inferSelect;

export const partnerCompensationLines = pgTable("partner_compensation_lines", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  eventId: varchar("event_id").notNull().references(() => partnerCompensationEvents.id, { onDelete: "restrict" }),
  positionId: varchar("position_id").references(() => partnerCompensationPositions.id, { onDelete: "restrict" }),
  agentId: varchar("agent_id").notNull().references(() => referralAgents.id, { onDelete: "restrict" }),
  projectId: varchar("project_id").notNull().references(() => investmentProjects.id, { onDelete: "restrict" }),
  engagementId: varchar("engagement_id").references(() => partnerProjectEngagements.id, { onDelete: "restrict" }),
  lineKind: text("line_kind").notNull().$type<
    "partner_upfront" | "class_a_upfront_share" | "veras_energy_upfront_share" | "partner_backend" | "class_a_backend_share" | "veras_energy_backend_share" | "partner_pace_bonus" | "veras_energy_pace_bonus" | "veras_energy_rebate_shortfall" | "partner_qualified_lead"
  >(),
  amountCents: bigint("amount_cents", { mode: "number" }).notNull(),
  loanYear: integer("loan_year"),
  statusApplied: text("status_applied").$type<"registered" | "silver" | "gold" | null>(),
  payableOn: date("payable_on"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (table) => ({
  eventIdx: index("partner_compensation_lines_event_idx").on(table.eventId),
  positionIdx: index("partner_compensation_lines_position_idx").on(table.positionId, table.lineKind, table.loanYear),
  agentIdx: index("partner_compensation_lines_agent_idx").on(table.agentId, table.projectId),
}));

export type PartnerCompensationLine = typeof partnerCompensationLines.$inferSelect;

export const partnerPricingStatuses = pgTable("partner_pricing_statuses", {
  agentId: varchar("agent_id").primaryKey().references(() => referralAgents.id, { onDelete: "restrict" }),
  status: text("status").notNull().default("registered").$type<"registered" | "silver" | "gold">(),
  statusSince: date("status_since"),
  nextReviewOn: date("next_review_on"),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export type PartnerPricingStatusRow = typeof partnerPricingStatuses.$inferSelect;

export const partnerPricingStatusAudit = pgTable("partner_pricing_status_audit", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  agentId: varchar("agent_id").notNull().references(() => referralAgents.id, { onDelete: "restrict" }),
  action: text("action").notNull().$type<"promotion" | "demotion" | "review" | "pace_bonus" | "void" | "clawback">(),
  source: text("source").notNull().$type<"nce_release" | "annual_review" | "admin_void" | "misconduct_clawback">(),
  beforeStatus: text("before_status").$type<"registered" | "silver" | "gold" | null>(),
  afterStatus: text("after_status").$type<"registered" | "silver" | "gold" | null>(),
  beforeSnapshot: jsonb("before_snapshot"),
  afterSnapshot: jsonb("after_snapshot"),
  actorUserId: varchar("actor_user_id").references((): AnyPgColumn => users.id, { onDelete: "set null" }),
  reason: text("reason"),
  eventId: varchar("event_id").references(() => partnerCompensationEvents.id, { onDelete: "restrict" }),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (table) => ({
  agentIdx: index("partner_pricing_status_audit_agent_idx").on(table.agentId, table.createdAt),
}));

export type PartnerPricingStatusAuditRow = typeof partnerPricingStatusAudit.$inferSelect;

export const partnerAnnualCertifications = pgTable("partner_annual_certifications", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  agentId: varchar("agent_id").notNull().references(() => referralAgents.id, { onDelete: "restrict" }),
  certifiedOn: date("certified_on").notNull(),
  recordedByUserId: varchar("recorded_by_user_id").references((): AnyPgColumn => users.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (table) => ({
  agentIdx: index("partner_annual_certifications_agent_idx").on(table.agentId, table.certifiedOn),
}));

export type PartnerAnnualCertification = typeof partnerAnnualCertifications.$inferSelect;

export const partnerPayoutApprovals = pgTable("partner_payout_approvals", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  lineId: varchar("line_id").notNull().references(() => partnerCompensationLines.id, { onDelete: "restrict" }),
  agentId: varchar("agent_id").notNull().references(() => referralAgents.id, { onDelete: "restrict" }),
  projectId: varchar("project_id").notNull().references(() => investmentProjects.id, { onDelete: "restrict" }),
  amountCents: bigint("amount_cents", { mode: "number" }).notNull(),
  beforeState: text("before_state").notNull().default("accrued"),
  afterState: text("after_state").notNull().default("approved"),
  reason: text("reason"),
  approvedByUserId: varchar("approved_by_user_id").references((): AnyPgColumn => users.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (table) => ({
  lineUniq: uniqueIndex("partner_payout_approvals_line_uniq").on(table.lineId),
  agentIdx: index("partner_payout_approvals_agent_idx").on(table.agentId, table.projectId),
}));

export type PartnerPayoutApproval = typeof partnerPayoutApprovals.$inferSelect;

export const partnerPayoutRuns = pgTable("partner_payout_runs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  runOn: date("run_on").notNull(),
  status: text("status").notNull().default("draft").$type<"draft" | "exported" | "paid">(),
  createdByUserId: varchar("created_by_user_id").references((): AnyPgColumn => users.id, { onDelete: "set null" }),
  exportedAt: timestamp("exported_at"),
  paidAt: timestamp("paid_at"),
  notes: text("notes"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (table) => ({
  runOnIdx: index("partner_payout_runs_run_on_idx").on(table.runOn, table.createdAt),
}));

export type PartnerPayoutRun = typeof partnerPayoutRuns.$inferSelect;

export const partnerPayoutRunLines = pgTable("partner_payout_run_lines", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  runId: varchar("run_id").notNull().references(() => partnerPayoutRuns.id, { onDelete: "restrict" }),
  approvalId: varchar("approval_id").notNull().references(() => partnerPayoutApprovals.id, { onDelete: "restrict" }),
  lineId: varchar("line_id").notNull().references(() => partnerCompensationLines.id, { onDelete: "restrict" }),
  agentId: varchar("agent_id").notNull().references(() => referralAgents.id, { onDelete: "restrict" }),
  projectId: varchar("project_id").notNull().references(() => investmentProjects.id, { onDelete: "restrict" }),
  amountCents: bigint("amount_cents", { mode: "number" }).notNull(),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (table) => ({
  runIdx: index("partner_payout_run_lines_run_idx").on(table.runId),
  approvalUniq: uniqueIndex("partner_payout_run_lines_approval_uniq").on(table.approvalId),
  lineUniq: uniqueIndex("partner_payout_run_lines_line_uniq").on(table.lineId),
}));

export type PartnerPayoutRunLine = typeof partnerPayoutRunLines.$inferSelect;

export const partnerRebateApprovals = pgTable("partner_rebate_approvals", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  eventId: varchar("event_id").notNull().references(() => partnerCompensationEvents.id, { onDelete: "restrict" }),
  positionId: varchar("position_id").notNull().references(() => partnerCompensationPositions.id, { onDelete: "restrict" }),
  agentId: varchar("agent_id").notNull().references(() => referralAgents.id, { onDelete: "restrict" }),
  projectId: varchar("project_id").notNull().references(() => investmentProjects.id, { onDelete: "restrict" }),
  rebateCents: bigint("rebate_cents", { mode: "number" }).notNull(),
  adminFeeCents: bigint("admin_fee_cents", { mode: "number" }).notNull(),
  beforeNetAdminCents: bigint("before_net_admin_cents", { mode: "number" }).notNull(),
  afterNetAdminCents: bigint("after_net_admin_cents", { mode: "number" }).notNull(),
  shortfallCents: bigint("shortfall_cents", { mode: "number" }).notNull(),
  reason: text("reason"),
  approvedByUserId: varchar("approved_by_user_id").references((): AnyPgColumn => users.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (table) => ({
  eventUniq: uniqueIndex("partner_rebate_approvals_event_uniq").on(table.eventId),
  positionIdx: index("partner_rebate_approvals_position_idx").on(table.positionId),
}));

export type PartnerRebateApproval = typeof partnerRebateApprovals.$inferSelect;

export const partnerFeeDisclosures = pgTable("partner_fee_disclosures", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  positionId: varchar("position_id").notNull().references(() => partnerCompensationPositions.id, { onDelete: "restrict" }),
  agentId: varchar("agent_id").notNull().references(() => referralAgents.id, { onDelete: "restrict" }),
  projectId: varchar("project_id").notNull().references(() => investmentProjects.id, { onDelete: "restrict" }),
  recipientName: text("recipient_name").notNull(),
  investorName: text("investor_name").notNull(),
  items: jsonb("items").$type<FeeDisclosureItem[]>().notNull(),
  coverageKey: text("coverage_key").notNull(),
  recordedByUserId: varchar("recorded_by_user_id").references((): AnyPgColumn => users.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (table) => ({
  positionIdx: index("partner_fee_disclosures_position_idx").on(table.positionId, table.createdAt),
}));

export type PartnerFeeDisclosure = typeof partnerFeeDisclosures.$inferSelect;

export const partnerSubAgentElections = pgTable("partner_sub_agent_elections", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  engagementId: varchar("engagement_id").notNull().references(() => partnerProjectEngagements.id, { onDelete: "restrict" }),
  agentId: varchar("agent_id").notNull().references(() => referralAgents.id, { onDelete: "restrict" }),
  usesSubAgents: boolean("uses_sub_agents").notNull(),
  recordedByUserId: varchar("recorded_by_user_id").references((): AnyPgColumn => users.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (table) => ({
  engagementIdx: index("partner_sub_agent_elections_engagement_idx").on(table.engagementId, table.createdAt),
}));

export type PartnerSubAgentElection = typeof partnerSubAgentElections.$inferSelect;

export const partnerSubAgents = pgTable("partner_sub_agents", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  engagementId: varchar("engagement_id").notNull().references(() => partnerProjectEngagements.id, { onDelete: "restrict" }),
  agentId: varchar("agent_id").notNull().references(() => referralAgents.id, { onDelete: "restrict" }),
  legalName: text("legal_name").notNull(),
  registrationRef: text("registration_ref").notNull(),
  disclosedByUserId: varchar("disclosed_by_user_id").references((): AnyPgColumn => users.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (table) => ({
  engagementIdx: index("partner_sub_agents_engagement_idx").on(table.engagementId),
}));

export type PartnerSubAgent = typeof partnerSubAgents.$inferSelect;

export const partnerProgramAudit = pgTable("partner_program_audit", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  agentId: varchar("agent_id").notNull().references(() => referralAgents.id, { onDelete: "restrict" }),
  action: text("action").notNull().$type<"initial_assignment" | "admin_override" | "program_change">(),
  beforeProgram: text("before_program").$type<"partner_program" | "qualified_lead" | null>(),
  afterProgram: text("after_program").notNull().$type<"partner_program" | "qualified_lead">(),
  reason: text("reason").notNull(),
  effectiveOn: date("effective_on").notNull(),
  actorUserId: varchar("actor_user_id").references((): AnyPgColumn => users.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (table) => ({
  agentIdx: index("partner_program_audit_agent_idx").on(table.agentId, table.createdAt),
}));

export type PartnerProgramAudit = typeof partnerProgramAudit.$inferSelect;

export const partnerTypeAudit = pgTable("partner_type_audit", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  agentId: varchar("agent_id").notNull().references(() => referralAgents.id, { onDelete: "restrict" }),
  action: text("action").notNull().$type<"initial_lock" | "type_change">(),
  beforePartnerTypeId: varchar("before_partner_type_id").references(() => partnerTypes.id, { onDelete: "restrict" }),
  afterPartnerTypeId: varchar("after_partner_type_id").notNull().references(() => partnerTypes.id, { onDelete: "restrict" }),
  reason: text("reason").notNull(),
  actorUserId: varchar("actor_user_id").references((): AnyPgColumn => users.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (table) => ({
  agentIdx: index("partner_type_audit_agent_idx").on(table.agentId, table.createdAt),
}));

export type PartnerTypeAudit = typeof partnerTypeAudit.$inferSelect;

/**
 * Who renamed a partner company, from what, to what, and when. The firm and
 * its CRM organization are one company, so one row covers the rename of
 * both `referral_agents.company_name` and the linked organization's name.
 */
export const partnerCompanyNameAudit = pgTable("partner_company_name_audit", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  agentId: varchar("agent_id").notNull().references(() => referralAgents.id, { onDelete: "restrict" }),
  beforeName: text("before_name").notNull(),
  afterName: text("after_name").notNull(),
  actorKind: text("actor_kind").notNull().$type<"admin" | "partner">(),
  actorUserId: varchar("actor_user_id").references((): AnyPgColumn => users.id, { onDelete: "set null" }),
  actorAgentUserId: varchar("actor_agent_user_id").references((): AnyPgColumn => agentUsers.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (table) => ({
  agentIdx: index("partner_company_name_audit_agent_idx").on(table.agentId, table.createdAt),
}));

export type PartnerCompanyNameAudit = typeof partnerCompanyNameAudit.$inferSelect;

/**
 * An admin's decision not to email the pitch-close proposal, usually
 * because the partner already picked a plan. Append-only; one row per
 * firm while it stands. Not a proposal document — `partner_terms_proposals`
 * holds only emailed PDFs, so history tells "emailed" from "skipped".
 */
export const partnerProposalSkips = pgTable("partner_proposal_skips", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  agentId: varchar("agent_id").notNull().references(() => referralAgents.id, { onDelete: "cascade" }),
  projectId: varchar("project_id").references(() => investmentProjects.id, { onDelete: "set null" }),
  projectName: text("project_name"),
  optionCode: text("option_code").$type<"A" | "B" | "C">(),
  reason: text("reason").notNull(),
  skippedByUserId: varchar("skipped_by_user_id").references((): AnyPgColumn => users.id, { onDelete: "set null" }),
  skippedByName: text("skipped_by_name"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (table) => ({
  agentUniq: uniqueIndex("partner_proposal_skips_agent_uniq").on(table.agentId),
}));

export type PartnerProposalSkip = typeof partnerProposalSkips.$inferSelect;

export const partnerRecordSubAgents = pgTable("partner_record_sub_agents", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  agentId: varchar("agent_id").notNull().references(() => referralAgents.id, { onDelete: "restrict" }),
  legalName: text("legal_name").notNull(),
  registrationRef: text("registration_ref").notNull(),
  disclosedOn: date("disclosed_on").notNull(),
  i956kFiledOn: date("i956k_filed_on").notNull(),
  recordedByUserId: varchar("recorded_by_user_id").references((): AnyPgColumn => users.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (table) => ({
  agentIdx: index("partner_record_sub_agents_agent_idx").on(table.agentId, table.disclosedOn),
}));

export type PartnerRecordSubAgent = typeof partnerRecordSubAgents.$inferSelect;

export const partnerTermsProposals = pgTable("partner_terms_proposals", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  agentId: varchar("agent_id").notNull().references(() => referralAgents.id, { onDelete: "cascade" }),
  sourceTermsId: varchar("source_terms_id").references(() => partnerDealTerms.id, { onDelete: "set null" }),
  sourceTermsVersion: integer("source_terms_version"),
  sourceProjectId: varchar("source_project_id").references(() => investmentProjects.id, { onDelete: "set null" }),
  sourceProjectName: text("source_project_name"),
  recipientName: text("recipient_name").notNull(),
  recipientEmail: text("recipient_email").notNull(),
  status: text("status").notNull().default("draft"),
  termsSnapshot: jsonb("terms_snapshot").$type<PricingProposalSnapshot | PitchCloseProposalSnapshot | {
    programKind: "paid_lead" | "referral_conversion";
    investmentAmountCents: number;
    listAdminFeeCents: number;
    retainedAdminFeeCents: number;
    irSpreadBps: number;
    termYears: number;
    sourceProjectId?: string | null;
    leadReferralRateCents: number;
    conversionFlatFeeCents: number;
    conversionBackendPointsBps: number;
    projectEconomics?: {
      projectTitle: string;
      division: string | null;
      dealShape: string | null;
      externalPartnerName: string | null;
      externalPartnerSplitBps: number | null;
    } | null;
  }>().notNull(),
  resendMessageId: text("resend_message_id"),
  emailedAt: timestamp("emailed_at"),
  viewedAt: timestamp("viewed_at"),
  followupCount: integer("followup_count").notNull().default(0),
  lastFollowupAt: timestamp("last_followup_at"),
  createdByUserId: varchar("created_by_user_id").references((): AnyPgColumn => users.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export type PartnerTermsProposal = typeof partnerTermsProposals.$inferSelect;

/** Presenter-note row that is not a slide. One program-status line for the console. */
export const PARTNER_PITCH_PROGRAM_STATUS_SECTION_ID = "__program_status";

export const partnerPitchNotes = pgTable("partner_pitch_notes", {
  sectionId: text("section_id").primaryKey(),
  body: text("body").notNull().default(""),
  updatedBy: varchar("updated_by").references(() => users.id, { onDelete: "set null" }),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export type PartnerPitchNote = typeof partnerPitchNotes.$inferSelect;

export const pitchSessions = pgTable("pitch_sessions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  createdBy: varchar("created_by").references(() => users.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
  closedAt: timestamp("closed_at"),
  isRehearsal: boolean("is_rehearsal").notNull().default(false),
  /** Deck review with no firm. Live and rehearsal sessions keep agentId. */
  isReview: boolean("is_review").notNull().default(false),
  agentId: varchar("agent_id").references(() => referralAgents.id, { onDelete: "restrict" }),
  sourceContactOrganizationId: varchar("source_contact_organization_id").references(() => contactOrganizations.id, { onDelete: "set null" }),
  partnerSnapshot: jsonb("partner_snapshot").$type<PitchPartnerSnapshot>().notNull(),
  attendees: jsonb("attendees").$type<PitchAttendee[]>().notNull().default(sql`'[]'::jsonb`),
  program: text("program").$type<PitchProgram | null>(),
  /** Presentation-only type for a session with no firm. Never written onto the firm. */
  presentationPartnerTypeId: varchar("presentation_partner_type_id"),
  paidLead: jsonb("paid_lead").$type<PitchPaidLead | null>(),
  conversion: jsonb("conversion").$type<PitchConversion | null>(),
  engagements: jsonb("engagements").$type<PitchEngagementMirror[]>().notNull().default(sql`'[]'::jsonb`),
  invites: jsonb("invites").$type<PitchInvite[]>().notNull().default(sql`'[]'::jsonb`),
  currentSlideId: text("current_slide_id"),
  completedSlideIds: jsonb("completed_slide_ids").$type<string[]>().notNull().default(sql`'[]'::jsonb`),
  shareTokenHash: text("share_token_hash"),
  shareExpiresAt: timestamp("share_expires_at"),
  shareRevokedAt: timestamp("share_revoked_at"),
}, (table) => ({
  agentIdx: index("pitch_sessions_agent_idx").on(table.agentId, table.createdAt),
  updatedIdx: index("pitch_sessions_updated_idx").on(table.updatedAt),
  shareTokenIdx: uniqueIndex("pitch_sessions_share_token_hash_idx").on(table.shareTokenHash),
}));

export type PitchSession = typeof pitchSessions.$inferSelect;

export const pitchSessionEvents = pgTable("pitch_session_events", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  sessionId: varchar("session_id").notNull().references(() => pitchSessions.id, { onDelete: "restrict" }),
  at: timestamp("at").notNull().defaultNow(),
  adminId: varchar("admin_id").references(() => users.id, { onDelete: "set null" }),
  type: text("type").notNull(),
  payload: jsonb("payload").$type<Record<string, unknown>>().notNull().default(sql`'{}'::jsonb`),
}, (table) => ({
  sessionIdx: index("pitch_session_events_session_idx").on(table.sessionId, table.at),
}));

export type PitchSessionEvent = typeof pitchSessionEvents.$inferSelect;

export const commissionEntries = pgTable("commission_entries", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  agentId: varchar("agent_id").notNull().references(() => referralAgents.id, { onDelete: "cascade" }),
  userId: varchar("user_id").references(() => users.id, { onDelete: "set null" }),
  projectId: varchar("project_id").references(() => investmentProjects.id, { onDelete: "set null" }),
  commitmentId: varchar("commitment_id").references(() => investmentCommitments.id, { onDelete: "set null" }),
  ruleId: varchar("rule_id").references(() => commissionRules.id, { onDelete: "set null" }),
  ruleGroupId: varchar("rule_group_id"),
  ruleVersion: integer("rule_version"),
  ruleType: text("rule_type").notNull(),
  periodStart: timestamp("period_start").notNull(),
  periodEnd: timestamp("period_end").notNull(),
  // bigint Postgres column. Drizzle's `bigint` returns string by default; we
  // pass `mode: "number"` because every realistic accrual fits in a JS number.
  amountCents: bigint("amount_cents", { mode: "number" }).notNull(),
  status: text("status").notNull().default("accrued"),
  paidAt: timestamp("paid_at"),
  paidByUserId: varchar("paid_by_user_id"),
  voidReason: text("void_reason"),
  voidedAt: timestamp("voided_at"),
  voidedByUserId: varchar("voided_by_user_id"),
  isManual: boolean("is_manual").notNull().default(false),
  notes: text("notes"),
  createdByUserId: varchar("created_by_user_id"),
  // Task #889 — Commissions V2: lifecycle wiring + frozen breakdown so the
  // "Why this number" drill-down stays reproducible regardless of upstream
  // value changes. payoutId points at the rollup batch the entry is being
  // paid in (NULL until an admin schedules a payout).
  payoutId: varchar("payout_id"),
  expectedPayoutDate: timestamp("expected_payout_date"),
  // The dollar base the rule was applied against, captured at write time.
  calculationBasisCents: bigint("calculation_basis_cents", { mode: "number" }),
  // jsonb describing the line-by-line math:
  //   { steps: [{label, value}], inputs: {...}, formula: '...' }
  calculationBreakdown: jsonb("calculation_breakdown"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export type CommissionEntry = typeof commissionEntries.$inferSelect;

// ----------------------------------------------------------------------------
// Task #889 — Commissions V2: payout method, payout lifecycle, audit.
// ----------------------------------------------------------------------------
export const COMMISSION_PAYOUT_METHOD_TYPES = ["ach", "wire"] as const;
export type CommissionPayoutMethodType = (typeof COMMISSION_PAYOUT_METHOD_TYPES)[number];

export const agentPayoutMethods = pgTable("agent_payout_methods", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  agentId: varchar("agent_id").notNull().references(() => referralAgents.id, { onDelete: "cascade" }),
  methodType: text("method_type").notNull().$type<CommissionPayoutMethodType>(),
  holderName: text("holder_name").notNull(),
  currency: text("currency").notNull().default("USD"),
  countryCode: text("country_code"),
  last4: text("last4"),
  bankName: text("bank_name"),
  // Encrypted via server/encryption.ts (encryptIfPresent / decryptIfPresent).
  // Frontend never receives this column directly; only last4 / holderName /
  // bankName are surfaced. The shape inside the encrypted blob is JSON:
  //   { accountNumber, routingNumber }      (ACH)
  //   { iban, swift, bankAddress, accountAddress }  (wire)
  detailsEncrypted: text("details_encrypted"),
  isPrimary: boolean("is_primary").notNull().default(true),
  createdByAgentUserId: varchar("created_by_agent_user_id"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
  deletedAt: timestamp("deleted_at"),
});

export type AgentPayoutMethod = typeof agentPayoutMethods.$inferSelect;

// Append-only audit trail of payout-method writes (create / update /
// set_primary / delete). Stores diffs but never the encrypted blob.
export const agentPayoutMethodAudit = pgTable("agent_payout_method_audit", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  agentId: varchar("agent_id").notNull().references(() => referralAgents.id, { onDelete: "cascade" }),
  payoutMethodId: varchar("payout_method_id"),
  action: text("action").notNull(),
  actorKind: text("actor_kind").notNull(),
  actorLabel: text("actor_label"),
  changes: jsonb("changes"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export type AgentPayoutMethodAuditEntry = typeof agentPayoutMethodAudit.$inferSelect;

export const COMMISSION_PAYOUT_STATUSES = ["scheduled", "sent", "cleared", "failed"] as const;
export type CommissionPayoutStatus = (typeof COMMISSION_PAYOUT_STATUSES)[number];

export const commissionPayouts = pgTable("commission_payouts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  agentId: varchar("agent_id").notNull().references(() => referralAgents.id, { onDelete: "cascade" }),
  payoutMethodId: varchar("payout_method_id"),
  status: text("status").notNull().default("scheduled").$type<CommissionPayoutStatus>(),
  amountCents: bigint("amount_cents", { mode: "number" }).notNull().default(0),
  currency: text("currency").notNull().default("USD"),
  expectedDate: timestamp("expected_date"),
  sentAt: timestamp("sent_at"),
  clearedAt: timestamp("cleared_at"),
  failedAt: timestamp("failed_at"),
  failureReason: text("failure_reason"),
  externalRef: text("external_ref"),
  notes: text("notes"),
  createdByUserId: varchar("created_by_user_id"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export type CommissionPayout = typeof commissionPayouts.$inferSelect;

// Form schema for creating / updating an ACH payout method. Sensitive
// fields are validated then handed to the encryption helper before insert.
export const achPayoutMethodSchema = z.object({
  methodType: z.literal("ach"),
  holderName: z.string().trim().min(2).max(120),
  bankName: z.string().trim().min(1).max(120).optional(),
  routingNumber: z.string().trim().regex(/^\d{9}$/, "Routing number must be 9 digits"),
  accountNumber: z.string().trim().regex(/^\d{4,17}$/, "Account number must be 4-17 digits"),
});

// NOTE: kept as a plain ZodObject (no `.refine`) so this can sit inside
// `z.discriminatedUnion`. The cross-field "iban OR accountNumber" check
// is applied via a `.superRefine` on the union below.
export const wirePayoutMethodSchema = z.object({
  methodType: z.literal("wire"),
  holderName: z.string().trim().min(2).max(120),
  bankName: z.string().trim().min(1).max(160).optional(),
  countryCode: z.string().trim().length(2),
  currency: z.string().trim().length(3).default("USD"),
  iban: z.string().trim().min(8).max(34).optional(),
  accountNumber: z.string().trim().min(4).max(34).optional(),
  swift: z.string().trim().min(8).max(11),
  bankAddress: z.string().trim().max(500).optional(),
});

export const payoutMethodInputSchema = z
  .discriminatedUnion("methodType", [
    achPayoutMethodSchema,
    wirePayoutMethodSchema,
  ])
  .superRefine((v, ctx) => {
    if (v.methodType === "wire" && !v.iban && !v.accountNumber) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["iban"],
        message: "Provide either an IBAN or an account number",
      });
    }
  });
export type PayoutMethodInput = z.infer<typeof payoutMethodInputSchema>;

// Task #640 — Public agent self-signup with admin approval queue.
// Applicants submit via /agents/apply; the row sits here until an
// admin approves (which creates a real referral_agents row), rejects,
// or requests info. We never insert directly into referral_agents from
// here — approval re-uses the existing POST /api/admin/agents flow.
// Status enum is intentionally tight: pending / approved / rejected.
// "Request more information" is a workflow action that records a note
// for the applicant follow-up but leaves the row in `pending` so it
// stays in the admin queue until the applicant responds and the team
// makes a final decision.
export const REFERRAL_AGENT_APPLICATION_STATUSES = [
  "pending",
  "approved",
  "rejected",
] as const;
export type ReferralAgentApplicationStatus =
  (typeof REFERRAL_AGENT_APPLICATION_STATUSES)[number];

export const referralAgentApplications = pgTable("referral_agent_applications", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  // Task #850 — opaque token for the public status-page URL. Minted at
  // submission, embedded in every applicant-facing email, and validated
  // server-side. Not nullable; legacy rows are back-filled by migration
  // 0100. Stored as a uuid so it cannot collide with the application
  // id and is uniformly long enough to be unguessable.
  statusToken: varchar("status_token").notNull().default(sql`gen_random_uuid()`),
  companyName: text("company_name").notNull(),
  // Task #720 — legacy single-value `country` / `language_code`
  // columns have been dropped. The applicant's coverage selections
  // live exclusively in `agent_application_countries` /
  // `agent_application_languages`, copied verbatim into the matching
  // `agent_*` join tables when the application is approved.
  contactFirstName: text("contact_first_name").notNull(),
  contactLastName: text("contact_last_name").notNull(),
  contactEmail: text("contact_email").notNull(),
  contactPhone: text("contact_phone"),
  website: text("website"),
  // Task #886 — structured agent classification captured on the
  // public application form. Copied verbatim into `referral_agents`
  // when the application is approved (see admin/agent-applications.ts).
  agentType: text("agent_type").$type<AgentType>(),
  licenseNumber: text("license_number"),
  notes: text("notes"),
  licenseFilePath: text("license_file_path"),
  licenseFileName: text("license_file_name"),
  w9FilePath: text("w9_file_path"),
  w9FileName: text("w9_file_name"),
  status: text("status").notNull().default("pending"),
  reviewedBy: varchar("reviewed_by"),
  reviewedAt: timestamp("reviewed_at"),
  // General note attached to a review action (approve / request-info).
  // Approval notes are internal context for whoever processes the
  // welcome email; request-info notes describe what the applicant must
  // supply next. Rejection rationale lives in `rejectionReason` so the
  // audit trail keeps the two semantically distinct.
  reviewNote: text("review_note"),
  // Only populated when an admin rejects an application — captures the
  // formal rationale the team chose, kept separate from `reviewNote` so
  // future flows (e.g. emailing the applicant a rejection letter) can
  // safely surface it without leaking internal approval/info notes.
  rejectionReason: text("rejection_reason"),
  // Pointer to the referral_agents row created when the admin approves
  // this application. Named created_agent_id (not approved_agent_id) so
  // it lines up with the verb used in the admin UI ("Approve & create
  // agent"); the underlying column was renamed in migration 0090.
  createdAgentId: varchar("created_agent_id"),
  submittedIp: text("submitted_ip"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  // Bumped server-side on every storage update (review, status change,
  // etc) so the admin queue can show stale-row freshness without
  // walking the audit-log table.
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
  deletedAt: timestamp("deleted_at"),
});

// Insert schema is intentionally narrow — the public form may only
// supply applicant-controlled fields. status / reviewer / approval
// linkage are managed server-side.
export const insertReferralAgentApplicationSchema = createInsertSchema(
  referralAgentApplications,
).omit({
  id: true,
  statusToken: true,
  status: true,
  reviewedBy: true,
  reviewedAt: true,
  reviewNote: true,
  rejectionReason: true,
  createdAgentId: true,
  submittedIp: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
  licenseFilePath: true,
  licenseFileName: true,
  w9FilePath: true,
  w9FileName: true,
}).extend({
  contactEmail: z
    .string()
    .trim()
    .toLowerCase()
    .min(1, "Email is required")
    .email("Please enter a valid email address"),
  contactPhone: z
    .string()
    .nullable()
    .optional()
    .refine(
      (v) => v == null || v === "" || (v.startsWith("+") && isValidPhoneNumber(v)),
      { message: "Phone number must be a valid international number" },
    ),
  website: z.string().trim().url("Website must be a valid URL").optional().or(z.literal("")).nullable(),
  companyName: z.string().trim().min(2, "Company name is required").max(200),
  contactFirstName: z.string().trim().min(1, "First name is required").max(80),
  contactLastName: z.string().trim().min(1, "Last name is required").max(80),
  licenseNumber: z.string().trim().max(120).optional().nullable(),
  notes: z.string().trim().max(2000).optional().nullable(),
  // Task #886 — public applicants self-classify as one of three
  // structured agent types. Optional at the schema level so legacy
  // tooling that still posts the older shape doesn't 400; the form
  // requires it and the admin queue surfaces unclassified rows for
  // manual cleanup.
  agentType: z.enum(AGENT_TYPES).nullable().optional(),
});

export type InsertReferralAgentApplication = z.infer<
  typeof insertReferralAgentApplicationSchema
>;
export type ReferralAgentApplication =
  typeof referralAgentApplications.$inferSelect;

// Mirror tables for the public application — same shape as the
// agent-side coverage tables so the approval flow can copy entries
// across without translation. See migration 0093 for the partial
// unique index that enforces "exactly one primary" per application.
export const agentApplicationCountries = pgTable("agent_application_countries", {
  applicationId: varchar("application_id").notNull().references(() => referralAgentApplications.id, { onDelete: "cascade" }),
  countryCode: text("country_code").notNull(),
  isPrimary: boolean("is_primary").notNull().default(false),
}, (t) => ({
  pk: primaryKey({ columns: [t.applicationId, t.countryCode] }),
}));

export const agentApplicationLanguages = pgTable("agent_application_languages", {
  applicationId: varchar("application_id").notNull().references(() => referralAgentApplications.id, { onDelete: "cascade" }),
  languageCode: text("language_code").notNull(),
  isPrimary: boolean("is_primary").notNull().default(false),
}, (t) => ({
  pk: primaryKey({ columns: [t.applicationId, t.languageCode] }),
}));

export type AgentApplicationCountry = typeof agentApplicationCountries.$inferSelect;
export type AgentApplicationLanguage = typeof agentApplicationLanguages.$inferSelect;

// Task #850 — Threaded info-request / response messages on a public
// agent application. Each row carries one direction of the
// conversation: an admin info request (with the admin's user id as
// author) or an applicant response (author_user_id NULL — the
// applicant has no account, just the status-page token).
export const REFERRAL_AGENT_APPLICATION_MESSAGE_DIRECTIONS = [
  "admin_request",
  "applicant_response",
] as const;
export type ReferralAgentApplicationMessageDirection =
  (typeof REFERRAL_AGENT_APPLICATION_MESSAGE_DIRECTIONS)[number];

export const referralAgentApplicationMessages = pgTable(
  "referral_agent_application_messages",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    applicationId: varchar("application_id")
      .notNull()
      .references(() => referralAgentApplications.id, { onDelete: "cascade" }),
    direction: text("direction").notNull(),
    // NULL when the message is an applicant response — the public
    // status-page token authenticates the post, not a user session.
    authorUserId: varchar("author_user_id"),
    body: text("body").notNull().default(""),
    createdAt: timestamp("created_at").notNull().defaultNow(),
  },
);

export const referralAgentApplicationMessageFiles = pgTable(
  "referral_agent_application_message_files",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    messageId: varchar("message_id")
      .notNull()
      .references(() => referralAgentApplicationMessages.id, { onDelete: "cascade" }),
    filePath: text("file_path").notNull(),
    fileName: text("file_name").notNull(),
    contentType: text("content_type"),
    size: integer("size"),
    createdAt: timestamp("created_at").notNull().defaultNow(),
  },
);

export const insertReferralAgentApplicationMessageSchema = createInsertSchema(
  referralAgentApplicationMessages,
).omit({
  id: true,
  createdAt: true,
});
export type InsertReferralAgentApplicationMessage = z.infer<
  typeof insertReferralAgentApplicationMessageSchema
>;
export type ReferralAgentApplicationMessage =
  typeof referralAgentApplicationMessages.$inferSelect;

export const insertReferralAgentApplicationMessageFileSchema = createInsertSchema(
  referralAgentApplicationMessageFiles,
).omit({
  id: true,
  createdAt: true,
});
export type InsertReferralAgentApplicationMessageFile = z.infer<
  typeof insertReferralAgentApplicationMessageFileSchema
>;
export type ReferralAgentApplicationMessageFile =
  typeof referralAgentApplicationMessageFiles.$inferSelect;

// Task #699 — Compliance document refresh.
export const AGENT_DOCUMENT_KINDS = ["license", "w9"] as const;
export type AgentDocumentKind = (typeof AGENT_DOCUMENT_KINDS)[number];

export const agentDocuments = pgTable("agent_documents", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  agentId: varchar("agent_id")
    .notNull()
    .references(() => referralAgents.id, { onDelete: "cascade" }),
  kind: text("kind").notNull(),
  filePath: text("file_path").notNull(),
  fileName: text("file_name").notNull(),
  fileSize: integer("file_size"),
  mimeType: text("mime_type"),
  uploadedAt: timestamp("uploaded_at").notNull().defaultNow(),
  uploadedVia: text("uploaded_via"),
});

export type AgentDocument = typeof agentDocuments.$inferSelect;
export const insertAgentDocumentSchema = createInsertSchema(agentDocuments).omit({
  id: true,
  uploadedAt: true,
});
export type InsertAgentDocument = z.infer<typeof insertAgentDocumentSchema>;

// Idempotency table for the compliance reminder cron (Task #699).
export const agentDocumentReminders = pgTable("agent_document_reminders", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  agentId: varchar("agent_id")
    .notNull()
    .references(() => referralAgents.id, { onDelete: "cascade" }),
  kind: text("kind").notNull(),
  milestone: text("milestone").notNull(),
  triggerDate: timestamp("trigger_date").notNull(),
  sentAt: timestamp("sent_at").notNull().defaultNow(),
});

export type AgentDocumentReminder = typeof agentDocumentReminders.$inferSelect;

// USCIS Form I-956K registration tracking for referral agents
// (regional center promoters).
//
// Two-table layout mirrors the existing W-9 / license pattern but adds
// supporting documents (written agreement + organization formation
// docs) directly on the registration row instead of a generic doc
// kind enum:
//
//   agent_i956k_registrations  — one row per agent (the "current"
//     pointer + supporting-doc metadata).
//   agent_i956k_revisions      — append-only revision history. Each
//     time a fresh I-956K PDF is uploaded a new row is added and the
//     parent registration's `current_revision_id` is moved.
//
// "Amendment due" is computed at read time as
//   writtenAgreementUploadedAt > currentRevision.uploadedAt
// so we never have to backfill a flag column when the agreement is
// replaced — the comparison falls out of the timestamps already on
// the row.
export const AGENT_I956K_STATUSES = [
  "not_started",
  "not_a_promoter",
  "in_progress",
  "needs_review",
  "ready_to_sign",
  "signed_uploaded",
  "on_file",
  "amendment_due",
  // Legacy value kept for backward compatibility with existing data/callers
  "missing",
] as const;
export type AgentI956kStatus = (typeof AGENT_I956K_STATUSES)[number];

// Structured data collected by the I-956K guided wizard. Stored as
// JSONB on agent_i956k_registrations.wizard_data. The completedStep
// field drives the status machine — 0 = started but not past step 1,
// 6 = review complete (ready_to_sign), 7 = signed PDF uploaded.
export interface I956kWizardData {
  completedStep: number;
  // Step 1 — Who you are (Part 2: Registrant Information)
  firstName?: string;
  lastName?: string;
  middleName?: string;
  title?: string;
  // Individual: US or foreign address
  streetAddress?: string;
  aptSuite?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  province?: string;
  country?: string;
  postalCode?: string;
  usTaxId?: string;
  alienRegistrationNumber?: string;
  dateOfBirth?: string;
  countryOfBirth?: string;
  // Organization: entity details
  organizationName?: string;
  organizationCity?: string;
  organizationState?: string;
  organizationCountry?: string;
  organizationTaxId?: string;
  // Step 2 — Who you promote for (Part 2, continued)
  rcName?: string;
  rcCertificationNumber?: string;
  receivesCompensation?: boolean;
  compensationDescription?: string;
  // Step 3 — Deals / written agreements (Part 3)
  deals?: Array<{
    id: string;
    projectName?: string;
    entityName?: string;
    entityType?: string;
    agreementDate?: string;
    parties?: string;
    lifecycleStatus?: "active" | "planned" | "inactive";
  }>;
  // Step 4 — Part 4 attestations (13 questions, all default-unset)
  // Keyed by attestation ID (e.g. "q1"…"q13"). A "yes" answer
  // triggers needsReview = true on the registration row.
  attestations?: Record<string, {
    answer: "yes" | "no" | null;
    explanation?: string;
    evidenceFilePath?: string;
    evidenceFileName?: string;
    evidenceFileSize?: number;
    evidenceUploadedAt?: string;
  }>;
  // Step 5 — Optional interpreter / preparer
  hasInterpreter?: boolean;
  interpreterName?: string;
  interpreterLanguage?: string;
  hasPreparer?: boolean;
  preparerName?: string;
  preparerOrganization?: string;
  preparerAddress?: string;
}

export const AGENT_I956K_UPLOADER_KINDS = [
  "admin",
  "agent_owner",
  "admin_impersonation",
] as const;
export type AgentI956kUploaderKind = (typeof AGENT_I956K_UPLOADER_KINDS)[number];

export const agentI956kRegistrations = pgTable("agent_i956k_registrations", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  agentId: varchar("agent_id")
    .notNull()
    .unique()
    .references(() => referralAgents.id, { onDelete: "cascade" }),
  // Pointer to the most recent revision row. Nullable until the first
  // upload lands; we don't add a real FK back to revisions because
  // doing so would create a chicken-and-egg ordering problem on
  // initial insert (the revision row references the registration).
  currentRevisionId: varchar("current_revision_id"),
  // ----- Supporting documents (kept on the registration row, not the
  // revision row, because they evolve independently of the I-956K
  // PDF itself) -----
  writtenAgreementFilePath: text("written_agreement_file_path"),
  writtenAgreementFileName: text("written_agreement_file_name"),
  writtenAgreementFileSize: integer("written_agreement_file_size"),
  writtenAgreementMimeType: text("written_agreement_mime_type"),
  writtenAgreementUploadedAt: timestamp("written_agreement_uploaded_at"),
  writtenAgreementUploadedById: varchar("written_agreement_uploaded_by_id"),
  writtenAgreementUploadedByKind: text("written_agreement_uploaded_by_kind").$type<AgentI956kUploaderKind>(),
  // Organization promoters only. We don't enforce that at the DB
  // layer — the route + UI gate it on `promoter_type = 'organization'`.
  formationDocsFilePath: text("formation_docs_file_path"),
  formationDocsFileName: text("formation_docs_file_name"),
  formationDocsFileSize: integer("formation_docs_file_size"),
  formationDocsMimeType: text("formation_docs_mime_type"),
  formationDocsUploadedAt: timestamp("formation_docs_uploaded_at"),
  formationDocsUploadedById: varchar("formation_docs_uploaded_by_id"),
  formationDocsUploadedByKind: text("formation_docs_uploaded_by_kind").$type<AgentI956kUploaderKind>(),
  // Task #2543 — guided wizard state
  wizardData: jsonb("wizard_data").$type<I956kWizardData>(),
  needsReview: boolean("needs_review").notNull().default(false),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export type AgentI956kRegistration = typeof agentI956kRegistrations.$inferSelect;

export const agentI956kRevisions = pgTable("agent_i956k_revisions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  registrationId: varchar("registration_id")
    .notNull()
    .references(() => agentI956kRegistrations.id, { onDelete: "cascade" }),
  agentId: varchar("agent_id")
    .notNull()
    .references(() => referralAgents.id, { onDelete: "cascade" }),
  filePath: text("file_path").notNull(),
  fileName: text("file_name").notNull(),
  fileSize: integer("file_size"),
  mimeType: text("mime_type"),
  filedDate: text("filed_date"),
  receiptNumber: text("receipt_number"),
  amendmentReason: text("amendment_reason"),
  supersedesRevisionId: varchar("supersedes_revision_id"),
  // Snapshot of the supporting documents that were on file at the
  // moment this revision was filed. Captured so the history view (and
  // any future audit) can show exactly which written agreement +
  // formation docs each filing relied on, even after the agent later
  // replaces those PDFs on the registration row.
  writtenAgreementSnapshotPath: text("written_agreement_snapshot_path"),
  writtenAgreementSnapshotName: text("written_agreement_snapshot_name"),
  writtenAgreementSnapshotSize: integer("written_agreement_snapshot_size"),
  writtenAgreementSnapshotMime: text("written_agreement_snapshot_mime"),
  writtenAgreementSnapshotUploadedAt: timestamp("written_agreement_snapshot_uploaded_at"),
  formationDocsSnapshotPath: text("formation_docs_snapshot_path"),
  formationDocsSnapshotName: text("formation_docs_snapshot_name"),
  formationDocsSnapshotSize: integer("formation_docs_snapshot_size"),
  formationDocsSnapshotMime: text("formation_docs_snapshot_mime"),
  formationDocsSnapshotUploadedAt: timestamp("formation_docs_snapshot_uploaded_at"),
  uploadedById: varchar("uploaded_by_id"),
  uploadedByKind: text("uploaded_by_kind").$type<AgentI956kUploaderKind>(),
  uploadedAt: timestamp("uploaded_at").notNull().defaultNow(),
});

export type AgentI956kRevision = typeof agentI956kRevisions.$inferSelect;

export const insertAgentI956kRevisionSchema = createInsertSchema(agentI956kRevisions).omit({
  id: true,
  uploadedAt: true,
});
export type InsertAgentI956kRevision = z.infer<typeof insertAgentI956kRevisionSchema>;

// Wire-shape used by the "new revision" upload route. Multipart
// FormData fields are coerced from strings client-side then validated
// here. The I-956K PDF itself flows through the `pdfUpload` middleware
// and is not part of this Zod object.
//
// Each field accepts undefined, null, or empty/whitespace-only strings
// and normalizes them to undefined so the route handlers can pass
// `req.body?.x ?? null` without tripping validation when the optional
// inputs are simply omitted on the initial filing.
const optionalI956kString = (max: number, msg: string, regex?: { re: RegExp; msg: string }) =>
  z.preprocess(
    (v) => {
      if (v === null || v === undefined) return undefined;
      if (typeof v !== "string") return v;
      const trimmed = v.trim();
      return trimmed === "" ? undefined : trimmed;
    },
    regex
      ? z.string().max(max, msg).regex(regex.re, regex.msg).optional()
      : z.string().max(max, msg).optional(),
  );

export const newAgentI956kRevisionSchema = z.object({
  filedDate: optionalI956kString(10, "Filed date must be 10 characters", {
    re: /^\d{4}-\d{2}-\d{2}$/,
    msg: "Filed date must be YYYY-MM-DD",
  }),
  receiptNumber: optionalI956kString(64, "Receipt number must be 64 characters or fewer"),
  amendmentReason: optionalI956kString(500, "Amendment reason must be 500 characters or fewer"),
});
export type NewAgentI956kRevisionInput = z.infer<typeof newAgentI956kRevisionSchema>;

// ---------------------------------------------------------------------------
// Admin self-service I-956K (Task #2762)
// ---------------------------------------------------------------------------
// Mirrors the agent I-956K two-table layout (registration + append-only
// revision history) but is keyed to the admin's own `users.id` instead of a
// referral_agents row. Because admins have no referral_agents row to hang the
// promoter classification on, the classification columns
// (promoter_type / promoter_form_type / not_a_promoter) live INLINE on the
// registration row here. Reuses the shared `I956kWizardData` blob and the
// shared status machine in `shared/agent-i956k.ts` so derivation logic is not
// duplicated. Self-service only: an admin completes their own I-956K from
// their profile page. Purely additive — it never gates anything.

export const ADMIN_I956K_UPLOADER_KINDS = ["admin"] as const;
export type AdminI956kUploaderKind = (typeof ADMIN_I956K_UPLOADER_KINDS)[number];

export const adminI956kRegistrations = pgTable("admin_i956k_registrations", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id")
    .notNull()
    .unique()
    .references(() => users.id, { onDelete: "cascade" }),
  // Pointer to the most recent revision row. Nullable until the first
  // upload lands (see agent table note re: no real FK).
  currentRevisionId: varchar("current_revision_id"),
  // Promoter classification — stored inline because admins have no
  // referral_agents row. promoterType is the USCIS registrant type
  // (individual / organization); promoterFormType is the promoter class.
  promoterType: text("promoter_type").$type<PromoterType>(),
  promoterFormType: text("promoter_form_type").$type<PromoterFormType>(),
  notAPromoter: boolean("not_a_promoter").notNull().default(false),
  // ----- Supporting documents (kept on the registration row) -----
  writtenAgreementFilePath: text("written_agreement_file_path"),
  writtenAgreementFileName: text("written_agreement_file_name"),
  writtenAgreementFileSize: integer("written_agreement_file_size"),
  writtenAgreementMimeType: text("written_agreement_mime_type"),
  writtenAgreementUploadedAt: timestamp("written_agreement_uploaded_at"),
  writtenAgreementUploadedById: varchar("written_agreement_uploaded_by_id"),
  writtenAgreementUploadedByKind: text("written_agreement_uploaded_by_kind").$type<AdminI956kUploaderKind>(),
  // Organization registrants only (gated by route + UI on promoter_type).
  formationDocsFilePath: text("formation_docs_file_path"),
  formationDocsFileName: text("formation_docs_file_name"),
  formationDocsFileSize: integer("formation_docs_file_size"),
  formationDocsMimeType: text("formation_docs_mime_type"),
  formationDocsUploadedAt: timestamp("formation_docs_uploaded_at"),
  formationDocsUploadedById: varchar("formation_docs_uploaded_by_id"),
  formationDocsUploadedByKind: text("formation_docs_uploaded_by_kind").$type<AdminI956kUploaderKind>(),
  // Guided wizard state
  wizardData: jsonb("wizard_data").$type<I956kWizardData>(),
  needsReview: boolean("needs_review").notNull().default(false),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export type AdminI956kRegistration = typeof adminI956kRegistrations.$inferSelect;

export const adminI956kRevisions = pgTable("admin_i956k_revisions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  registrationId: varchar("registration_id")
    .notNull()
    .references(() => adminI956kRegistrations.id, { onDelete: "cascade" }),
  userId: varchar("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  filePath: text("file_path").notNull(),
  fileName: text("file_name").notNull(),
  fileSize: integer("file_size"),
  mimeType: text("mime_type"),
  filedDate: text("filed_date"),
  receiptNumber: text("receipt_number"),
  amendmentReason: text("amendment_reason"),
  supersedesRevisionId: varchar("supersedes_revision_id"),
  // Snapshot of supporting documents on file at filing time.
  writtenAgreementSnapshotPath: text("written_agreement_snapshot_path"),
  writtenAgreementSnapshotName: text("written_agreement_snapshot_name"),
  writtenAgreementSnapshotSize: integer("written_agreement_snapshot_size"),
  writtenAgreementSnapshotMime: text("written_agreement_snapshot_mime"),
  writtenAgreementSnapshotUploadedAt: timestamp("written_agreement_snapshot_uploaded_at"),
  formationDocsSnapshotPath: text("formation_docs_snapshot_path"),
  formationDocsSnapshotName: text("formation_docs_snapshot_name"),
  formationDocsSnapshotSize: integer("formation_docs_snapshot_size"),
  formationDocsSnapshotMime: text("formation_docs_snapshot_mime"),
  formationDocsSnapshotUploadedAt: timestamp("formation_docs_snapshot_uploaded_at"),
  uploadedById: varchar("uploaded_by_id"),
  uploadedByKind: text("uploaded_by_kind").$type<AdminI956kUploaderKind>(),
  uploadedAt: timestamp("uploaded_at").notNull().defaultNow(),
});

export type AdminI956kRevision = typeof adminI956kRevisions.$inferSelect;

// Idempotency table for the daily admin verification reminder cron
// (Task #751). Keyed on (admin_email, send_date) so a partial
// transient failure (e.g. Resend stalled on one of N admins) can
// retry the loop without re-sending to admins who already received
// today's reminder. The unique index is the source of truth — even
// in the unlikely event two cron-ticks race, the second insert
// throws and the second send is skipped.
export const verificationReminderSends = pgTable(
  "verification_reminder_sends",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    adminEmail: text("admin_email").notNull(),
    sendDate: text("send_date").notNull(), // YYYY-MM-DD in UTC
    sentAt: timestamp("sent_at").notNull().defaultNow(),
  },
  (table) => ({
    uniqAdminPerDay: uniqueIndex("verification_reminder_sends_admin_date_uniq").on(
      table.adminEmail,
      table.sendDate,
    ),
  }),
);

export type VerificationReminderSend = typeof verificationReminderSends.$inferSelect;

// Portal login nudges for invited users who never sign in (48h + day-5).
// One row per (persona, recipient). invite_sent_at is frozen when the
// one-shot invite (or admin resend) goes out; nudge minting must not
// rewrite it. Service-role only — see docs/rls-policies.md.
export const PORTAL_LOGIN_NUDGE_PERSONAS = [
  "investor",
  "agent",
  "vendor",
  "sponsor",
  "admin",
] as const;
export type PortalLoginNudgePersona = (typeof PORTAL_LOGIN_NUDGE_PERSONAS)[number];

export const portalLoginNudgeSends = pgTable(
  "portal_login_nudge_sends",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    personaType: text("persona_type").$type<PortalLoginNudgePersona>().notNull(),
    recipientId: varchar("recipient_id").notNull(),
    inviteSentAt: timestamp("invite_sent_at").notNull(),
    nudge1SentAt: timestamp("nudge_1_sent_at"),
    nudge2SentAt: timestamp("nudge_2_sent_at"),
    createdAt: timestamp("created_at").notNull().defaultNow(),
    updatedAt: timestamp("updated_at").notNull().defaultNow(),
  },
  (table) => ({
    uniqPersonaRecipient: uniqueIndex("portal_login_nudge_sends_persona_recipient_uniq").on(
      table.personaType,
      table.recipientId,
    ),
    inviteSentAtIdx: index("portal_login_nudge_sends_invite_sent_at_idx").on(table.inviteSentAt),
  }),
);

export type PortalLoginNudgeSend = typeof portalLoginNudgeSends.$inferSelect;

/** Render staging/production deploy records for ship digests. */
export const DEPLOY_ENVS = ["staging", "production"] as const;
export type DeployEnv = (typeof DEPLOY_ENVS)[number];
export const DEPLOY_STATUSES = ["succeeded", "failed", "canceled"] as const;
export type DeployStatus = (typeof DEPLOY_STATUSES)[number];

export const deployEvents = pgTable(
  "deploy_events",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    env: text("env").$type<DeployEnv>().notNull(),
    status: text("status").$type<DeployStatus>().notNull(),
    commitSha: text("commit_sha"),
    renderEventId: text("render_event_id").notNull(),
    renderDeployId: text("render_deploy_id"),
    serviceId: text("service_id"),
    serviceName: text("service_name"),
    finishedAt: timestamp("finished_at").notNull(),
    createdAt: timestamp("created_at").notNull().defaultNow(),
  },
  (table) => ({
    uniqRenderEvent: uniqueIndex("deploy_events_render_event_id_uniq").on(table.renderEventId),
    // Partial unique on deploy id is applied in SQL migration 0264 (WHERE NOT NULL).
    envFinishedAt: index("deploy_events_env_finished_at_idx").on(table.env, table.finishedAt),
    envStatusFinishedAt: index("deploy_events_env_status_finished_at_idx").on(
      table.env,
      table.status,
      table.finishedAt,
    ),
  }),
);

export type DeployEvent = typeof deployEvents.$inferSelect;

/** Durable Monday claim for weekly product digest (prevents duplicate fan-out). */
export const WEEKLY_DIGEST_CLAIM_STATUSES = [
  "running",
  "success",
  "degraded",
  "failure",
  "skipped_no_admins",
] as const;
export type WeeklyDigestClaimStatus = (typeof WEEKLY_DIGEST_CLAIM_STATUSES)[number];

export const weeklyProductDigestClaims = pgTable("weekly_product_digest_claims", {
  etDate: text("et_date").primaryKey(),
  status: text("status").$type<WeeklyDigestClaimStatus>().notNull(),
  claimedAt: timestamp("claimed_at").notNull().defaultNow(),
  finishedAt: timestamp("finished_at"),
  details: jsonb("details"),
});

export type WeeklyProductDigestClaim = typeof weeklyProductDigestClaims.$inferSelect;

export const attorneys = pgTable("attorneys", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull(),
  firstName: text("first_name"),
  lastName: text("last_name"),
  firmName: text("firm_name"),
  // Task #2318 — optional public firm website, rendered as a link on the
  // public project-detail page (Quick Facts + Professional Partners).
  website: text("website"),
  email: text("email"),
  phone: text("phone"),
  barNumber: text("bar_number"),
  barState: text("bar_state"),
  notes: text("notes"),
  roles: text("roles").array(),
  // Rate card (all nullable; per-project fees are still recorded on the project).
  securitiesPpmFee: integer("securities_ppm_fee"),
  securitiesAmendmentFee: integer("securities_amendment_fee"),
  immigrationFlatFee: integer("immigration_flat_fee"),
  perInvestorI526eFee: integer("per_investor_i526e_fee"),
  // Per-document securities fee schedule (label + USD amount + optional turnaround days).
  // Order is preserved. Discarded when 'securities' is removed from roles on save.
  // LEGACY: superseded by `securitiesEngagement.feeLines` for new engagements,
  // but kept for back-compat with attorneys who have not been upgraded yet.
  securitiesFeeLineItems: jsonb("securities_fee_line_items").$type<{ label: string; amount: number; turnaroundDays?: number | null }[]>().notNull().default(sql`'[]'::jsonb`),
  // Structured securities engagement-letter spec. NULL = legacy attorney
  // (use `securitiesFeeLineItems` + role-default payment terms instead).
  securitiesEngagement: jsonb("securities_engagement").$type<SecuritiesEngagement>(),
  // Turnaround day assumptions (all nullable). Used by Timeline Assumptions to
  // forecast how long each attorney product takes to deliver.
  securitiesPpmTurnaroundDays: integer("securities_ppm_turnaround_days"),
  securitiesAmendmentTurnaroundDays: integer("securities_amendment_turnaround_days"),
  immigrationTurnaroundDays: integer("immigration_turnaround_days"),
  perInvestorI526eTurnaroundDays: integer("per_investor_i526e_turnaround_days"),
  pricingNotes: text("pricing_notes"),
  // Mailing / firm address (used on engagement letters and invoices).
  addressLine1: text("address_line_1"),
  addressLine2: text("address_line_2"),
  addressCity: text("address_city"),
  addressState: text("address_state"),
  addressZip: text("address_zip"),
  addressCountry: text("address_country"),
  // Structured payment-terms installments (see shared/payment-terms.ts).
  // Empty array means "use the role-default preset" so existing rows
  // remain valid without a backfill.
  paymentTerms: jsonb("payment_terms").$type<{ portionPct: number; anchor: "engagement" | "delivery" | "marketing_ready"; offsetDays?: number }[]>().notNull().default(sql`'[]'::jsonb`),
  // When true, terms are unknown/irrelevant. Finance engine still falls back
  // to the role default but tags entries as "assumed" so P&L can flag them.
  paymentTermsUnknown: boolean("payment_terms_unknown").notNull().default(false),
  // Task #1328 — public-facing profile fields + admin approval flow.
  headshotUrl: text("headshot_url"),
  jobTitle: text("job_title"),
  bio: text("bio"),
  profileApprovalStatus: text("profile_approval_status").default("draft").notNull(),
  profileApprovedAt: timestamp("profile_approved_at"),
  profileApprovedById: varchar("profile_approved_by_id"),
  profileRejectionNote: text("profile_rejection_note"),
  profileUpdatedAt: timestamp("profile_updated_at"),
  isActive: boolean("is_active").default(true).notNull(),
  createdAt: timestamp("created_at").defaultNow(),
});

export const securitiesFeeLineItemSchema = z.object({
  label: z.string().trim().min(1, "Label is required").max(200, "Label is too long"),
  amount: z.coerce.number().int("Amount must be a whole dollar amount").min(0, "Amount must be non-negative"),
  turnaroundDays: z.preprocess(
    (v) => (v === "" || v == null ? null : v),
    z.coerce.number().int("Turnaround must be whole days").min(0, "Turnaround must be non-negative").nullable().optional(),
  ),
});
export type SecuritiesFeeLineItem = z.infer<typeof securitiesFeeLineItemSchema>;

const optionalContactEmail = z.preprocess(
  (v) => {
    if (v == null) return null;
    if (typeof v !== "string") return v;
    const t = v.trim().toLowerCase();
    return t === "" ? null : t;
  },
  z.string().email("Please enter a valid email address").nullable().optional(),
);

// Task #2318 — optional public firm website. Empty/blank coerces to null so
// firms without a site stay plain text; a non-empty value must be a valid URL.
const optionalWebsiteUrl = z.preprocess(
  (v) => {
    if (v == null) return null;
    if (typeof v !== "string") return v;
    const t = v.trim();
    return t === "" ? null : t;
  },
  z.string().url("Please enter a valid website URL (including https://)").nullable().optional(),
);

export const insertAttorneySchema = createInsertSchema(attorneys).omit({
  id: true,
  createdAt: true,
  name: true,
}).extend({
  firstName: z.string().trim().min(1, "First name is required"),
  lastName: z.string().trim().min(1, "Last name is required"),
  email: optionalContactEmail,
  website: optionalWebsiteUrl,
  securitiesFeeLineItems: z.array(securitiesFeeLineItemSchema).optional(),
  securitiesEngagement: securitiesEngagementSchema.nullable().optional(),
  paymentTerms: vendorPaymentTermsSchema.optional(),
  paymentTermsUnknown: z.boolean().optional(),
});

// ---------------------------------------------------------------------------
// Per-project securities billing schedule
// ---------------------------------------------------------------------------
// Generated from the assigned attorney's `securitiesEngagement.feeLines` when
// the attorney is assigned. Each row is one billable installment (a single
// fee line may produce one row for `single` billing, or N rows for `split`
// billing). Once created, rows are editable and become the source of truth
// for the master P&L's securities-counsel expansion.
export const projectBillingInstallments = pgTable("project_billing_installments", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  projectId: varchar("project_id").notNull().references(() => investmentProjects.id, { onDelete: "cascade" }),
  // Vendor role this installment is for. Currently only "securities" is
  // generated; the column is here so the table can be extended to other
  // engagement-letter-driven vendors later (immigration, fund admin).
  role: text("role").notNull(),
  // Engagement-letter feeLine.key this row was generated from.
  feeLineKey: text("fee_line_key").notNull(),
  // Human-readable label snapshot ("Offering Documents \u2014 on engagement").
  label: text("label").notNull(),
  amount: integer("amount").notNull(),
  // Trigger encoded the same way as securities-engagement.BillingTrigger.
  triggerType: text("trigger_type").notNull(),
  triggerEvent: text("trigger_event"),
  triggerDate: timestamp("trigger_date"),
  // Computed due-date once the trigger has resolved (event fired or date
  // reached). Null until then. Updated by the routes that fire triggers.
  dueAt: timestamp("due_at"),
  // Payment receipt.
  paidAt: timestamp("paid_at"),
  paidAmount: integer("paid_amount"),
  paidBy: varchar("paid_by"),
  notes: text("notes"),
  sortOrder: integer("sort_order").default(0).notNull(),
  // Skipped installments (e.g. a conditional Form D fee that turned out
  // not to apply). Excluded from P&L but preserved for audit.
  voidedAt: timestamp("voided_at"),
  voidedReason: text("voided_reason"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});
export type ProjectBillingInstallment = typeof projectBillingInstallments.$inferSelect;

// ---------------------------------------------------------------------------
// Refundable cost retainer (one row per project+role)
// ---------------------------------------------------------------------------
// Tracks the engagement-letter cost retainer (Torres: $2,000) used for
// out-of-pocket disbursements. Increases on deposit, decreases on draws,
// and is refundable on engagement close. Granular history lives in
// `projectRetainerLedger`.
export const projectCostRetainers = pgTable("project_cost_retainers", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  projectId: varchar("project_id").notNull().references(() => investmentProjects.id, { onDelete: "cascade" }),
  role: text("role").notNull(),
  requiredAmount: integer("required_amount").notNull(),
  depositedAmount: integer("deposited_amount").default(0).notNull(),
  drawnAmount: integer("drawn_amount").default(0).notNull(),
  refundedAmount: integer("refunded_amount").default(0).notNull(),
  closedAt: timestamp("closed_at"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});
export type ProjectCostRetainer = typeof projectCostRetainers.$inferSelect;

export const projectRetainerLedger = pgTable("project_retainer_ledger", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  retainerId: varchar("retainer_id").notNull().references(() => projectCostRetainers.id, { onDelete: "cascade" }),
  // 'deposit' | 'draw' | 'refund'
  kind: text("kind").notNull(),
  amount: integer("amount").notNull(),
  occurredAt: timestamp("occurred_at").defaultNow().notNull(),
  reason: text("reason"),
  // FK to projectOopCosts when a draw funds a specific OOP cost; null otherwise.
  oopCostId: varchar("oop_cost_id"),
  recordedBy: varchar("recorded_by"),
  createdAt: timestamp("created_at").defaultNow(),
});
export type ProjectRetainerLedgerEntry = typeof projectRetainerLedger.$inferSelect;

// ---------------------------------------------------------------------------
// Time entries (additional / out-of-scope work)
// ---------------------------------------------------------------------------
// Hourly billable work logged against the project. Each entry references the
// hourly role (Partner / Senior Associate / Associate / Paralegal) so the
// rate snapshot is reproducible even if the rate card changes later.
export const projectTimeEntries = pgTable("project_time_entries", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  projectId: varchar("project_id").notNull().references(() => investmentProjects.id, { onDelete: "cascade" }),
  role: text("role").notNull(),
  workedAt: timestamp("worked_at").notNull(),
  personName: text("person_name").notNull(),
  // 'partner' | 'senior_associate' | 'associate' | 'paralegal'
  hourlyRole: text("hourly_role").notNull(),
  hours: integer("hours_x100").notNull(), // hours * 100 (centi-hours) for precision
  rateAmount: integer("rate_amount").notNull(),
  amount: integer("amount").notNull(), // computed snapshot = round(hours * rate / 100)
  description: text("description"),
  // True when the work is post-offering-commencement additional work
  // (per Torres engagement letter section). Drives P&L bucketing.
  additionalWork: boolean("additional_work").default(false).notNull(),
  // Optional fee-line key this work belongs to (e.g. "intercreditor",
  // or "loan_documents" for hourly revisions).
  feeLineKey: text("fee_line_key"),
  invoicedAt: timestamp("invoiced_at"),
  recordedBy: varchar("recorded_by"),
  createdAt: timestamp("created_at").defaultNow(),
});
export type ProjectTimeEntry = typeof projectTimeEntries.$inferSelect;

// ---------------------------------------------------------------------------
// Out-of-pocket costs
// ---------------------------------------------------------------------------
// SEC filing fees, EDGAR codes, courier, PACER, etc. When `paidFromRetainer`
// is true, the corresponding draw from `projectCostRetainers` is recorded in
// the ledger and references this row via `oopCostId`.
export const projectOopCosts = pgTable("project_oop_costs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  projectId: varchar("project_id").notNull().references(() => investmentProjects.id, { onDelete: "cascade" }),
  role: text("role").notNull(),
  incurredAt: timestamp("incurred_at").notNull(),
  category: text("category").notNull(), // 'sec_fee' | 'edgar' | 'pacer' | 'courier' | 'travel' | 'other'
  vendor: text("vendor"),
  amount: integer("amount").notNull(),
  description: text("description"),
  preApproved: boolean("pre_approved").default(false).notNull(),
  paidFromRetainer: boolean("paid_from_retainer").default(false).notNull(),
  invoicedAt: timestamp("invoiced_at"),
  recordedBy: varchar("recorded_by"),
  createdAt: timestamp("created_at").defaultNow(),
});
export type ProjectOopCost = typeof projectOopCosts.$inferSelect;

export const projectLegalChecklistItems = pgTable("project_legal_checklist_items", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  projectId: varchar("project_id").notNull().references(() => investmentProjects.id, { onDelete: "cascade" }),
  role: text("role").notNull(),
  itemKey: text("item_key").notNull(),
  // Status values:
  //   securities/immigration roles: 'pending' | 'complete'
  //   i956f role: 'outstanding' | 'uploaded' | 'verified' | 'na'
  status: text("status").default("pending").notNull(),
  completedAt: timestamp("completed_at"),
  completedBy: varchar("completed_by"),
  linkedDocumentId: varchar("linked_document_id"),
  // For i956f rows: free-form admin notes about the requirement.
  notes: text("notes"),
  // Required when status='na'; explains why this requirement does not apply.
  naReason: text("na_reason"),
  // Optional ordering hint within a section.
  sortOrder: integer("sort_order").default(0).notNull(),
  // For requirements that repeat per filing entity (e.g. each JCE).
  // Null for non-repeating items.
  entityInstanceId: varchar("entity_instance_id"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export type ProjectLegalChecklistItem = typeof projectLegalChecklistItems.$inferSelect;
export const insertProjectLegalChecklistItemSchema = createInsertSchema(projectLegalChecklistItems).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertProjectLegalChecklistItem = z.infer<typeof insertProjectLegalChecklistItemSchema>;

// ---------------------------------------------------------------------------
// Project legal document exchange rows (#366, Step 4 of the lane UI)
// ---------------------------------------------------------------------------
// Tracks who-sends-what-when between us and outside counsel, per lane.
// Predefined rows are seeded from SECURITIES_DOC_EXCHANGE_ITEMS /
// IMMIGRATION_DOC_EXCHANGE_ITEMS the first time a lane is opened. Admins
// can add freeform rows on top with isPredefined=false.
export const projectLegalDocExchange = pgTable("project_legal_doc_exchange", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  projectId: varchar("project_id").notNull().references(() => investmentProjects.id, { onDelete: "cascade" }),
  // 'securities' | 'immigration'
  lane: text("lane").notNull(),
  // Stable key for predefined rows (matches SECURITIES_DOC_EXCHANGE_ITEMS /
  // IMMIGRATION_DOC_EXCHANGE_ITEMS); null for freeform rows.
  itemKey: text("item_key"),
  label: text("label").notNull(),
  // 'we_send' | 'they_send'
  direction: text("direction").notNull(),
  // 'not_sent' | 'sent' | 'received'
  status: text("status").default("not_sent").notNull(),
  occurredAt: timestamp("occurred_at"),
  // Optional reference to a project_internal_documents row.
  documentId: varchar("document_id"),
  isPredefined: boolean("is_predefined").default(false).notNull(),
  sortOrder: integer("sort_order").default(0).notNull(),
  notes: text("notes"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export type ProjectLegalDocExchange = typeof projectLegalDocExchange.$inferSelect;
export const insertProjectLegalDocExchangeSchema = createInsertSchema(projectLegalDocExchange).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertProjectLegalDocExchange = z.infer<typeof insertProjectLegalDocExchangeSchema>;

export type InsertAttorney = z.infer<typeof insertAttorneySchema>;
export type Attorney = typeof attorneys.$inferSelect;

export const economists = pgTable("economists", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull(),
  firstName: text("first_name"),
  lastName: text("last_name"),
  firmName: text("firm_name"),
  // Task #2318 — optional public firm website, rendered as a link on the
  // public project-detail page (Quick Facts + Professional Partners).
  website: text("website"),
  email: text("email"),
  phone: text("phone"),
  notes: text("notes"),
  // Rate card (all nullable).
  jobCreationReportFee: integer("job_creation_report_fee"),
  // Turnaround day assumptions (all nullable).
  jobCreationReportTurnaroundDays: integer("job_creation_report_turnaround_days"),
  pricingNotes: text("pricing_notes"),
  // Mailing / firm address (used on engagement letters and invoices).
  addressLine1: text("address_line_1"),
  addressLine2: text("address_line_2"),
  addressCity: text("address_city"),
  addressState: text("address_state"),
  addressZip: text("address_zip"),
  addressCountry: text("address_country"),
  paymentTerms: jsonb("payment_terms").$type<{ portionPct: number; anchor: "engagement" | "delivery" | "marketing_ready"; offsetDays?: number }[]>().notNull().default(sql`'[]'::jsonb`),
  paymentTermsUnknown: boolean("payment_terms_unknown").notNull().default(false),
  isActive: boolean("is_active").default(true).notNull(),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertEconomistSchema = createInsertSchema(economists).omit({
  id: true,
  createdAt: true,
  name: true,
}).extend({
  firstName: z.string().trim().min(1, "First name is required"),
  lastName: z.string().trim().min(1, "Last name is required"),
  email: optionalContactEmail,
  website: optionalWebsiteUrl,
  paymentTerms: vendorPaymentTermsSchema.optional(),
  paymentTermsUnknown: z.boolean().optional(),
});

export type InsertEconomist = z.infer<typeof insertEconomistSchema>;
export type Economist = typeof economists.$inferSelect;

export const businessPlanWriters = pgTable("business_plan_writers", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  firstName: text("first_name").notNull(),
  lastName: text("last_name").notNull(),
  firmName: text("firm_name"),
  // Task #2318 — optional public firm website, rendered as a link on the
  // public project-detail page (Quick Facts + Professional Partners).
  website: text("website"),
  email: text("email"),
  phone: text("phone"),
  notes: text("notes"),
  // Rate card (all nullable).
  businessPlanFee: integer("business_plan_fee"),
  amendmentFee: integer("amendment_fee"),
  // Turnaround day assumptions (all nullable).
  businessPlanTurnaroundDays: integer("business_plan_turnaround_days"),
  amendmentTurnaroundDays: integer("amendment_turnaround_days"),
  pricingNotes: text("pricing_notes"),
  // Mailing / firm address (used on engagement letters and invoices).
  addressLine1: text("address_line_1"),
  addressLine2: text("address_line_2"),
  addressCity: text("address_city"),
  addressState: text("address_state"),
  addressZip: text("address_zip"),
  addressCountry: text("address_country"),
  paymentTerms: jsonb("payment_terms").$type<{ portionPct: number; anchor: "engagement" | "delivery" | "marketing_ready"; offsetDays?: number }[]>().notNull().default(sql`'[]'::jsonb`),
  paymentTermsUnknown: boolean("payment_terms_unknown").notNull().default(false),
  isActive: boolean("is_active").default(true).notNull(),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertBusinessPlanWriterSchema = createInsertSchema(businessPlanWriters).omit({
  id: true,
  createdAt: true,
}).extend({
  email: optionalContactEmail,
  website: optionalWebsiteUrl,
  paymentTerms: vendorPaymentTermsSchema.optional(),
  paymentTermsUnknown: z.boolean().optional(),
});

export type InsertBusinessPlanWriter = z.infer<typeof insertBusinessPlanWriterSchema>;
export type BusinessPlanWriter = typeof businessPlanWriters.$inferSelect;

// Fund administrator pricing is modeled around PRXY's published rate card:
//   - A tiered one-time onboarding fee (small/large offering, split by a USD
//     threshold on the project's raise target).
//   - A monthly admin fee with a flat base for up to N investors plus a
//     per-investor overage above that, charged across the active raise +
//     deployment window.
//   - A flat annual portal/storage fee that recurs after the raise closes.
// All pricing fields are nullable so other administrators with simpler
// structures can leave irrelevant fields blank.
export const fundAdministrators = pgTable("fund_administrators", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  firstName: text("first_name").notNull(),
  lastName: text("last_name").notNull(),
  firmName: text("firm_name"),
  email: text("email"),
  phone: text("phone"),
  notes: text("notes"),
  // Onboarding (one-time, tiered by raise target).
  onboardingTierThreshold: integer("onboarding_tier_threshold").default(20000000),
  onboardingFeeSmall: integer("onboarding_fee_small"),
  onboardingFeeLarge: integer("onboarding_fee_large"),
  // Monthly administration fee during the active raise + deployment window.
  monthlyAdminBaseFee: integer("monthly_admin_base_fee"),
  monthlyAdminBaseInvestorCount: integer("monthly_admin_base_investor_count").default(25),
  monthlyAdminPerInvestorOverage: integer("monthly_admin_per_investor_overage"),
  // Post-close annual portal / data storage fee.
  annualStorageFee: integer("annual_storage_fee"),
  // Turnaround day assumption: how long onboarding (account setup, first NAV, etc.)
  // takes once an engagement is signed. Nullable.
  onboardingTurnaroundDays: integer("onboarding_turnaround_days"),
  pricingNotes: text("pricing_notes"),
  // Mailing / firm address (used on engagement letters and invoices).
  addressLine1: text("address_line_1"),
  addressLine2: text("address_line_2"),
  addressCity: text("address_city"),
  addressState: text("address_state"),
  addressZip: text("address_zip"),
  addressCountry: text("address_country"),
  paymentTerms: jsonb("payment_terms").$type<{ portionPct: number; anchor: "engagement" | "delivery" | "marketing_ready"; offsetDays?: number }[]>().notNull().default(sql`'[]'::jsonb`),
  paymentTermsUnknown: boolean("payment_terms_unknown").notNull().default(false),
  isActive: boolean("is_active").default(true).notNull(),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertFundAdministratorSchema = createInsertSchema(fundAdministrators).omit({
  id: true,
  createdAt: true,
}).extend({
  firstName: z.string().trim().min(1, "First name is required"),
  lastName: z.string().trim().min(1, "Last name is required"),
  email: optionalContactEmail,
  paymentTerms: vendorPaymentTermsSchema.optional(),
  paymentTermsUnknown: z.boolean().optional(),
});

export type InsertFundAdministrator = z.infer<typeof insertFundAdministratorSchema>;
export type FundAdministrator = typeof fundAdministrators.$inferSelect;

// Regional centers we actively partner with on deals. The full USCIS catalog
// lives in client/src/lib/regional-centers-data.ts and is used as the picker
// source when creating a row here; once selected, name + uscis_id + state_abbr
// are copied into this row so we own the data going forward.
export const workingRegionalCenters = pgTable("working_regional_centers", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  uscisId: text("uscis_id").notNull(),
  name: text("name").notNull(),
  stateAbbr: text("state_abbr"),

  // Primary contact at the RC.
  contactName: text("contact_name"),
  contactTitle: text("contact_title"),
  contactEmail: text("contact_email"),
  contactPhone: text("contact_phone"),

  // Principal place of business (used on the I-956F).
  addressLine1: text("address_line_1"),
  addressLine2: text("address_line_2"),
  addressCity: text("address_city"),
  addressState: text("address_state"),
  addressZip: text("address_zip"),
  addressCountry: text("address_country"),

  // General info.
  mainPhone: text("main_phone"),
  website: text("website"),
  logoUrl: text("logo_url"),

  // Pricing baseline (FFRC-style; everything nullable for non-standard RCs).
  initialAffiliationFee: integer("initial_affiliation_fee"),
  perInvestorAdminFee: integer("per_investor_admin_fee"),
  annualPerInvestorFeeBps: integer("annual_per_investor_fee_bps"),
  legalReviewReimbursement: integer("legal_review_reimbursement"),
  integrityFundContribution: integer("integrity_fund_contribution"),
  annualLegalFeeCap: integer("annual_legal_fee_cap"),
  pricingNotes: text("pricing_notes"),

  // "Pricing not tracked" marker (Task #1850). When true, this regional
  // center's pricing is handled outside the platform (e.g. co-GP deals), so
  // every pricing field above is stored NULL and downstream views/rollups
  // render "Pricing not tracked" instead of treating the blanks as $0.
  pricingNotTracked: boolean("pricing_not_tracked").notNull().default(false),

  // Turnaround day assumption: how long affiliation paperwork takes once a
  // project commits to using this RC. Nullable.
  affiliationTurnaroundDays: integer("affiliation_turnaround_days"),

  // States the RC is approved to operate in. Used by the project picker
  // to flag selections that fall outside the RC's geographic coverage.
  // Stored as USPS abbreviations (e.g. ["FL","GA","AL"]).
  coverageStates: text("coverage_states").array(),

  notes: text("notes"),
  isActive: boolean("is_active").default(true).notNull(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertWorkingRegionalCenterSchema = createInsertSchema(workingRegionalCenters).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  isActive: true,
}).extend({
  uscisId: z.string().trim().min(1, "Pick a regional center from the list"),
  name: z.string().trim().min(1, "Name is required"),
});

export type InsertWorkingRegionalCenter = z.infer<typeof insertWorkingRegionalCenterSchema>;
export type WorkingRegionalCenter = typeof workingRegionalCenters.$inferSelect;

export const passwordResetTokens = pgTable("password_reset_tokens", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  // SHA-256 hex of the plaintext emailed to the user. Never store the raw token.
  token: text("token").notNull().unique(),
  expiresAt: timestamp("expires_at").notNull(),
  usedAt: timestamp("used_at"),
  createdAt: timestamp("created_at").defaultNow(),
});

export type PasswordResetToken = typeof passwordResetTokens.$inferSelect;

export const EMAIL_CHANGE_ACCOUNT_FAMILIES = ["investor", "partner", "vendor"] as const;
export type EmailChangeAccountFamily = (typeof EMAIL_CHANGE_ACCOUNT_FAMILIES)[number];

export const EMAIL_CHANGE_REQUESTED_BY = ["self", "admin"] as const;
export type EmailChangeRequestedBy = (typeof EMAIL_CHANGE_REQUESTED_BY)[number];

export const emailChangeRequests = pgTable(
  "email_change_requests",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    accountFamily: text("account_family").$type<EmailChangeAccountFamily>().notNull(),
    accountId: varchar("account_id").notNull(),
    currentEmail: text("current_email").notNull(),
    newEmail: text("new_email").notNull(),
    confirmTokenHash: text("confirm_token_hash").notNull(),
    cancelTokenHash: text("cancel_token_hash").notNull(),
    requestedBy: text("requested_by").$type<EmailChangeRequestedBy>().notNull(),
    requestedByAdminId: varchar("requested_by_admin_id"),
    reason: text("reason"),
    expiresAt: timestamp("expires_at").notNull(),
    confirmedAt: timestamp("confirmed_at"),
    cancelledAt: timestamp("cancelled_at"),
    consumedAt: timestamp("consumed_at"),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (t) => ({
    confirmHashUniq: uniqueIndex("email_change_requests_confirm_hash_uniq").on(t.confirmTokenHash),
    cancelHashUniq: uniqueIndex("email_change_requests_cancel_hash_uniq").on(t.cancelTokenHash),
    accountIdx: index("email_change_requests_account_idx").on(t.accountFamily, t.accountId, t.createdAt),
  }),
);

export type EmailChangeRequest = typeof emailChangeRequests.$inferSelect;
export type InsertEmailChangeRequest = typeof emailChangeRequests.$inferInsert;

// Task #887 — agent-side seats live in `agent_users`, not `users`. To
// attribute a note written by a partner-firm seat (so the agent CRM can
// show "Maria from Acme Law wrote this"), we keep `authorId` for admin/
// internal authors but make it nullable, and add `authorAgentSeatId`
// referencing `agent_users`. Exactly one of the two is populated for
// new rows; legacy rows keep `authorId` set.
export const investorNotes = pgTable("investor_notes", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  investorId: varchar("investor_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  authorId: varchar("author_id").references(() => users.id),
  authorAgentSeatId: varchar("author_agent_seat_id").references(() => agentUsers.id, { onDelete: "set null" }),
  authorName: text("author_name").notNull(),
  content: text("content").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertInvestorNoteSchema = createInsertSchema(investorNotes).omit({
  id: true,
  createdAt: true,
});

export type InsertInvestorNote = z.infer<typeof insertInvestorNoteSchema>;
export type InvestorNote = typeof investorNotes.$inferSelect;

// Task #887 — partner-firm CRM extensions. All three tables are scoped
// to `(agentId, investorId)` so two firms working with the same investor
// (cross-handoff edge case) keep their own tags / reminders / activity
// trails. None of these are visible to the investor.

// Free-text labels a partner can add to an investor for their own
// segmentation (e.g. "vip", "spring2026", "warm"). Same tag may not be
// stored twice for the same agent+investor.
export const agentInvestorTags = pgTable(
  "agent_investor_tags",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    agentId: varchar("agent_id").notNull().references(() => referralAgents.id, { onDelete: "cascade" }),
    investorId: varchar("investor_id").notNull().references(() => users.id, { onDelete: "cascade" }),
    tag: text("tag").notNull(),
    createdBySeatId: varchar("created_by_seat_id").references(() => agentUsers.id, { onDelete: "set null" }),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (t) => ({
    uniq: uniqueIndex("agent_investor_tags_uniq").on(t.agentId, t.investorId, t.tag),
  }),
);
export type AgentInvestorTag = typeof agentInvestorTags.$inferSelect;
export type InsertAgentInvestorTag = typeof agentInvestorTags.$inferInsert;

// Follow-up reminders the partner sets on an investor. `dueDate` is a
// calendar date (no timezone) per task spec. `seatId` records which
// teammate created the reminder so the dashboard can show "you owe X" vs
// "your teammate owes X". `completedAt` flips when a seat marks it done;
// reminders are kept (not deleted) so the activity log can show closure.
export const agentInvestorReminders = pgTable("agent_investor_reminders", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  agentId: varchar("agent_id").notNull().references(() => referralAgents.id, { onDelete: "cascade" }),
  investorId: varchar("investor_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  seatId: varchar("seat_id").references(() => agentUsers.id, { onDelete: "set null" }),
  seatName: text("seat_name"),
  dueDate: text("due_date").notNull(),
  message: text("message").notNull(),
  completedAt: timestamp("completed_at"),
  completedBySeatId: varchar("completed_by_seat_id").references(() => agentUsers.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").defaultNow(),
});
export type AgentInvestorReminder = typeof agentInvestorReminders.$inferSelect;
export type InsertAgentInvestorReminder = typeof agentInvestorReminders.$inferInsert;

// Per-(agent, investor) activity log of what teammates have done — note
// added, tag added, reminder created/completed, investor imported, etc.
// Distinct from the global `system_activity_log` (admin-facing) so the
// partner UI can show only their own firm's actions without leaking
// admin events.
export const AGENT_INVESTOR_ACTIVITY_KINDS = [
  "investor_added",
  "investor_imported",
  "note_added",
  "tag_added",
  "tag_removed",
  "reminder_created",
  "reminder_completed",
  "reminder_deleted",
  "status_viewed",
  "document_viewed",
  "document_downloaded",
  // Task #888 — call lifecycle. Mirrored into the per-investor
  // activity feed so referral partners see the same booking events
  // their email inbox does, attributed to "system" (no seat).
  "call_scheduled",
  "call_completed",
  "call_rescheduled",
  "call_cancelled",
  // Task #888 — daily stuck-deal sweep. One row per
  // (agent, investor, UTC day) when the investor has been past the
  // stage stall threshold for at least one full day.
  "deal_stuck",
] as const;
export type AgentInvestorActivityKind = (typeof AGENT_INVESTOR_ACTIVITY_KINDS)[number];

export const agentInvestorActivity = pgTable("agent_investor_activity", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  agentId: varchar("agent_id").notNull().references(() => referralAgents.id, { onDelete: "cascade" }),
  investorId: varchar("investor_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  seatId: varchar("seat_id").references(() => agentUsers.id, { onDelete: "set null" }),
  seatName: text("seat_name"),
  kind: text("kind").$type<AgentInvestorActivityKind>().notNull(),
  summary: text("summary").notNull(),
  details: jsonb("details"),
  createdAt: timestamp("created_at").defaultNow(),
});
export type AgentInvestorActivity = typeof agentInvestorActivity.$inferSelect;
export type InsertAgentInvestorActivity = typeof agentInvestorActivity.$inferInsert;

export const visaBulletinSnapshots = pgTable("visa_bulletin_snapshots", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  month: text("month").notNull(),
  year: integer("year").notNull(),
  finalActionDates: jsonb("final_action_dates").notNull(),
  datesForFiling: jsonb("dates_for_filing").notNull(),
  sourceUrl: text("source_url"),
  fetchedAt: timestamp("fetched_at").defaultNow().notNull(),
}, (t) => ({
  monthYearUnique: uniqueIndex("visa_bulletin_snapshots_month_year_idx").on(t.month, t.year),
}));

export const visaBulletinMeta = pgTable("visa_bulletin_meta", {
  id: varchar("id").primaryKey().default(sql`'singleton'`),
  lastCheckedAt: timestamp("last_checked_at").defaultNow().notNull(),
  lastNewBulletinAt: timestamp("last_new_bulletin_at"),
  currentMonth: text("current_month"),
  currentYear: integer("current_year"),
});

export interface VisaBulletinData {
  month: string;
  year: number;
  finalActionDates: {
    country: string;
    unreserved: string;
    rural: string;
    highUnemployment: string;
    infrastructure: string;
  }[];
  datesForFiling: {
    country: string;
    unreserved: string;
    rural: string;
    highUnemployment: string;
    infrastructure: string;
  }[];
  lastUpdated: string;
  lastCheckedAt?: string;
}

export interface CountryInfo {
  code: string;
  name: string;
  nativeName: string;
  flag: string;
  language: string;
  eb5Stats: {
    currentWaitTime: string;
    priorityDate: string;
    trend: "improving" | "stable" | "worsening";
  };
}

export const projectWaitlistEntries = pgTable("project_waitlist_entries", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  projectId: varchar("project_id").notNull().references(() => investmentProjects.id, { onDelete: "cascade" }),
  userId: varchar("user_id").references(() => users.id, { onDelete: "set null" }),
  name: text("name").notNull(),
  email: text("email").notNull(),
  preferredAmount: integer("preferred_amount"),
  notes: text("notes"),
  status: text("status").notNull().default("waiting"),
  priority: integer("priority").default(0),
  offeredAt: timestamp("offered_at"),
  declinedAt: timestamp("declined_at"),
  convertedAt: timestamp("converted_at"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertProjectWaitlistEntrySchema = createInsertSchema(projectWaitlistEntries).omit({
  id: true,
  createdAt: true,
  offeredAt: true,
  declinedAt: true,
  convertedAt: true,
});

export type InsertProjectWaitlistEntry = z.infer<typeof insertProjectWaitlistEntrySchema>;
export type ProjectWaitlistEntry = typeof projectWaitlistEntries.$inferSelect;

export const apiTokens = pgTable("api_tokens", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull(),
  tokenHash: text("token_hash").notNull().unique(),
  tokenPrefix: text("token_prefix").notNull(),
  scopes: jsonb("scopes").$type<string[]>().default(["read"]).notNull(),
  lastUsedAt: timestamp("last_used_at"),
  revokedAt: timestamp("revoked_at"),
  createdBy: varchar("created_by").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
});

export const webhookSubscriptions = pgTable("webhook_subscriptions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  url: text("url").notNull(),
  events: jsonb("events").$type<string[]>().notNull(),
  secret: text("secret").notNull(),
  active: boolean("active").default(true).notNull(),
  lastDeliveredAt: timestamp("last_delivered_at"),
  failCount: integer("fail_count").default(0).notNull(),
  createdBy: varchar("created_by").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
});

export const webhookDeliveries = pgTable("webhook_deliveries", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  subscriptionId: varchar("subscription_id").notNull().references(() => webhookSubscriptions.id, { onDelete: "cascade" }),
  event: text("event").notNull(),
  payload: jsonb("payload").notNull(),
  statusCode: integer("status_code"),
  success: boolean("success"),
  error: text("error"),
  deliveredAt: timestamp("delivered_at").defaultNow(),
});

// Task #2383 — a compact, retention-bounded record of which endpoint each
// partner API token actually hit. `api_tokens.last_used_at` answers "was
// this token used at all"; this table answers "roughly what did it call"
// so we can verify "no partner depends on this endpoint" with data before
// retiring it. Bounded by opportunistic per-token pruning (newest N rows
// kept) so it can never grow unbounded. Service-role-only (default-deny),
// mirroring api_tokens.
export const apiTokenUsage = pgTable("api_token_usage", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tokenId: varchar("token_id").notNull().references(() => apiTokens.id, { onDelete: "cascade" }),
  method: text("method").notNull(),
  path: text("path").notNull(),
  scope: text("scope"),
  statusCode: integer("status_code"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

// Idempotency ledger for research contact ingest (POST /api/v1/contacts).
// Service-role-only (default-deny), mirroring api_tokens / api_token_usage.
export const apiContactIdempotency = pgTable("api_contact_idempotency", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tokenId: varchar("token_id").notNull().references(() => apiTokens.id, { onDelete: "cascade" }),
  idempotencyKey: text("idempotency_key").notNull(),
  requestHash: text("request_hash").notNull(),
  contactId: varchar("contact_id").notNull().references(() => contacts.id, { onDelete: "cascade" }),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (table) => ({
  tokenKeyUnique: uniqueIndex("api_contact_idempotency_token_key_uidx")
    .on(table.tokenId, table.idempotencyKey),
}));

export type ApiToken = typeof apiTokens.$inferSelect;
export type ApiTokenUsage = typeof apiTokenUsage.$inferSelect;
export type ApiContactIdempotency = typeof apiContactIdempotency.$inferSelect;
export type WebhookSubscription = typeof webhookSubscriptions.$inferSelect;
export type WebhookDelivery = typeof webhookDeliveries.$inferSelect;

/** Brands that can have logo / kit assets in admin Branding. */
export const BRAND_ASSET_BRANDS = ["capital", "energy", "real_estate"] as const;
export type BrandAssetBrand = (typeof BRAND_ASSET_BRANDS)[number];

export const brandAssets = pgTable("brand_assets", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  brand: text("brand").notNull(),
  category: text("category").notNull().default("logos"),
  filename: text("filename").notNull(),
  displayName: text("display_name").notNull(),
  objectKey: text("object_key").notNull(),
  format: text("format").notNull().default("SVG"),
  dimensions: text("dimensions").notNull().default(""),
  tone: text("tone").notNull().default("neutral"),
  useWhen: text("use_when").notNull().default(""),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertBrandAssetSchema = createInsertSchema(brandAssets).omit({
  id: true,
  createdAt: true,
});

export type BrandAsset = typeof brandAssets.$inferSelect;
export type InsertBrandAsset = z.infer<typeof insertBrandAssetSchema>;

// Task #1156 — kept as a legacy compile-time list of slugs that were
// historically wired into handoffs. The runtime source of truth is now
// the `divisions` table (managed at /admin/project-settings → Divisions
// tab). Code that needs to know "is this a real, active division?" must
// look it up via storage.getActiveDivisions() instead of this constant.
export const DIVISIONS = ["energy", "real_estate"] as const;
export type Division = (typeof DIVISIONS)[number];

// ---------------------------------------------------------------------------
// Task #1156 — Divisions as a first-class managed entity
// ---------------------------------------------------------------------------
// `divisions` is the canonical metadata table. Existing free-text
// `division` columns on `project_types`, `investment_projects`, and
// `users.adminDivisions` continue to store the slug; this table owns
// the label / active flag / experts. Slug is locked at creation time
// so existing rows never need to be rewritten. Divisions sort
// alphabetically by label; the former sort_order column was dropped.
export const divisions = pgTable("divisions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  slug: text("slug").notNull().unique(),
  label: text("label").notNull(),
  isActive: boolean("is_active").notNull().default(true),
  // [{ owner: "veras_capital", shareBps: 6667 }, ...] — must sum to 10000
  internalOwnership: jsonb("internal_ownership").notNull().default([]),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export type DivisionRow = typeof divisions.$inferSelect;
export const insertDivisionSchema = createInsertSchema(divisions).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertDivision = z.infer<typeof insertDivisionSchema>;

// Roster of admin users who are "division experts" for a given
// division. `removedAt` is null for the currently-active assignment
// and stamped with a timestamp when an admin removes the expert. Past
// rows are kept for historical roster lookups (snapshot semantics
// already happen at funding time on `fundedDealEarningsExperts`, but
// keeping the audit trail here is cheap).
export const divisionExperts = pgTable("division_experts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  divisionId: varchar("division_id").notNull().references(() => divisions.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  addedAt: timestamp("added_at").notNull().defaultNow(),
  removedAt: timestamp("removed_at"),
}, (t) => ({
  // One active row per (division, user). Past (removedAt-stamped) rows
  // are unconstrained so an expert can be removed and re-added without
  // colliding.
  uniqActive: uniqueIndex("division_experts_uniq_active_idx")
    .on(t.divisionId, t.userId)
    .where(sql`removed_at IS NULL`),
}));

export type DivisionExpert = typeof divisionExperts.$inferSelect;

// One row per funded commitment, capturing the deal-level division
// snapshot. The fixed split is 2/3 Veras + 1/3 evenly across the
// expert roster at funding time. Stored as basis-points-of-100%
// (10000 = 100.00%) so the math is integer-safe and reconciles
// exactly. When the division has zero experts at funding time, Veras
// gets 100% (10000) and `expertCount = 0`.
export const fundedDealEarnings = pgTable("funded_deal_earnings", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  commitmentId: varchar("commitment_id").notNull().unique(),
  projectId: varchar("project_id").notNull(),
  divisionSlug: text("division_slug"),
  divisionId: varchar("division_id"),
  verasShareBp: integer("veras_share_bp").notNull(),
  expertCount: integer("expert_count").notNull(),
  fundedAt: timestamp("funded_at").notNull(),
  capturedAt: timestamp("captured_at").notNull().defaultNow(),
});

export type FundedDealEarnings = typeof fundedDealEarnings.$inferSelect;

// Per-expert share for one funded deal. `userShareBp` already accounts
// for the even 1/3-split-N-ways and adds up (with verasShareBp) to
// 10000 across the parent earnings row.
export const fundedDealEarningsExperts = pgTable("funded_deal_earnings_experts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  earningsId: varchar("earnings_id").notNull().references(() => fundedDealEarnings.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull(),
  userShareBp: integer("user_share_bp").notNull(),
});

export type FundedDealEarningsExpert = typeof fundedDealEarningsExperts.$inferSelect;

export const HANDOFF_STATUSES = [
  "pending",
  "scheduled",
  "pitched",
  "committed",
  "declined",
  "lost",
] as const;
export type HandoffStatus = (typeof HANDOFF_STATUSES)[number];

export const expertHandoffs = pgTable("expert_handoffs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  investorId: varchar("investor_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  projectId: varchar("project_id").notNull().references(() => investmentProjects.id, { onDelete: "cascade" }),
  division: text("division").notNull(),
  // Task #1170 — was NOT NULL + RESTRICT; relaxed to nullable +
  // ON DELETE SET NULL so deleting the actor preserves the audit row.
  initiatedById: varchar("initiated_by_id").references(() => users.id, { onDelete: "set null" }),
  assignedExpertIds: jsonb("assigned_expert_ids").$type<string[]>().notNull().default([]),
  qualificationNotes: text("qualification_notes"),
  status: text("status").notNull().default("pending"),
  bookingId: varchar("booking_id").references(() => callBookings.id, { onDelete: "set null" }),
  outcomeNotes: text("outcome_notes"),
  scheduledAt: timestamp("scheduled_at"),
  pitchedAt: timestamp("pitched_at"),
  closedAt: timestamp("closed_at"),
  lastStalledNudgeAt: timestamp("last_stalled_nudge_at"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const expertHandoffActivity = pgTable("expert_handoff_activity", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  handoffId: varchar("handoff_id").notNull().references(() => expertHandoffs.id, { onDelete: "cascade" }),
  actorId: varchar("actor_id").references(() => users.id, { onDelete: "set null" }),
  actorName: text("actor_name"),
  action: text("action").notNull(),
  previousStatus: text("previous_status"),
  newStatus: text("new_status"),
  note: text("note"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertExpertHandoffSchema = createInsertSchema(expertHandoffs).omit({
  id: true,
  status: true,
  bookingId: true,
  outcomeNotes: true,
  scheduledAt: true,
  pitchedAt: true,
  closedAt: true,
  lastStalledNudgeAt: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertExpertHandoff = z.infer<typeof insertExpertHandoffSchema>;
export type ExpertHandoff = typeof expertHandoffs.$inferSelect;
export type ExpertHandoffActivity = typeof expertHandoffActivity.$inferSelect;

// "degraded" (Task #2477) — a batch job that finished without throwing but
// dropped one or more individual items (e.g. some reminder emails failed to
// send). It is distinct from a clean "success" so the System Activity page
// and ops digest can surface partially-dropped work instead of hiding it
// behind a green badge. Jobs that report it carry success/failure counts in
// `details`.
export const SYSTEM_ACTIVITY_STATUSES = ["success", "failure", "skipped", "running", "degraded"] as const;
export type SystemActivityStatus = (typeof SYSTEM_ACTIVITY_STATUSES)[number];

export const SYSTEM_ACTIVITY_SOURCES = [
  "visa_bulletin_scraper",
  "eia_henry_hub_scraper",
  "cleanup",
  "cleanup_test_fixtures",
  "verification_reminders",
  "portal_login_nudges",
  "stalled_handoffs",
  "stuck_deals",
  "outreach_list",
  "booking_reminders",
  "no_show_processor",
  "commission_accrual",
  "preview_mirror_refresh",
  "openai",
  "resend",
  // Customer.io named-go audit (identify/track only — not a mail sender).
  "customerio",
  "google_calendar",
  "object_storage",
  "auth",
  "rate_limit",
  "csrf",
  "webhook",
  "vendor_portal",
  "sentry",
  // Task #1004 — attribution SLA worker (auto-confirms pending_review).
  "attribution_sla",
  // Task #1016 — admin Project Economics writes (channel splits,
  // deal-shape edits) and the per-project P&L assembler.
  "project_economics",
  "partner_pricing",
  // Task #1094 — lead ownership window sweep (expires confirmed_agent
  // rows back to the house pool after inactivity).
  "lead_ownership_sweep",
  // Task #1328 — admin Profiles review queue (cross-role profile
  // edits, headshot uploads, approve/reject decisions).
  "profile_review",
  // Task #1450 — admin E-Sign expiry sweeper and reminder mailer.
  "esign_reminders",
  // Unviewed partner proposal reminders (day 3 and day 7).
  "partner_proposal_followups",
  // Task #1619 — daily ops digest email at 6 AM ET.
  "daily_ops_digest",
  // Ship digests — Render deploy webhook + Monday weekly product email.
  "render_deploy",
  "weekly_product_digest",
  // Task #2147 — nightly record-based contact label refresh.
  "contact_reclassify",
  // Task #2272 — broker slot reservation expiry sweep.
  "slot_reservation_expiry",
  // In-person consultation soft-hold TTL release (call_bookings status=held).
  "in_person_hold_expiry",
  // Task #2868 — investor email nurture sequence engine.
  "email_sequence",
  // Task #3000 — email hygiene sweep (auto-sunsets cold non-engaging contacts).
  "email_sequence_hygiene",
  "content_publication_sync",
  // Firm outreach hybrid — Hot Board due follow-ups + auto-Park.
  "hot_board_outreach_cadence",
  // Core-admin edits to investor profile fields (geography) — one row per
  // save with before/after values and the acting admin id.
  "admin_investor_edit",
  // Person-level Dead mark and revive. Moved reads these rows.
  "investor_lifecycle",
  // Immigration attorney opened a deal room without the platform NDA.
  "partner_deal_room",
] as const;
export type SystemActivitySource = (typeof SYSTEM_ACTIVITY_SOURCES)[number];

export interface KnownJobDescriptor {
  source: SystemActivitySource;
  label: string;
  category: "job" | "integration";
  schedule: string;
}

export const KNOWN_SYSTEM_ACTIVITY_JOBS: KnownJobDescriptor[] = [
  { source: "visa_bulletin_scraper", label: "Visa Bulletin Scraper", category: "job", schedule: "Daily at 06:00 UTC (and on server boot)" },
  { source: "eia_henry_hub_scraper", label: "EIA Henry Hub Spot Price", category: "job", schedule: "Daily at 08:00 UTC (and on server boot)" },
  { source: "cleanup", label: "Daily Cleanup", category: "job", schedule: "Daily at 03:00 UTC" },
  { source: "cleanup_test_fixtures", label: "Test Fixture Sweep", category: "job", schedule: "Daily at 04:00 UTC" },
  { source: "verification_reminders", label: "Verification Reminders", category: "job", schedule: "Daily at 14:00 UTC (deployed only)" },
  { source: "portal_login_nudges", label: "Portal Login Nudges", category: "job", schedule: "Daily at 19:00 UTC (deployed only)" },
  { source: "hot_board_outreach_cadence", label: "Hot Board Outreach Cadence", category: "job", schedule: "Daily at 20:00 UTC (deployed only)" },
  { source: "stalled_handoffs", label: "Stalled Handoff Nudges", category: "job", schedule: "Daily at 15:00 UTC (deployed only)" },
  { source: "booking_reminders", label: "Booking Reminders", category: "job", schedule: "Every 2 minutes (deployed only)" },
  { source: "in_person_hold_expiry", label: "In-Person Soft-Hold Expiry", category: "job", schedule: "Every 5 minutes" },
  { source: "no_show_processor", label: "No-Show Processor", category: "job", schedule: "Disabled — attendance is marked manually" },
  { source: "commission_accrual", label: "Commission Accrual", category: "job", schedule: "Daily at 09:00 UTC (and on server boot)" },
  { source: "preview_mirror_refresh", label: "Preview Mirror Refresh", category: "job", schedule: "Weekly on Sunday at 05:00 UTC (preview only; requires PROD_DATABASE_URL)" },
  { source: "esign_reminders", label: "E-Sign Reminders & Expiry", category: "job", schedule: "Daily at 10:00 UTC (deployed only)" },
  { source: "partner_proposal_followups", label: "Partner Proposal Follow-ups", category: "job", schedule: "Daily at 10:00 UTC (deployed only)" },
  { source: "contact_reclassify", label: "Contact Label Refresh", category: "job", schedule: "Daily at 02:00 UTC" },
  { source: "email_sequence_hygiene", label: "Email Hygiene Sweep", category: "job", schedule: "Hourly (deployed only)" },
  { source: "weekly_product_digest", label: "Weekly Product Digest", category: "job", schedule: "Mondays at 9:00 AM America/New_York (deployed only)" },
  { source: "render_deploy", label: "Render Deploy Webhook", category: "integration", schedule: "On deploy_ended webhook" },
  { source: "openai", label: "OpenAI", category: "integration", schedule: "On demand" },
  { source: "resend", label: "Resend Email", category: "integration", schedule: "On demand" },
  { source: "google_calendar", label: "Google Calendar", category: "integration", schedule: "On demand" },
  { source: "object_storage", label: "Object Storage", category: "integration", schedule: "On demand" },
  { source: "content_publication_sync", label: "Buffer Publication Sync", category: "job", schedule: "Every 5 minutes (deployed only)" },
];

export const systemActivityLog = pgTable("system_activity_log", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  source: text("source").notNull(),
  operation: text("operation").notNull(),
  status: text("status").notNull(),
  startedAt: timestamp("started_at").notNull().defaultNow(),
  finishedAt: timestamp("finished_at"),
  durationMs: integer("duration_ms"),
  summary: text("summary"),
  details: jsonb("details"),
  errorMessage: text("error_message"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export const insertSystemActivityLogSchema = createInsertSchema(systemActivityLog).omit({
  id: true,
  createdAt: true,
});

export type SystemActivityLog = typeof systemActivityLog.$inferSelect;
export type InsertSystemActivityLog = z.infer<typeof insertSystemActivityLogSchema>;

// ============================================================================
// Auth events (Task #1203)
// ============================================================================
// Unified audit table for auth-shaped events (logins, failed logins,
// password resets, impersonation start/stop, agent/vendor seat changes).
// Writes are still mirrored to `system_activity_log` during cutover via
// `server/services/auth-events.ts` so other readers don't regress; the
// admin Security page reads from this table.
//
// `principalId` is the RLS-ready owner column — Supabase row-level
// policies will scope agent/investor self-service audit views on it
// once that migration lands.

export const AUTH_EVENT_TYPES = [
  "failed_login",
  "password_changed",
  "password_reset_requested",
  "password_reset_completed",
  "impersonation_started",
  "impersonation_stopped",
  "partner_impersonation_started",
  "partner_impersonation_stopped",
  "admin_impersonation_started",
  "admin_impersonation_stopped",
  "agent_login_success",
  "agent_login_failed",
  "vendor_login",
  "vendor_login_failed",
  "admin_agent_type_changed",
  "admin_agent_promoter_type_changed",
  "preview_started",
  "preview_stopped",
  "passkey_registered",
  "passkey_login",
  "passkey_renamed",
  "passkey_removed",
  "admin_login_success",
  "email_change_requested",
  "email_change_confirmed",
  "email_change_cancelled",
  "email_change_applied",
  "api_token_minted",
  // Bounded provisioner (PR-3) — machine seat + scoped key mutations.
  "provisioner_machine_created",
  "provisioner_menu_permissions_changed",
  "provisioner_divisions_changed",
  "provisioner_invite_resent",
  "provisioner_machine_demoted",
  "provisioner_api_token_minted",
  "provisioner_api_token_revoked",
] as const;
export type AuthEventType = (typeof AUTH_EVENT_TYPES)[number];

export const AUTH_PRINCIPAL_KINDS = [
  "user",
  "agent",
  "agent_user",
  "vendor_user",
  "unknown",
] as const;
export type AuthPrincipalKind = (typeof AUTH_PRINCIPAL_KINDS)[number];

export const AUTH_ACTOR_KINDS = ["self", "admin", "system"] as const;
export type AuthActorKind = (typeof AUTH_ACTOR_KINDS)[number];

export const authEvents = pgTable("auth_events", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  eventType: text("event_type").notNull(),
  principalKind: text("principal_kind").notNull(),
  principalId: varchar("principal_id"),
  actorKind: text("actor_kind"),
  actorId: varchar("actor_id"),
  status: text("status").notNull().default("success"),
  ip: text("ip"),
  userAgent: text("user_agent"),
  summary: text("summary"),
  metadata: jsonb("metadata"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export const insertAuthEventSchema = createInsertSchema(authEvents).omit({
  id: true,
  createdAt: true,
});

export type AuthEvent = typeof authEvents.$inferSelect;
export type InsertAuthEvent = z.infer<typeof insertAuthEventSchema>;

// ============================================================================
// Sentry feedback loop (Task #933)
// ============================================================================
// One row per Sentry issue. Sentry pushes issue + metric_alert events into
// `POST /api/webhooks/sentry`; the receiver upserts by `sentryIssueId`,
// keeps `eventCount` / `lastSeen` fresh, and never overwrites the triage
// fields once an admin has touched them. `level=performance` rows back
// metric_alert deliveries.
export const SENTRY_TRIAGE_STATUSES = [
  "new",
  "acknowledged",
  "snoozed",
  "assigned",
  "fixed",
  "wont_fix",
] as const;
export type SentryTriageStatus = (typeof SENTRY_TRIAGE_STATUSES)[number];

export const SENTRY_ISSUE_LEVELS = [
  "error",
  "warning",
  "info",
  "debug",
  "fatal",
  "performance",
] as const;
export type SentryIssueLevel = (typeof SENTRY_ISSUE_LEVELS)[number];

export const sentryIssues = pgTable("sentry_issues", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  sentryIssueId: text("sentry_issue_id").notNull().unique(),
  shortId: text("short_id"),
  projectSlug: text("project_slug"),
  title: text("title").notNull(),
  culprit: text("culprit"),
  level: text("level").notNull().default("error"),
  platform: text("platform"),
  permalink: text("permalink"),
  environment: text("environment"),
  release: text("release"),
  firstSeen: timestamp("first_seen"),
  lastSeen: timestamp("last_seen"),
  eventCount: integer("event_count").notNull().default(0),
  userCount: integer("user_count").notNull().default(0),
  // Snapshot of the previous webhook's eventCount so the alert evaluator
  // can compute a 1-hour spike ratio without joining a history table.
  prevEventCount: integer("prev_event_count").notNull().default(0),
  prevSnapshotAt: timestamp("prev_snapshot_at"),
  metricAlertRule: text("metric_alert_rule"),
  latestEvent: jsonb("latest_event").$type<Record<string, unknown> | null>(),
  // Triage block — never overwritten by webhook deliveries.
  triageStatus: text("triage_status").notNull().default("new"),
  assignedToUserId: varchar("assigned_to_user_id"),
  snoozedUntil: timestamp("snoozed_until"),
  notes: text("notes"),
  acknowledgedAt: timestamp("acknowledged_at"),
  acknowledgedBy: text("acknowledged_by"),
  resolvedAt: timestamp("resolved_at"),
  resolvedBy: text("resolved_by"),
  // Per-issue 24h alert dedupe so a flapping spike doesn't email every
  // 5-minute scheduler tick.
  lastAlertedAt: timestamp("last_alerted_at"),
  lastAlertKind: text("last_alert_kind"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const insertSentryIssueSchema = createInsertSchema(sentryIssues).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type SentryIssue = typeof sentryIssues.$inferSelect;
export type InsertSentryIssue = z.infer<typeof insertSentryIssueSchema>;

export const sentryTriageUpdateSchema = z.object({
  triageStatus: z.enum(SENTRY_TRIAGE_STATUSES).optional(),
  assignedToUserId: z.string().nullable().optional(),
  snoozeDays: z.number().int().positive().max(365).nullable().optional(),
  notes: z.string().max(4000).nullable().optional(),
});
export type SentryTriageUpdate = z.infer<typeof sentryTriageUpdateSchema>;

// ============================================================================
// Timeline Assumptions: I-956F approval-time assumptions
// ============================================================================
// One row per (projectType, teaPathway). The scheduled USCIS scraper writes
// the latest published baseline into `scrapedDays` for every row. Admins can
// optionally override that baseline in `overrideDays`. The effective assumed
// number of days for forecasting is `overrideDays ?? scrapedDays`.
export const I956F_PROJECT_TYPES = [
  "energy",
  "real_estate",
  "hospitality",
  "infrastructure",
  "manufacturing",
  "general",
] as const;
export type I956FProjectType = (typeof I956F_PROJECT_TYPES)[number];

export const I956F_PROJECT_TYPE_LABELS: Record<I956FProjectType, string> = {
  energy: "Energy",
  real_estate: "Real Estate",
  hospitality: "Hospitality",
  infrastructure: "Infrastructure",
  manufacturing: "Manufacturing",
  general: "Other",
};

// ---------------------------------------------------------------------------
// Project Types: dynamic, admin-managed taxonomy. Seeded from
// I956F_PROJECT_TYPES above (which is now a fallback list used only by the
// migration). At runtime, all pickers and validation read from this table.
// ---------------------------------------------------------------------------
export const projectTypes = pgTable("project_types", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  key: text("key").notNull().unique(),
  label: text("label").notNull(),
  // Two-level taxonomy: each asset-class row sits under a Division (e.g.
  // "real_estate", "energy"). The slug column is the historical
  // free-text key; `divisionId` (Task #1156 round 2) is the FK into
  // the divisions table and is the field new code should write.
  division: text("division"),
  divisionId: varchar("division_id"),
  sortOrder: integer("sort_order").notNull().default(0),
  disabled: boolean("disabled").notNull().default(false),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});
export type ProjectTypeRow = typeof projectTypes.$inferSelect;

// Shared list of US states (50 + DC). Two-letter codes are persisted and
// full names are shown in project and filing forms.
export const US_STATES: ReadonlyArray<{ code: string; name: string }> = [
  { code: "AL", name: "Alabama" },
  { code: "AK", name: "Alaska" },
  { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" },
  { code: "CA", name: "California" },
  { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" },
  { code: "DE", name: "Delaware" },
  { code: "DC", name: "District of Columbia" },
  { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" },
  { code: "HI", name: "Hawaii" },
  { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" },
  { code: "IN", name: "Indiana" },
  { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" },
  { code: "KY", name: "Kentucky" },
  { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" },
  { code: "MD", name: "Maryland" },
  { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" },
  { code: "MN", name: "Minnesota" },
  { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" },
  { code: "MT", name: "Montana" },
  { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" },
  { code: "NH", name: "New Hampshire" },
  { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" },
  { code: "NY", name: "New York" },
  { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" },
  { code: "OH", name: "Ohio" },
  { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" },
  { code: "PA", name: "Pennsylvania" },
  { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" },
  { code: "SD", name: "South Dakota" },
  { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" },
  { code: "UT", name: "Utah" },
  { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" },
  { code: "WA", name: "Washington" },
  { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" },
  { code: "WY", name: "Wyoming" },
];

// Quick lookup helpers for the dropdown — rendered values are pre-built
// once so the dropdown doesn't re-sort on every render.
export const US_STATE_BY_CODE: Record<string, { code: string; name: string }> = Object.fromEntries(
  US_STATES.map(s => [s.code, s]),
);
// Also map full state names (case-insensitive) back to codes so legacy
// free-text values like "Texas" or "texas" round-trip into the dropdown.
export const US_STATE_CODE_BY_NAME_LOWER: Record<string, string> = Object.fromEntries(
  US_STATES.map(s => [s.name.toLowerCase(), s.code]),
);

export const insertProjectTypeSchema = createInsertSchema(projectTypes).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertProjectType = z.infer<typeof insertProjectTypeSchema>;

// Project type "key" rules: lowercase letters, digits, underscores; 2-40 chars,
// must start with a letter. Matches the slug we auto-derive from the label.
export const PROJECT_TYPE_KEY_REGEX = /^[a-z][a-z0-9_]{1,39}$/;

export function deriveProjectTypeKey(label: string): string {
  return label
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .replace(/^([0-9])/, "n_$1")
    .slice(0, 40);
}

// Deal Economics — Stage 3.
export const DEAL_ECONOMICS_INCOME_MODES = ["spread", "upfront"] as const;
export type DealEconomicsIncomeMode = (typeof DEAL_ECONOMICS_INCOME_MODES)[number];

export const DealEconomicsFeeRowZod = z.object({
  label: z.string().trim().max(120),
  amount: z.number().nullable().optional(),
  // Free-text timing offset, e.g. "term_sheet+14d", "closing", "funding+12mo"
  timing: z.string().trim().max(80).optional().nullable(),
});
export type DealEconomicsFeeRow = z.infer<typeof DealEconomicsFeeRowZod>;

export const DealEconomicsZod = z.object({
  incomeMode: z.enum(DEAL_ECONOMICS_INCOME_MODES).optional().nullable(),
  feeSchedule: z.array(DealEconomicsFeeRowZod).max(40).optional(),
  perInvestorAdminFee: z.number().nullable().optional(),
  // Spread-mode fields — canonical names (Task #668). The legacy keys
  // (`rateToJcePct`, `rateToInvestorsPct`) are still accepted via
  // `.passthrough()` and resolved via `shared/deal-economics-rates.ts`
  // so older snapshots / term sheets keep reading correctly.
  developerInterestRatePct: z.number().nullable().optional(),
  investorInterestRatePct: z.number().nullable().optional(),
  // Upfront-mode fields
  upfrontLumpSum: z.number().nullable().optional(),
  upfrontReducedInvestorRatePct: z.number().nullable().optional(),
}).passthrough();
export type DealEconomics = z.infer<typeof DealEconomicsZod>;

export const DEAL_ECONOMICS_DEFAULT_FEE_SCHEDULE: DealEconomicsFeeRow[] = [
  { label: "Initial origination fee", amount: null, timing: "term_sheet+14d" },
  { label: "Diligence / structuring", amount: 125000, timing: "term_sheet+90d" },
  { label: "Closing tranche", amount: null, timing: "closing" },
];
export const DEAL_ECONOMICS_DEFAULT_PER_INVESTOR_ADMIN_FEE = 80000;

export const createProjectTypeSchema = z.object({
  label: z.string().trim().min(2, "Label must be at least 2 characters").max(80),
  key: z.string().trim().regex(PROJECT_TYPE_KEY_REGEX, "Key must be lowercase letters/digits/underscores, starting with a letter").optional(),
  division: z.string().trim().min(1).max(40).optional(),
  sortOrder: z.number().int().optional(),
});

export const updateProjectTypeSchema = z.object({
  label: z.string().trim().min(2).max(80).optional(),
  disabled: z.boolean().optional(),
  sortOrder: z.number().int().optional(),
  division: z.string().trim().min(1).max(40).nullable().optional(),
});

// Task #1189 — Per-(projectType × TEA pathway) USCIS approval-day matrix
// (scraper + admin override + table) was retired in favor of two flat
// platform constants based on TEA pathway only. See
// `shared/i956f-forecast.ts::approvalDaysForPathway` (rural=150d,
// everything else=270d). The previous `i956f_approval_assumptions`
// table is dropped by migrations/0131_drop_i956f_approval_assumptions.sql.

export interface FAQItem {
  id: string;
  question: string;
  answer: string;
  category: string;
  relatedQuestions?: string[];
}

export interface LegislationItem {
  id: string;
  title: string;
  status: "active" | "proposed" | "expired";
  effectiveDate: string;
  expirationDate?: string;
  summary: string;
  impact: string;
  grandfatheringInfo?: string;
}

export const FILE_SCAN_STATES = ["pending", "clean", "infected", "error"] as const;
export type FileScanState = typeof FILE_SCAN_STATES[number];

export const fileScans = pgTable("file_scans", {
  fileUrl: text("file_url").primaryKey(),
  state: text("state").notNull().default("pending"),
  result: text("result"),
  scanner: text("scanner"),
  scannedAt: timestamp("scanned_at").defaultNow(),
  // Wall-clock duration of the scanner.scan() call that produced this
  // verdict, in milliseconds. NULL for rows written by markScanPending
  // (the bytes have not been scanned yet) and for pre-scan size-guard
  // rejections that never invoked the scanner.
  durationMs: integer("duration_ms"),
});
export type FileScan = typeof fileScans.$inferSelect;

// Persisted history of admin Google Maps diagnostic probes. The /maps/test
// (Geocoding) and /maps/places-test (Places Autocomplete) admin endpoints
// each insert one row per invocation so the "Recent checks" panel can show
// trends across server restarts. Pruned to the last N rows (see
// MAPS_DIAGNOSTICS_HISTORY_LIMIT in server/routes/admin.ts) so unbounded
// growth is impossible.
export const mapsDiagnosticsRuns = pgTable("maps_diagnostics_runs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  runAt: timestamp("run_at").defaultNow().notNull(),
  // Which probe this row records: 'places' or 'geocoding'.
  check: text("check").notNull(),
  ok: boolean("ok").notNull(),
  // Verbatim Google status string (e.g. 'OK', 'REQUEST_DENIED', 'PERMISSION_DENIED').
  status: text("status"),
  // Lower-case error code from the route's response envelope ('request_denied',
  // 'missing_key', 'exception', etc.). Useful for grouping repeat failures.
  errorCode: text("error_code"),
  // Human-readable detail (the verbatim Google error_message or our envelope's
  // message field). Truncated at write time, see admin route handler.
  message: text("message"),
  httpStatus: integer("http_status"),
});
export type MapsDiagnosticsRun = typeof mapsDiagnosticsRuns.$inferSelect;

// Per-role notification system + in-app inbox (Task #934).
//
// Categories drive the per-(recipient, category) email opt-out
// preference and the inbox filter chips. Adding a new category
// requires only appending a string here — the schema/SQL are
// generic on `category`.
export const NOTIFICATION_CATEGORIES = [
  "handoff",
  "accreditation_status",
  "agent_milestone",
  "upload_scan_alert",
  "verification_reminder",
  "agent_document_reminder",
  "document_request",
  // Task #939 — direct messaging, threaded comments, and @mentions.
  "message",
  "comment",
  "mention",
  // Task #937 — cross-role broadcast of project milestones (TEA
  // confirmed, term sheet executed, business plan finalized, I-956F
  // filed/approved, etc.). Admin inbox only (no email).
  "project_milestone",
  // Task #938 — post-commitment investor lifecycle workspace.
  "project_update",
  "distribution",
  "tax_document",
  "petition_event",
  // Task #976 — Inbox messaging center. Three categories so each side
  // of the conversation can opt-out of the email half independently:
  //   * `inbox_admin_new_message`   — admin gets pinged on new threads
  //                                    + investor/agent replies.
  //   * `inbox_investor_reply`      — investor gets pinged on admin
  //                                    replies to their own thread.
  //   * `inbox_agent_investor_message` — referring agent gets pinged on
  //                                    admin replies / new messages on
  //                                    their referred investor's thread.
  "inbox_admin_new_message",
  "inbox_investor_reply",
  "inbox_agent_investor_message",
  // Admin-only business email alerts (preference-gated; classic
  // sendAdmin* paths check these before sending).
  "admin_commitment",
  "admin_project_interest",
  "admin_agent_application",
  "admin_booking",
  "admin_calendar_sync",
  "admin_vendor_submission",
  "admin_bd_compliance",
  "admin_ops_digest",
  "general",
] as const;
export type NotificationCategory = (typeof NOTIFICATION_CATEGORIES)[number];

// Recipient kinds the in-app inbox understands. `user` covers both
// admin and investor logins (gated by users.role); `agent` covers
// referral_agents seats; `vendor` covers vendor_users seats
// (Task #936 — economists / business plan writers). Sponsor team
// contacts still have no portal so they are not represented here.
export const NOTIFICATION_RECIPIENT_KINDS = ["user", "agent", "vendor"] as const;
export type NotificationRecipientKind = (typeof NOTIFICATION_RECIPIENT_KINDS)[number];

export const notifications = pgTable("notifications", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  recipientKind: text("recipient_kind").$type<NotificationRecipientKind>().notNull(),
  recipientId: varchar("recipient_id").notNull(),
  category: text("category").$type<NotificationCategory>().notNull(),
  title: text("title").notNull(),
  body: text("body"),
  link: text("link"),
  metadata: jsonb("metadata").$type<Record<string, unknown>>(),
  readAt: timestamp("read_at"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export const insertNotificationSchema = createInsertSchema(notifications).omit({
  id: true,
  readAt: true,
  createdAt: true,
});
export type Notification = typeof notifications.$inferSelect;
export type InsertNotification = z.infer<typeof insertNotificationSchema>;

export const notificationPreferences = pgTable("notification_preferences", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  recipientKind: text("recipient_kind").$type<NotificationRecipientKind>().notNull(),
  recipientId: varchar("recipient_id").notNull(),
  category: text("category").$type<NotificationCategory>().notNull(),
  emailEnabled: boolean("email_enabled").notNull().default(true),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const insertNotificationPreferenceSchema = createInsertSchema(notificationPreferences).omit({
  id: true,
  updatedAt: true,
});
export type NotificationPreference = typeof notificationPreferences.$inferSelect;
export type InsertNotificationPreference = z.infer<typeof insertNotificationPreferenceSchema>;

// ============================================================
// Task #936 — Vendor portal (economists + business plan writers)
// ============================================================
//
// `vendor_users` — login seats scoped to a single vendor record (an
// economist or a business plan writer). One vendor record can have
// multiple seats. Email is unique per (vendorType, vendorId, lower(email)).
// Mirrors the agent_users table pattern (Task #698) so admins manage
// access the same way they do for agent firms.

export const VENDOR_USER_TYPES = ["economist", "business_plan_writer"] as const;
export type VendorUserType = (typeof VENDOR_USER_TYPES)[number];

export const vendorUsers = pgTable(
  "vendor_users",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    vendorType: text("vendor_type").$type<VendorUserType>().notNull(),
    vendorId: varchar("vendor_id").notNull(),
    email: text("email").notNull(),
    // Task #1263 — `password_hash` dropped. Supabase Auth is the sole
    // credential store; the seat is linked via `auth_principal_id`.
    isActive: boolean("is_active").notNull().default(true),
    inviteToken: text("invite_token"),
    inviteTokenExpiresAt: timestamp("invite_token_expires_at"),
    lastLoginAt: timestamp("last_login_at"),
    // Task #1200 — Supabase auth prep. Nullable until the cutover
    // copies the matching `auth.users.id`. Partial-unique-when-set
    // index lives in migration 0133.
    authPrincipalId: varchar("auth_principal_id"),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (t) => ({
    uniqVendorEmail: uniqueIndex("vendor_users_vendor_email_lower_uniq").on(
      t.vendorType,
      t.vendorId,
      sql`lower(${t.email})`,
    ),
  }),
);

export type VendorUser = typeof vendorUsers.$inferSelect;
export type SafeVendorUser = Omit<VendorUser, "inviteToken">;

// Task #1200 — Memberships join table for vendor firms. Mirrors
// agent_firm_memberships but with a polymorphic `(vendorType,
// vendorId)` pair (the parent table depends on vendor type).
// Backfilled 1:1 from `vendor_users` by migration 0135.
export const vendorMemberships = pgTable(
  "vendor_memberships",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    vendorUserId: varchar("vendor_user_id")
      .notNull()
      .references(() => vendorUsers.id, { onDelete: "cascade" }),
    vendorType: text("vendor_type").$type<VendorUserType>().notNull(),
    vendorId: varchar("vendor_id").notNull(),
    // Mirrors vendor_users.auth_principal_id so RLS predicates can
    // key off this table directly. Backfilled by the auth cutover.
    authPrincipalId: varchar("auth_principal_id"),
    isActive: boolean("is_active").notNull().default(true),
    createdAt: timestamp("created_at").notNull().defaultNow(),
  },
  (t) => ({
    uniqUserVendor: uniqueIndex("idx_vendor_memberships_user_vendor_uniq").on(
      t.vendorUserId,
      t.vendorType,
      t.vendorId,
    ),
  }),
);

export type VendorMembership = typeof vendorMemberships.$inferSelect;

// `project_vendor_deliverables` — files & submissions uploaded by
// vendors. Used by the business-plan-writer workspace to store
// drafts, revisions, and the final plan; the economist workspace
// uses the existing project_job_creation_estimates table for
// numeric submissions, but the same table also records uploaded
// supporting documents from economists when present.

export const VENDOR_DELIVERABLE_KINDS = [
  "business_plan_draft",
  "business_plan_revision",
  "business_plan_final",
  "economist_supporting_doc",
] as const;
export type VendorDeliverableKind = (typeof VENDOR_DELIVERABLE_KINDS)[number];

export const projectVendorDeliverables = pgTable("project_vendor_deliverables", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  projectId: varchar("project_id").notNull().references(() => investmentProjects.id, { onDelete: "cascade" }),
  vendorType: text("vendor_type").$type<VendorUserType>().notNull(),
  vendorId: varchar("vendor_id").notNull(),
  vendorUserId: varchar("vendor_user_id"),
  kind: text("kind").$type<VendorDeliverableKind>().notNull(),
  revisionNumber: integer("revision_number").notNull().default(1),
  fileName: text("file_name").notNull(),
  fileUrl: text("file_url").notNull(),
  fileSize: integer("file_size"),
  contentType: text("content_type"),
  notes: text("notes"),
  submittedAt: timestamp("submitted_at").defaultNow().notNull(),
});

export type ProjectVendorDeliverable = typeof projectVendorDeliverables.$inferSelect;

// ===========================================================================
// Task #1334 — Project Marketing Materials Vault.
//
// Centralized per-project shelf for everything we have ever produced to
// promote a project (Marketing Master + derivatives, QR codes, link hubs,
// marketing pages, teaser content, vendor marketing collateral, plus
// hand-uploaded items) so admins can package the full set for a regional
// center on demand.
// ===========================================================================

export const PROJECT_MARKETING_VAULT_SOURCES = [
  "master",
  "derivative",
  "short_link",
  "qr_code",
  "link_hub",
  "marketing_page",
  "teaser",
  "vendor_deliverable",
  "manual",
  "deal_room_doc",
  "project_photo",
  "project_detail_page",
] as const;
export type ProjectMarketingVaultSource =
  (typeof PROJECT_MARKETING_VAULT_SOURCES)[number];

export const PROJECT_MARKETING_VAULT_ITEM_TYPES = [
  "image",
  "pdf",
  "deck",
  "video",
  "qr",
  "link",
  "page",
  "text",
  "document",
  "other",
] as const;
export type ProjectMarketingVaultItemType =
  (typeof PROJECT_MARKETING_VAULT_ITEM_TYPES)[number];

// Manual uploads added directly to the vault.
export const projectMarketingMaterials = pgTable("project_marketing_materials", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  // Null project id is a Veras-wide partner material. Vault rows always
  // keep a project id. See migrations/0414_partner_materials.sql.
  projectId: varchar("project_id")
    .references(() => investmentProjects.id, { onDelete: "cascade" }),
  title: text("title").notNull(),
  notes: text("notes"),
  fileUrl: text("file_url"),
  fileName: text("file_name"),
  fileSize: integer("file_size"),
  contentType: text("content_type"),
  externalUrl: text("external_url"),
  itemType: text("item_type").notNull().default("other").$type<ProjectMarketingVaultItemType>(),
  addedById: varchar("added_by_id").references(() => users.id, { onDelete: "set null" }),
  complianceReady: boolean("compliance_ready").notNull().default(true),
  archivedAt: timestamp("archived_at"),
  // Partner library. Null partnerKind means the row was never classified.
  // The admin library still lists it in its project folder. Partners do
  // not see it until a kind is set and partnerPublishedAt is set.
  // partnerPublishedAt null is a draft.
  partnerPublishedAt: timestamp("partner_published_at"),
  partnerKind: text("partner_kind").$type<"deck" | "pdf" | "one_pager" | "image" | "fact_sheet" | "video" | null>(),
  isSample: boolean("is_sample").notNull().default(false),
  slideCount: integer("slide_count"),
  pageCount: integer("page_count"),
  // ISO 639 code from SUPPORTED_AGENT_LANGUAGES. New uploads default to English.
  languageCode: text("language_code").notNull().default("en"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const insertProjectMarketingMaterialSchema = createInsertSchema(
  projectMarketingMaterials,
)
  .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
    archivedAt: true,
    partnerPublishedAt: true,
    partnerKind: true,
    isSample: true,
    slideCount: true,
    pageCount: true,
    languageCode: true,
  })
  .extend({
    title: z.string().trim().min(1).max(200),
    notes: z.string().trim().max(2000).nullable().optional(),
    fileUrl: z.string().trim().max(2048).nullable().optional(),
    fileName: z.string().trim().max(255).nullable().optional(),
    fileSize: z.number().int().nonnegative().nullable().optional(),
    contentType: z.string().trim().max(255).nullable().optional(),
    externalUrl: z.string().trim().url().max(2048).nullable().optional(),
    itemType: z.enum(PROJECT_MARKETING_VAULT_ITEM_TYPES).default("other"),
    complianceReady: z.boolean().default(true),
  });

export type InsertProjectMarketingMaterial = z.infer<
  typeof insertProjectMarketingMaterialSchema
>;
export type ProjectMarketingMaterial = typeof projectMarketingMaterials.$inferSelect;

// Investor present links for a published deck. The plaintext token is
// returned once; only the sha256 is stored.
export const partnerMaterialShareLinks = pgTable("partner_material_share_links", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  materialId: varchar("material_id")
    .notNull()
    .references(() => projectMarketingMaterials.id, { onDelete: "cascade" }),
  agentId: varchar("agent_id")
    .notNull()
    .references(() => referralAgents.id, { onDelete: "cascade" }),
  tokenHash: text("token_hash").notNull().unique(),
  revokedAt: timestamp("revoked_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export type PartnerMaterialShareLink = typeof partnerMaterialShareLinks.$inferSelect;

// Optional per-firm allow-list for a partner material. No rows means the
// audience rule applies (Veras-wide, or partners who can see the project).
export const partnerMaterialAudienceGrants = pgTable("partner_material_audience_grants", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  materialId: varchar("material_id")
    .notNull()
    .references(() => projectMarketingMaterials.id, { onDelete: "cascade" }),
  agentId: varchar("agent_id")
    .notNull()
    .references(() => referralAgents.id, { onDelete: "cascade" }),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  materialAgentUniq: uniqueIndex("partner_material_audience_grants_material_agent_uniq")
    .on(table.materialId, table.agentId),
}));

export type PartnerMaterialAudienceGrant = typeof partnerMaterialAudienceGrants.$inferSelect;

// Compliance-ready / archived overrides for AGGREGATED items (whose
// underlying rows live in their original tables and are out of scope to
// modify here).
export const projectMarketingItemOverrides = pgTable("project_marketing_item_overrides", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  projectId: varchar("project_id")
    .notNull()
    .references(() => investmentProjects.id, { onDelete: "cascade" }),
  source: text("source").notNull().$type<ProjectMarketingVaultSource>(),
  sourceId: text("source_id").notNull(),
  complianceReady: boolean("compliance_ready").notNull().default(true),
  archivedAt: timestamp("archived_at"),
  notes: text("notes"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export type ProjectMarketingItemOverride = typeof projectMarketingItemOverrides.$inferSelect;

// Secure recipient share package.
export const projectMarketingShareLinks = pgTable("project_marketing_share_links", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  projectId: varchar("project_id")
    .notNull()
    .references(() => investmentProjects.id, { onDelete: "cascade" }),
  token: text("token").notNull().unique(),
  recipientEmail: text("recipient_email"),
  recipientName: text("recipient_name"),
  message: text("message"),
  // Snapshot of {source, sourceId} pairs included in the package.
  includedItems: jsonb("included_items")
    .notNull()
    .default(sql`'[]'::jsonb`)
    .$type<Array<{ source: ProjectMarketingVaultSource; sourceId: string }>>(),
  expiresAt: timestamp("expires_at").notNull(),
  createdById: varchar("created_by_id").references(() => users.id, { onDelete: "set null" }),
  revokedAt: timestamp("revoked_at"),
  revokedById: varchar("revoked_by_id").references(() => users.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export type ProjectMarketingShareLink = typeof projectMarketingShareLinks.$inferSelect;

export const PROJECT_MARKETING_SHARE_EVENT_TYPES = [
  "view",
  "item_download",
  "download_zip",
] as const;
export type ProjectMarketingShareEventType =
  (typeof PROJECT_MARKETING_SHARE_EVENT_TYPES)[number];

export const projectMarketingShareEvents = pgTable("project_marketing_share_events", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  shareLinkId: varchar("share_link_id")
    .notNull()
    .references(() => projectMarketingShareLinks.id, { onDelete: "cascade" }),
  eventType: text("event_type").notNull().$type<ProjectMarketingShareEventType>(),
  itemRef: jsonb("item_ref").$type<{
    source: ProjectMarketingVaultSource;
    sourceId: string;
  } | null>(),
  ipHash: text("ip_hash"),
  userAgent: text("user_agent"),
  // Task #1359 — optional event metadata (e.g. itemsAdded/itemsSkipped for download_zip).
  meta: jsonb("meta").$type<Record<string, unknown> | null>(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export type ProjectMarketingShareEvent = typeof projectMarketingShareEvents.$inferSelect;

// ---------------------------------------------------------------------------
// Task #1360 — Project detail change log.
// Records per-field edits to the public-facing investmentProjects fields so
// admins can show exactly what the public page said at any point in time.
// ---------------------------------------------------------------------------

export const TRACKED_PROJECT_DETAIL_FIELDS = [
  "title",
  "subtitle",
  "description",
  "teaserContent",
  "location",
  "city",
  "state",
  "investmentMinimum",
  "investmentType",
  "investmentStructure",
  "positionType",
  "category",
  "jobCreationEstimate",
  "highlights",
  "coverImageUrl",
  "regionalCenterId",
  "teaQualificationStatus",
] as const;

export type TrackedProjectDetailField =
  (typeof TRACKED_PROJECT_DETAIL_FIELDS)[number];

export const projectDetailHistory = pgTable("project_detail_history", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  projectId: varchar("project_id")
    .notNull()
    .references(() => investmentProjects.id, { onDelete: "cascade" }),
  fieldName: text("field_name").notNull(),
  oldValue: jsonb("old_value"),
  newValue: jsonb("new_value"),
  changedById: varchar("changed_by_id").references(() => users.id, { onDelete: "set null" }),
  changedByName: text("changed_by_name"),
  changedAt: timestamp("changed_at").defaultNow().notNull(),
});

export type ProjectDetailHistoryEntry = typeof projectDetailHistory.$inferSelect;

// ===========================================================================
// Task #935 — Sponsor portal & project intake
// ===========================================================================

// Many-to-many link between sponsor users (users.role="sponsor") and the
// projects they own/help on. Created as a join table rather than an
// `ownerId` on investmentProjects so a project can have multiple sponsor
// teammates and a sponsor user can later cover several projects.
export const SPONSOR_TEAM_ROLES = ["owner", "member"] as const;
export type SponsorTeamRole = (typeof SPONSOR_TEAM_ROLES)[number];

export const projectSponsors = pgTable("project_sponsors", {
  projectId: varchar("project_id").notNull().references(() => investmentProjects.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  role: text("role").notNull().default("member").$type<SponsorTeamRole>(),
  invitedAt: timestamp("invited_at").defaultNow().notNull(),
  invitedById: varchar("invited_by_id"),
  lastLoginAt: timestamp("last_login_at"),
}, (t) => ({
  pk: primaryKey({ columns: [t.projectId, t.userId] }),
}));
export type ProjectSponsor = typeof projectSponsors.$inferSelect;

// Public sponsor intake submissions. Mirrors referralAgentApplications.
export const SPONSOR_APPLICATION_STATUSES = ["pending", "approved", "rejected"] as const;
export type SponsorApplicationStatus = (typeof SPONSOR_APPLICATION_STATUSES)[number];

export const sponsorApplications = pgTable("sponsor_applications", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  statusToken: varchar("status_token").notNull().default(sql`gen_random_uuid()`),
  // Project basics
  projectName: text("project_name").notNull(),
  sponsorOrg: text("sponsor_org").notNull(),
  assetClass: text("asset_class").notNull(),
  city: text("city"),
  state: text("state"),
  location: text("location"),
  targetRaise: integer("target_raise"),
  description: text("description"),
  // Contact
  contactFirstName: text("contact_first_name").notNull(),
  contactLastName: text("contact_last_name").notNull(),
  contactEmail: text("contact_email").notNull(),
  contactPhone: text("contact_phone"),
  // Misc
  notes: text("notes"),
  status: text("status").notNull().default("pending").$type<SponsorApplicationStatus>(),
  reviewedBy: varchar("reviewed_by"),
  reviewedAt: timestamp("reviewed_at"),
  reviewNote: text("review_note"),
  rejectionReason: text("rejection_reason"),
  createdProjectId: varchar("created_project_id"),
  createdSponsorUserId: varchar("created_sponsor_user_id"),
  submittedIp: text("submitted_ip"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
  deletedAt: timestamp("deleted_at"),
});

export const insertSponsorApplicationSchema = createInsertSchema(sponsorApplications).omit({
  id: true,
  statusToken: true,
  status: true,
  reviewedBy: true,
  reviewedAt: true,
  reviewNote: true,
  rejectionReason: true,
  createdProjectId: true,
  createdSponsorUserId: true,
  submittedIp: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
}).extend({
  projectName: z.string().trim().min(2, "Project name is required").max(200),
  sponsorOrg: z.string().trim().min(2, "Sponsor organization is required").max(200),
  assetClass: z.string().trim().min(1, "Asset class is required").max(80),
  city: z.string().trim().max(120).optional().nullable(),
  state: z.string().trim().max(80).optional().nullable(),
  location: z.string().trim().max(255).optional().nullable(),
  targetRaise: z.coerce.number().int().nonnegative().optional().nullable(),
  description: z.string().trim().max(4000).optional().nullable(),
  contactFirstName: z.string().trim().min(1, "First name is required").max(80),
  contactLastName: z.string().trim().min(1, "Last name is required").max(80),
  contactEmail: z.string().trim().toLowerCase().email("Please enter a valid email address"),
  contactPhone: z.string().trim().max(40).optional().nullable(),
  notes: z.string().trim().max(2000).optional().nullable(),
});
export type InsertSponsorApplication = z.infer<typeof insertSponsorApplicationSchema>;
export type SponsorApplication = typeof sponsorApplications.$inferSelect;

// Document requests admins raise to sponsors on a project. Sponsors satisfy
// these by uploading via the portal — the resulting projectInternalDocuments
// row is referenced via fulfilledDocumentId.
export const SPONSOR_DOCUMENT_REQUEST_STATUSES = ["open", "fulfilled", "cancelled"] as const;
export type SponsorDocumentRequestStatus = (typeof SPONSOR_DOCUMENT_REQUEST_STATUSES)[number];

export const sponsorDocumentRequests = pgTable("sponsor_document_requests", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  projectId: varchar("project_id").notNull().references(() => investmentProjects.id, { onDelete: "cascade" }),
  requestedById: varchar("requested_by_id"),
  title: text("title").notNull(),
  description: text("description"),
  status: text("status").notNull().default("open").$type<SponsorDocumentRequestStatus>(),
  fulfilledDocumentId: varchar("fulfilled_document_id"),
  fulfilledAt: timestamp("fulfilled_at"),
  fulfilledByUserId: varchar("fulfilled_by_user_id"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});
export const insertSponsorDocumentRequestSchema = createInsertSchema(sponsorDocumentRequests).omit({
  id: true,
  status: true,
  fulfilledDocumentId: true,
  fulfilledAt: true,
  fulfilledByUserId: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  title: z.string().trim().min(2, "Title is required").max(200),
  description: z.string().trim().max(2000).optional().nullable(),
});
export type SponsorDocumentRequest = typeof sponsorDocumentRequests.$inferSelect;
export type InsertSponsorDocumentRequest = z.infer<typeof insertSponsorDocumentRequestSchema>;

// Lightweight in-app inbox for sponsor users.
export const SPONSOR_NOTIFICATION_KINDS = [
  "stage_advanced",
  "document_requested",
  "commitment_received",
  "team_invited",
] as const;
export type SponsorNotificationKind = (typeof SPONSOR_NOTIFICATION_KINDS)[number];

export const sponsorNotifications = pgTable("sponsor_notifications", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  projectId: varchar("project_id").references(() => investmentProjects.id, { onDelete: "cascade" }),
  kind: text("kind").notNull().$type<SponsorNotificationKind>(),
  title: text("title").notNull(),
  body: text("body"),
  link: text("link"),
  readAt: timestamp("read_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});
export type SponsorNotification = typeof sponsorNotifications.$inferSelect;
// Task #940 — Centralised document request primitive.
//
// Any admin can request a single named document from a user (by id) or
// from a non-user (by email + optional name) for an optional project
// context. The recipient receives an email with a tokenised link and
// (when they're a user) an in-app inbox notification. They upload a
// single file through the existing scan+storage pipeline; the requester
// then accepts or declines the fulfilment.
//
// `token` is a UUID used as the bearer credential on the public upload
// page — it stays stable for the request's lifetime (similar to
// referral_agent_applications.status_token). Status transitions:
//   open       — initial state, awaiting recipient upload
//   fulfilled  — recipient uploaded a file, awaiting requester decision
//   accepted   — requester accepted the fulfilment (terminal)
//   declined   — requester declined; recipient may upload again (re-opens to open via separate logic) or stays declined
//   cancelled  — admin cancelled the request (terminal)
export const DOCUMENT_REQUEST_STATUSES = [
  "open",
  "fulfilled",
  "accepted",
  "declined",
  "cancelled",
] as const;
export type DocumentRequestStatus = (typeof DOCUMENT_REQUEST_STATUSES)[number];

export const documentRequests = pgTable("document_requests", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  token: varchar("token").notNull().unique().default(sql`gen_random_uuid()`),
  projectId: varchar("project_id"),
  // Requester is always an admin user. We log who created the request
  // so the project Documents tab can attribute it.
  requestedById: varchar("requested_by_id").notNull(),
  // Recipient is either a registered user (recipientUserId set) or an
  // off-platform party (recipientUserId NULL, recipientEmail set).
  recipientUserId: varchar("recipient_user_id"),
  recipientEmail: text("recipient_email").notNull(),
  recipientName: text("recipient_name"),
  title: text("title").notNull(),
  description: text("description"),
  status: text("status").$type<DocumentRequestStatus>().notNull().default("open"),
  dueAt: timestamp("due_at"),
  // Reminder bookkeeping for the daily cron.
  lastReminderAt: timestamp("last_reminder_at"),
  reminderCount: integer("reminder_count").notNull().default(0),
  // Fulfilment metadata — populated when the recipient uploads a file.
  fulfilledFileUrl: text("fulfilled_file_url"),
  fulfilledFileName: text("fulfilled_file_name"),
  fulfilledFileSize: bigint("fulfilled_file_size", { mode: "number" }),
  fulfilledContentType: text("fulfilled_content_type"),
  fulfilledAt: timestamp("fulfilled_at"),
  // Decision metadata — populated on accept / decline / cancel.
  decisionAt: timestamp("decision_at"),
  decisionByUserId: varchar("decision_by_user_id"),
  decisionNote: text("decision_note"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const insertDocumentRequestSchema = createInsertSchema(documentRequests).omit({
  id: true,
  token: true,
  status: true,
  lastReminderAt: true,
  reminderCount: true,
  fulfilledFileUrl: true,
  fulfilledFileName: true,
  fulfilledFileSize: true,
  fulfilledContentType: true,
  fulfilledAt: true,
  decisionAt: true,
  decisionByUserId: true,
  decisionNote: true,
  createdAt: true,
  updatedAt: true,
});
export type DocumentRequest = typeof documentRequests.$inferSelect;
export type InsertDocumentRequest = z.infer<typeof insertDocumentRequestSchema>;

// Append-only event log capturing every transition for audit and the
// project Documents tab timeline.
export const DOCUMENT_REQUEST_EVENT_KINDS = [
  "created",
  "reminded",
  "fulfilled",
  "accepted",
  "declined",
  "cancelled",
  "note",
  "reopened",
] as const;
export type DocumentRequestEventKind = (typeof DOCUMENT_REQUEST_EVENT_KINDS)[number];

export const DOCUMENT_REQUEST_ACTOR_KINDS = ["admin", "recipient", "system"] as const;
export type DocumentRequestActorKind = (typeof DOCUMENT_REQUEST_ACTOR_KINDS)[number];

export const documentRequestEvents = pgTable("document_request_events", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  requestId: varchar("request_id").notNull(),
  kind: text("kind").$type<DocumentRequestEventKind>().notNull(),
  actorKind: text("actor_kind").$type<DocumentRequestActorKind>().notNull(),
  actorId: varchar("actor_id"),
  note: text("note"),
  metadata: jsonb("metadata").$type<Record<string, unknown>>(),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export const insertDocumentRequestEventSchema = createInsertSchema(documentRequestEvents).omit({
  id: true,
  createdAt: true,
});
export type DocumentRequestEvent = typeof documentRequestEvents.$inferSelect;
export type InsertDocumentRequestEvent = z.infer<typeof insertDocumentRequestEventSchema>;

// =====================================================================
// Task #939 — direct messaging, threaded comments, and @mentions.
//
// Conversations are strictly 1:1 between an allowed pair of party
// identities. Each party is keyed by (kind, id) so investors / admins
// (kind = 'user') and referral agents (kind = 'agent') share the same
// table without role-specific duplication. Sponsor / vendor logins
// don't exist today; rows can still be written for future use.
//
// Allowed pairs (enforced at the route layer, see server/routes/messaging):
//   admin-user  ↔  investor-user
//   admin-user  ↔  agent
//   investor    ↔  their referring agent
//
// Group chat and file attachments are intentionally out of scope.
//
// `channel` + `externalId` + `status` exist so a future WhatsApp Cloud
// API provider can persist webhook-delivered messages and outbound
// `wamid` references against the same tables — without forcing a
// rewrite of the in-app surface. See server/services/messaging/provider.ts.
// =====================================================================

export const PARTY_KINDS = ["user", "agent"] as const;
export type PartyKind = (typeof PARTY_KINDS)[number];

// Transport for a conversation / message. `in_app` is the only one
// implemented today. Adding `whatsapp`, `sms`, etc. is a config-only
// change once the corresponding provider is registered.
export const MESSAGE_CHANNELS = ["in_app", "whatsapp", "sms", "email"] as const;
export type MessageChannel = (typeof MESSAGE_CHANNELS)[number];

export const MESSAGE_DIRECTIONS = ["outbound", "inbound"] as const;
export type MessageDirection = (typeof MESSAGE_DIRECTIONS)[number];

// Lifecycle status. `queued` → `sent` → `delivered` → `read`, or
// terminal `failed`. The in-app provider jumps straight to `sent`
// because there's no remote leg; WhatsApp will walk all four.
export const MESSAGE_STATUSES = [
  "queued",
  "sent",
  "delivered",
  "read",
  "failed",
] as const;
export type MessageStatus = (typeof MESSAGE_STATUSES)[number];

// ===========================================================================
// Task #976 — Inbox messaging center
// ===========================================================================
//
// `inbox_threads` and `inbox_messages` form the two-way messaging system
// that replaces the one-way "Investor Inquiries" list. We use the
// `inbox_*` prefix because the existing direct-messaging primitives
// (Task #939) already own the unprefixed `conversations` / `messages`
// table names and they serve a different surface (peer-to-peer DMs
// scoped to a single channel + party pair).
//
// A thread is rooted in either a registered investor (userId set) or
// an anonymous public form submission (userId null). Anonymous threads
// stay reachable to admins via the inbox UI; the original inquirer
// receives admin replies by email since they have no portal session.
//
// `sourceInquiryId` keeps a permanent FK back to the legacy
// investor_inquiries row that seeded the thread (or null for threads
// created post-migration). The legacy table is preserved so existing
// admin reports and CRM exports keep working.

export const INBOX_THREAD_STATUSES = ["open", "closed"] as const;
export type InboxThreadStatus = (typeof INBOX_THREAD_STATUSES)[number];

export const INBOX_SENDER_KINDS = [
  "admin",
  "investor",
  "agent",
  "anonymous",
  "system",
] as const;
export type InboxSenderKind = (typeof INBOX_SENDER_KINDS)[number];

// Thread origin — surfaced in the admin list so triage can tell apart
// a public-form lead from a logged-in investor's outreach (Task #976).
export const INBOX_THREAD_SOURCES = [
  "public_inquiry",
  "investor_initiated",
  "agent_initiated",
  "admin_initiated",
] as const;
export type InboxThreadSource = (typeof INBOX_THREAD_SOURCES)[number];

export const inboxThreads = pgTable(
  "inbox_threads",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    sourceInquiryId: varchar("source_inquiry_id").references(
      () => investorInquiries.id,
      { onDelete: "set null" },
    ),
    projectId: varchar("project_id").references(
      () => investmentProjects.id,
      { onDelete: "set null" },
    ),
    userId: varchar("user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    referralAgentId: varchar("referral_agent_id").references(
      () => referralAgents.id,
      { onDelete: "set null" },
    ),
    name: text("name").notNull(),
    email: text("email").notNull(),
    phone: text("phone"),
    country: text("country"),
    subject: text("subject").notNull(),
    source: text("source")
      .$type<InboxThreadSource>()
      .notNull()
      .default("public_inquiry"),
    status: text("status")
      .$type<InboxThreadStatus>()
      .notNull()
      .default("open"),
    lastMessageAt: timestamp("last_message_at").defaultNow().notNull(),
    lastMessageSenderKind: text("last_message_sender_kind")
      .$type<InboxSenderKind>()
      .notNull()
      .default("anonymous"),
    // Read pointers per role; track when the last person of each kind
    // viewed the thread so we can render unread badges in each portal.
    adminReadAt: timestamp("admin_read_at"),
    investorReadAt: timestamp("investor_read_at"),
    agentReadAt: timestamp("agent_read_at"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
  (t) => ({
    inboxThreadsUserIdx: index("inbox_threads_user_idx").on(t.userId),
    inboxThreadsAgentIdx: index("inbox_threads_agent_idx").on(t.referralAgentId),
    inboxThreadsLastMsgIdx: index("inbox_threads_last_msg_idx").on(t.lastMessageAt),
  }),
);

export const inboxMessages = pgTable(
  "inbox_messages",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    threadId: varchar("thread_id")
      .notNull()
      .references(() => inboxThreads.id, { onDelete: "cascade" }),
    senderKind: text("sender_kind").$type<InboxSenderKind>().notNull(),
    // For admin/investor/agent the senderId points at the row in the
    // matching table (users.id or referral_agents.id). For anonymous
    // and system it stays null — the original requester's contact is
    // captured on the thread row itself.
    senderId: varchar("sender_id"),
    senderName: text("sender_name").notNull(),
    body: text("body").notNull(),
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
  (t) => ({
    inboxMessagesThreadIdx: index("inbox_messages_thread_idx").on(t.threadId, t.createdAt),
  }),
);

export type InboxThread = typeof inboxThreads.$inferSelect;
export type InboxMessage = typeof inboxMessages.$inferSelect;

export const insertInboxThreadSchema = createInsertSchema(inboxThreads).omit({
  id: true,
  createdAt: true,
  lastMessageAt: true,
  lastMessageSenderKind: true,
  adminReadAt: true,
  investorReadAt: true,
  agentReadAt: true,
});
export type InsertInboxThread = z.infer<typeof insertInboxThreadSchema>;

export const insertInboxMessageSchema = createInsertSchema(inboxMessages).omit({
  id: true,
  createdAt: true,
});
export type InsertInboxMessage = z.infer<typeof insertInboxMessageSchema>;

export const conversations = pgTable("conversations", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  // Optional project context — admins use it to scope a thread to a
  // pipeline project for the compliance view; null = no project.
  projectId: varchar("project_id"),
  // Channel selects the transport. Same two parties on different
  // channels are distinct conversations so WhatsApp threads never
  // bleed into the in-app inbox.
  channel: text("channel").$type<MessageChannel>().notNull().default("in_app"),
  // Provider-side thread/conversation id (e.g. WhatsApp account-pair
  // identifier). Null on in-app rows.
  externalThreadId: text("external_thread_id"),
  participantAKind: text("participant_a_kind").$type<PartyKind>().notNull(),
  participantAId: varchar("participant_a_id").notNull(),
  participantBKind: text("participant_b_kind").$type<PartyKind>().notNull(),
  participantBId: varchar("participant_b_id").notNull(),
  lastReadAtA: timestamp("last_read_at_a"),
  lastReadAtB: timestamp("last_read_at_b"),
  lastMessageAt: timestamp("last_message_at"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});
export type Conversation = typeof conversations.$inferSelect;

export const messages = pgTable("messages", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  conversationId: varchar("conversation_id").notNull(),
  senderKind: text("sender_kind").$type<PartyKind>().notNull(),
  senderId: varchar("sender_id").notNull(),
  body: text("body").notNull(),
  // WhatsApp-friendly transport metadata. Defaults make existing in-app
  // call sites a no-op: outbound from the sender, immediately sent.
  channel: text("channel").$type<MessageChannel>().notNull().default("in_app"),
  direction: text("direction").$type<MessageDirection>().notNull().default("outbound"),
  status: text("status").$type<MessageStatus>().notNull().default("sent"),
  // Provider-issued id (WhatsApp `wamid`, Twilio SID, etc.). Unique
  // when not null so webhook redelivery dedupes naturally.
  externalId: text("external_id"),
  // Pre-approved template name + variables for outbound WhatsApp
  // template messages (required outside the 24h customer-care window).
  templateName: text("template_name"),
  templateVariables: jsonb("template_variables"),
  // Last error returned by the remote provider, for the failed state.
  errorMessage: text("error_message"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});
export type Message = typeof messages.$inferSelect;
export const insertMessageSchema = createInsertSchema(messages).omit({
  id: true,
  createdAt: true,
});
export type InsertMessage = z.infer<typeof insertMessageSchema>;

// Threaded comments + @mentions were rolled back in Task #966 — the
// product now keeps only the 1:1 direct-message inbox. The underlying
// `comments` and `mentions` tables are dropped by migration
// 0109_drop_messaging_comments_and_mentions.sql.

// =====================================================================
// Task #938 — Post-commitment investor lifecycle workspace.
//
// Once an investor signs their subscription agreement, the platform
// needs a structured place for ongoing project updates, distribution
// records, tax / fund document delivery, and a per-case petition
// timeline (I-526E filing → receipt → RFE → approval → I-829). All four
// tables live downstream of `investmentCommitments` so per-commitment
// scoping (and `ON DELETE CASCADE` cleanup) is the obvious source of
// truth. Notifications fan-out to the affected investor on every write.
//
// NOTE: Task #939 (messaging) introduced `project_update` as a
// `commentTargetTypes` value pointing at the same `projectUpdates`
// rows defined here, so threaded admin/sponsor comments can be
// attached to any update posted below.
// =====================================================================

export const projectUpdates = pgTable("project_updates", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  projectId: varchar("project_id").notNull().references(() => investmentProjects.id, { onDelete: "cascade" }),
  title: text("title").notNull(),
  body: text("body").notNull(),
  attachmentUrl: text("attachment_url"),
  attachmentName: text("attachment_name"),
  createdById: varchar("created_by_id").references(() => users.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});
export const insertProjectUpdateSchema = createInsertSchema(projectUpdates).omit({
  id: true,
  createdAt: true,
});
export type ProjectUpdate = typeof projectUpdates.$inferSelect;
export type InsertProjectUpdate = z.infer<typeof insertProjectUpdateSchema>;

export const DISTRIBUTION_TYPES = [
  "interest",
  "preferred_return",
  "principal",
  "other",
] as const;
export type DistributionType = (typeof DISTRIBUTION_TYPES)[number];

export const investorDistributions = pgTable("investor_distributions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  commitmentId: varchar("commitment_id").notNull().references(() => investmentCommitments.id, { onDelete: "cascade" }),
  distributionDate: timestamp("distribution_date").notNull(),
  // Stored in cents to avoid float drift on quarterly statements.
  amountCents: bigint("amount_cents", { mode: "number" }).notNull(),
  distributionType: text("distribution_type").$type<DistributionType>().notNull(),
  notes: text("notes"),
  createdById: varchar("created_by_id").references(() => users.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});
export const insertInvestorDistributionSchema = createInsertSchema(investorDistributions).omit({
  id: true,
  createdAt: true,
});
export type InvestorDistribution = typeof investorDistributions.$inferSelect;
export type InsertInvestorDistribution = z.infer<typeof insertInvestorDistributionSchema>;

export const INVESTOR_DOCUMENT_KINDS = [
  "k1",
  "tax_other",
  "fund_report",
  "other",
] as const;
export type InvestorDocumentKind = (typeof INVESTOR_DOCUMENT_KINDS)[number];

export const investorDocuments = pgTable("investor_documents", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  commitmentId: varchar("commitment_id").notNull().references(() => investmentCommitments.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  kind: text("kind").$type<InvestorDocumentKind>().notNull(),
  taxYear: integer("tax_year"),
  fileName: text("file_name").notNull(),
  filePath: text("file_path").notNull(),
  fileSize: bigint("file_size", { mode: "number" }),
  contentType: text("content_type"),
  uploadedById: varchar("uploaded_by_id").references(() => users.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});
export const insertInvestorDocumentSchema = createInsertSchema(investorDocuments).omit({
  id: true,
  createdAt: true,
});
export type InvestorDocument = typeof investorDocuments.$inferSelect;
export type InsertInvestorDocument = z.infer<typeof insertInvestorDocumentSchema>;

export const PETITION_EVENT_TYPES = [
  "i526e_filed",
  "i526e_receipt",
  "i526e_rfe_issued",
  "i526e_rfe_responded",
  "i526e_approved",
  "i526e_denied",
  "conditional_residency",
  "i829_filed",
  "i829_receipt",
  "i829_approved",
  "i829_denied",
  "permanent_residency",
] as const;
export type PetitionEventType = (typeof PETITION_EVENT_TYPES)[number];

export const investorPetitionEvents = pgTable("investor_petition_events", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  commitmentId: varchar("commitment_id").notNull().references(() => investmentCommitments.id, { onDelete: "cascade" }),
  // Task #1200 — direct owner column for sensitive-data RLS prep.
  // Always equals the owning investor (mirrors `investmentCommitments.userId`
  // for the row's `commitmentId`); enforced NOT NULL after the
  // 0133 backfill.
  ownerUserId: varchar("owner_user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  eventType: text("event_type").$type<PetitionEventType>().notNull(),
  occurredAt: timestamp("occurred_at").notNull(),
  receiptNumber: text("receipt_number"),
  notes: text("notes"),
  createdById: varchar("created_by_id").references(() => users.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});
export const insertInvestorPetitionEventSchema = createInsertSchema(investorPetitionEvents).omit({
  id: true,
  createdAt: true,
  // Task #1200 — derived from `commitmentId` in the storage layer.
  ownerUserId: true,
});
export type InvestorPetitionEvent = typeof investorPetitionEvents.$inferSelect;
export type InsertInvestorPetitionEvent = z.infer<typeof insertInvestorPetitionEventSchema>;

// ---------------------------------------------------------------------------
// Task #1016 — Project economics: per-project channel splits + GL map
// ---------------------------------------------------------------------------
// `projectChannelSplits` records the veras/partner basis-points share
// for each sourcing channel (direct/attorney/agent/broker). The active
// row for a given (project, channel, partner) carries `effective_to =
// NULL`; mutations close the prior row and insert a new one with an
// incremented `version`. `projectChannelSplitRevisions` is an
// append-only history mirror written by the same write path.
//
// `glAccountMap` is the QBO chart-of-accounts lookup — seeded by the
// 0119 migration. Read by `accountCodeFor()` in
// `shared/project-economics.ts` to label P&L lines.
export const PROJECT_CHANNEL_KINDS = ["direct", "attorney", "agent", "broker"] as const;
export type ProjectChannelKind = (typeof PROJECT_CHANNEL_KINDS)[number];

export const projectChannelSplits = pgTable("project_channel_splits", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  projectId: varchar("project_id").notNull().references(() => investmentProjects.id, { onDelete: "cascade" }),
  channel: text("channel").notNull().$type<ProjectChannelKind>(),
  verasShareBps: integer("veras_share_bps").notNull(),
  partnerShareBps: integer("partner_share_bps").notNull(),
  partnerId: varchar("partner_id"),
  effectiveFrom: timestamp("effective_from").notNull().defaultNow(),
  effectiveTo: timestamp("effective_to"),
  version: integer("version").notNull().default(1),
  notes: text("notes"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  createdByUserId: varchar("created_by_user_id"),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});
export type ProjectChannelSplit = typeof projectChannelSplits.$inferSelect;

export const projectChannelSplitRevisions = pgTable("project_channel_split_revisions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  projectId: varchar("project_id").notNull().references(() => investmentProjects.id, { onDelete: "cascade" }),
  channel: text("channel").notNull().$type<ProjectChannelKind>(),
  partnerId: varchar("partner_id"),
  verasShareBps: integer("veras_share_bps").notNull(),
  partnerShareBps: integer("partner_share_bps").notNull(),
  version: integer("version").notNull(),
  effectiveFrom: timestamp("effective_from").notNull(),
  effectiveTo: timestamp("effective_to"),
  notes: text("notes"),
  writtenAt: timestamp("written_at").notNull().defaultNow(),
  writtenByUserId: varchar("written_by_user_id"),
  reason: text("reason"),
});
export type ProjectChannelSplitRevision = typeof projectChannelSplitRevisions.$inferSelect;

export const glAccountMap = pgTable("gl_account_map", {
  code: text("code").primaryKey(),
  name: text("name").notNull(),
  accountType: text("account_type").notNull(),
  normalBalance: text("normal_balance").notNull(),
  description: text("description"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});
export type GlAccount = typeof glAccountMap.$inferSelect;

// Task #1016 — deal-economics revisions: every admin write to
// `investmentProjects.dealEconomics` (deal-shape picker, channel
// switch) snapshots the prior + new payload here so the audit trail
// survives in-place PATCH overwrites.
export const projectDealEconomicsRevisions = pgTable("project_deal_economics_revisions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  projectId: varchar("project_id").notNull().references(() => investmentProjects.id, { onDelete: "cascade" }),
  version: integer("version").notNull(),
  payload: jsonb("payload").notNull(),
  reason: text("reason"),
  writtenAt: timestamp("written_at").notNull().defaultNow(),
  writtenByUserId: varchar("written_by_user_id"),
});
export type ProjectDealEconomicsRevision = typeof projectDealEconomicsRevisions.$inferSelect;

export const FINANCIAL_DEAL_SHAPES = ["co_gp_revenue_share", "developer_fee"] as const;
export type FinancialDealShape = (typeof FINANCIAL_DEAL_SHAPES)[number];

export const internalOwnershipEntrySchema = z.object({
  owner: z.string().min(1).max(64),
  shareBps: z.number().int().min(0).max(10000),
});

export const developerFeeMilestoneSchema = z.object({
  amountCents: z.number().int().min(0),
  milestoneLabel: z.string().max(200).optional(),
  received: z.boolean().optional(),
});

// Milestone 0 — greenfield financial model config (parallel to legacy dealEconomics).
export const projectFinancialModels = pgTable("project_financial_models", {
  projectId: varchar("project_id").primaryKey().references(() => investmentProjects.id, { onDelete: "cascade" }),
  dealShape: text("deal_shape").notNull(),
  externalPartnerName: text("external_partner_name"),
  externalPartnerSplitBps: integer("external_partner_split_bps"),
  costsPooledWithPartner: boolean("costs_pooled_with_partner").notNull().default(false),
  adminFeePerInvestorCents: bigint("admin_fee_per_investor_cents", { mode: "number" }),
  capitalPerInvestorCents: bigint("capital_per_investor_cents", { mode: "number" }),
  baseInterestRateBps: integer("base_interest_rate_bps"),
  preferredReturnRateBps: integer("preferred_return_rate_bps").notNull().default(100),
  termYears: integer("term_years"),
  extensionYears: integer("extension_years").notNull().default(0),
  maxInvestors: integer("max_investors"),
  developerFeeSchedule: jsonb("developer_fee_schedule"),
  setupCostsCents: bigint("setup_costs_cents", { mode: "number" }).notNull().default(0),
  /** Seeded reference data — UI shows placeholder banner when true. */
  isPlaceholderSeed: boolean("is_placeholder_seed").notNull().default(false),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export type ProjectFinancialModel = typeof projectFinancialModels.$inferSelect;

export const FINANCIAL_COST_TIERS = ["corporate", "division", "project"] as const;
export type FinancialCostTier = (typeof FINANCIAL_COST_TIERS)[number];

export const financialCostItems = pgTable("financial_cost_items", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  amountCents: bigint("amount_cents", { mode: "number" }).notNull(),
  description: text("description").notNull(),
  incurredAt: date("incurred_at"),
  isPlaceholderSeed: boolean("is_placeholder_seed").notNull().default(false),
  sourceBillingInstallmentId: varchar("source_billing_installment_id").references((): AnyPgColumn => projectBillingInstallments.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const financialCostAssignments = pgTable("financial_cost_assignments", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  costItemId: varchar("cost_item_id").notNull().references(() => financialCostItems.id, { onDelete: "cascade" }),
  tier: text("tier").notNull(),
  divisionId: varchar("division_id").references(() => divisions.id, { onDelete: "set null" }),
  projectId: varchar("project_id").references(() => investmentProjects.id, { onDelete: "cascade" }),
  /** Shape A: true = pooled (deduct before external split); false = Veras-only (after external split). */
  sharedWithPartner: boolean("shared_with_partner").notNull().default(false),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export type FinancialCostItem = typeof financialCostItems.$inferSelect;
export type FinancialCostAssignment = typeof financialCostAssignments.$inferSelect;

export const financialCostReimbursements = pgTable("financial_cost_reimbursements", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  costItemId: varchar("cost_item_id").notNull().unique().references(() => financialCostItems.id, { onDelete: "cascade" }),
  paidBy: text("paid_by").notNull(),
  partnerShareCents: bigint("partner_share_cents", { mode: "number" }).notNull(),
  reimbursementStatus: text("reimbursement_status").notNull().default("pending"),
  submittedAt: timestamp("submitted_at"),
  dueAt: timestamp("due_at"),
  receivedAt: timestamp("received_at"),
  notes: text("notes"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export type FinancialCostReimbursement = typeof financialCostReimbursements.$inferSelect;

// Task #1078 — Investor flow forecast house defaults (single-row).
// `payload` carries the global default per-channel run rates (investors
// per month, with broker-dealer / immigration-attorney / foreign-
// migration-agent slots keyed by AGENT_TYPES + a `direct` slot), the
// default ramp shape ("flat" | "s_curve" | "front_loaded"), and the
// default subscription→funding lag in days. Per-project overrides
// live on investment_projects.investor_flow_overrides.
export const marketingHouseDefaults = pgTable("marketing_house_defaults", {
  id: text("id").primaryKey().default("singleton"),
  payload: jsonb("payload").notNull().default({}),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
  updatedBy: text("updated_by"),
});
export type MarketingHouseDefaultsRow = typeof marketingHouseDefaults.$inferSelect;

// Task #1397 — Internal entity vault.
// Tracks LLCs / JVs / NCEs the company forms internally and the
// formation documents attached to each. Admin-only; not linked to
// projects in this iteration. RLS lives in migration 0152.
// Unified ownership: each row is a person (free-text name) or a linked
// entity. Legacy rows without `kind` parse as person via the default.
export const ownerRowSchema = z
  .object({
    kind: z.enum(["person", "entity"]).default("person"),
    name: z.string().min(1).max(200),
    entityId: z.string().max(36).optional(),
    percentage: z.number().min(0).max(100),
  })
  .superRefine((row, ctx) => {
    if (row.kind === "entity" && !(row.entityId && row.entityId.trim())) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "entityId is required for entity owners",
        path: ["entityId"],
      });
    }
  });
export type OwnerRow = z.infer<typeof ownerRowSchema>;

export const debtHolderRowSchema = z.object({
  name: z.string().min(1).max(200),
  instrument: z.string().max(200).optional(),
  notes: z.string().max(1000).optional(),
});
export type DebtHolderRow = z.infer<typeof debtHolderRowSchema>;

export const ENTITY_DOCUMENT_TYPES = [
  "operating_agreement",
  "ein_letter",
  "certificate",
  "good_standing",
  "amendment",
  "k1",
  "tax_filing",
  "i956k",
  "other",
] as const;
export type EntityDocumentType = (typeof ENTITY_DOCUMENT_TYPES)[number];

export const PROJECT_ENTITY_ROLES = [
  "nce",
  "nce_manager",
  "jce",
  "holding_company",
] as const;
export type ProjectEntityRole = (typeof PROJECT_ENTITY_ROLES)[number];

export const PROJECT_ENTITY_ROLE_LABELS: Record<ProjectEntityRole, string> = {
  nce: "NCE",
  nce_manager: "NCE Manager",
  jce: "JCE (Project Company)",
  holding_company: "Holding Company",
};

/** Roles that may appear only once per project. */
export const PROJECT_ENTITY_SINGLETON_ROLES: readonly ProjectEntityRole[] = [
  "nce",
  "nce_manager",
  "holding_company",
];

/** Roles that sync a project_filing_entities row for I-956F repeats. */
export const PROJECT_ENTITY_FILING_ROLES: readonly ProjectEntityRole[] = [
  "nce_manager",
  "jce",
];

/** Legal entity type choices for Entity Vault / project parties (NCE, JCE, Manager). */
export const ENTITY_TYPES = [
  "LLC",
  "LLP",
  "LLLP",
  "C-Corp",
  "S-Corp",
  "Partnership",
  "JV",
  "Other",
] as const;
export type EntityTypeOption = (typeof ENTITY_TYPES)[number];

export const entities = pgTable("entities", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull(),
  entityType: text("entity_type").notNull().default("LLC"),
  jurisdiction: text("jurisdiction"),
  formationDate: text("formation_date"),
  ein: text("ein"),
  addressLine1: text("address_line1"),
  addressLine2: text("address_line2"),
  city: text("city"),
  state: text("state"),
  postalCode: text("postal_code"),
  country: text("country"),
  phone: text("phone"),
  email: text("email"),
  ownersText: text("owners_text"),
  // Task #1404 — structured ownership rows (replaces free-text ownersText for new entries)
  ownersJson: jsonb("owners_json").$type<OwnerRow[]>(),
  debtHoldersJson: jsonb("debt_holders_json").$type<DebtHolderRow[]>(),
  notes: text("notes"),
  // Task #1404 — subsidiary / parent entity tracking
  parentEntityId: varchar("parent_entity_id").references((): AnyPgColumn => entities.id, { onDelete: "set null" }),
  parentRelationship: text("parent_relationship"),
  createdById: varchar("created_by_id"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
}, (t) => ({
  nameIdx: index("idx_entities_name").on(t.name),
  parentIdx: index("idx_entities_parent").on(t.parentEntityId),
}));
export type Entity = typeof entities.$inferSelect;
export const insertEntitySchema = createInsertSchema(entities, {
  name: z.string().min(1, "Name is required").max(200),
  entityType: z.string().min(1).max(60),
  jurisdiction: z.string().max(120).nullish(),
  formationDate: z.string().max(40).nullish(),
  ein: z.string().max(40).nullish(),
  addressLine1: z.string().max(200).nullish(),
  addressLine2: z.string().max(200).nullish(),
  city: z.string().max(120).nullish(),
  state: z.string().max(120).nullish(),
  postalCode: z.string().max(40).nullish(),
  country: z.string().max(120).nullish(),
  phone: z.string().max(40).nullish(),
  email: z.string().max(254).nullish(),
  ownersText: z.string().max(4000).nullish(),
  ownersJson: z.array(ownerRowSchema).nullish(),
  debtHoldersJson: z.array(debtHolderRowSchema).nullish(),
  notes: z.string().max(8000).nullish(),
  parentEntityId: z.string().max(36).nullish(),
  parentRelationship: z.enum(["wholly_owned", "jv"]).nullish(),
}).omit({ id: true, createdAt: true, updatedAt: true, createdById: true });
export type InsertEntity = z.infer<typeof insertEntitySchema>;

export const projectEntities = pgTable("project_entities", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  projectId: varchar("project_id").notNull().references(() => investmentProjects.id, { onDelete: "cascade" }),
  role: text("role").notNull(),
  entityId: varchar("entity_id").references(() => entities.id, { onDelete: "set null" }),
  filingEntityId: varchar("filing_entity_id").references(() => projectFilingEntities.id, { onDelete: "set null" }),
  sortOrder: integer("sort_order").default(0).notNull(),
  notes: text("notes"),
  debtHoldersNa: boolean("debt_holders_na").default(false).notNull(),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
}, (t) => ({
  projectIdx: index("idx_project_entities_project").on(t.projectId),
  entityIdx: index("idx_project_entities_entity").on(t.entityId),
  filingEntityIdx: index("idx_project_entities_filing_entity").on(t.filingEntityId),
}));
export type ProjectEntity = typeof projectEntities.$inferSelect;
export const insertProjectEntitySchema = createInsertSchema(projectEntities, {
  role: z.enum(PROJECT_ENTITY_ROLES),
  entityId: z.string().max(36).nullish(),
  filingEntityId: z.string().max(36).nullish(),
  notes: z.string().max(4000).nullish(),
  debtHoldersNa: z.boolean().optional(),
}).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertProjectEntity = z.infer<typeof insertProjectEntitySchema>;

export const entityDocuments = pgTable("entity_documents", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  entityId: varchar("entity_id").notNull().references(() => entities.id, { onDelete: "cascade" }),
  documentType: text("document_type").notNull().default("other"),
  title: text("title").notNull(),
  fileUrl: text("file_url").notNull(),
  fileName: text("file_name"),
  fileSize: integer("file_size"),
  contentType: text("content_type"),
  uploadedById: varchar("uploaded_by_id"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (t) => ({
  entityIdx: index("idx_entity_documents_entity").on(t.entityId),
}));
export type EntityDocument = typeof entityDocuments.$inferSelect;
export const insertEntityDocumentSchema = createInsertSchema(entityDocuments, {
  documentType: z.string().min(1).max(60),
  title: z.string().min(1, "Title is required").max(200),
  fileUrl: z.string().min(1).max(2000),
  fileName: z.string().max(400).nullish(),
  contentType: z.string().max(200).nullish(),
}).omit({ id: true, createdAt: true, uploadedById: true });
export type InsertEntityDocument = z.infer<typeof insertEntityDocumentSchema>;

// Task #1419 — Terms & Privacy Policy acceptance at registration.
export const termsAcceptances = pgTable("terms_acceptances", {
  id:             varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  principalKind:  text("principal_kind").notNull(),
  email:          text("email").notNull(),
  userId:         varchar("user_id").references(() => users.id, { onDelete: "set null" }),
  termsVersion:   text("terms_version").notNull(),
  ipAddress:      text("ip_address"),
  userAgent:      text("user_agent"),
  acceptedAt:     timestamp("accepted_at").defaultNow().notNull(),
}, (t) => ({
  emailIdx:      index("idx_terms_acceptances_email").on(t.email),
  userIdIdx:     index("idx_terms_acceptances_user_id").on(t.userId),
  acceptedAtIdx: index("idx_terms_acceptances_accepted_at").on(t.acceptedAt),
  principalKindCheck: check("terms_acceptances_principal_kind_check", sql`principal_kind IN ('investor','agent','vendor','sponsor')`),
}));
export type TermsAcceptance = typeof termsAcceptances.$inferSelect;
export const insertTermsAcceptanceSchema = createInsertSchema(termsAcceptances).omit({
  id: true,
  acceptedAt: true,
});
export type InsertTermsAcceptance = z.infer<typeof insertTermsAcceptanceSchema>;

// ---------------------------------------------------------------------------
// Task #1450 — Admin E-Sign section
// ---------------------------------------------------------------------------

export const esignDocuments = pgTable("esign_documents", {
  id:                   varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name:                 text("name").notNull(),
  slug:                 text("slug").notNull().unique(),
  status:               text("status").notNull().default("active"),
  expiryDays:           integer("expiry_days").notNull().default(30),
  reminderDays:         integer("reminder_days").notNull().default(7),
  // Task #2474 — pointer to the currently published uploaded-PDF version.
  // NULL for software-generated docs (e.g. NDA). FK constraint added after
  // esign_document_versions table exists (migration 0223).
  publishedVersionId:   varchar("published_version_id"),
  createdAt:            timestamp("created_at").notNull().defaultNow(),
  updatedAt:            timestamp("updated_at").notNull().defaultNow(),
});

export const insertEsignDocumentSchema = createInsertSchema(esignDocuments).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertEsignDocument = z.infer<typeof insertEsignDocumentSchema>;
export type EsignDocument = typeof esignDocuments.$inferSelect;

// Task #2474 — one row per uploaded PDF version of an esign_documents template.
export const esignDocumentVersions = pgTable(
  "esign_document_versions",
  {
    id:                  varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    documentId:          varchar("document_id").notNull().references(() => esignDocuments.id, { onDelete: "cascade" }),
    versionNumber:       integer("version_number").notNull(),
    filePath:            text("file_path"),
    fileHash:            text("file_hash"),
    fileSize:            bigint("file_size", { mode: "number" }),
    uploadedByAdminId:   varchar("uploaded_by_admin_id").references(() => users.id, { onDelete: "set null" }),
    note:                text("note"),
    isPublished:         boolean("is_published").notNull().default(false),
    createdAt:           timestamp("created_at").notNull().defaultNow(),
  },
  (t) => ({
    docVersionUniq: uniqueIndex("esign_document_versions_doc_version_uniq").on(t.documentId, t.versionNumber),
    docIdx: index("esign_document_versions_doc_idx").on(t.documentId),
  }),
);

export const insertEsignDocumentVersionSchema = createInsertSchema(esignDocumentVersions).omit({
  id: true,
  createdAt: true,
});
export type InsertEsignDocumentVersion = z.infer<typeof insertEsignDocumentVersionSchema>;
export type EsignDocumentVersion = typeof esignDocumentVersions.$inferSelect;

// Task #2474 — immutable audit trail of meaningful changes to an esign_documents row.
export const esignDocumentChangelog = pgTable("esign_document_changelog", {
  id:             varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  documentId:     varchar("document_id").notNull().references(() => esignDocuments.id, { onDelete: "cascade" }),
  // 'version_published' | 'status_changed' | 'settings_edited'
  changeType:     text("change_type").notNull(),
  actorAdminId:   varchar("actor_admin_id").references(() => users.id, { onDelete: "set null" }),
  detail:         text("detail"),
  createdAt:      timestamp("created_at").notNull().defaultNow(),
});

export const insertEsignDocumentChangelogSchema = createInsertSchema(esignDocumentChangelog).omit({
  id: true,
  createdAt: true,
});
export type InsertEsignDocumentChangelog = z.infer<typeof insertEsignDocumentChangelogSchema>;
export type EsignDocumentChangelog = typeof esignDocumentChangelog.$inferSelect;

export const esignEnvelopes = pgTable("esign_envelopes", {
  id:                    varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  documentId:            varchar("document_id").notNull().references(() => esignDocuments.id, { onDelete: "restrict" }),
  packetId:              varchar("packet_id"),
  recipientUserId:       varchar("recipient_user_id").references(() => users.id, { onDelete: "set null" }),
  recipientEmail:        text("recipient_email").notNull(),
  sentAt:                timestamp("sent_at").notNull().defaultNow(),
  expiresAt:             timestamp("expires_at").notNull().default(sql`now() + interval '30 days'`),
  signedAt:              timestamp("signed_at"),
  expiredAt:             timestamp("expired_at"),
  lastReminderSentAt:    timestamp("last_reminder_sent_at"),
  token:                 varchar("token").notNull().unique().default(sql`gen_random_uuid()`),
  createdByAdminId:      varchar("created_by_admin_id").references(() => users.id, { onDelete: "set null" }),
  // Task #1453 — E-SIGN / UETA court-readiness audit trail captured at completion.
  ipAddress:             text("ip_address"),
  userAgent:             text("user_agent"),
  signatureMethod:       text("signature_method"),
  documentVersion:       text("document_version"),
  contentHash:           text("content_hash"),
  consentAcknowledgedAt: timestamp("consent_acknowledged_at"),
  intentAcknowledgedAt:  timestamp("intent_acknowledged_at"),
  sealedPdfPath:         text("sealed_pdf_path"),
  sealedPdfHash:         text("sealed_pdf_hash"),
  // Task #2474 — published PDF version at send time (NULL for software-generated docs).
  docPublishedVersionId: varchar("doc_published_version_id").references(() => esignDocumentVersions.id, { onDelete: "set null" }),
  createdAt:             timestamp("created_at").notNull().defaultNow(),
});

export const insertEsignEnvelopeSchema = createInsertSchema(esignEnvelopes).omit({
  id: true,
  sentAt: true,
  signedAt: true,
  expiredAt: true,
  token: true,
  createdAt: true,
});
export type InsertEsignEnvelope = z.infer<typeof insertEsignEnvelopeSchema>;
export type EsignEnvelope = typeof esignEnvelopes.$inferSelect;

// Phase 1 packet model. Contract templates are intentionally deferred; these
// tables establish NDA-only packets, NDA reuse, sequential dual-signing, and
// immutable lifecycle evidence without rewriting historical signature rows.
export const esignPackets = pgTable("esign_packets", {
  id:                   varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  packetType:           text("packet_type").notNull().default("nda_only"),
  projectId:            varchar("project_id").references(() => investmentProjects.id, { onDelete: "restrict" }),
  partnerFirmId:        varchar("partner_firm_id").references(() => referralAgents.id, { onDelete: "restrict" }),
  commercialPlanCode:   text("commercial_plan_code"),
  status:               text("status").notNull().default("pending"),
  manifestHash:         text("manifest_hash").notNull(),
  expiresAt:            timestamp("expires_at").notNull(),
  createdByAdminId:     varchar("created_by_admin_id").references(() => users.id, { onDelete: "set null" }),
  completedAt:          timestamp("completed_at"),
  createdAt:            timestamp("created_at").notNull().defaultNow(),
}, (t) => ({
  projectFirmPlanUniq: uniqueIndex("esign_packets_project_firm_active_plan_uniq")
    .on(t.projectId, t.partnerFirmId)
    .where(sql`commercial_plan_code IS NOT NULL AND status IN ('draft', 'pending', 'completed')`),
  expiryIdx: index("esign_packets_expiry_idx").on(t.expiresAt),
  partnerActiveNdaUniq: uniqueIndex("esign_packets_partner_active_nda_uniq")
    .on(t.partnerFirmId, t.packetType)
    .where(sql`partner_firm_id IS NOT NULL AND packet_type = 'nda_only' AND status IN ('pending', 'awaiting_countersignature')`),
}));

export const esignPacketParticipants = pgTable("esign_packet_participants", {
  id:                     varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  packetId:               varchar("packet_id").notNull().references(() => esignPackets.id, { onDelete: "cascade" }),
  principalType:          text("principal_type").notNull(),
  userId:                 varchar("user_id").references(() => users.id, { onDelete: "restrict" }),
  agentUserId:            varchar("agent_user_id").references(() => agentUsers.id, { onDelete: "restrict" }),
  signerRole:             text("signer_role").notNull(),
  routingOrder:           integer("routing_order").notNull().default(1),
  authenticationRequired: boolean("authentication_required").notNull().default(true),
  signedAt:               timestamp("signed_at"),
  intentAcknowledgedAt:   timestamp("intent_acknowledged_at"),
  signatureMethod:        text("signature_method"),
  signerName:             text("signer_name"),
  createdAt:              timestamp("created_at").notNull().defaultNow(),
}, (t) => ({
  packetIdx: index("esign_packet_participants_packet_idx").on(t.packetId),
  userRoleUniq: uniqueIndex("esign_packet_participants_user_role_uniq")
    .on(t.packetId, t.userId, t.signerRole)
    .where(sql`user_id IS NOT NULL`),
  agentRoleUniq: uniqueIndex("esign_packet_participants_agent_role_uniq")
    .on(t.packetId, t.agentUserId, t.signerRole)
    .where(sql`agent_user_id IS NOT NULL`),
}));

export const esignPacketDocuments = pgTable("esign_packet_documents", {
  id:                         varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  packetId:                   varchar("packet_id").notNull().references(() => esignPackets.id, { onDelete: "cascade" }),
  documentId:                 varchar("document_id").notNull().references(() => esignDocuments.id, { onDelete: "restrict" }),
  documentVersionId:          varchar("document_version_id").references(() => esignDocumentVersions.id, { onDelete: "restrict" }),
  documentType:               text("document_type").notNull().default("nda"),
  sequenceNumber:             integer("sequence_number").notNull(),
  requiresIndividualSignature: boolean("requires_individual_signature").notNull().default(true),
  satisfactionType:           text("satisfaction_type").notNull().default("pending"),
  reusedNdaSignatureId:       varchar("reused_nda_signature_id").references(() => ndaSignatures.id, { onDelete: "restrict" }),
  reusedAgentNdaSignatureId:  varchar("reused_agent_nda_signature_id").references(() => agentNdaSignatures.id, { onDelete: "restrict" }),
  contentHash:                text("content_hash"),
  createdAt:                  timestamp("created_at").notNull().defaultNow(),
}, (t) => ({
  packetIdx: index("esign_packet_documents_packet_idx").on(t.packetId),
  packetSequenceUniq: uniqueIndex("esign_packet_documents_packet_sequence_uniq").on(t.packetId, t.sequenceNumber),
}));

export const esignPacketEvents = pgTable("esign_packet_events", {
  id:                   varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  packetId:             varchar("packet_id").notNull().references(() => esignPackets.id, { onDelete: "cascade" }),
  eventType:            text("event_type").notNull(),
  actorPrincipalType:   text("actor_principal_type"),
  actorUserId:          varchar("actor_user_id").references(() => users.id, { onDelete: "set null" }),
  actorAgentUserId:     varchar("actor_agent_user_id").references(() => agentUsers.id, { onDelete: "set null" }),
  idempotencyKey:       text("idempotency_key").notNull(),
  metadata:             jsonb("metadata").$type<Record<string, unknown>>().notNull().default({}),
  occurredAt:           timestamp("occurred_at").notNull().defaultNow(),
}, (t) => ({
  packetTimeIdx: index("esign_packet_events_packet_time_idx").on(t.packetId, t.occurredAt),
  packetIdempotencyUniq: uniqueIndex("esign_packet_events_packet_idempotency_uniq").on(t.packetId, t.idempotencyKey),
}));

export const esignPacketDocumentAcceptances = pgTable("esign_packet_document_acceptances", {
  id:                     varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  packetDocumentId:       varchar("packet_document_id").notNull().references(() => esignPacketDocuments.id, { onDelete: "cascade" }),
  participantId:          varchar("participant_id").notNull().references(() => esignPacketParticipants.id, { onDelete: "cascade" }),
  satisfactionType:       text("satisfaction_type").notNull(),
  signatureMethod:        text("signature_method").notNull(),
  signerName:             text("signer_name").notNull(),
  consentAcknowledgedAt:  timestamp("consent_acknowledged_at").notNull(),
  intentAcknowledgedAt:   timestamp("intent_acknowledged_at").notNull(),
  documentPresentedAt:    timestamp("document_presented_at"),
  ipAddress:              text("ip_address"),
  userAgent:              text("user_agent"),
  ndaSignatureId:         varchar("nda_signature_id").references(() => ndaSignatures.id, { onDelete: "restrict" }),
  agentNdaSignatureId:    varchar("agent_nda_signature_id").references(() => agentNdaSignatures.id, { onDelete: "restrict" }),
  contentHash:            text("content_hash"),
  createdAt:              timestamp("created_at").notNull().defaultNow(),
}, (t) => ({
  documentIdx: index("esign_packet_document_acceptances_document_idx").on(t.packetDocumentId),
  participantUniq: uniqueIndex("esign_packet_document_acceptances_participant_uniq")
    .on(t.packetDocumentId, t.participantId),
}));

export const esignPacketArtifacts = pgTable("esign_packet_artifacts", {
  id:                 varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  packetId:           varchar("packet_id").notNull().references(() => esignPackets.id, { onDelete: "cascade" }),
  packetDocumentId:   varchar("packet_document_id").references(() => esignPacketDocuments.id, { onDelete: "cascade" }),
  artifactType:       text("artifact_type").notNull(),
  storagePath:        text("storage_path").notNull(),
  sha256Hash:         text("sha256_hash").notNull(),
  createdAt:          timestamp("created_at").notNull().defaultNow(),
}, (t) => ({
  packetIdx: index("esign_packet_artifacts_packet_idx").on(t.packetId),
  packetTypePathUniq: uniqueIndex("esign_packet_artifacts_packet_type_path_uniq")
    .on(t.packetId, t.artifactType, t.storagePath),
}));

export type EsignPacket = typeof esignPackets.$inferSelect;
export type EsignPacketParticipant = typeof esignPacketParticipants.$inferSelect;
export type EsignPacketDocument = typeof esignPacketDocuments.$inferSelect;
export type EsignPacketEvent = typeof esignPacketEvents.$inferSelect;
export type EsignPacketDocumentAcceptance = typeof esignPacketDocumentAcceptances.$inferSelect;
export type EsignPacketArtifact = typeof esignPacketArtifacts.$inferSelect;

export const esignCeremonySessions = pgTable("esign_ceremony_sessions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  subjectKind: text("subject_kind").notNull(),
  subjectId: varchar("subject_id").notNull(),
  actorKind: text("actor_kind").notNull(),
  agentUserId: varchar("agent_user_id").references(() => agentUsers.id, { onDelete: "cascade" }),
  userId: varchar("user_id").references(() => users.id, { onDelete: "cascade" }),
  status: text("status").notNull().default("in_progress"),
  documentHash: text("document_hash"),
  presentedAt: timestamp("presented_at"),
  consentAt: timestamp("consent_at"),
  intentAt: timestamp("intent_at"),
  declinedAt: timestamp("declined_at"),
  declineReason: text("decline_reason"),
  completedAt: timestamp("completed_at"),
  signatureMethod: text("signature_method"),
  signerName: text("signer_name"),
  signerTitle: text("signer_title"),
  fieldActs: jsonb("field_acts").$type<Array<Record<string, unknown>>>().notNull().default([]),
  executedPdfPath: text("executed_pdf_path"),
  executedPdfSha256: text("executed_pdf_sha256"),
  ipAddress: text("ip_address"),
  userAgent: text("user_agent"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export type EsignCeremonySession = typeof esignCeremonySessions.$inferSelect;

// ---------------------------------------------------------------------------
// Cookie consent audit trail
// ---------------------------------------------------------------------------

// Mirrors the DB enum created in migration 0187.
// Using pgEnum keeps the Drizzle type contract in sync with the Postgres
// CHECK constraint so invalid choice_kind values are rejected at the ORM
// layer before they reach the DB.
export const consentChoiceKindEnum = pgEnum("consent_choice_kind", [
  "accept_all",
  "essential_only",
  "custom",
  "changed",
]);

export const consentEvents = pgTable("consent_events", {
  id:            varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  visitorUuid:   varchar("visitor_uuid").notNull(),
  userId:        varchar("user_id").references(() => users.id, { onDelete: "set null" }),
  occurredAt:    timestamp("occurred_at").notNull().defaultNow(),
  policyVersion: varchar("policy_version").notNull(),
  bannerVersion: varchar("banner_version").notNull(),
  choiceKind:    consentChoiceKindEnum("choice_kind").notNull(),
  grants:        jsonb("grants").notNull(),
  pageUrl:       text("page_url"),
  userAgent:     text("user_agent"),
  ipHash:        varchar("ip_hash"),
});

export const insertConsentEventSchema = createInsertSchema(consentEvents).omit({
  id: true,
  occurredAt: true,
});
export type InsertConsentEvent = z.infer<typeof insertConsentEventSchema>;
export type ConsentEvent = typeof consentEvents.$inferSelect;

// ---------------------------------------------------------------------------
// Passkey credentials (WebAuthn / FIDO2)
// ---------------------------------------------------------------------------
// Each row represents one passkey registered by a user (Face ID, fingerprint,
// device PIN, etc.).  A user may have many passkeys — one per device.
//
// Column notes:
//  - credentialId       Base64URL-encoded credential ID returned by the
//                       authenticator; used as the lookup key during login.
//  - publicKey          Base64URL-encoded CBOR public key stored by the server
//                       to verify assertions.
//  - counter            Signature counter; incremented by the authenticator on
//                       every use.  We store it as a bigint because the spec
//                       allows up to 2^32-1 for U2F and higher for CTAP2.
//  - transports         JSON array of transport hints ('internal', 'hybrid', …)
//                       so the browser can choose the best transport.
//  - label              User-editable friendly name ("MacBook", "iPhone 15").
//                       Defaults to "My passkey" and can be changed at any time.
//  - authPrincipalId    The Supabase auth.users.id this credential belongs to.
//                       Also stored on the `users` row as `authPrincipalId`.
//  - userId             FK to our local `users.id` for easy RLS scoping.
//
// RLS: the migration enables FORCE RLS and installs a policy so `authenticated`
// sessions can only see/manage their own rows (matched via app_auth.uid()).

export const passkeyCredentials = pgTable(
  "passkey_credentials",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    userId: varchar("user_id").notNull(),
    authPrincipalId: text("auth_principal_id").notNull(),
    credentialId: text("credential_id").notNull(),
    publicKey: text("public_key").notNull(),
    counter: bigint("counter", { mode: "number" }).notNull().default(0),
    transports: text("transports").array(),
    label: text("label").notNull().default("My passkey"),
    createdAt: timestamp("created_at").notNull().defaultNow(),
    lastUsedAt: timestamp("last_used_at"),
  },
  (t) => ({
    credentialIdUniq: uniqueIndex("passkey_credentials_credential_id_uniq").on(t.credentialId),
    userIdx: index("passkey_credentials_user_id_idx").on(t.userId),
  }),
);

export const insertPasskeyCredentialSchema = createInsertSchema(passkeyCredentials).omit({
  id: true,
  createdAt: true,
  lastUsedAt: true,
});
export type InsertPasskeyCredential = z.infer<typeof insertPasskeyCredentialSchema>;
export type PasskeyCredential = typeof passkeyCredentials.$inferSelect;

// ---------------------------------------------------------------------------
// passkey_challenges — short-lived WebAuthn registration/authentication
// challenges. These used to live in a process-level Map, which breaks on
// multi-instance deployments (a /start that lands on instance A and a
// /finish that lands on instance B can no longer find the challenge). They
// are now persisted with a short TTL (60 s) so any container can complete a
// ceremony started by any other.
//
// `kind` separates the two ceremonies ("register" | "login"). `challengeKey`
// is the lookup key: the session id for registration, a random challenge id
// echoed back by the client for login. Rows are deleted on consume and any
// expired stragglers are pruned opportunistically at finish time.
//
// RLS: service-role only (default-deny). The route handlers run on the app
// pool (service_role / BYPASSRLS); there is no authenticated/anon access.

export const passkeyChallenges = pgTable(
  "passkey_challenges",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    kind: text("kind").notNull(),
    challengeKey: text("challenge_key").notNull(),
    challenge: text("challenge").notNull(),
    expiresAt: timestamp("expires_at").notNull(),
    createdAt: timestamp("created_at").notNull().defaultNow(),
  },
  (t) => ({
    kindKeyUniq: uniqueIndex("passkey_challenges_kind_key_uniq").on(t.kind, t.challengeKey),
    expiresAtIdx: index("passkey_challenges_expires_at_idx").on(t.expiresAt),
  }),
);

export const insertPasskeyChallengeSchema = createInsertSchema(passkeyChallenges).omit({
  id: true,
  createdAt: true,
});
export type InsertPasskeyChallenge = z.infer<typeof insertPasskeyChallengeSchema>;
export type PasskeyChallenge = typeof passkeyChallenges.$inferSelect;

// ---------------------------------------------------------------------------
// Task #2272 — Broker slot reservations
// ---------------------------------------------------------------------------
// A broker can "hold" a slot on a project for a specific investor for 2 weeks.
// Reservations consume a slot toward the project's EB-5 capacity (= eb5RaiseTarget
// ÷ investmentMinimum) and prevent oversubscription. When the investor's commitment
// is confirmed the reservation converts so the slot is counted once, not twice.
// Statuses: active → expired (sweep) | converted (on commitment confirm) | released (manual)
export const projectSlotReservations = pgTable(
  "project_slot_reservations",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    projectId: varchar("project_id").notNull().references(() => investmentProjects.id, { onDelete: "cascade" }),
    userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
    // The referral_agent who made the reservation (firm-scoped).
    agentId: varchar("agent_id").notNull().references((): AnyPgColumn => referralAgents.id, { onDelete: "cascade" }),
    status: text("status").notNull().default("active"),
    expiresAt: timestamp("expires_at").notNull(),
    // Stamped once when the partner is told the hold expires soon.
    expiryReminderSentAt: timestamp("expiry_reminder_sent_at"),
    createdAt: timestamp("created_at").notNull().defaultNow(),
    updatedAt: timestamp("updated_at").notNull().defaultNow(),
  },
  (t) => ({
    // Enforce one active reservation per (investor, project) pair.
    userProjectActiveIdx: uniqueIndex("psr_user_project_active_uniq")
      .on(t.userId, t.projectId)
      .where(sql`status = 'active'`),
    projectStatusExpiresIdx: index("psr_project_status_expires_idx").on(
      t.projectId,
      t.status,
      t.expiresAt,
    ),
    agentIdx: index("psr_agent_idx").on(t.agentId),
  }),
);

export const insertSlotReservationSchema = createInsertSchema(projectSlotReservations).omit({
  id: true,
  status: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertSlotReservation = z.infer<typeof insertSlotReservationSchema>;
export type SlotReservation = typeof projectSlotReservations.$inferSelect;

// Task #2729 — Marketing content items (video / content-marketing ideas).
// Content-production kanban (Task #2766). An admin captures a content idea
// (title + free-text script / talking-points), classifies it by content
// type and target platforms, assigns it to an admin team member, and moves
// it through a five-stage production-to-publish board:
//   backlog → filmed → needs_scheduling → scheduled → posted
// Items start in `backlog` (the holding pin) and are "moved forward" through
// production: entering `filmed` requires the raw recording file to be present,
// entering `needs_scheduling` requires the edited/final file to be present, and
// entering `scheduled` requires Buffer to accept at least one publication
// (Send to Buffer) — scheduled_at is written only then; a draft picker time
// alone is not enough. Riverside/batch
// final uploads land directly in `needs_scheduling`. Admin-only; RLS enforces
// app_auth.is_admin() through the request-scoped client.
export const CONTENT_ITEM_STATUSES = [
  "backlog",
  "filmed",
  "needs_scheduling",
  "scheduled",
  "posted",
] as const;
export type ContentItemStatus = (typeof CONTENT_ITEM_STATUSES)[number];

/** Legacy board status removed when Edited collapsed into Needs scheduling. */
export function normalizeContentItemStatus(
  status: string | null | undefined,
): ContentItemStatus | string {
  if (status === "edited") return "needs_scheduling";
  return status ?? "backlog";
}

export const CONTENT_ITEM_TYPES = [
  "video",
  "post",
  "poll",
  "story_reel",
  "carousel",
  "article_blog",
] as const;
export type ContentItemType = (typeof CONTENT_ITEM_TYPES)[number];

export const CONTENT_ITEM_PLATFORMS = [
  "instagram",
  "tiktok",
  "youtube_long",
  "youtube_shorts",
  "linkedin",
  "x",
  "facebook",
] as const;
export type ContentItemPlatform = (typeof CONTENT_ITEM_PLATFORMS)[number];

/** Exact on-camera names — colloquial forms break imports / rotation. */
export const CONTENT_SPOKESPERSONS = [
  "Alexandra Loveless",
  "Craig Borque",
  "Robert Kincannon",
  "Mitchell Saum",
] as const;
export type ContentSpokesperson = (typeof CONTENT_SPOKESPERSONS)[number];

export const CONTENT_MEDIA_TYPES = [
  "video",
  "image",
  "text",
  "article",
] as const;
export type ContentMediaType = (typeof CONTENT_MEDIA_TYPES)[number];

export const CONTENT_ORIENTATIONS = [
  "vertical",
  "horizontal",
  "square",
] as const;
export type ContentOrientation = (typeof CONTENT_ORIENTATIONS)[number];

export const CONTENT_PILLARS = [
  "eb5-education",
  "energy-thesis",
  "how-we-operate",
  "people",
] as const;
export type ContentPillar = (typeof CONTENT_PILLARS)[number];

/**
 * Controlled discovery topics for content items.
 * Kept small on purpose so similar posts stack (gallery filters, FAQ grounding).
 * Labels + FAQ category map live in shared/content-topics.ts.
 */
export const CONTENT_TOPICS = [
  "eb5-basics",
  "investment-amount",
  "tea-rural",
  "tea-high-unemployment",
  "set-asides",
  "regional-centers",
  "direct-eb5",
  "source-of-funds",
  "job-creation",
  "processing-times",
  "visa-backlogs",
  "family-dependents",
  "green-card-path",
  "after-approval",
  "natural-gas-lng",
  "oil-gas-operations",
  "energy-finance",
  "project-economics",
  "how-veras-works",
  "team",
] as const;
export type ContentTopic = (typeof CONTENT_TOPICS)[number];

/**
 * Content Ops primary clip Type — one per item when set.
 * Distinct from contentType (video/post/…) and from discovery topicTags.
 * Labels live in shared/content-clip-taxonomy.ts.
 */
export const CONTENT_CLIP_TYPES = [
  "origin",
  "honest-clock",
  "ground-level-jobs",
  "soft-bridge",
  "rural-processing",
  "risk-clarity",
  "job-creation",
  "at-risk",
  "diligence",
] as const;
export type ContentClipType = (typeof CONTENT_CLIP_TYPES)[number];

/**
 * Content Ops schedule Topics — many per item (controlled; not free tags).
 * Distinct from CONTENT_TOPICS / topic_tags (discovery / FAQ).
 * EB-3 is allowed here; Veras board/account routing stays EB-5 only.
 * Labels live in shared/content-clip-taxonomy.ts.
 */
export const CONTENT_CARD_TOPICS = [
  "EB-3",
  "EB-5",
  "RIA",
  "rural",
  "timelines",
  "I-526",
  "I-829",
  "SOF",
  "denials",
  "10-jobs",
  "regional-center",
  "direct-EB-5",
  "at-risk-capital",
  "no-guarantees",
  "agents",
  "unskilled",
  "fast-food",
  "visa-bulletin",
  "family",
  "personal-story",
] as const;
export type ContentCardTopic = (typeof CONTENT_CARD_TOPICS)[number];

/** Scheduler contentFormat — distinct from contentType / publication format. */
export const CONTENT_FORMATS = [
  "solo_ugc",
  "panel_clip",
  "long_form",
  "text",
  "graphic",
] as const;
export type ContentFormat = (typeof CONTENT_FORMATS)[number];

export const contentItems = pgTable("content_items", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  title: text("title").notNull(),
  scriptText: text("script_text"),
  /** Post-film transcript (Riverside export / paste) for AI caption generation. */
  transcriptText: text("transcript_text"),
  contentType: text("content_type").notNull().default("video"),
  platforms: text("platforms").array().notNull().default(sql`ARRAY[]::text[]`),
  audience: text("audience"),
  mediaBrief: text("media_brief"),
  mediaReferenceLinks: text("media_reference_links").array().notNull().default(sql`ARRAY[]::text[]`),
  assignedAdminId: varchar("assigned_admin_id").references(() => users.id, { onDelete: "set null" }),
  /**
   * Primary on-camera talent (canonical CONTENT_SPOKESPERSONS). Spec name:
   * primarySpokesperson — rotation / gap keys on this field only.
   */
  spokesperson: text("spokesperson"),
  /** Co-featured talent on panel clips; does not burn rotation gap. */
  spokespeople: text("spokespeople").array().notNull().default(sql`ARRAY[]::text[]`),
  /** Deterministic media inspection (ingest). */
  mediaType: text("media_type"),
  orientation: text("orientation"),
  durationSec: integer("duration_sec"),
  speakerCount: integer("speaker_count"),
  /** Transcript / topic classifier outputs (scheduler mix — not discovery tags). */
  pillar: text("pillar"),
  contentFormat: text("content_format"),
  /**
   * Controlled discovery topics (CONTENT_TOPICS). Prefer 1–3 so similar posts
   * stack for gallery filters and FAQ grounding — never freeform.
   */
  topicTags: text("topic_tags").array().notNull().default(sql`ARRAY[]::text[]`),
  /**
   * Content Ops primary Type (CONTENT_CLIP_TYPES). Nullable — existing rows
   * stay empty until tagged. Not contentType (video vs article).
   */
  clipType: text("clip_type"),
  /**
   * Content Ops schedule Topics (CONTENT_CARD_TOPICS). Empty until tagged.
   * Not topic_tags (discovery vocabulary).
   */
  topics: text("topics").array().notNull().default(sql`ARRAY[]::text[]`),
  /** Clip family — panel long-form + derived shorts share one sourceId. */
  sourceId: text("source_id"),
  /** Timely lane: may bump evergreen; never schedule past expiresAt. */
  schedulePriority: boolean("schedule_priority").notNull().default(false),
  expiresAt: timestamp("expires_at"),
  /** Locked disclaimer present on the asset record — required to schedule video. */
  disclaimerLocked: boolean("disclaimer_locked").notNull().default(false),
  classificationNeedsReview: boolean("classification_needs_review")
    .notNull()
    .default(false),
  classificationConfidence: real("classification_confidence"),
  status: text("status").notNull().default("backlog"),
  scheduledAt: timestamp("scheduled_at"),
  /** @deprecated Unused — always null. Column retained pending a later drop migration. */
  publishDeadlineAt: timestamp("publish_deadline_at"),
  approvedById: varchar("approved_by_id").references(() => users.id, { onDelete: "set null" }),
  approvedAt: timestamp("approved_at"),
  /** McFadyen request-edits note (Publish → Send back). Cleared on Approve. */
  editNote: text("edit_note"),
  editRequestedAt: timestamp("edit_requested_at"),
  editRequestedById: varchar("edit_requested_by_id").references(() => users.id, {
    onDelete: "set null",
  }),
  recordingFileUrl: text("recording_file_url"),
  recordingFileName: text("recording_file_name"),
  recordingFileSize: bigint("recording_file_size", { mode: "number" }),
  recordingContentType: text("recording_content_type"),
  finalFileUrl: text("final_file_url"),
  finalFileName: text("final_file_name"),
  finalFileSize: bigint("final_file_size", { mode: "number" }),
  finalContentType: text("final_content_type"),
  // Task #3075 — reject-with-reason. A rejected item keeps its status/column
  // but is stamped here so it drops out of the board's active listing while
  // preserving why it was cut and by whom (surfaced in the feedback records).
  rejectedAt: timestamp("rejected_at"),
  rejectionReason: text("rejection_reason"),
  rejectedById: varchar("rejected_by_id").references(() => users.id, { onDelete: "set null" }),
  /**
   * When true (default), posted items with a reconciled Buffer `external_url`
   * can appear on the public /media gallery and homepage teaser. Hide without
   * unpublishing.
   */
  showInGallery: boolean("show_in_gallery").notNull().default(true),
  /**
   * Approved content asset this card was created from (migration 0442).
   * The foreign key is declared in that migration so this table does not
   * form a type cycle with content_assets. Null when the card was not
   * created from an asset. Setting it does not approve the asset.
   */
  sourceAssetId: varchar("source_asset_id"),
  createdById: varchar("created_by_id").references(() => users.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const insertContentItemSchema = createInsertSchema(contentItems).omit({
  id: true,
  approvedById: true,
  approvedAt: true,
  editNote: true,
  editRequestedAt: true,
  editRequestedById: true,
  recordingFileUrl: true,
  recordingFileName: true,
  recordingFileSize: true,
  recordingContentType: true,
  finalFileUrl: true,
  finalFileName: true,
  finalFileSize: true,
  finalContentType: true,
  rejectedAt: true,
  rejectionReason: true,
  rejectedById: true,
  createdById: true,
  createdAt: true,
  updatedAt: true,
  sourceAssetId: true,
});
export type InsertContentItem = z.infer<typeof insertContentItemSchema>;
export type ContentItem = typeof contentItems.$inferSelect;

export const CONTENT_INGESTION_STATES = [
  "pending",
  "processing",
  "imported",
  "failed",
  "ignored",
] as const;
export type ContentIngestionState = (typeof CONTENT_INGESTION_STATES)[number];

export const contentIngestions = pgTable(
  "content_ingestions",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    source: text("source").notNull().default("direct_upload"),
    externalFileId: text("external_file_id").notNull(),
    sourceChecksum: text("source_checksum"),
    sourceFilename: text("source_filename").notNull(),
    sourceMimeType: text("source_mime_type").notNull(),
    sourceSize: bigint("source_size", { mode: "number" }).notNull(),
    sourceCreatedAt: timestamp("source_created_at"),
    sourceModifiedAt: timestamp("source_modified_at"),
    state: text("state").notNull().default("pending"),
    contentItemId: varchar("content_item_id").references(() => contentItems.id, {
      onDelete: "set null",
    }),
    attemptCount: integer("attempt_count").notNull().default(0),
    lastError: text("last_error"),
    discoveredAt: timestamp("discovered_at").defaultNow().notNull(),
    processedAt: timestamp("processed_at"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => ({
    sourceFileUniq: uniqueIndex("content_ingestions_source_file_uniq").on(
      table.source,
      table.externalFileId,
    ),
    sourceChecksumUniq: uniqueIndex(
      "content_ingestions_source_checksum_uniq",
    )
      .on(table.source, table.sourceChecksum)
      .where(sql`${table.sourceChecksum} IS NOT NULL`),
    stateIdx: index("idx_content_ingestions_state").on(
      table.state,
      table.discoveredAt,
    ),
    contentItemIdx: index("idx_content_ingestions_content_item").on(
      table.contentItemId,
    ),
  }),
);

export const CONTENT_PUBLICATION_STATUSES = [
  "draft",
  "ready",
  "submitting",
  "scheduled",
  "published",
  "failed",
  "canceled",
] as const;
export type ContentPublicationStatus =
  (typeof CONTENT_PUBLICATION_STATUSES)[number];
export const CONTENT_PUBLICATION_FORMATS = [
  "video",
  "reel",
  "story",
  "short",
  "image",
  "document",
  "link",
  "thread",
] as const;
export type ContentPublicationFormat =
  (typeof CONTENT_PUBLICATION_FORMATS)[number];
export const CONTENT_PUBLICATION_FORMATS_BY_PLATFORM = {
  instagram: ["video", "reel", "story", "image"],
  tiktok: ["video", "image"],
  youtube_long: ["video"],
  youtube_shorts: ["short"],
  linkedin: ["video", "image", "document", "link"],
  x: ["video", "image", "link", "thread"],
  facebook: ["video", "reel", "story", "image", "link"],
} as const satisfies Record<
  ContentItemPlatform,
  readonly ContentPublicationFormat[]
>;

/** Ordered tweet texts for format=thread (X). caption mirrors segments[0].text. */
export type ContentPublicationThreadSegment = {
  text: string;
};

/**
 * Buffer asset variant for createPost. Veras Content is the system of record;
 * Buffer only delivers. Design covers image/video/document/link; V1.5 ships
 * video + image + link (document follows).
 */
export const CONTENT_PUBLICATION_ASSET_KINDS = [
  "none",
  "image",
  "video",
  "document",
  "link",
] as const;
export type ContentPublicationAssetKind =
  (typeof CONTENT_PUBLICATION_ASSET_KINDS)[number];

export const CONTENT_PUBLICATION_YOUTUBE_PRIVACY = [
  "public",
  "unlisted",
  "private",
] as const;
export type ContentPublicationYoutubePrivacy =
  (typeof CONTENT_PUBLICATION_YOUTUBE_PRIVACY)[number];

/** Ordered Buffer assets (carousel = multiple images). External HTTPS or empty (use item final file). */
export type ContentPublicationAsset = {
  kind: "image" | "video" | "document";
  /** Public https URL, or omit to use the item's finalFileUrl via HMAC delivery. */
  url?: string;
  title?: string;
  thumbnailUrl?: string;
  alt?: string;
};

export const CONTENT_PUBLICATION_SCHEDULING_MODES = ["queue", "custom"] as const;
export type ContentPublicationSchedulingMode =
  (typeof CONTENT_PUBLICATION_SCHEDULING_MODES)[number];

export const contentPublications = pgTable(
  "content_publications",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    contentItemId: varchar("content_item_id")
      .notNull()
      .references(() => contentItems.id, { onDelete: "cascade" }),
    platform: text("platform").notNull(),
    caption: text("caption").notNull().default(""),
    format: text("format").notNull().default("video"),
    /** Buffer asset shape: video | image | document | link | none */
    assetKind: text("asset_kind").notNull().default("video"),
    /** Ordered assets for image/carousel/document; empty means use finalFileUrl when assetKind needs media. */
    assets: jsonb("assets").$type<ContentPublicationAsset[]>().notNull().default(sql`'[]'::jsonb`),
    /** X thread tweets when format=thread; empty for other formats. */
    threadSegments: jsonb("thread_segments")
      .$type<ContentPublicationThreadSegment[]>()
      .notNull()
      .default(sql`'[]'::jsonb`),
    linkUrl: text("link_url"),
    firstComment: text("first_comment"),
    youtubeTitle: text("youtube_title"),
    youtubePrivacy: text("youtube_privacy"),
    youtubeCategoryId: text("youtube_category_id"),
    schedulingMode: text("scheduling_mode").notNull().default("queue"),
    scheduledAt: timestamp("scheduled_at"),
    thumbnailOffsetMs: integer("thumbnail_offset_ms"),
    status: text("status").notNull().default("draft"),
    bufferPostId: text("buffer_post_id").unique(),
    mediaTokenExpiresAt: timestamp("media_token_expires_at"),
    externalUrl: text("external_url"),
    publishedAt: timestamp("published_at"),
    lastError: text("last_error"),
    attemptCount: integer("attempt_count").notNull().default(0),
    lastAttemptAt: timestamp("last_attempt_at"),
    lastSyncedAt: timestamp("last_synced_at"),
    createdById: varchar("created_by_id").references(() => users.id, {
      onDelete: "set null",
    }),
    approvedById: varchar("approved_by_id").references(() => users.id, {
      onDelete: "set null",
    }),
    approvedAt: timestamp("approved_at"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => ({
    itemPlatformUniq: uniqueIndex(
      "content_publications_item_platform_uniq",
    ).on(table.contentItemId, table.platform),
    statusIdx: index("idx_content_publications_status").on(
      table.status,
      table.scheduledAt,
    ),
    contentItemIdx: index("idx_content_publications_content_item").on(
      table.contentItemId,
    ),
  }),
);

export const insertContentIngestionSchema = createInsertSchema(
  contentIngestions,
).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertContentIngestion = z.infer<
  typeof insertContentIngestionSchema
>;
export type ContentIngestion = typeof contentIngestions.$inferSelect;

// Reusable content assets (PRD A1). Linked to content cards when one exists.
// Approval is a database trigger, not a column default.
export const contentAssets = pgTable("content_assets", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  assetType: text("asset_type").notNull(),
  sourceKind: text("source_kind").notNull(),
  sourceRef: text("source_ref").notNull(),
  approvalStatus: text("approval_status").notNull().default("proposed"),
  intendedScope: text("intended_scope"),
  contentItemId: varchar("content_item_id").references(() => contentItems.id, { onDelete: "set null" }),
  contentIngestionId: varchar("content_ingestion_id").references(() => contentIngestions.id, { onDelete: "set null" }),
  startMs: integer("start_ms"),
  endMs: integer("end_ms"),
  exactText: text("exact_text"),
  citation: text("citation"),
  // Private-bucket object key after an approved clip is copied. Null until
  // ASSET_FILE_EXPORT_GO is set and the export job runs. Never file bytes.
  filePointer: text("file_pointer"),
  cluster: text("cluster"),
  audiences: text("audiences").array(),
  language: text("language").notNull().default("en"),
  rightsStatus: text("rights_status").notNull().default("unknown"),
  disclaimerRequired: boolean("disclaimer_required").notNull().default(false),
  claimReviewRequired: boolean("claim_review_required").notNull().default(false),
  donSignedAt: timestamp("don_signed_at"),
  aiGenerated: boolean("ai_generated").notNull().default(false),
  timeRangeUnknown: boolean("time_range_unknown").notNull().default(false),
  transcriptLineKey: text("transcript_line_key"),
  extractionRunId: varchar("extraction_run_id"),
  expiresAt: timestamp("expires_at"),
  createdBy: text("created_by").notNull(),
  createdMethod: text("created_method").notNull(),
  confidence: integer("confidence"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export type ContentAsset = typeof contentAssets.$inferSelect;

export const contentAssetPeople = pgTable("content_asset_people", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  assetId: varchar("asset_id").notNull().references(() => contentAssets.id, { onDelete: "cascade" }),
  role: text("role").notNull(),
  contactId: varchar("contact_id"),
  userId: varchar("user_id"),
  displayName: text("display_name").notNull(),
  synthetic: boolean("synthetic").notNull().default(false),
});

export type ContentAssetPerson = typeof contentAssetPeople.$inferSelect;

export const contentAssetConsent = pgTable("content_asset_consent", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  personId: varchar("person_id").notNull().references(() => contentAssetPeople.id, { onDelete: "cascade" }),
  scope: text("scope").notNull(),
  status: text("status").notNull(),
  evidenceRef: text("evidence_ref"),
  recordedBy: text("recorded_by").notNull(),
  grantedAt: timestamp("granted_at"),
  revokedAt: timestamp("revoked_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export type ContentAssetConsent = typeof contentAssetConsent.$inferSelect;

export const contentAssetUses = pgTable("content_asset_uses", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  assetId: varchar("asset_id").notNull().references(() => contentAssets.id, { onDelete: "cascade" }),
  useKind: text("use_kind").notNull(),
  targetId: text("target_id").notNull(),
  scope: text("scope").notNull(),
  utmContent: text("utm_content").notNull(),
  usedAt: timestamp("used_at").defaultNow().notNull(),
  createdBy: text("created_by").notNull(),
});

export type ContentAssetUse = typeof contentAssetUses.$inferSelect;

// Content idea lane (PRD C1). Separate from content_items so an idea cannot
// be scheduled or published. Writes are gated by CONTENT_IDEA_QUEUE_GO.
export const contentIdeaRuns = pgTable("content_idea_runs", {
  id: varchar("id").primaryKey(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const contentIdeaHeldTopics = pgTable("content_idea_held_topics", {
  phrase: text("phrase").primaryKey(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const contentIdeas = pgTable("content_ideas", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  runId: varchar("run_id").notNull().references(() => contentIdeaRuns.id),
  dedupeKey: text("dedupe_key").notNull().unique(),
  hook: text("hook").notNull(),
  format: text("format").notNull(),
  audience: text("audience").array().notNull(),
  whyNow: text("why_now").notNull(),
  scoreTotal: integer("score_total").notNull(),
  scoreParts: jsonb("score_parts").notNull(),
  rubricVersion: text("rubric_version").notNull(),
  topics: text("topics").array().notNull().default(sql`ARRAY[]::text[]`),
  pillar: text("pillar"),
  expiresAt: timestamp("expires_at").notNull(),
  status: text("status").notNull().default("proposed"),
  killReason: text("kill_reason"),
  reviewedBy: varchar("reviewed_by"),
  reviewedAt: timestamp("reviewed_at"),
  contentItemId: varchar("content_item_id").references(() => contentItems.id, { onDelete: "set null" }),
  createdBy: text("created_by").notNull(),
  createdMethod: text("created_method").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const contentIdeaSources = pgTable("content_idea_sources", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  ideaId: varchar("idea_id").notNull().references(() => contentIdeas.id, { onDelete: "cascade" }),
  kind: text("kind").notNull(),
  url: text("url"),
  path: text("path"),
});

export const contentIdeaAssets = pgTable("content_idea_assets", {
  ideaId: varchar("idea_id").notNull().references(() => contentIdeas.id, { onDelete: "cascade" }),
  assetId: varchar("asset_id").notNull().references(() => contentAssets.id, { onDelete: "cascade" }),
}, (table) => ({
  pk: primaryKey({ columns: [table.ideaId, table.assetId] }),
}));

export const contentIdeaEvents = pgTable("content_idea_events", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  runId: varchar("run_id"),
  ideaId: varchar("idea_id"),
  kind: text("kind").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

// Append-only advisory audit log (migration 0451). A row records a
// rules check. It does not approve, publish, schedule, or send.
export const marketingCreativeAuditRuns = pgTable("marketing_creative_audit_runs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  draftRef: text("draft_ref").notNull(),
  draftType: text("draft_type").notNull(),
  draftTable: text("draft_table"),
  ruleVersion: text("rule_version").notNull(),
  result: text("result").notNull(),
  readyForReview: boolean("ready_for_review").notNull(),
  matches: jsonb("matches").notNull(),
  textSha256: text("text_sha256").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export type ContentIdea = typeof contentIdeas.$inferSelect;
export type MarketingCreativeAuditRun = typeof marketingCreativeAuditRuns.$inferSelect;

export const insertContentPublicationSchema = createInsertSchema(
  contentPublications,
).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertContentPublication = z.infer<
  typeof insertContentPublicationSchema
>;
export type ContentPublication = typeof contentPublications.$inferSelect;

// Task #3075 — optional post-film feedback note. When an admin moves an item
// into Filmed they're prompted for a quick, dismissible note (what they filmed,
// what will work, why). Skippable — the move succeeds with no note. Many notes
// can attach to one item (re-films / multiple takes); surfaced in the admin
// feedback records view alongside rejected ideas.
export const contentPostFilmNotes = pgTable("content_post_film_notes", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  contentItemId: varchar("content_item_id")
    .notNull()
    .references(() => contentItems.id, { onDelete: "cascade" }),
  note: text("note").notNull(),
  authorId: varchar("author_id").references(() => users.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const insertContentPostFilmNoteSchema = createInsertSchema(
  contentPostFilmNotes,
).omit({
  id: true,
  createdAt: true,
});
export type InsertContentPostFilmNote = z.infer<
  typeof insertContentPostFilmNoteSchema
>;
export type ContentPostFilmNote = typeof contentPostFilmNotes.$inferSelect;

// ---------------------------------------------------------------------------
// Qualified-lead referral ($750) + white-label + Admin Finances
// (migration 0307). Separate from funded-deal commission_* tables.
// ---------------------------------------------------------------------------

export {
  LEAD_REFERRAL_REFERRER_TYPES,
  LEAD_REFERRAL_BOUNTY_STATUSES,
  DEFAULT_LEAD_REFERRAL_RATE_CENTS,
  JUNK_STRIKE_PAUSE_THRESHOLD,
  type LeadReferralReferrerType,
  type LeadReferralBountyStatus,
} from "./lead-referral";

export const partnerProgramSettings = pgTable("partner_program_settings", {
  id: varchar("id").primaryKey().default("default"),
  leadReferralEnabledInvestor: boolean("lead_referral_enabled_investor").notNull().default(false),
  leadReferralEnabledAttorney: boolean("lead_referral_enabled_attorney").notNull().default(false),
  leadReferralEnabledAgent: boolean("lead_referral_enabled_agent").notNull().default(false),
  leadReferralDefaultRateCents: integer("lead_referral_default_rate_cents").notNull().default(75000),
  leadCompensationProjectId: varchar("lead_compensation_project_id").references((): AnyPgColumn => investmentProjects.id, { onDelete: "restrict" }),
  whiteLabelEnabledInvestor: boolean("white_label_enabled_investor").notNull().default(false),
  whiteLabelEnabledAttorney: boolean("white_label_enabled_attorney").notNull().default(false),
  whiteLabelEnabledAgent: boolean("white_label_enabled_agent").notNull().default(false),
  whiteLabelVerasFloorCents: integer("white_label_veras_floor_cents").notNull().default(0),
  silverTrailingFundedCount: integer("silver_trailing_funded_count").notNull().default(5),
  goldTrailingFundedCount: integer("gold_trailing_funded_count").notNull().default(10),
  trailingStatusMonths: integer("trailing_status_months").notNull().default(24),
  silverUpliftBps: integer("silver_uplift_bps").notNull().default(1000),
  goldUpliftBps: integer("gold_uplift_bps").notNull().default(2000),
  upfrontCapBps: integer("upfront_cap_bps").notNull().default(5000),
  silverAnniversaryMinFunded: integer("silver_anniversary_min_funded").notNull().default(3),
  goldAnniversaryMinFunded: integer("gold_anniversary_min_funded").notNull().default(5),
  paceBonusCents: integer("pace_bonus_cents").notNull().default(2_500_000),
  paceBonusFundingCount: integer("pace_bonus_funding_count").notNull().default(10),
  paceBonusWindowMonths: integer("pace_bonus_window_months").notNull().default(12),
  rebateCapCents: integer("rebate_cap_cents").notNull().default(2_000_000),
  netAdminApprovalFloorCents: integer("net_admin_approval_floor_cents").notNull().default(6_000_000),
  upfrontPayBusinessDays: integer("upfront_pay_business_days").notNull().default(15),
  backendPayDays: integer("backend_pay_days").notNull().default(30),
  introductionProtectionMonths: integer("introduction_protection_months").notNull().default(12),
  nonCircumventionMonths: integer("non_circumvention_months").notNull().default(24),
  terminationNoticeDays: integer("termination_notice_days").notNull().default(30),
  tailMonths: integer("tail_months").notNull().default(12),
  leadTerminationNoticeDays: integer("lead_termination_notice_days").notNull().default(30),
  leadPayDayOfMonth: integer("lead_pay_day_of_month").notNull().default(15),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
  updatedByUserId: varchar("updated_by_user_id").references((): AnyPgColumn => users.id, { onDelete: "set null" }),
});
export type PartnerProgramSettings = typeof partnerProgramSettings.$inferSelect;

export const leadReferralRateOverrides = pgTable("lead_referral_rate_overrides", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  referrerType: text("referrer_type").notNull(),
  referrerId: varchar("referrer_id").notNull(),
  rateCents: integer("rate_cents").notNull(),
  effectiveFrom: timestamp("effective_from").notNull().defaultNow(),
  effectiveTo: timestamp("effective_to"),
  createdByUserId: varchar("created_by_user_id").references((): AnyPgColumn => users.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});
export type LeadReferralRateOverride = typeof leadReferralRateOverrides.$inferSelect;

export const leadReferralReferrerState = pgTable("lead_referral_referrer_state", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  referrerType: text("referrer_type").notNull(),
  referrerId: varchar("referrer_id").notNull(),
  junkStrikeCount: integer("junk_strike_count").notNull().default(0),
  pausedAt: timestamp("paused_at"),
  pauseReason: text("pause_reason"),
  legalName: text("legal_name"),
  personVsEntity: text("person_vs_entity").notNull().default("person"),
  entityLegalName: text("entity_legal_name"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});
export type LeadReferralReferrerState = typeof leadReferralReferrerState.$inferSelect;

export const leadProofOfFundsDocuments = pgTable("lead_proof_of_funds_documents", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  leadUserId: varchar("lead_user_id").notNull().references((): AnyPgColumn => users.id, { onDelete: "cascade" }),
  fileName: text("file_name").notNull(),
  filePath: text("file_path").notNull(),
  fileSize: integer("file_size"),
  contentType: text("content_type"),
  uploadedByUserId: varchar("uploaded_by_user_id").references((): AnyPgColumn => users.id, { onDelete: "set null" }),
  uploadedAt: timestamp("uploaded_at").notNull().defaultNow(),
  removedAt: timestamp("removed_at"),
  removedByUserId: varchar("removed_by_user_id").references((): AnyPgColumn => users.id, { onDelete: "set null" }),
  notes: text("notes"),
});
export type LeadProofOfFundsDocument = typeof leadProofOfFundsDocuments.$inferSelect;

export const leadReferralTaxDocuments = pgTable("lead_referral_tax_documents", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  referrerType: text("referrer_type").notNull(),
  referrerId: varchar("referrer_id").notNull(),
  kind: text("kind").notNull(),
  fileName: text("file_name").notNull(),
  filePath: text("file_path").notNull(),
  fileSize: integer("file_size"),
  contentType: text("content_type"),
  uploadedAt: timestamp("uploaded_at").notNull().defaultNow(),
  uploadedByUserId: varchar("uploaded_by_user_id").references((): AnyPgColumn => users.id, { onDelete: "set null" }),
});
export type LeadReferralTaxDocument = typeof leadReferralTaxDocuments.$inferSelect;

export const leadReferralBounties = pgTable("lead_referral_bounties", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  referrerType: text("referrer_type").notNull(),
  referrerId: varchar("referrer_id").notNull(),
  leadUserId: varchar("lead_user_id").references((): AnyPgColumn => users.id, { onDelete: "set null" }),
  leadEmail: text("lead_email").notNull(),
  leadFirstName: text("lead_first_name"),
  leadLastName: text("lead_last_name"),
  personId: varchar("person_id").references((): AnyPgColumn => persons.id, { onDelete: "set null" }),
  status: text("status").notNull().default("submitted"),
  meetingBookingId: varchar("meeting_booking_id").references((): AnyPgColumn => callBookings.id, { onDelete: "set null" }),
  meetingCompletedAt: timestamp("meeting_completed_at"),
  meetingApprovedAt: timestamp("meeting_approved_at"),
  meetingApprovedByUserId: varchar("meeting_approved_by_user_id").references((): AnyPgColumn => users.id, { onDelete: "set null" }),
  pofDocumentId: varchar("pof_document_id").references((): AnyPgColumn => leadProofOfFundsDocuments.id, { onDelete: "set null" }),
  pofOnFileAt: timestamp("pof_on_file_at"),
  pofApprovedAt: timestamp("pof_approved_at"),
  pofApprovedByUserId: varchar("pof_approved_by_user_id").references((): AnyPgColumn => users.id, { onDelete: "set null" }),
  earnedAt: timestamp("earned_at"),
  dueDate: date("due_date"),
  rateSnapshotCents: integer("rate_snapshot_cents"),
  compensationEventId: varchar("compensation_event_id").references((): AnyPgColumn => partnerCompensationEvents.id, { onDelete: "restrict" }),
  paidAt: timestamp("paid_at"),
  paidAmountCents: integer("paid_amount_cents"),
  paymentRef: text("payment_ref"),
  paidByUserId: varchar("paid_by_user_id").references((): AnyPgColumn => users.id, { onDelete: "set null" }),
  heldAt: timestamp("held_at"),
  heldReason: text("held_reason"),
  junkReason: text("junk_reason"),
  junkedAt: timestamp("junked_at"),
  junkedByUserId: varchar("junked_by_user_id").references((): AnyPgColumn => users.id, { onDelete: "set null" }),
  notes: text("notes"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});
export type LeadReferralBounty = typeof leadReferralBounties.$inferSelect;

export const leadReferralAudit = pgTable("lead_referral_audit", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  bountyId: varchar("bounty_id").references((): AnyPgColumn => leadReferralBounties.id, { onDelete: "set null" }),
  referrerType: text("referrer_type"),
  referrerId: varchar("referrer_id"),
  action: text("action").notNull(),
  actorKind: text("actor_kind").notNull(),
  actorId: varchar("actor_id"),
  actorLabel: text("actor_label"),
  detail: jsonb("detail"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});
export type LeadReferralAuditEntry = typeof leadReferralAudit.$inferSelect;

export const WHITE_LABEL_APPLICATION_STATUSES = [
  "pending",
  "approved",
  "rejected",
  "withdrawn",
] as const;
export type WhiteLabelApplicationStatus =
  (typeof WHITE_LABEL_APPLICATION_STATUSES)[number];

export const whiteLabelApplications = pgTable("white_label_applications", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  applicantType: text("applicant_type").notNull(),
  applicantId: varchar("applicant_id").notNull(),
  clientPriceCents: integer("client_price_cents").notNull(),
  floorSnapshotCents: integer("floor_snapshot_cents").notNull(),
  status: text("status").notNull().default("pending"),
  notes: text("notes"),
  reviewedByUserId: varchar("reviewed_by_user_id").references((): AnyPgColumn => users.id, { onDelete: "set null" }),
  reviewedAt: timestamp("reviewed_at"),
  reviewNotes: text("review_notes"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});
export type WhiteLabelApplication = typeof whiteLabelApplications.$inferSelect;

export const whiteLabelPrograms = pgTable("white_label_programs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  applicationId: varchar("application_id").notNull().references((): AnyPgColumn => whiteLabelApplications.id, { onDelete: "restrict" }),
  partnerType: text("partner_type").notNull(),
  partnerId: varchar("partner_id").notNull(),
  clientPriceCents: integer("client_price_cents").notNull(),
  verasFloorCents: integer("veras_floor_cents").notNull(),
  clientBlind: boolean("client_blind").notNull().default(false),
  assignedByUserId: varchar("assigned_by_user_id").references((): AnyPgColumn => users.id, { onDelete: "set null" }),
  assignedAt: timestamp("assigned_at").notNull().defaultNow(),
  revokedAt: timestamp("revoked_at"),
  revokedByUserId: varchar("revoked_by_user_id").references((): AnyPgColumn => users.id, { onDelete: "set null" }),
  revokeReason: text("revoke_reason"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});
export type WhiteLabelProgram = typeof whiteLabelPrograms.$inferSelect;

// =====================================================================
// Per-investor subscription checklist + document vault (Phase 1)
// Project-scoped templates (AE seed includes Metropolitan Commercial Bank
// escrow slots). Admin collects docs in Veras before manual PRXY handoff.
// =====================================================================

export const subscriptionChecklistTemplates = pgTable("subscription_checklist_templates", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  projectId: varchar("project_id").notNull().references(() => investmentProjects.id, { onDelete: "cascade" }),
  name: text("name").notNull(),
  seedKey: text("seed_key").notNull().default("custom"),
  escrowBankName: text("escrow_bank_name"),
  isActive: boolean("is_active").notNull().default(true),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});
export type SubscriptionChecklistTemplate = typeof subscriptionChecklistTemplates.$inferSelect;

export const subscriptionChecklistTemplateItems = pgTable("subscription_checklist_template_items", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  templateId: varchar("template_id").notNull().references(() => subscriptionChecklistTemplates.id, { onDelete: "cascade" }),
  itemKey: text("item_key").notNull(),
  label: text("label").notNull(),
  description: text("description"),
  category: text("category").notNull(),
  sortOrder: integer("sort_order").notNull().default(0),
  applicability: text("applicability").notNull().default("required"),
  conditionKey: text("condition_key"),
  gate: text("gate").notNull().default("subscription_pack"),
  allowsMultipleFiles: boolean("allows_multiple_files").notNull().default(true),
  tracksExecutionStages: boolean("tracks_execution_stages").notNull().default(false),
  wetInkOnly: boolean("wet_ink_only").notNull().default(false),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});
export type SubscriptionChecklistTemplateItem = typeof subscriptionChecklistTemplateItems.$inferSelect;

export const investorSubscriptionChecklists = pgTable("investor_subscription_checklists", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  commitmentId: varchar("commitment_id").notNull().references(() => investmentCommitments.id, { onDelete: "cascade" }),
  templateId: varchar("template_id").notNull().references(() => subscriptionChecklistTemplates.id, { onDelete: "restrict" }),
  ownerUserId: varchar("owner_user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  isMarried: boolean("is_married"),
  hasReplacementSubscriber: boolean("has_replacement_subscriber").notNull().default(false),
  subscriberLegalName: text("subscriber_legal_name"),
  subscriberAddress: text("subscriber_address"),
  subscriberTin: text("subscriber_tin"),
  prxyInvestorCreatedAt: timestamp("prxy_investor_created_at"),
  prxyDocumentsUploadedAt: timestamp("prxy_documents_uploaded_at"),
  readyForPrxyFundMovementAt: timestamp("ready_for_prxy_fund_movement_at"),
  prxyNotes: text("prxy_notes"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});
export type InvestorSubscriptionChecklist = typeof investorSubscriptionChecklists.$inferSelect;

export const investorSubscriptionChecklistItems = pgTable("investor_subscription_checklist_items", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  checklistId: varchar("checklist_id").notNull().references(() => investorSubscriptionChecklists.id, { onDelete: "cascade" }),
  itemKey: text("item_key").notNull(),
  status: text("status").notNull().default("not_started"),
  notes: text("notes"),
  signaturePagesReceivedAt: timestamp("signature_pages_received_at"),
  fullExecutedReceivedAt: timestamp("full_executed_received_at"),
  statusUpdatedAt: timestamp("status_updated_at"),
  statusUpdatedBy: varchar("status_updated_by").references(() => users.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});
export type InvestorSubscriptionChecklistItem = typeof investorSubscriptionChecklistItems.$inferSelect;

export const investorSubscriptionChecklistFiles = pgTable("investor_subscription_checklist_files", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  checklistItemId: varchar("checklist_item_id").notNull().references(() => investorSubscriptionChecklistItems.id, { onDelete: "cascade" }),
  fileName: text("file_name").notNull(),
  filePath: text("file_path").notNull(),
  fileSize: bigint("file_size", { mode: "number" }),
  contentType: text("content_type"),
  fileLabel: text("file_label"),
  uploadedById: varchar("uploaded_by_id").references(() => users.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});
export type InvestorSubscriptionChecklistFile = typeof investorSubscriptionChecklistFiles.$inferSelect;

// ---------------------------------------------------------------------------
// Counsel due-diligence tracker (migration 0394). Vocabulary + seed catalog
// live in shared/due-diligence.ts.
// ---------------------------------------------------------------------------
export const dueDiligenceChecklists = pgTable("due_diligence_checklists", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  projectId: varchar("project_id").notNull().references(() => investmentProjects.id, { onDelete: "cascade" }),
  entityScope: text("entity_scope").notNull().default("other"),
  title: text("title").notNull(),
  entityName: text("entity_name"),
  projectEntityId: varchar("project_entity_id").references(() => projectEntities.id, { onDelete: "set null" }),
  counselFirm: text("counsel_firm"),
  versionLabel: text("version_label"),
  sourceDocumentName: text("source_document_name"),
  datedOn: date("dated_on"),
  seedKey: text("seed_key"),
  sections: jsonb("sections").$type<DueDiligenceSectionDef[]>().notNull().default([]),
  sortOrder: integer("sort_order").notNull().default(0),
  createdById: varchar("created_by_id").references(() => users.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
}, (t) => ({
  projectIdx: index("due_diligence_checklists_project_idx").on(t.projectId, t.sortOrder),
  seedUniq: uniqueIndex("due_diligence_checklists_seed_uniq")
    .on(t.projectId, t.seedKey, t.entityScope)
    .where(sql`seed_key IS NOT NULL`),
}));
export type DueDiligenceChecklist = typeof dueDiligenceChecklists.$inferSelect;

export const dueDiligenceItems = pgTable("due_diligence_items", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  checklistId: varchar("checklist_id").notNull().references(() => dueDiligenceChecklists.id, { onDelete: "cascade" }),
  projectId: varchar("project_id").notNull().references(() => investmentProjects.id, { onDelete: "cascade" }),
  sectionLetter: text("section_letter").notNull(),
  itemNumber: integer("item_number").notNull(),
  itemKey: text("item_key").notNull(),
  title: text("title").notNull(),
  counselNote: text("counsel_note"),
  responseText: text("response_text"),
  status: text("status").$type<DueDiligenceItemStatus>().notNull().default("not_started"),
  priority: text("priority").$type<DueDiligencePriority>().notNull().default("normal"),
  ownerUserId: varchar("owner_user_id").references(() => users.id, { onDelete: "set null" }),
  dueDate: date("due_date"),
  sentToCounselAt: timestamp("sent_to_counsel_at"),
  receivedByCounselAt: timestamp("received_by_counsel_at"),
  statusUpdatedAt: timestamp("status_updated_at"),
  statusUpdatedBy: varchar("status_updated_by").references(() => users.id, { onDelete: "set null" }),
  sortOrder: integer("sort_order").notNull().default(0),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
}, (t) => ({
  checklistIdx: index("due_diligence_items_checklist_idx").on(t.checklistId, t.sortOrder),
  projectIdx: index("due_diligence_items_project_idx").on(t.projectId),
  keyUniq: uniqueIndex("due_diligence_items_key_uniq").on(t.checklistId, t.itemKey),
}));
export type DueDiligenceItem = typeof dueDiligenceItems.$inferSelect;

export const dueDiligenceQuestionnaireTemplates = pgTable("due_diligence_questionnaire_templates", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  projectId: varchar("project_id").notNull().references(() => investmentProjects.id, { onDelete: "cascade" }),
  entityScope: text("entity_scope").notNull().default("other"),
  title: text("title").notNull(),
  reLine: text("re_line"),
  addressee: text("addressee"),
  intro: text("intro"),
  certification: text("certification"),
  versionLabel: text("version_label"),
  sourceDocumentName: text("source_document_name"),
  seedKey: text("seed_key"),
  questions: jsonb("questions").$type<DueDiligenceQuestionDef[]>().notNull().default([]),
  sortOrder: integer("sort_order").notNull().default(0),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
}, (t) => ({
  projectIdx: index("due_diligence_questionnaire_templates_project_idx").on(t.projectId, t.sortOrder),
  seedUniq: uniqueIndex("due_diligence_questionnaire_templates_seed_uniq")
    .on(t.projectId, t.seedKey, t.entityScope)
    .where(sql`seed_key IS NOT NULL`),
}));
export type DueDiligenceQuestionnaireTemplate = typeof dueDiligenceQuestionnaireTemplates.$inferSelect;

export const dueDiligenceQuestionnaires = pgTable("due_diligence_questionnaires", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  templateId: varchar("template_id").notNull().references(() => dueDiligenceQuestionnaireTemplates.id, { onDelete: "cascade" }),
  projectId: varchar("project_id").notNull().references(() => investmentProjects.id, { onDelete: "cascade" }),
  respondentName: text("respondent_name").notNull(),
  respondentTitle: text("respondent_title"),
  respondentEmail: text("respondent_email"),
  respondentContactId: varchar("respondent_contact_id").references(() => projectContacts.id, { onDelete: "set null" }),
  status: text("status").$type<DueDiligenceQuestionnaireStatus>().notNull().default("not_started"),
  ownerUserId: varchar("owner_user_id").references(() => users.id, { onDelete: "set null" }),
  dueDate: date("due_date"),
  notes: text("notes"),
  sentAt: timestamp("sent_at"),
  signedAt: timestamp("signed_at"),
  receivedByCounselAt: timestamp("received_by_counsel_at"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
}, (t) => ({
  projectIdx: index("due_diligence_questionnaires_project_idx").on(t.projectId),
  templateIdx: index("due_diligence_questionnaires_template_idx").on(t.templateId),
}));
export type DueDiligenceQuestionnaire = typeof dueDiligenceQuestionnaires.$inferSelect;

export const dueDiligenceQuestionnaireResponses = pgTable("due_diligence_questionnaire_responses", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  questionnaireId: varchar("questionnaire_id").notNull().references(() => dueDiligenceQuestionnaires.id, { onDelete: "cascade" }),
  projectId: varchar("project_id").notNull().references(() => investmentProjects.id, { onDelete: "cascade" }),
  questionKey: text("question_key").notNull(),
  answerBool: boolean("answer_bool"),
  answerText: text("answer_text"),
  answerRows: jsonb("answer_rows").$type<string[][] | null>(),
  updatedById: varchar("updated_by_id").references(() => users.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
}, (t) => ({
  questionnaireIdx: index("due_diligence_questionnaire_responses_q_idx").on(t.questionnaireId),
  uniq: uniqueIndex("due_diligence_questionnaire_responses_uniq").on(t.questionnaireId, t.questionKey),
}));
export type DueDiligenceQuestionnaireResponse = typeof dueDiligenceQuestionnaireResponses.$inferSelect;

export const dueDiligenceFiles = pgTable("due_diligence_files", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  projectId: varchar("project_id").notNull().references(() => investmentProjects.id, { onDelete: "cascade" }),
  itemId: varchar("item_id").references(() => dueDiligenceItems.id, { onDelete: "cascade" }),
  questionnaireId: varchar("questionnaire_id").references(() => dueDiligenceQuestionnaires.id, { onDelete: "cascade" }),
  questionKey: text("question_key"),
  fileName: text("file_name").notNull(),
  filePath: text("file_path").notNull(),
  fileSize: bigint("file_size", { mode: "number" }),
  contentType: text("content_type"),
  label: text("label"),
  uploadedById: varchar("uploaded_by_id").references(() => users.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (t) => ({
  projectIdx: index("due_diligence_files_project_idx").on(t.projectId),
}));
export type DueDiligenceFile = typeof dueDiligenceFiles.$inferSelect;

export const dueDiligenceEvents = pgTable("due_diligence_events", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  projectId: varchar("project_id").notNull().references(() => investmentProjects.id, { onDelete: "cascade" }),
  itemId: varchar("item_id").references(() => dueDiligenceItems.id, { onDelete: "cascade" }),
  questionnaireId: varchar("questionnaire_id").references(() => dueDiligenceQuestionnaires.id, { onDelete: "cascade" }),
  kind: text("kind").$type<DueDiligenceEventKind>().notNull(),
  body: text("body"),
  meta: jsonb("meta").$type<Record<string, unknown> | null>(),
  actorUserId: varchar("actor_user_id").references(() => users.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (t) => ({
  projectIdx: index("due_diligence_events_project_idx").on(t.projectId, t.createdAt),
}));
export type DueDiligenceEvent = typeof dueDiligenceEvents.$inferSelect;
